import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBlocker } from 'react-router';
import { GraphCanvas } from '../components/GraphCanvas';
import { pruneExcessiveLeaves } from '../utils/graphLimiters';
import { riskFieldsFor } from '../utils/riskFields';
import { sanctionBatchTotal, sanctionHitsFromBatch } from '../utils/sanctionsBatch';
import { detectSubjectIsland } from '../utils/placementClusters';
import { logEdge, logNode, flushSummary, logConsolidation, logCanvasState, logTxStore, logTriage, logEntityEnrich, logInspector, logTracePath, logStoreReset } from '../utils/canvasDebug';
import { recordUserAction, recordSSEEvent } from '../utils/pipelineRecorder';
import { formatConsolidatedLabel, formatCurrency, consolidationKey, consolidateExistingEdges, rehydrateEdgeLabels, tsToMillis, isDustEdge, getUsdEquivalent, resolveUsd, DUST_USD_THRESHOLD, magnitudeTier, type TxEntry } from '../utils/edgeConsolidation';
import { normalizeVisibilityOnLoad, normalizeVisibilityForSerialize } from '../utils/snapshotVisibility';
import { stripDataLayerInPlace } from '../utils/stripDataLayer';
import type { GraphCanvasRef, RearrangeMode } from '../components/GraphCanvas';
import { CanvasNodeIntent, CanvasEdgeIntent } from '../types/canvasIntent';
import { NodeHoverActions } from '../components/NodeHoverActions';
import type { NodeBBox } from '../components/NodeHoverActions';
import { GraphLegend } from '../components/GraphLegend';
import { EdgeContextMenu } from '../components/EdgeContextMenu';
import { NodeContextMenu } from '../components/NodeContextMenu';
import { addressService, watchlistService, flagService, p2pService, entityService, investigationService, caseService, searchService, analyticsService, identityService, transactionService, demoService, investigationAnalysisService, adminService } from '../services/api';
import { useForensicOverlayStore } from '../stores/useForensicOverlayStore';
import { buildForensicElements, decorateExistingElementsWithOverlay } from '../utils/forensicOverlayElements';
import { bundleStarburstEdges, expandStarburstClusterDetailed } from '../utils/bundleStarburstEdges';
import NoticeModal from '../components/NoticeModal';
import type { NoticeTransaction, NoticeCounterparty } from '../components/NoticeModal';
import BatchNoticeModal from '../components/BatchNoticeModal';
import { Coins, AlertTriangle, ArrowUpRight, ArrowDownLeft, Clock, Wand2, Eye, FileText, Zap, ListPlus, CheckCircle, Search, Copy, ExternalLink, Tag, Minimize2, Maximize2, X, Film } from 'lucide-react';
import { PlaybackControls } from '../components/PlaybackControls';
import { TimelineBar } from '../components/TimelineBar';
import { AutoCaseNotification } from '../components/AutoCaseNotification';
import { AnalysisModal } from '../components/AnalysisModal';
import { DemoGuide } from '../components/DemoGuide';
import { useConfirmDialog, usePromptDialog } from '../components/DialogHost';
import ExposurePanel from '../components/ExposurePanel';
import { WorkbenchHeader } from '../components/workbench/WorkbenchHeader';
import { openInFusion } from '../components/fusion/fusionLink';
import { StashTray } from '../components/workbench/StashTray';
// StashedItem / stash state was removed in Phase 1 — stash is now owned by useStashStore via canvasController.
import { useTransactionStore, toExpandedTx, toExpandedTxs } from '../stores/useTransactionStore';
import { safeStr, toErrorMessage, cleanTokenSymbol } from '../utils/safeStr';
import {
    hubExpandLimit,
    isTxLoadNetworkFailure,
    TX_LOAD_RETRY_LIMIT,
    txLoadFailureToast,
    isHotWalletNode,
} from '../utils/hubExpand';
import { coverageAfterLoad, isExhausted, isHubCapNotice, NEXT_TX_BATCH } from '../utils/txCoverage';
import { isMockDataMode, wantsCacheOnly, MOCK_DEMO_TX_TOAST } from '../utils/demoTxLoad';
import {
    readWorkbenchDataMode,
    writeWorkbenchDataMode,
    MOCK_MODE_TOAST,
    CACHED_MISS_TOAST,
    type WorkbenchDataMode,
} from '../utils/workbenchDataMode';
import { CaseSelectionModal } from '../components/workbench/CaseSelectionModal';
import { consumeWorkbenchRestoreAfterAuth, hasActiveSession, isAppNavigating, SESSION_ENDED_MESSAGE } from '../utils/session';
import {
    GUEST_LIVE_BLOCKED_MESSAGE,
    GUEST_SIGN_IN_MESSAGE,
    isGuestDemo,
} from '../utils/guestDemo';
import { GraphFilterBar, EMPTY_FILTERS } from '../components/workbench/GraphFilterBar';
import { GraphFindBox } from '../components/workbench/GraphFindBox';
import { buildTxCsvRow, spreadsheetMl, TX_CSV_HEADER, txExportCells } from '../utils/workbenchCsv';
import { clearWorkbenchCanvasOnly } from '../utils/clearWorkbenchCanvas';
import type { GraphFilters } from '../components/workbench/GraphFilterBar';
import { computeTierMap, isTierFilterEngaged, passesHighRiskOnly, passesRiskLevelFilter, resolveEdgeClickSelection, tierHides } from '../utils/graphFilterLogic';
import { TIER1_ENTITY_TYPES, addToCanvas, normalizeCanvasAddr, normalizeEntityType, scheduleEdgeBackfill, computeEdgeLabel, retargetAddressChain, normalizeChain, resolveUsableChain, isPlaceholderChain } from '../utils/canvasSync';
import { inferRoleFromEdgeDirection } from '../constants/placementRoles';
import { enrichExpandedTransactions } from '../utils/enrichExpandedTransactions';
import {
    computeLinksMemoVersion,
    loadLinksMemo,
    saveLinksMemo,
    clearLinksMemoForInvestigation,
} from '../utils/linksSessionMemo';
import { isPlaceholderId, isPlaceholderNode, preservePlaceholderFields } from '../utils/placeholderNode';
import { toast } from 'sonner';
import { caseCreationErrorMessage, isAuthenticationError, isAuthorizationError } from '../utils/caseSaveErrors';
import { NodeInspector } from '../components/NodeInspector';
import { InfoHint } from '../components/InfoHint';
import { useGraphOverlay } from '../hooks/useGraphOverlay';
import { useAutoInvestigation } from '../hooks/useAutoInvestigation';
import { useEntityEnrichment } from '../hooks/useEntityEnrichment';
import { GraphOverlayLegend } from '../components/workbench/GraphOverlayLegend';
import { GraphMinimap } from '../components/workbench/GraphMinimap';
import { KeyboardShortcutsOverlay } from '../components/workbench/KeyboardShortcutsOverlay';
import { GraphSummaryTrayButton } from '../components/workbench/GraphSummaryTrayButton';
import { GraphSummaryPanel } from '../components/workbench/GraphSummaryPanel';
import {
    isAiUnavailable, aiErrorText, readAiMeta, buildAiTraceElements,
    corridorStatus, corridorNoEvidenceMessage, caseScopedErrorNotice, CASE_REQUIRED_REASON, aiUnavailableText } from '../components/workbench/SyndicateCorridorCard';
import type { MacroAnalysisType, MacroAiResults } from '../components/NodeInspector/types';
import { getWorkbenchShortcutAction } from '../components/workbench/workbenchShortcutKeys';
import { useMultiSelect } from '../hooks/useMultiSelect';
import { useSelectionStore } from '../stores/useSelectionStore';
import { useGraphStore } from '../stores/useGraphStore';
import { useWorkbenchSessionStore } from '../stores/useWorkbenchSessionStore';
import {
    WORKBENCH_DRAFT_KEY,
    clearWorkbenchDraft,
    draftMatchesCase,
    readWorkbenchDraft,
    writeWorkbenchDraft,
    shouldWriteDraftOnLeave,
} from '../data/workbenchDraft';
import { resolveReturnSession } from '../data/workbenchReturn';
import { useCanvasViewMemory } from '../hooks/useCanvasViewMemory';
import { clearCanvasView } from '../utils/canvasViewMemory';
import { discardWorkbenchSession } from '../utils/discardWorkbenchSession';
import { useStashStore } from '../stores/useStashStore';
import { usePendingLayoutStore } from '../stores/usePendingLayoutStore';
import { beginControlledWrite, endControlledWrite } from '../services/canvasControlledWrite';
import { canvasController } from '../services/canvasController';
import { markSuspicious, unmarkAll } from '../services/flagOps';
import { useFilterStore } from '../stores/useFilterStore';
import { useFtmStore } from '../stores/useFtmStore';
import { useInvestigationCacheStore } from '../stores/useInvestigationCacheStore';
import { useBridgeInsightStore } from '../stores/useBridgeInsightStore';
import { usePanelStore } from '../stores/usePanelStore';
import { useTriageStore, getIsTriageMatch, getTriageRiskScore } from '../stores/useTriageStore';
import type { TriagePayload } from '../stores/useTriageStore';
import type { ComplianceHit } from '../stores/usePanelStore';
import { useInvestigationStore } from '../stores/useInvestigationStore';
import { MultiSelectView } from '../components/workbench/MultiSelectView';
import { CaseWalletsPanel } from '../components/workbench/CaseWalletsPanel';
import { planCaseWalletSelect, type CaseWalletRow } from '../components/workbench/caseWallets';
import {
    INSTAGRAM_PUSH_KEY, addToWatchlistForCase, instagramPushCase, parseInstagramPush,
} from '../utils/workbenchCaseLink';
import { HolisticTraceModal } from '../components/workbench/HolisticTraceModal';
import { useHolisticStore } from '../stores/useHolisticStore';
import { demoGraphs } from '../data/demoGraphs';
import { CsvImportModal } from '../components/cases/CsvImportModal';
import type { ParsedAddress } from '../components/cases/CsvImportModal';
import type { DemoDataMode } from '../data/demoGraphs';
import { calculateBetweenness, calculateCommunities, calculatePageRank, type GraphScore } from '../utils/graphAnalytics';
import { REPORT_TYPES, buildReportTitle } from '../utils/reportArtifacts';
// ── Shared types (extracted to types/graph.ts for cross-component use) ──
import type {
    Extensible,
    CytoscapeElement,
    GraphNodeData,
    TransactionEvidence,
    GraphEdgeData,
    IntelligenceAttribution,
    ScanTypology,
} from '../types/graph';

// ComplianceHit and TriagePayload types are now imported from stores

// Chain detection — see frontend/src/utils/chainDetect.ts for the canonical
// implementation that mirrors backend `address_utils.detect_chain_candidates`.
// detectChain() retains the legacy 'ETH' fallback for the ~22 callsites below
// that assume a non-null string. Use detectChainCandidates() for the omnibar
// path that needs to disambiguate the EVM family via the chain_probe service.
import { detectChain, detectChainStrict, detectChainCandidates, coerceEvmAddress } from '../utils/chainDetect';
import { detectInputType, resolveOmnibarChainHint } from '../utils/WorkbenchSearch';
import { bareWalletAddress } from '../case-canvas/entityJoin';

const normalizeAddressInput = (value: string): string => {
    // Strip Fusion canvas prefixes (ent:wallet: / TRX:) before EVM coerce.
    const stripped = bareWalletAddress((value || '').trim());
    const normalized = coerceEvmAddress(stripped);
    if (/^bc1/i.test(normalized)) return normalized.toLowerCase();
    if (/^0x[0-9a-fA-F]{40}$/.test(normalized)) return normalized.toLowerCase();
    return normalized;
};

// Node types that are NOT on-chain addresses and must never be sent to the
// address-lookup / entity-enrichment / auto-connection APIs (they'd be queried
// under a bogus default chain). UPI VPAs and Instagram social_handle hubs are
// first-class off-chain canvas nodes.
const NON_ADDRESS_NODE_TYPES = new Set(['upi', 'social_handle', 'phone', 'email', 'handle']);

// Helper to format currency based on chain
// Local definition removed to use imported formatCurrency from edgeConsolidation.ts

const normalizeStringArray = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const item of value) {
        if (typeof item !== 'string') continue;
        const trimmed = item.trim();
        if (!trimmed) continue;
        const key = trimmed.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(trimmed);
    }
    return out;
};

const normalizeAttributions = (value: unknown): IntelligenceAttribution[] => {
    if (!Array.isArray(value)) return [];

    const entries: IntelligenceAttribution[] = [];
    const seen = new Set<string>();

    for (const raw of value) {
        if (!raw || typeof raw !== 'object') continue;
        const rec = raw as Record<string, unknown>;
        const key = String(rec.key || '').trim();
        const attrValue = String(rec.value ?? '').trim();
        if (!key || !attrValue) continue;

        const dedupeKey = `${key.toLowerCase()}|${attrValue.toLowerCase()}|${String(rec.source || '').toLowerCase()}|${String(rec.evidence || '').toLowerCase()}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);

        const confidenceRaw = rec.confidence;
        let confidence: number | undefined = undefined;
        if (typeof confidenceRaw === 'number' && Number.isFinite(confidenceRaw)) {
            confidence = Math.max(0, Math.min(1, confidenceRaw));
        } else if (typeof confidenceRaw === 'string') {
            const parsed = Number(confidenceRaw);
            if (Number.isFinite(parsed)) confidence = Math.max(0, Math.min(1, parsed));
        }

        entries.push({
            key,
            value: attrValue,
            source: String(rec.source || '').trim() || 'unknown',
            confidence,
            evidence: String(rec.evidence || '').trim(),
            timestamp: String(rec.timestamp || '').trim(),
            analyst_or_system: String(rec.analyst_or_system || '').trim(),
        });
    }

    return entries;
};

// Helper to record search in history
// Helper to record search in history (Fixed to use searchService for Auth)
const recordSearch = async (searchType: 'address' | 'upi' | 'entity', searchQuery: string, chain: string | null, foundEntity: any, resultsCount: number = 0, onAutoCaseCreated?: (caseId: number, caseName: string) => void) => {
    try {
        const data = await searchService.recordSearch(
            searchQuery,
            searchType,
            chain || detectChain(searchQuery),
            resultsCount,
            {
                risk_score: foundEntity?.risk_score,
                entity_type: foundEntity?.entity_type || foundEntity?.category,
                balance: foundEntity?.balance
            }
        );
        if (!data) return; // Guard against null/empty response

        // Check if an auto-case was created
        if (data.auto_case_id && data.auto_case_name && onAutoCaseCreated) {
            onAutoCaseCreated(data.auto_case_id, data.auto_case_name);
        }
    } catch (error) {
        console.error('Failed to record search:', error);
    }
};

export const Workbench: React.FC = () => {
    const navigate = useNavigate();
    const { confirm, confirmDialog } = useConfirmDialog();
    const { prompt, promptDialog } = usePromptDialog();
    const [searchParams, setSearchParams] = useSearchParams();

    // ── Search param extraction (moved to top for use in hooks) ──
    const urlChain = searchParams.get('chain');
    const urlAddress = searchParams.get('address');
    const urlUpiId = searchParams.get('upi');
    const urlDemoId = searchParams.get('demo');
    const urlInvestigationId = searchParams.get('investigation_id');
    const urlCaseIdForLoad = searchParams.get('case_id');
    const urlTxHashParam = searchParams.get('tx');
    const urlQueryParam = searchParams.get('query');
    const urlPhoneParam = searchParams.get('phone');
    const urlAddressesParam = searchParams.get('addresses');
    const urlSeedsParam = searchParams.get('seeds');
    const urlFreshParam = searchParams.get('fresh');
    const urlResumeParam = searchParams.get('resume');

    // Aliases to avoid breaking existing code that expects these names,
    // while keeping the extraction at the top level.
    const chain = urlChain;
    const address = urlAddress;
    const upiId = urlUpiId;
    const demoId = urlDemoId;
    const investigationId = urlInvestigationId;
    const caseIdForLoad = urlCaseIdForLoad;
    const txHashParam = urlTxHashParam;
    const queryParam = urlQueryParam;
    const phoneParam = urlPhoneParam;
    const addressesParam = urlAddressesParam;
    const seedsParam = urlSeedsParam;
    const freshParam = urlFreshParam;
    const resumeParam = urlResumeParam;

    // Move hooks from IIFE (at ~7336) to top-level to satisfy Rules of Hooks.
    // These track canvas changes since last layout.
    const pendingRemoved = usePendingLayoutStore(s => s.removedSinceLayout);
    const pendingAdded = usePendingLayoutStore(s => s.addedSinceLayout);

    // ── Graph elements: owned by useGraphStore (Phase 0 full) ──
    const elements = useGraphStore(s => s.elements);
    const lastSavedElementCount = useRef<number>(0);
    const isDirtyRef = useRef(false);
    const justSavedRef = useRef(false);
    const skipNextUrlLoadRef = useRef(false);
    const isLoadingInvestigationRef = useRef(false);
    /** Skip duplicate loadInitialGraph when omnibar already triggered the load. */
    const pendingOmnibarLoadRef = useRef<string | null>(null);
    const markDirty = useCallback(() => { isDirtyRef.current = true; }, []);
    // Wrapper that auto-marks dirty + supports functional updaters via store.
    // Marked as a controlled write so it doesn't emit the deprecation warning
    // that `useGraphStore.setElements` fires for raw external callers. Once
    // Phase 2+ migrations are done, most callers will stop needing this path
    // and the remaining full-replace cases (load investigation, pipeline
    // replay) will go through `canvasController`.
    const setElements = useCallback((action: React.SetStateAction<CytoscapeElement[]>) => {
        if (!isLoadingInvestigationRef.current) isDirtyRef.current = true;
        const store = useGraphStore.getState();
        const next = typeof action === 'function' ? action(store.elements) : action;
        beginControlledWrite();
        try { store.setElements(next); } finally { endControlledWrite(); }
    }, []);
    const [loading, setLoading] = useState(false);
    const [accessDenied, setAccessDenied] = useState(false);
    // Privacy chain warning — shown when a tx is found on XMR/ZEC/DASH/etc.
    const [privacyChainWarning, setPrivacyChainWarning] = useState<any | null>(null);
    const [deepCheckResult, setDeepCheckResult] = useState<any | null>(null);
    const [deepCheckLoading, setDeepCheckLoading] = useState(false);
    // Session context — set by tx hash search or other non-URL flows so buttons/features work
    const [sessionChain, setSessionChain] = useState<string | null>(null);
    const [sessionAddress, setSessionAddress] = useState<string | null>(null);
    // ── Selection state: owned by useSelectionStore (Phase 0 full) ──
    const selectedNode = useSelectionStore(s => s.selectedNode);
    // Wrapper that supports both direct-set and functional-updater patterns
    const setSelectedNode = useCallback((action: React.SetStateAction<GraphNodeData | null>) => {
        const store = useSelectionStore.getState();
        const next = typeof action === 'function' ? action(store.selectedNode) : action;
        if (next === null) {
            store.clearSelection();
        } else if (next === store.selectedNode) {
            // No change — the updater returned prev (optimization)
        } else if (store.selectedNode && next.id === store.selectedNode.id) {
            // Same node, data update
            store.updateSelectedNode(next);
        } else {
            // New node
            store.selectNode(next);
        }
    }, []);
    // Global per-node transaction store — survives node switches, canvas changes, eye toggle.
    // Single source of truth for Node Inspector (Transfers + Counterparties tabs).
    const txStore = useTransactionStore();
    const getTransactions = (nodeId: string): TransactionEvidence[] => txStore.getNodeTransactions(nodeId);
    const activeTab = useSelectionStore(s => s.activeTab);
    const setActiveTab = useSelectionStore(s => s.setActiveTab);
    // ── Granular actions state: owned by usePanelStore (Phase 0 full) ──
    const actionStatuses = usePanelStore(s => s.actionStatuses);
    const findings = usePanelStore(s => s.findings);
    const panelMode = useSelectionStore(s => s.panelMode);
    const setPanelMode = useSelectionStore(s => s.setPanelMode);
    // Subscribe only to the setter — reading nodeDetailMinimized here forced a
    // full Workbench re-render on every inspector collapse/expand (slow vs sidebar).
    const setNodeDetailMinimized = useSelectionStore(s => s.setNodeDetailMinimized);
    const txFilter = useSelectionStore(s => s.txFilter);
    const setTxFilter = useSelectionStore(s => s.setTxFilter);
    const graphRef = useRef<GraphCanvasRef>(null);

    // Follow the Money last result (for report generation)
    const lastFtmResultRef = useRef<{ chain: string; address: string; response: any } | null>(null);

    // CSV Import modal
    const [showCsvImport, setShowCsvImport] = useState(false);
    const [isCheckingConnections, setIsCheckingConnections] = useState(false);

    // Per-session memo of trace-path attempts for the Links algorithm.
    // tracePath returns *multi-hop* paths; gold-path edges drawn for
    // intermediate hops never match the original src/tgt pair, so without
    // a memo every Links click re-fires the same pair-checks. Most random
    // wallet pairs have no direct connection, so 80%+ of repeated calls
    // are wasted blockchain fetches (~3s each). Key is canonical
    // 'CHAIN:lo|hi' so (A,B) and (B,A) collide.
    //
    // Persisted to localStorage keyed by investigationId + canvas version
    // (P1-CV4). Version = FNV hash of address-node ids — adding/removing a
    // wallet invalidates the memo automatically.
    const linksAttemptsRef = useRef<Map<string, 'no-path' | 'has-path'>>(new Map());
    const linksMemoMetaRef = useRef<{ investigationId: string; version: string }>({
        investigationId: '',
        version: '',
    });

    const addressNodeIdsForLinks = useMemo(
        () => elements
            .filter(el => !el.data?.source && el.data?.type === 'address' && el.data?.id)
            .map(el => String(el.data.id)),
        [elements],
    );
    // Camera and dragged-node positions survive leaving /workbench (per tab session).
    const canvasViewVersion = useMemo(() => computeLinksMemoVersion(addressNodeIdsForLinks), [addressNodeIdsForLinks]);
    useCanvasViewMemory(graphRef, elements.length > 0, canvasViewVersion);

    // Hydrate / invalidate Links memo when investigation or canvas membership changes.
    useEffect(() => {
        const invId = investigationId || 'draft';
        const version = computeLinksMemoVersion(addressNodeIdsForLinks);
        const prev = linksMemoMetaRef.current;
        if (prev.investigationId === invId && prev.version === version) return;

        // Persist outgoing memo under the previous key before switching.
        if (prev.investigationId && prev.version && linksAttemptsRef.current.size > 0) {
            saveLinksMemo(prev.investigationId, prev.version, linksAttemptsRef.current);
        }

        linksAttemptsRef.current = loadLinksMemo(invId, version);
        linksMemoMetaRef.current = { investigationId: invId, version };
    }, [investigationId, addressNodeIdsForLinks]);

    // Persist on unmount / tab hide so a refresh keeps the memo.
    useEffect(() => {
        const persist = () => {
            const { investigationId: invId, version } = linksMemoMetaRef.current;
            if (!invId || !version) return;
            saveLinksMemo(invId, version, linksAttemptsRef.current);
        };
        const onVisibility = () => {
            if (document.visibilityState === 'hidden') persist();
        };
        window.addEventListener('beforeunload', persist);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            persist();
            window.removeEventListener('beforeunload', persist);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);

    // Animation Playback State
    const [isAnimating, setIsAnimating] = useState(false);
    const [showPlayback, setShowPlayback] = useState(false);
    const [animationStep, setAnimationStep] = useState(0);
    const [animationSpeed, setAnimationSpeed] = useState(1);
    const [transactions, setTransactions] = useState<Array<{
        timestamp: Date;
        from: string;
        to: string;
        amount: number;
        currency: string;
        txHash: string;
        edgeId?: string;
    }>>([]);
    const animationTimerRef = useRef<number | undefined>(undefined);
    const triageDelayTimerRef = useRef<number | null>(null);

    // Case Selection Modal State
    const isCaseModalOpen = usePanelStore(s => s.isCaseModalOpen);
    const setIsCaseModalOpen = usePanelStore(s => s.setCaseModalOpen);
    const [pendingSaveOptions, setPendingSaveOptions] = useState<{ namePrefix?: string; notes?: string; defaultName?: string; skipPrompt?: boolean; resumeAfterModal?: boolean } | null>(null);
    // When a save is triggered with no case in the URL we open the case picker.
    // Callers that need the SAVED result (report persistence) pass
    // resumeAfterModal:true and await this promise, which resolves only after the
    // user picks/creates a case (with the result) or dismisses the modal (null).
    // Without this, those callers got an immediate null and bailed before the
    // user ever chose a case, so the report was never archived.
    const pendingSaveResolverRef = useRef<((r: { caseId: string; investigationId: string } | null) => void) | null>(null);
    const resolvePendingSave = (result: { caseId: string; investigationId: string } | null) => {
        const resolve = pendingSaveResolverRef.current;
        pendingSaveResolverRef.current = null;
        resolve?.(result);
    };

    // ── Analyze Graph Card & Clustering: owned by usePanelStore (Phase 0 full) ──
    const showAnalyzeCard = usePanelStore(s => s.showAnalyzeCard);
    const setShowAnalyzeCard = usePanelStore(s => s.setShowAnalyzeCard);
    const analyzeLoading = usePanelStore(s => s.analyzeLoading);
    const setAnalyzeLoading = usePanelStore(s => s.setAnalyzeLoading);
    const analyzeResultCounts = usePanelStore(s => s.analyzeResultCounts);
    const setAnalyzeResultCounts = usePanelStore(s => s.setAnalyzeResultCounts);
    const clusterLoading = usePanelStore(s => s.clusterLoading);
    const setClusterLoading = usePanelStore(s => s.setClusterLoading);

    // ── Auto-Investigate Card: owned by useInvestigationStore (Phase 0 full) ──
    const showAutoInvestigateCard = useInvestigationStore(s => s.showAutoInvestigateCard);
    const setShowAutoInvestigateCard = useInvestigationStore(s => s.setShowAutoInvestigateCard);
    const autoInvestigation = useAutoInvestigation();
    // Sync hook return into store so other components can read it (Phase 0 full)
    useEffect(() => {
        useInvestigationStore.getState().syncInvestigation(autoInvestigation);
    }, [autoInvestigation.sections, autoInvestigation.isRunning, autoInvestigation.isComplete, autoInvestigation.spinePromotion]);
    // ── Investigation root: owned by useInvestigationStore (Phase 0 full) ──
    const investigationRootId = useInvestigationStore(s => s.investigationRootId);
    const setInvestigationRootId = useInvestigationStore(s => s.setInvestigationRootId);

    const {
        activeOverlay,
        legend,
        isComputing,
        nodeScores,
        applyScoreOverlay,
        applyCommunityOverlay,
        clearOverlay,
        getNodeColor,
        getNodeSize,
        setComputing,
    } = useGraphOverlay();

    const multiSelect = useMultiSelect();

    // ── Analytics workspace: owned by usePanelStore (Phase 0 full) ──
    const analyticsSection = usePanelStore(s => s.analyticsSection);
    const setAnalyticsSection = usePanelStore(s => s.setAnalyticsSection);
    const inlineAiSummary = usePanelStore(s => s.inlineAiSummary);
    const setInlineAiSummary = usePanelStore(s => s.setInlineAiSummary);
    const inlineAiLoading = usePanelStore(s => s.inlineAiLoading);
    const setInlineAiLoading = usePanelStore(s => s.setInlineAiLoading);
    const inlinePatterns = usePanelStore(s => s.inlinePatterns);
    const setInlinePatterns = usePanelStore(s => s.setInlinePatterns);
    const inlinePatternsLoading = usePanelStore(s => s.inlinePatternsLoading);
    const setInlinePatternsLoading = usePanelStore(s => s.setInlinePatternsLoading);
    const graphSummaryOpen = usePanelStore(s => s.graphSummaryOpen);
    const graphSummaryMeta = usePanelStore(s => s.graphSummaryMeta);
    const nodeCompliance = usePanelStore(s => s.nodeCompliance);
    const setNodeCompliance = usePanelStore(s => s.setNodeCompliance);
    const complianceHits = usePanelStore(s => s.complianceHits);
    const setComplianceHits = usePanelStore(s => s.setComplianceHits);
    const complianceLoading = usePanelStore(s => s.complianceLoading);
    const setComplianceLoading = usePanelStore(s => s.setComplianceLoading);

    // ── Transaction selection: owned by useSelectionStore (Phase 0 full) ──
    const selectedTxs = useSelectionStore(s => s.selectedTxs);
    const setSelectedTxs = useCallback((action: React.SetStateAction<TransactionEvidence[]>) => {
        const store = useSelectionStore.getState();
        const next = typeof action === 'function' ? action(store.selectedTxs) : action;
        store.setSelectedTxs(next);
    }, []);
    const [selectedTxForNotice, setSelectedTxForNotice] = useState<TransactionEvidence | null>(null);

    // ── UI modals: owned by usePanelStore (Phase 0 full) ──
    const showHelp = usePanelStore(s => s.showHelp);
    const setShowHelp = usePanelStore(s => s.setShowHelp);
    const showLegend = usePanelStore(s => s.showLegend);
    const setShowLegend = usePanelStore(s => s.setShowLegend);
    const isNoticeModalOpen = usePanelStore(s => s.isNoticeModalOpen);
    const setNoticeModalOpen = usePanelStore(s => s.setNoticeModalOpen);

    // Graph state — ELK is the sole layout engine, no layout switching needed

    // Hover node state — tracks hovered node + its viewport bbox for flanking ← → buttons
    const [hoverNodeData, setHoverNodeData] = useState<any | null>(null);
    const [hoverNodeBBox, setHoverNodeBBox] = useState<NodeBBox | null>(null);
    // Delay-hide so mouse can move from node to the flanking buttons without them disappearing
    const hoverHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Stable ref so the viewport callback closure doesn't go stale
    const hoverNodeDataRef = useRef<any | null>(null);
    hoverNodeDataRef.current = hoverNodeData;

    // Context Menu State
    const [contextMenuNode, setContextMenuNode] = useState<GraphNodeData | null>(null);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    const [contextMenuEdge, setContextMenuEdge] = useState<GraphEdgeData | null>(null);
    const [contextMenuEdgePosition, setContextMenuEdgePosition] = useState({ x: 0, y: 0 });
    // One-shot signal to TransfersTab: auto-expand this tx_hash group on next
    // render. Set when the user opens a starburst cluster in Transfers (left-
    // click on a cluster, or right-click → "View members in Transfers").
    const [autoExpandTxHash, setAutoExpandTxHash] = useState<string | null>(null);
    // ── Edge selection: owned by useSelectionStore (Phase 0 full) ──
    const selectedEdgeIds = useSelectionStore(s => s.selectedEdgeIds);
    const setSelectedEdgeIds = useSelectionStore(s => s.setSelectedEdgeIds);
    const [edgeFocusActive, setEdgeFocusActive] = useState(false);
    // E1: Edge consolidation — owned by useSelectionStore (Phase 0 full)
    const edgeTxMap = useSelectionStore(s => s.edgeTxMap);
    const setEdgeTxMap = useCallback((action: React.SetStateAction<Map<string, any[]>>) => {
        const store = useSelectionStore.getState();
        const next = typeof action === 'function' ? action(store.edgeTxMap) : action;
        store.setEdgeTxMap(next);
    }, []);
    const selectedEdgePair = useSelectionStore(s => s.selectedEdgePair);
    const setSelectedEdgePair = useSelectionStore(s => s.setSelectedEdgePair);
    const [addedTxHashes, setAddedTxHashes] = useState<string[]>([]);
    // Set of tx hashes represented by canvas edges connected to selected node (for Transfers tab highlighting)
    const canvasTxHashes = useMemo(() => {
        if (!selectedNode) return new Set<string>();
        const hashes = new Set<string>();
        elements.forEach(el => {
            if (!el.data?.source) return; // skip nodes
            if (el.data.source !== selectedNode.id && el.data.target !== selectedNode.id) return;
            if (el.data.is_consolidated) {
                const key = `${el.data.source}->${el.data.target}`;
                (edgeTxMap.get(key) || []).forEach((tx: any) => {
                    if (tx.tx_hash) hashes.add(tx.tx_hash);
                });
            } else if (el.data.tx_hash) {
                hashes.add(el.data.tx_hash);
            }
        });
        return hashes;
    }, [selectedNode?.id, elements, edgeTxMap]);

    // ── Notice transactions: selected tx checkboxes UNION the txs implied by
    //    clicked edges. The Section 91/94 notice previously received only
    //    `selectedTxs`, so edges the officer LEFT-clicks (which populate
    //    `selectedEdgeIds` — a separate selection concept) silently dropped out
    //    of the report. Resolve each selected edge to its underlying tx(s) —
    //    consolidated edges expand via edgeTxMap, single edges carry their own
    //    tx_hash — and merge, deduped by tx_hash. (We reconcile at the notice
    //    boundary rather than mutating selectedTxs on edge-click, to avoid
    //    rippling into the canvas-emphasis / Activity-checkbox sync.)
    const noticeTransactions = useMemo<TransactionEvidence[]>(() => {
        const byHash = new Map<string, TransactionEvidence>();
        const add = (tx: any) => {
            const h = tx?.tx_hash;
            if (!h || byHash.has(h)) return;
            byHash.set(h, tx as TransactionEvidence);
        };
        selectedTxs.forEach(add);
        if (selectedEdgeIds.length) {
            const wanted = new Set(selectedEdgeIds);
            elements.forEach(el => {
                const d: any = el.data;
                if (!d?.source || !wanted.has(d.id)) return;
                if (d.is_consolidated) {
                    (edgeTxMap.get(`${d.source}->${d.target}`) || []).forEach(add);
                } else if (d.tx_hash) {
                    add({
                        tx_hash: d.tx_hash,
                        from_address: d.source,
                        to_address: d.target,
                        value: d.value,
                        token_symbol: d.token_symbol,
                        timestamp: d.timestamp,
                    });
                }
            });
        }
        return Array.from(byHash.values());
    }, [selectedTxs, selectedEdgeIds, elements, edgeTxMap]);

    // ── Notice modal: top-N counterparties + rich target metadata ──
    //
    // The modal previously received EVERY canvas node (could be 50+ for a
    // mature investigation), forcing the operator to manually un-toggle
    // the irrelevant ones. We pre-rank by risk_score and cap the default
    // payload to the 25 most material — investigators care about the
    // sanctioned/mixer/exchange counterparties, and the modal still shows
    // them as a togglable list.
    //
    // Memoised so a 500-node canvas doesn't re-build this on every paint.
    const NOTICE_RELATED_CAP = 25;
    const noticeRelatedNodes = useMemo<NoticeCounterparty[]>(() => {
        if (!selectedNode) return [];
        const selfId = selectedNode.id;
        const candidates: NoticeCounterparty[] = elements
            .filter(el => el.group === 'nodes' && el.data.id !== selfId && el.data.type !== 'placeholder')
            .map(el => {
                const intel = (el.data as any).intelligence_attributions;
                const cat = ((el.data as any).entity_category || el.data.entity_type || '').toLowerCase();
                return {
                    address: el.data.id,
                    label: el.data.label || el.data.id.slice(0, 10),
                    entity_name: el.data.entity_name,
                    entity_category: el.data.entity_type,
                    risk_score: el.data.risk_score,
                    sanctioned: !!(el.data as any).sanctioned || cat === 'sanction',
                    is_mixer: cat === 'mixer' || cat === 'tumbler',
                    is_exchange: cat === 'exchange',
                    is_bridge: cat === 'bridge',
                    intelligence_attributions: Array.isArray(intel) ? intel : [],
                } as NoticeCounterparty;
            });
        // Sort: sanctioned first, then by risk score desc, then by name presence.
        candidates.sort((a, b) => {
            if (a.sanctioned !== b.sanctioned) return a.sanctioned ? -1 : 1;
            const r = (b.risk_score || 0) - (a.risk_score || 0);
            if (r !== 0) return r;
            return (b.entity_name ? 1 : 0) - (a.entity_name ? 1 : 0);
        });
        return candidates.slice(0, NOTICE_RELATED_CAP);
    }, [selectedNode?.id, elements]);

    // Pull rich Suspect-Wallet metadata off the selected node so the
    // base notice template can render ENS, attributed entity, activity
    // summary, sanctions, prior-disclosure references, etc.
    const noticeTargetExtras = useMemo(() => {
        if (!selectedNode) return undefined;
        const node = selectedNode as any;
        return {
            ens_name: node.ens_name,
            name_service: node.name_service,
            entity_name: node.entity_name,
            entity_category: node.entity_category || node.entity_type,
            risk_score: node.risk_score,
            risk_level: node.risk_level,
            sanctioned: node.sanctioned || (node.entity_category === 'sanction'),
            sanctions_program: node.sanctions_program,
            activity_summary: (node.tx_count || node.first_seen || node.last_active)
                ? {
                    tx_count: node.tx_count,
                    first_seen: node.first_seen,
                    last_seen: node.last_active || node.last_seen,
                    total_volume: node.total_received || node.total_sent
                        ? `${(node.total_received || 0) + (node.total_sent || 0)} ${node.chain || ''}`.trim()
                        : undefined,
                }
                : undefined,
            forensic_records: Array.isArray(node.forensic_records) ? node.forensic_records : undefined,
        };
    }, [selectedNode]);

    // ── Sync derived canvasTxHashes into store for consumer components ──
    useEffect(() => {
        const nodeAddresses = new Set<string>();
        elements.forEach(el => {
            if (!el.data?.source && el.data?.address) {
                nodeAddresses.add(el.data.address);
            }
        });
        useSelectionStore.getState().syncCanvasState(nodeAddresses, canvasTxHashes);
    }, [canvasTxHashes, elements]);

    // R-CANVAS-SYNC: Ensure trace transactions from enrichment (canvasSync.ts) 
    // are correctly populated into edgeTxMap for details panel drill-down.
    useEffect(() => {
        const traceEdges = elements.filter(el => !!el.data?.source && Array.isArray(el.data.trace_transactions) && el.data.trace_transactions.length > 0);
        if (traceEdges.length === 0) return;

        setEdgeTxMap(prev => {
            let changed = false;
            const next = new Map(prev);
            for (const el of traceEdges) {
                const key = el.data.edge_tx_map_key || `${el.data.source}->${el.data.target}`;
                const txs = el.data.trace_transactions;
                const existing = next.get(key) || [];
                const existingHashes = new Set(existing.map((t: any) => t.tx_hash));
                
                const toAdd = txs.filter((t: any) => t.tx_hash && !existingHashes.has(t.tx_hash));
                if (toAdd.length > 0) {
                    next.set(key, [...existing, ...toAdd]);
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [elements, setEdgeTxMap]);

    // ── Sync selectedTxs ↔ canvas edge emphasis (unified "Select" concept) ──
    // When officer checks a tx checkbox in Transfers tab, the edge lights up on canvas.
    // When officer right-clicks "Select" on canvas edge, the tx gets checked in Activity.
    const prevSelectedTxHashesRef = useRef<Set<string>>(new Set());
    useEffect(() => {
        const cy = graphRef.current?.getCy();
        if (!cy) return;

        const currentHashes = new Set(selectedTxs.map(t => t.tx_hash).filter(Boolean));
        const prevHashes = prevSelectedTxHashesRef.current;

        // Find added and removed
        const added = [...currentHashes].filter(h => !prevHashes.has(h));
        const removed = [...prevHashes].filter(h => !currentHashes.has(h));

        if (added.length === 0 && removed.length === 0) return;

        cy.batch(() => {
            // Emphasize newly selected
            for (const hash of added) {
                // Match edges by tx_hash in data, or by edge ID containing the hash
                const edges = cy.edges().filter((e: any) => {
                    const d = e.data();
                    return d.tx_hash === hash || d.id === hash;
                });
                edges.addClass('edge-emphasized').removeClass('edge-deemphasized');
            }
            // Clear emphasis on deselected
            for (const hash of removed) {
                const edges = cy.edges().filter((e: any) => {
                    const d = e.data();
                    return d.tx_hash === hash || d.id === hash;
                });
                edges.removeClass('edge-emphasized');
            }
        });

        prevSelectedTxHashesRef.current = currentHashes;
    }, [selectedTxs]);

    // Trace path context for selected node — shows which trace paths pass through it
    const traceContext = useMemo(() => {
        if (!selectedNode || !autoInvestigation.isComplete) return null;
        const nodeId = selectedNode.id;
        const normalize = (addr: string) =>
            (addr.startsWith('0x') || addr.startsWith('0X')) ? addr.toLowerCase() : addr;
        const normalizedId = normalize(nodeId);
        const contexts: Array<{ direction: 'backward' | 'forward'; hopIndex: number; totalHops: number; entityName?: string; entityType?: string }> = [];

        const bwd = autoInvestigation.sections.backward_trace?.data;
        if (bwd?.paths) {
            bwd.paths.forEach((p: any) => {
                const path: string[] = p.path || p.addresses || [];
                const idx = path.findIndex((a: string) => normalize(a) === normalizedId);
                if (idx >= 0) {
                    contexts.push({
                        direction: 'backward',
                        hopIndex: idx,
                        totalHops: path.length - 1,
                        entityName: p.target_entity?.name || p.source?.name,
                        entityType: p.target_entity?.type || p.source?.type,
                    });
                }
            });
        }
        const fwd = autoInvestigation.sections.forward_trace?.data;
        if (fwd?.paths) {
            fwd.paths.forEach((p: any) => {
                const path: string[] = p.path || p.addresses || [];
                const idx = path.findIndex((a: string) => normalize(a) === normalizedId);
                if (idx >= 0) {
                    contexts.push({
                        direction: 'forward',
                        hopIndex: idx,
                        totalHops: path.length - 1,
                        entityName: p.target_entity?.name,
                        entityType: p.target_entity?.type,
                    });
                }
            });
        }
        return contexts.length > 0 ? contexts : null;
    }, [selectedNode?.id, autoInvestigation.isComplete, autoInvestigation.sections.backward_trace?.data, autoInvestigation.sections.forward_trace?.data]);

    // Stash state now lives in useStashStore (accessed via canvasController).
    // Phase 1: stash writers go through canvasController.stash(ids, reason, by).
    // StashTray subscribes to useStashStore directly — no props needed.
    const [demoGuideVisible, setDemoGuideVisible] = useState(false);
    const [demoGuideMinimized, setDemoGuideMinimized] = useState(false);
    const [activeDemoId, setActiveDemoId] = useState<string | null>(null);
    const [demoDataMode, setDemoDataMode] = useState<WorkbenchDataMode>(() => {
        const mode = readWorkbenchDataMode();
        // Guest sandbox never starts in Live — force Mock until Cached is chosen.
        if (isGuestDemo() && mode === 'live') {
            writeWorkbenchDataMode('mock');
            return 'mock';
        }
        return mode;
    });
    // Keep refs in sync so auto-expand after Mock→Live (setTimeout) does not
    // read a stale demoDataMode==='mock' and short-circuit the live fetch (#223).
    const activeDemoIdRef = useRef<string | null>(null);
    const demoDataModeRef = useRef<WorkbenchDataMode>(demoDataMode);
    activeDemoIdRef.current = activeDemoId;
    demoDataModeRef.current = demoDataMode;
    const [demoCachedAvailable, setDemoCachedAvailable] = useState<Set<string>>(new Set());
    const [isBatchNoticeModalOpen, setBatchNoticeModalOpen] = useState(false);
    const [noticeExchangeOverride, setNoticeExchangeOverride] = useState<string | null>(null);
    const [hotWalletModalNode, setHotWalletModalNode] = useState<any | null>(null);
    // AI-backed Macro Graph Analysis results (corridor playbook, AI-assisted
    // trace, AI-summary availability + provenance). Graph-level like
    // inlineAiSummary: cleared by resetWorkbenchToFresh, not on node change.
    const [macroAiResults, setMacroAiResults] = useState<MacroAiResults>({});
    const [pendingExpandAction, setPendingExpandAction] = useState<(() => Promise<void> | void) | null>(null);
    const [caseContext, setCaseContext] = useState<{
        firNo?: string;
        officerName?: string;
    } | null>(null);

    // Case/Investigation name display state
    const [caseName, setCaseName] = useState<string | null>(null);
    const [investigationName, setInvestigationName] = useState<string | null>(null);

    // Cached list of report artifacts for the currently-loaded investigation.
    // Populated when an investigation is loaded so the AutoInvestigateCard
    // can surface a "View saved report" link without making an extra round
    // trip per node selection.  Each entry: { id, title, report_type,
    // metadata, created_at }.
    const [savedReportArtifacts, setSavedReportArtifacts] = useState<Array<{
        id: string | number;
        title: string;
        report_type: string;
        metadata?: Record<string, any>;
        created_at?: string;
    }>>([]);

    // Stable refs for report persist (used from nested FTM / AI Summary paths
    // declared earlier in the component body than the useCallbacks).
    const persistFtmReportRef = useRef<((payload: {
        title: string;
        htmlContent: string;
        metadata?: Record<string, any>;
    }) => Promise<void>) | null>(null);
    const persistAiSummaryReportRef = useRef<((payload: {
        title: string;
        htmlContent: string;
        metadata?: Record<string, any>;
    }) => Promise<void>) | null>(null);
    const aiSummaryArchivedKeyRef = useRef<string | null>(null);

    // Auto-Case Notification State
    const [autoCaseNotification, setAutoCaseNotification] = useState<{
        caseId: number;
        caseName: string;
        address: string;
        riskScore: number;
    } | null>(null);

    // Date-Range Filter State (for expanding a node's transactions)
    const [txStartDate, setTxStartDate] = useState<string>('');
    const [txEndDate, setTxEndDate] = useState<string>('');
    const [dateRangeAllLoaded, setDateRangeAllLoaded] = useState(false);
    const cancelDateRangeRef = useRef(false);

    // ── Debug: debugMode owned by usePanelStore (Phase 0 full), trace panel is local ──
    const debugMode = usePanelStore(s => s.debugMode);
    const setDebugMode = usePanelStore(s => s.setDebugMode);
    const [lastTrace, setLastTrace] = useState<any>(null);
    const [showTracePanel, setShowTracePanel] = useState(false);

    // Merge a new backend trace into the running debug-panel trace so a
    // subsequent 0-call endpoint doesn't wipe context from a rich prior call.
    // Caps calls[] at 200 so the panel scrolls reasonably.
    const mergeApiTrace = (prev: any, next: any) => {
        if (!next) return prev;
        if (!prev) return next;
        const combined = [...(prev.calls || []), ...(next.calls || [])];
        const calls = combined.length > 200 ? combined.slice(combined.length - 200) : combined;
        return {
            ...next,
            request_id: next.request_id || prev.request_id,
            total_api_calls: (prev.total_api_calls || 0) + (next.total_api_calls || 0),
            succeeded: (prev.succeeded || 0) + (next.succeeded || 0),
            failed: (prev.failed || 0) + (next.failed || 0),
            retried: (prev.retried || 0) + (next.retried || 0),
            elapsed_ms: next.elapsed_ms ?? prev.elapsed_ms,
            calls,
            errors: [...(prev.errors || []), ...(next.errors || [])],
        };
    };

    // UPI-Wallet alias linking
    const [linkedUPIs, setLinkedUPIs] = useState<Array<{ upi_id: string; source: string; linked_at: string }>>([]);
    const [upiLinkInput, setUpiLinkInput] = useState('');
    const [upiLinkLoading, setUpiLinkLoading] = useState(false);

    // Advanced EVM tab data now self-loaded by IntelligenceTab

    // ── Triage state: owned by useTriageStore (Phase 0 full) ──
    const triageData = useTriageStore(s => s.triageData);
    const triageLoading = useTriageStore(s => s.triageLoading);
    const triageError = useTriageStore(s => s.triageError);
    const setTriageData = (d: TriagePayload | null) => { /* write via store */ useTriageStore.getState().clearTriage(); if (d) useTriageStore.setState({ triageData: d }); };
    const setTriageLoading = (l: boolean) => useTriageStore.setState({ triageLoading: l });
    const setTriageError = useTriageStore(s => s.setTriageError);

    const [traceInput, setTraceInput] = useState('');

    // ── Return to /workbench: re-seed the page session from the persisted stores ──
    // The canvas elements rehydrate on their own, but sessionChain/sessionAddress are
    // component state and the selected node is in a plain store, so a bare return
    // showed a populated canvas with a dead toolbar (hasSession false). Runs once on
    // mount, before the persist effects below can overwrite the stored values.
    const returnSeedSettledRef = useRef(false);
    useEffect(() => {
        const bare = !chain && !address && !upiId && !demoId && !investigationId && !txHashParam
            && !queryParam && !addressesParam && !seedsParam && freshParam !== '1' && resumeParam !== '1';
        const timer = setTimeout(() => { returnSeedSettledRef.current = true; }, 0);
        if (!bare) return () => clearTimeout(timer);
        const stored = useWorkbenchSessionStore.getState();
        // A different case in the URL owns its own canvas; do not seed another case's session onto it.
        if (caseIdForLoad && stored.caseId && stored.caseId !== caseIdForLoad) return () => clearTimeout(timer);
        const seed = resolveReturnSession({
            elements: useGraphStore.getState().elements,
            sessionChain: stored.sessionChain,
            sessionAddress: stored.sessionAddress,
            selectedNodeId: stored.selectedNodeId,
        });
        if (seed) {
            if (seed.chain) setSessionChain(seed.chain);
            if (seed.address) setSessionAddress(seed.address);
            if (seed.selectedNodeData && !useSelectionStore.getState().selectedNode) {
                setSelectedNode(seed.selectedNodeData);
            }
        }
        if (stored.omnibarText) setTraceInput(stored.omnibarText);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep the session store current so the next visit can re-seed.
    useEffect(() => {
        if (!sessionChain && !sessionAddress && !returnSeedSettledRef.current) return;
        useWorkbenchSessionStore.getState().setSession(sessionChain, sessionAddress);
    }, [sessionChain, sessionAddress]);
    useEffect(() => {
        if (!selectedNode && !returnSeedSettledRef.current) return;
        useWorkbenchSessionStore.getState().setSelectedNodeId(selectedNode?.id ?? null);
    }, [selectedNode?.id]);
    useEffect(() => {
        if (!traceInput && !returnSeedSettledRef.current) return;
        const t = setTimeout(() => useWorkbenchSessionStore.getState().setOmnibarText(traceInput), 300);
        return () => clearTimeout(t);
    }, [traceInput]);

    // ── Filter state: owned by useFilterStore (Phase 0 full) ──
    const graphFilters = useFilterStore(s => s.graphFilters);
    const setGraphFilters = useFilterStore(s => s.setGraphFilters);
    const filterCounts = useFilterStore(s => s.filterCounts);
    const setFilterCounts = useFilterStore(s => s.setFilterCounts);
    const showFilterBar = useFilterStore(s => s.showFilterBar);
    const setShowFilterBar = useFilterStore(s => s.setShowFilterBar);
    const followMoneyMode = useFilterStore(s => s.followMoneyMode);
    const setFollowMoneyMode = useFilterStore(s => s.setFollowMoneyMode);
    const hideDust = useFilterStore(s => s.hideDust);  // R-SSOT-4: one dust flag, canvas + inspector

    // G10: Auto-expand filter bar when graph has enough data
    const nodeCount = elements.filter(el => !el.data?.source).length;

    useEffect(() => {
        useFilterStore.getState().maybeAutoExpand(nodeCount);
    }, [nodeCount]);

    // Check if the currently selected node matches the triage data we have loaded
    const isSelectedNodeTriageMatch = !!(selectedNode?.id && triageData?.address && selectedNode.id.toLowerCase() === triageData.address.toLowerCase());
    const selectedNodeEntityName = selectedNode?.entity_name || (isSelectedNodeTriageMatch ? triageData?.summary?.entity_name : undefined);
    const selectedNodeEntityType = selectedNode?.entity_type || (isSelectedNodeTriageMatch ? triageData?.summary?.entity_type : undefined);
    const selectedNodeEntitySource = selectedNode?.entity_source || (isSelectedNodeTriageMatch ? (triageData as any)?.details?.entity?.source : undefined);
    const triageRiskScore = (isSelectedNodeTriageMatch && typeof triageData?.summary?.risk_score === 'number')
        ? triageData.summary.risk_score
        : undefined;
    const displayedRiskScore = typeof triageRiskScore === 'number'
        ? Math.max(selectedNode?.risk_score ?? 0, triageRiskScore)
        : selectedNode?.risk_score;

    // ── Notice (subpoena) target: merge exchange-typing fields from triage ──
    // For an OFF-RAMP depositor the selected node is NOT the exchange — the
    // exchange lives in the triage summary's offramp_exchanges. Prefer it so the
    // notice is addressed to the right VASP; otherwise use the node's own entity.
    const noticeTriageSummary = (isSelectedNodeTriageMatch ? triageData?.summary : undefined) as any;
    const noticeExchangeName = useMemo(() => {
        if (noticeExchangeOverride) return noticeExchangeOverride;
        if (noticeTriageSummary?.is_offramp_point
            && Array.isArray(noticeTriageSummary?.offramp_exchanges)
            && noticeTriageSummary.offramp_exchanges.length > 0) {
            return safeStr(noticeTriageSummary.offramp_exchanges[0]) || 'Unknown Exchange';
        }
        return safeStr(selectedNode?.entity_name) || 'Unknown Exchange';
    }, [noticeExchangeOverride, noticeTriageSummary, selectedNode]);
    const noticeTargetExtrasFull = useMemo(() => {
        if (!noticeTargetExtras) return undefined;
        const node = selectedNode as any;
        const s = noticeTriageSummary;
        return {
            ...noticeTargetExtras,
            wallet_role: s?.offramp_wallet_role ?? s?.wallet_role ?? node?.wallet_role,
            attribution_method: s?.attribution_method ?? node?.attribution_method,
            attribution_tier: s?.attribution_tier ?? node?.attribution_tier,
            is_offramp_point: s?.is_offramp_point ?? node?.is_offramp_point ?? false,
        };
    }, [noticeTargetExtras, noticeTriageSummary, selectedNode]);

    // ── Navigation guard: unsaved-work detection ──────────────────────
    const isSavedInvestigation = searchParams.has('investigation_id') && searchParams.has('case_id');
    const hasUnsavedWork = elements.length > 0 && (
        !isSavedInvestigation || isDirtyRef.current
    );

    // Block navigation away from /workbench when there is unsaved work
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            hasUnsavedWork &&
            currentLocation.pathname !== nextLocation.pathname
    );

    // Prevent concurrent blocker handling (race condition guard)
    const isHandlingBlockerRef = useRef(false);

    // ── sessionStorage draft helpers ────────────────────────────────
    const DRAFT_KEY = WORKBENCH_DRAFT_KEY;

    const writeDraftToSessionStorage = useCallback(() => {
        try {
            // From the router state, not window.location: on unmount the location is already the next route.
            const caseId = searchParams.get('case_id');
            const invId = searchParams.get('investigation_id');
            const ok = writeWorkbenchDraft({
                elements,
                rootChain: chain || sessionChain || null,
                rootAddress: address || sessionAddress || null,
                selectedNodeId: selectedNode?.id || null,
                caseId: caseId || null,
                investigationId: invId || null,
            });
            if (!ok) console.warn('Failed to write workbench draft');
            useWorkbenchSessionStore.getState().setCaseContext(caseId, invId);
        } catch (e) {
            console.warn('Failed to write workbench draft', e);
        }
    }, [elements, chain, address, sessionChain, sessionAddress, selectedNode?.id, searchParams]);

    // Draft on SPA navigation: when the page unmounts with unsaved work and the officer did not
    // choose to discard it (or save it), keep a draft so the next plain visit can offer it.
    // beforeunload only covers tab close and reload.
    const leaveHandledRef = useRef(false);
    const unsavedAtUnmountRef = useRef(false);
    const writeDraftAtUnmountRef = useRef<() => void>(() => {});
    unsavedAtUnmountRef.current = hasUnsavedWork;
    writeDraftAtUnmountRef.current = writeDraftToSessionStorage;
    useEffect(() => () => {
        if (shouldWriteDraftOnLeave(unsavedAtUnmountRef.current, leaveHandledRef.current)) writeDraftAtUnmountRef.current();
    }, []);

    const clearDraft = useCallback(() => {
        clearWorkbenchDraft();
    }, []);

    // ── Apply graph filters via canvasController.stash (v5.2) ─────────────
    // Filter-hidden items are STASHED (removed from elements[]) under reason
    // 'filter'. When the filter relaxes, items no longer matching are restored
    // automatically. Layout (ELK) now runs on the reduced canvas — large
    // graphs become fast to filter.
    //
    // The effect is re-entrant safe: it iterates a STABLE universe (canvas
    // elements + filter-stashed store entries), so after a stash pass the
    // next run computes the same target set and the diff is empty.
    useEffect(() => {
        const cy = graphRef.current?.getCy();
        if (!cy) return;

        const f = graphFilters;
        const dateFromTs = f.dateFrom ? new Date(f.dateFrom).getTime() / 1000 : null;
        const dateToTs = f.dateTo ? new Date(f.dateTo + 'T23:59:59').getTime() / 1000 : null;
        // Dust hiding runs independently of the other filters because it is ON by
        // default (R-SSOT-4). `hasActiveFilter` already covers the dustThresholdUsd
        // snap-stop; `dustActive` adds the binary hideDust toggle.
        const dustActive = hideDust || f.dustThresholdUsd !== null;

        let totalEdges = 0, visibleEdges = 0, totalNodes = 0, visibleNodes = 0, hiddenByTier = 0;

        // ── Stable universe = canvas + filter-stashed entries ────────────
        // Iterating only cy.nodes()/cy.edges() would cause an infinite loop: the
        // first run stashes items (shrinks cy), the second run sees a reduced
        // canvas and decides nothing needs stashing, so it restores everything,
        // then the third run stashes again, and so on.
        //
        // By iterating the union of on-canvas elements + filter-stashed store
        // entries, the universe stays constant across re-runs → same decision
        // → empty diff → loop breaks.
        const stashSnapshot = useStashStore.getState().entries;
        type UniverseItem = { id: string; data: any; group: 'nodes' | 'edges' };
        const nodeUniverse: UniverseItem[] = [];
        const edgeUniverse: UniverseItem[] = [];
        // Dedup by id — if cy and stash store briefly hold the same id
        // during the transition window (e.g. between setElements and
        // react-cytoscapejs reconciliation), we must NOT double-count.
        const seenNodeIds = new Set<string>();
        const seenEdgeIds = new Set<string>();
        cy.nodes().forEach((node: any) => {
            const d = node.data();
            if (d?.id && !seenNodeIds.has(d.id)) {
                seenNodeIds.add(d.id);
                nodeUniverse.push({ id: d.id, data: d, group: 'nodes' });
            }
        });
        cy.edges().forEach((edge: any) => {
            const d = edge.data();
            const id = edge.id();
            if (id && !seenEdgeIds.has(id)) {
                seenEdgeIds.add(id);
                edgeUniverse.push({ id, data: d, group: 'edges' });
            }
        });
        // Include filter-stashed items (and orphan edges stashed alongside them)
        // so subsequent effect runs see the same universe.
        for (const id in stashSnapshot) {
            const entry = stashSnapshot[id];
            const reasonQualifies = entry.reason === 'filter'
                || (entry.reason === 'orphan' && entry.stashedBy === 'filter');
            if (!reasonQualifies) continue;
            const d = entry.element?.data;
            if (!d?.id) continue;
            if (entry.group === 'nodes') {
                if (!seenNodeIds.has(d.id)) {
                    seenNodeIds.add(d.id);
                    nodeUniverse.push({ id: d.id, data: d, group: 'nodes' });
                }
            } else {
                if (!seenEdgeIds.has(d.id)) {
                    seenEdgeIds.add(d.id);
                    edgeUniverse.push({ id: d.id, data: d, group: 'edges' });
                }
            }
        }

        // Tier filter only activates when investigation has completed (root is known)
        // AND the user has not chosen "All nodes". Package 8: "All nodes" is the
        // default after a run unless the graph is dense enough that cluster
        // folding takes over (utils/graphFilterLogic TIER_DENSITY_THRESHOLD), or
        // the user picked a tier mode themselves.
        const hasTierFilter = isTierFilterEngaged(f, investigationRootId, nodeUniverse.length);
        const hasActiveFilter = f.dustThresholdUsd !== null || f.dateFrom || f.dateTo
            || f.riskLevels.size < 4 || f.entityTypes.size > 0
            || f.maxHops !== null || f.hideExchanges || f.showOnlyHighRisk
            || f.showNotAssessed === false
            || hasTierFilter;

        // Build adjacency from the universe (for BFS + tier computation)
        const adjacency = new Map<string, Set<string>>();
        for (const edge of edgeUniverse) {
            const s = edge.data?.source, t = edge.data?.target;
            if (!s || !t) continue;
            if (!adjacency.has(s)) adjacency.set(s, new Set());
            if (!adjacency.has(t)) adjacency.set(t, new Set());
            adjacency.get(s)!.add(t);
            adjacency.get(t)!.add(s);
        }

        // Compute hop distances from selected node via BFS (for maxHops filter)
        const hopDistances = new Map<string, number>();
        if (f.maxHops !== null && selectedNode?.id) {
            const queue: [string, number][] = [[selectedNode.id, 0]];
            hopDistances.set(selectedNode.id, 0);
            while (queue.length > 0) {
                const [nodeId, dist] = queue.shift()!;
                if (dist >= f.maxHops) continue;
                const nbrs = adjacency.get(nodeId);
                if (!nbrs) continue;
                nbrs.forEach(nb => {
                    if (!hopDistances.has(nb)) {
                        hopDistances.set(nb, dist + 1);
                        queue.push([nb, dist + 1]);
                    }
                });
            }
        }

        // ── Tier computation on the universe ─────────────────────────────
        const tierRootId = investigationRootId;
        let tierMap: Map<string, number> | null = null;
        if (hasTierFilter && tierRootId) {
            tierMap = computeTierMap(nodeUniverse, edgeUniverse, adjacency, tierRootId);
        }

        // Pass 1: Pre-filter nodes based on Risk, Entity Type, hop distance, quick-hide, tier
        const preVisibleNodes = new Set<string>();
        for (const n of nodeUniverse) {
            totalNodes++;
            const d = n.data;
            let show = true;

            if (hasActiveFilter) {
                // Hop distance filter
                if (f.maxHops !== null && selectedNode?.id) {
                    if (!hopDistances.has(d.id)) show = false;
                }

                // Risk level filter. Assessed nodes follow the four level chips;
                // never-assessed nodes (no triage result) are their own
                // "Not assessed" chip, never lumped into 'low'.
                if (show && !passesRiskLevelFilter(d, f)) show = false;

                // Entity type filter — when active, hide nodes whose type isn't in the set
                // (includes nodes with no entity_type, unless 'unknown' is in the filter set)
                if (show && f.entityTypes.size > 0) {
                    const nodeType = d.entity_type || 'unknown';
                    if (!f.entityTypes.has(nodeType)) show = false;
                }

                // Quick-hide: exchanges
                if (show && f.hideExchanges) {
                    const cat = (d.entity_category || d.entity_type || '').toLowerCase();
                    const labels = Array.isArray(d.node_labels) ? d.node_labels.join(' ').toLowerCase() : '';
                    const name = (d.entity_name || '').toLowerCase();
                    if (cat.includes('exchange') || labels.includes('exchange') || name.includes('exchange')) show = false;
                }

                // Show only high risk
                if (show && f.showOnlyHighRisk && !passesHighRiskOnly(d, f)) show = false;

                // Canvas intelligence tiering (computed inline, not from node.data)
                if (show && tierMap) {
                    const tier = tierMap.get(d.id) ?? 3;
                    if (tierHides(tier, f)) {
                        show = false;
                        hiddenByTier++; // counted so the notice can say how many
                    }
                }
            }

            if (show) preVisibleNodes.add(d.id);
        }

        // Pass 2: Pre-filter edges based on Amount, Date, Direction AND ensure endpoints are pre-visible
        const trulyVisibleEdges = new Set<string>();
        const nodesWithVisibleEdges = new Set<string>();
        // Nodes that have ANY edge in the universe (regardless of visibility) — lets us
        // distinguish a dust-orphaned node (had edges, all hidden) from a node that was
        // already isolated (e.g. a freshly added address with no transactions yet).
        const nodesWithAnyEdge = new Set<string>();

        for (const e of edgeUniverse) {
            totalEdges++;
            const d = e.data;
            nodesWithAnyEdge.add(d.source);
            nodesWithAnyEdge.add(d.target);
            let show = true;

            if (hasActiveFilter || dustActive) {
                // Must connect to visible nodes (no-op when only dust is active:
                // with no node filter every node is pre-visible, so this passes)
                if (!preVisibleNodes.has(d.source) || !preVisibleNodes.has(d.target)) show = false;

                // Dust filter — hide edges whose USD value falls below threshold.
                // Exempt: forensic-overlay edges, gold-path (trace) edges, and
                // bundle (cluster) edges. They represent investigative
                // evidence, not noise — never hide them.
                //
                // USD source: prefer value_usd; fall back to total_value_usd
                // (consolidated edges) or raw value (token amount, accepting
                // that "≥$1" on a SHIB edge means "≥1 SHIB" — caveat is
                // documented; a follow-up PR is planned to backfill value_usd
                // wherever a price is available).
                // Dust hide — ONE unified concept (R-SSOT-4). The binary `hideDust`
                // (default on, shared with the Node Inspector) hides sub-cent noise
                // via the shared isDustValue() floor; the optional dustThresholdUsd
                // graph filter widens it to a user-chosen $ amount. Forensic /
                // gold-path / bundle edges are always exempt (evidence, not noise).
                if (show && dustActive) {
                    const isForensicEvidence = !!d.forensic_record_id || !!d.forensic_edge;
                    const isGoldPath = d.edgeType === 'gold-path'
                        || (Array.isArray(d.investigation_classes) && d.investigation_classes.includes('gold-path'));
                    const isBundle = !!d.is_bundle;
                    // OSINT attribution edges (Instagram Tier-4 leads) carry no on-chain
                    // USD value by nature — they're investigative evidence, not noise.
                    // Without this exemption the dust floor hides them, orphaning the
                    // social_handle hub + lead nodes so the whole push gets stash-wiped.
                    const isOsintLink = d.edgeType === 'osint-link'
                        || (Array.isArray(d.investigation_classes) && d.investigation_classes.includes('osint-link'));
                    const exempt = isForensicEvidence || isGoldPath || isBundle || isOsintLink;
                    if (!exempt) {
                        const usd = typeof d.value_usd === 'number' ? d.value_usd
                            : typeof d.total_value_usd === 'number' ? d.total_value_usd
                            : null;
                        const rawVal = typeof d.total_value === 'number' ? d.total_value
                            : typeof d.value === 'number' ? d.value
                            : parseFloat(String(d.value ?? '0'));
                        const token = d.token_symbol || d.token || null;
                        // ONE USD-equivalent for this edge, so the binary dust floor AND
                        // the dustThresholdUsd snap-stop agree and both mean real dollars.
                        // WB07 (NS-38): the figure the edge carries first, then stablecoin at
                        // par, then the fixed native price table. An edge whose value cannot be
                        // priced is "unknown value": it is NOT dust and stays visible (the
                        // stylesheet already draws it dashed via _valueBasis = "unknown").
                        const usdVal = d._valueBasis === 'unknown' && usd === null
                            ? null
                            : resolveUsd(rawVal, token, usd);
                        const binaryDust = hideDust && usdVal !== null && Number.isFinite(usdVal) && usdVal < DUST_USD_THRESHOLD;
                        const belowThreshold = f.dustThresholdUsd !== null
                            && usdVal !== null && Number.isFinite(usdVal) && usdVal < f.dustThresholdUsd;
                        if (binaryDust || belowThreshold) show = false;
                    }
                }

                // Date filter — check all possible timestamp fields
                // Consolidated edges use firstTimestamp/lastTimestamp; individual use timestamp
                if (show && (dateFromTs || dateToTs)) {
                    const rawTs = d.timestamp ?? d.lastTimestamp ?? d.firstTimestamp;
                    let ts: number | null = null;
                    if (typeof rawTs === 'number') {
                        ts = rawTs > 1e12 ? rawTs / 1000 : rawTs; // millis→sec if needed
                    } else if (rawTs) {
                        const parsed = new Date(rawTs).getTime();
                        if (!isNaN(parsed)) ts = parsed / 1000;
                    }
                    if (ts) {
                        if (dateFromTs && ts < dateFromTs) show = false;
                        if (dateToTs && ts > dateToTs) show = false;
                    }
                    // If no timestamp data at all, keep visible (don't hide unknowns)
                }
            }

            if (show) {
                trulyVisibleEdges.add(e.id);
                nodesWithVisibleEdges.add(d.source);
                nodesWithVisibleEdges.add(d.target);
            }
        }

        // ── Compute the set of ids that SHOULD be filter-stashed ────────
        // Start from nodes that didn't pass the pre-filter.
        const shouldStashIds = new Set<string>();

        for (const n of nodeUniverse) {
            const d = n.data;
            let show = preVisibleNodes.has(d.id);

            // Orphan check: hide nodes with no visible edges, except important ones.
            if ((hasActiveFilter || dustActive) && show && !nodesWithVisibleEdges.has(d.id) && d.id !== selectedNode?.id) {
                const tier = tierMap ? (tierMap.get(d.id) ?? 3) : 3;
                const isImportantEntity = d.entity_type && TIER1_ENTITY_TYPES.has(d.entity_type);
                const isInvestigationRoot = d.id === investigationRootId;
                if (tier >= 3 && !isImportantEntity && !isInvestigationRoot) {
                    // With ONLY dust active (no explicit graph filter), hide a node only
                    // if it HAD edges that all got dust-hidden — never a pre-isolated node.
                    if (hasActiveFilter || nodesWithAnyEdge.has(d.id)) show = false;
                }
            }

            if (show) visibleNodes++;
            else shouldStashIds.add(d.id);
        }

        for (const e of edgeUniverse) {
            const d = e.data;
            if (trulyVisibleEdges.has(e.id) && !shouldStashIds.has(d.source) && !shouldStashIds.has(d.target)) {
                visibleEdges++;
            } else {
                // Edges attached to stashed nodes will be auto-orphan-stashed by
                // canvasController.stash — no need to add them here explicitly.
                if (!shouldStashIds.has(d.source) && !shouldStashIds.has(d.target)) {
                    shouldStashIds.add(e.id);
                }
            }
        }

        setFilterCounts({ totalEdges, visibleEdges, totalNodes, visibleNodes, hiddenByTier });

        // ── Diff against ACTUAL filter-stash state, apply via canvasController ──
        // We read the current filter-stashed ids straight from the store (not a
        // local ref) so that manual restores via the Stash Tray are respected:
        // if the user restored an item, the store no longer tags it as filter-
        // stashed, so `currentFilterStashed` won't include it, and we'll re-
        // stash on the next pass if it still fails the filter criteria.
        //
        // Only reason='filter' direct stashes are tracked — orphan edges are
        // auto-managed by canvasController.stash/restore.
        const currentFilterStashed = new Set<string>();
        for (const id in stashSnapshot) {
            if (stashSnapshot[id].reason === 'filter') currentFilterStashed.add(id);
        }
        const toRestore: string[] = [];
        const toStash: string[] = [];
        currentFilterStashed.forEach(id => { if (!shouldStashIds.has(id)) toRestore.push(id); });
        shouldStashIds.forEach(id => { if (!currentFilterStashed.has(id)) toStash.push(id); });

        if (toRestore.length > 0) canvasController.restore(toRestore, 'filter');
        if (toStash.length > 0) canvasController.stash(toStash, 'filter', 'filter');

        // P1 Dual-Write: Re-apply investigation_classes from element data to
        // Cytoscape. This ensures investigation styling (gold-path, exchange,
        // etc.) survives save/load.
        cy.elements().forEach((ele: any) => {
            const invClasses: string[] = ele.data('investigation_classes');
            if (Array.isArray(invClasses) && invClasses.length > 0) {
                invClasses.forEach(cls => {
                    if (!ele.hasClass(cls)) ele.addClass(cls);
                });
            }
        });
    }, [graphFilters, hideDust, elements, selectedNode?.id, investigationRootId, chain, address, sessionAddress]);

    // Sync graph panel date filter → Transfers tab date filter
    useEffect(() => {
        setTxStartDate(graphFilters.dateFrom || '');
        setTxEndDate(graphFilters.dateTo || '');
    }, [graphFilters.dateFrom, graphFilters.dateTo]);

    // ── Sync autoInvestigation hook return into useInvestigationStore (Phase 0 full) ──
    useEffect(() => {
        useInvestigationStore.getState().syncInvestigation(autoInvestigation);
    }, [autoInvestigation.sections, autoInvestigation.isRunning, autoInvestigation.isComplete, autoInvestigation.spinePromotion]);

    // Reset transaction loading state when selected node changes
    useEffect(() => {
        setDateRangeAllLoaded(false);
        cancelDateRangeRef.current = false;
        setTxStartDate('');
        setTxEndDate('');
    }, [selectedNode?.id]);

    // Keyboard shortcut: Ctrl+Shift+D toggles debug mode
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.shiftKey && e.key === 'D') {
                e.preventDefault();
                const next = !usePanelStore.getState().debugMode;
                setDebugMode(next);
                toast(next ? '🔧 Debug mode ON — API traces will appear in responses' : 'Debug mode OFF', { duration: 2000 });
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    // Keyboard shortcuts: G → Macro Graph Analysis, I → Targeted Node Intelligence.
    // Both open the docked Analysis tab (floating cards are legacy).
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const el = e.target as HTMLElement | null;
            const tag = el?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (el?.isContentEditable) return;

            const isG = e.key === 'g' || e.key === 'G';
            const isI = e.key === 'i' || e.key === 'I';
            if (!(isG || isI) || e.ctrlKey || e.metaKey || e.altKey) return;

            e.preventDefault();

            const focus = isG ? 'analyze-graph' as const : 'auto-investigate' as const;
            usePanelStore.getState().setAnalysisCardFocus(focus);
            useSelectionStore.getState().setNodeDetailMinimized(false);

            const openAnalysis = () => {
                // Defer past NodeInspector's forensic auto-route effect so our
                // 'analysis' choice wins when selectNode just changed the node.
                setTimeout(() => {
                    useSelectionStore.getState().setActiveTab('analysis');
                }, 0);
            };

            const sel = useSelectionStore.getState().selectedNode;
            if (sel) {
                openAnalysis();
                return;
            }

            const els = useGraphStore.getState().elements;
            const first = els.find(nodeEl => !nodeEl.data?.source);
            if (first?.data) {
                useSelectionStore.getState().selectNode(first.data as any);
                openAnalysis();
            } else {
                toast.info('Add an address to the canvas first');
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    // Keyboard shortcuts: Ctrl+Z undo, Ctrl+Shift+Z / Ctrl+Y redo.
    // Comparing e.key case-insensitively: Shift held flips letters to upper case
    // ('Z'), so the redo branch must accept 'Z' too. CapsLock can flip the undo
    // branch's letter to upper case as well — same fix covers both.
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

            if ((e.ctrlKey || e.metaKey) && !e.altKey) {
                const k = e.key.toLowerCase();
                if (k === 'z' && !e.shiftKey) {
                    e.preventDefault();
                    const applied = useGraphStore.getState().undo();
                    if (applied) toast.info('Undo', { duration: 1500 });
                } else if ((k === 'z' && e.shiftKey) || k === 'y') {
                    e.preventDefault();
                    const applied = useGraphStore.getState().redo();
                    if (applied) toast.info('Redo', { duration: 1500 });
                }
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    // Stable refs for File/Tools shortcuts (Ctrl+S/N, T/L/F) — handlers are
    // assigned after handleSaveState / autoTrace / etc. are defined below.
    const workbenchShortcutActionsRef = useRef<{
        save: () => void;
        newInvestigation: () => void;
        trace: () => void;
        links: () => void;
        toggleFilters: () => void;
    }>({
        save: () => {},
        newInvestigation: () => {},
        trace: () => {},
        links: () => {},
        toggleFilters: () => {},
    });

    // chain/address/upiId/demoId/investigationId/caseIdForLoad/txHashParam/
    // queryParam/phoneParam/addressesParam are declared at the top of the
    // component (hoisted so effects + hooks can read them without
    // re-extracting from searchParams).

    // Effective chain/address: URL params take priority, fall back to session state (set by tx hash search)
    const effectiveChain = chain || sessionChain;
    const effectiveAddress = address || sessionAddress;

    // Reset panel state when selected node changes
    // Most state resets are handled by useSelectionStore.resetOnNodeChange() (triggered by selectNode).
    // This effect handles only: (a) graphRef follow-money toggle, (b) conditional activeTab reset.
    useEffect(() => {
        // Reset follow-money mode so state doesn't leak between trace sessions
        if (followMoneyMode) {
            setFollowMoneyMode(false);
            graphRef.current?.toggleFollowMoney(false);
        }
        if (activeTab === 'intelligence') {
            setActiveTab('overview');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNode?.id]);

    // Sync selectedNode.expanded_transactions → global transaction store on every update.
    // This ensures any path that sets expanded_transactions on element data also feeds the store.
    useEffect(() => {
        if (!selectedNode?.id || !selectedNode.expanded_transactions?.length) return;
        txStore.appendNodeTransactions(selectedNode.id, selectedNode.expanded_transactions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNode?.id, selectedNode?.expanded_transactions]);

    // Phase 0-micro: rehydrate transactions from store when switching to a node
    // Rehydrate selectedNode.expanded_transactions from store when:
    // (a) switching to a different node, or (b) store gets new txs for current node
    const currentNodeTxCount = txStore.getTransactionCount(selectedNode?.id || '');
    useEffect(() => {
        if (!selectedNode?.id) return;
        const stored = getTransactions(selectedNode.id);
        if (!stored.length) return;
        setSelectedNode(prev => {
            if (!prev) return prev;
            const existing = prev.expanded_transactions ?? [];
            if (existing.length >= stored.length) return prev;
            const existingHashes = new Set(existing.map(t => t.tx_hash));
            const toAdd = stored.filter(t => !existingHashes.has(t.tx_hash));
            if (toAdd.length === 0) return prev;
            return { ...prev, expanded_transactions: [...existing, ...toAdd] };
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNode?.id, currentNodeTxCount]);

    // EVM chain guard for advanced tabs
    const isEvmChain = ['ETH', 'BNB', 'MATIC', 'ARB', 'BASE', 'OP', 'AVAX', 'LINEA', 'SCROLL'].includes(chain || '');
    const isDetailTab = activeTab === 'overview' || activeTab === 'counterparties' || activeTab === 'intelligence' || activeTab === 'activity' || activeTab === 'transfers' || activeTab === 'actions';
    const isAnalysisTab = false; // Analysis tabs now live in AnalyzeGraphCard overlay

    const analyticsGuides: Record<'flow' | 'patterns' | 'compliance' | 'pathing', {
        title: string;
        explainer: string;
        scenarioId: string;
        whyDemo: string;
    }> = {
        flow: {
            title: 'Flow Intelligence',
            explainer: 'Builds a narrative from graph shape, value movement, and counterparties to explain where money came from and where it exits.',
            scenarioId: 'exposure-breakdown',
            whyDemo: 'Best for showing source/destination exposure and exchange cash-out context.'
        },
        patterns: {
            title: 'Pattern Scan',
            explainer: 'Runs typology detection (e.g., pig-butchering, smurfing, fan-in, peel chains). Signals are probabilistic and should be corroborated.',
            scenarioId: 'pig-butchering',
            whyDemo: 'Shows clear escalating deposits and staged off-ramp behavior.'
        },
        compliance: {
            title: 'Compliance Screening',
            explainer: 'Screens selected nodes or the entire graph for OFAC sanctions exposure and highlights direct high-severity compliance risk.',
            scenarioId: 'sanctions-screening-demo',
            whyDemo: 'Demonstrates sanctioned entities, clean address contrast, and triage workflow.'
        },
        pathing: {
            title: 'Path Tracing',
            explainer: 'Finds explicit source→target paths or auto-traces to known exchanges. Use when investigators need evidentiary movement chains.',
            scenarioId: 'mixer-trace',
            whyDemo: 'Classic multi-hop laundering path ending at exchange off-ramp.'
        },
    };

    const launchAnalyticsDemo = async (section: 'flow' | 'patterns' | 'compliance' | 'pathing', preferredMode: DemoDataMode = 'mock') => {
        const guide = analyticsGuides[section];
        const demo = demoGraphs[guide.scenarioId];
        if (!demo) {
            toast.error('Demo scenario not found.');
            return;
        }

        const supportsMode = (m: DemoDataMode) => (demo.supportedModes || ['mock']).includes(m);
        let resolvedMode: DemoDataMode = preferredMode;

        if (resolvedMode === 'cached' && !demoCachedAvailable.has(demo.id)) {
            resolvedMode = supportsMode('mock') ? 'mock' : (supportsMode('live') ? 'live' : preferredMode);
        }

        if (!supportsMode(resolvedMode)) {
            resolvedMode = supportsMode('mock') ? 'mock' : (supportsMode('cached') ? 'cached' : 'live');
        }

        await loadDemoGraph(demo.id, resolvedMode);
        setPanelMode('analysis');
        setActiveTab('intelligence');
        setAnalyticsSection(section);
        toast.success(`Loaded demo: ${demo.title} (${resolvedMode})`);
    };

    const openDetailsPanel = () => {
        setPanelMode('details');
        if (!isDetailTab) setActiveTab('overview');
    };

    const openAnalysisPanel = () => {
        setPanelMode('analysis');
        if (!isAnalysisTab) setActiveTab('intelligence');
    };



    // Check which demos have cached real data available
    useEffect(() => {
        demoService.listCached().then(list => {
            const rows = Array.isArray(list)
                ? list
                : (Array.isArray((list as { items?: unknown })?.items) ? (list as { items: { demo_id: string }[] }).items : []);
            setDemoCachedAvailable(new Set(rows.map((c: { demo_id: string }) => c.demo_id)));
        }).catch(() => {
            // Cache endpoint not available — that's fine, cached mode just won't be offered
        });
    }, []);

    useEffect(() => {
        // Highest priority: Instagram OSINT "push to canvas" handoff (the value
        // loop). The Instagram page stashes pre-built Tier-4 nodes/edges and
        // navigates here; consume them once, then fall through to normal params.
        try {
            const rawPush = sessionStorage.getItem(INSTAGRAM_PUSH_KEY);
            if (rawPush) {
                sessionStorage.removeItem(INSTAGRAM_PUSH_KEY);
                const push = parseInstagramPush(rawPush);
                if (push) {
                    // C-03: open on the case the leads were pushed from.
                    const pushCase = instagramPushCase(caseIdForLoad, push.caseId);
                    if (pushCase.adoptIntoUrl && pushCase.caseId) {
                        const adopted = pushCase.caseId;
                        skipNextUrlLoadRef.current = true;
                        setSearchParams((prev) => {
                            const next = new URLSearchParams(prev);
                            next.set('case_id', adopted);
                            return next;
                        }, { replace: true });
                    } else if (pushCase.mismatch) {
                        toast.warning('These Instagram leads were pushed from another case. They are on the canvas, but the Workbench stays on the case in the address bar.');
                    }
                    isLoadingInvestigationRef.current = true;
                    // Backend already labelled these; skip enrichment/auto-connect
                    // so synthetic social/UPI ids never hit address APIs.
                    addNodesToCanvas(push.elements, { userAction: false, skipEnrichment: true, skipConnectionCheck: true })
                        .finally(() => { isLoadingInvestigationRef.current = false; });
                    // Bring the pushed leads into view once the layout settles.
                    // The GraphCanvas layout effect only auto-fits when the canvas
                    // was empty (isExpansion=false); when leads are pushed onto an
                    // existing investigation it preserves the prior viewport, leaving
                    // the new cluster off-screen. Fit explicitly to the pushed nodes so
                    // the value loop always surfaces the leads — this also covers the
                    // empty-canvas timing case where the first fit can run before the
                    // Cytoscape container has real dimensions (seen in headless runs).
                    const pushedIds = new Set(
                        push.elements.map((el: any) => el?.data?.id).filter(Boolean) as string[]
                    );
                    setTimeout(() => {
                        const cy = graphRef.current?.getCy();
                        if (!cy || cy.destroyed()) return;
                        const focus = cy.nodes().filter((n: any) => pushedIds.has(n.id()));
                        if (focus.length > 0) cy.fit(focus, 60);
                        else graphRef.current?.fitToView();
                    }, 700);
                    // Backfill placeholder / missing chains from address format so
                    // Overview + tx fetch never sit on "Unknown" after IG handoff.
                    for (const el of push.elements) {
                        if (el?.data?.source || el?.data?.type !== 'address' || !el?.data?.id) continue;
                        if (!isPlaceholderChain(el.data.chain)) continue;
                        const detected = detectChainStrict(el.data.id);
                        if (detected) el.data.chain = detected;
                    }
                    const firstAddr = push.elements.find((el: any) => !el?.data?.source && el?.data?.type === 'address');
                    if (firstAddr) {
                        const resolved = resolveUsableChain(
                            firstAddr.data.id,
                            firstAddr.data.chain,
                        );
                        setSessionChain(resolved);
                        setSessionAddress(firstAddr.data.id || null);
                    }
                    const nodeCount = push.elements.filter((e: any) => !e.data?.source).length;
                    toast.success(`Added ${nodeCount} Instagram lead${nodeCount === 1 ? '' : 's'} to canvas — click a wallet to Inspect / Trace`);
                    return;
                }
            }
        } catch { /* corrupt push payload — ignore */ }

        // The Instagram hand-off just put its case into the URL; the leads are
        // already on the canvas, so this re-run must not offer a draft over them.
        if (skipNextUrlLoadRef.current) {
            skipNextUrlLoadRef.current = false;
            return;
        }

        // Explicit blank-canvas entry from CaseDetail (fresh=1): wipe leftover
        // graph/draft for this case, keep case_id in the URL.
        if (freshParam === '1') {
            beginControlledWrite();
            try { useGraphStore.getState().setElements([]); } finally { endControlledWrite(); }
            useGraphStore.getState().clearHistory();
            useStashStore.getState().clearAll();
            useSelectionStore.getState().clearSelection();
            clearDraft();
            setSessionChain(null);
            setSessionAddress(null);
            useWorkbenchSessionStore.getState().setCaseContext(caseIdForLoad, null);
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete('fresh');
                return next;
            }, { replace: true });
            // Fall through so case context / forensic overlay still load.
        }

        // After idle OTP / session expiry re-login: keep the persisted canvas.
        // Returning with ?chain=&address= would otherwise call loadInitialGraph and
        // wipe the expanded graph that survived in sessionStorage.
        if (consumeWorkbenchRestoreAfterAuth()) {
            const persisted = useGraphStore.getState().elements;
            if (persisted.length > 0) {
                const session = useWorkbenchSessionStore.getState();
                const firstNode = persisted.find((el: any) => !el?.data?.source);
                const restoredChain = session.sessionChain
                    || firstNode?.data?.chain
                    || (chain ? normalizeChain(chain) : null);
                const restoredAddress = session.sessionAddress
                    || firstNode?.data?.id
                    || (address ? normalizeAddressInput(address) : null);
                setSessionChain(restoredChain);
                setSessionAddress(restoredAddress);
                if (restoredAddress) setTraceInput(String(restoredAddress));
                if (session.caseId || caseIdForLoad) {
                    useWorkbenchSessionStore.getState().setCaseContext(
                        session.caseId || caseIdForLoad || null,
                        session.investigationId || null,
                    );
                }
                toast.success('Restored Workbench session after sign-in');
                return;
            }
            const draft = readWorkbenchDraft();
            if (draft?.elements?.length && (!caseIdForLoad || draftMatchesCase(draft, caseIdForLoad))) {
                isLoadingInvestigationRef.current = true;
                setElements(draft.elements);
                isLoadingInvestigationRef.current = false;
                isDirtyRef.current = false;
                const firstDraftNode = draft.elements.find((el: any) => !el?.data?.source);
                setSessionChain(draft.rootChain || firstDraftNode?.data?.chain || null);
                setSessionAddress(draft.rootAddress || firstDraftNode?.data?.id || null);
                const node = draft.elements.find((el: any) => el.data?.id === draft.selectedNodeId && !el.data?.source);
                if (node) {
                    setSelectedNode(node.data);
                    setActiveTab('overview');
                }
                useWorkbenchSessionStore.getState().setCaseContext(
                    draft.caseId || caseIdForLoad || null,
                    draft.investigationId || null,
                );
                clearWorkbenchDraft();
                toast.success('Restored Workbench session after sign-in');
                return;
            }
            // Nothing to restore - fall through to normal URL-driven loads.
        }

        // Explicit resume from CaseDetail entry modal - restore matching draft now.
        if (resumeParam === '1' && !investigationId) {
            const draft = readWorkbenchDraft();
            if (draft && (!caseIdForLoad || draftMatchesCase(draft, caseIdForLoad))) {
                isLoadingInvestigationRef.current = true;
                setElements(draft.elements);
                isLoadingInvestigationRef.current = false;
                isDirtyRef.current = false;
                const firstDraftNode = draft.elements.find((el: any) => !el?.data?.source);
                setSessionChain(draft.rootChain || firstDraftNode?.data?.chain || null);
                setSessionAddress(draft.rootAddress || firstDraftNode?.data?.id || null);
                const node = draft.elements.find((el: any) => el.data?.id === draft.selectedNodeId && !el.data?.source);
                if (node) {
                    setSelectedNode(node.data);
                    setActiveTab('overview');
                }
                useWorkbenchSessionStore.getState().setCaseContext(
                    draft.caseId || caseIdForLoad || null,
                    draft.investigationId || null,
                );
                clearWorkbenchDraft();
                toast.success('Restored unfinished Workbench session');
            }
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete('resume');
                return next;
            }, { replace: true });
            return;
        }

        if (investigationId && caseIdForLoad) {
            // Skip re-loading if we just saved (setSearchParams triggers this effect)
            if (justSavedRef.current) { justSavedRef.current = false; return; }
            loadInvestigation(investigationId, caseIdForLoad);
            return;
        }

        if (demoId) {
            loadDemoGraph(demoId);
            return;
        }

        if (txHashParam) {
            loadTransactionHash(txHashParam, chain);
            return;
        }

        if (chain && address) {
            const loadKey = `${normalizeChain(chain)}:${normalizeAddressInput(address)}`;
            if (pendingOmnibarLoadRef.current === loadKey) {
                pendingOmnibarLoadRef.current = null;
                return;
            }
            loadInitialGraph(normalizeChain(chain), normalizeAddressInput(address));
            return;
        }

        if (upiId) {
            loadInitialUPI(upiId);
            return;
        }

        if (queryParam) {
            const normalizedQuery = normalizeAddressInput(queryParam.trim());
            if (transactionService.isTransactionHash(normalizedQuery)) {
                loadTransactionHash(normalizedQuery, chain);
                return;
            }

            if (normalizedQuery.includes('@')) {
                loadInitialUPI(normalizedQuery);
                return;
            }

            const queryChain = normalizeChain(chain || detectChain(normalizedQuery));
            loadInitialGraph(queryChain, normalizedQuery);
            return;
        }

        if (addressesParam) {
            const firstAddress = addressesParam.split(',').map(a => normalizeAddressInput(a.trim())).find(Boolean);
            if (firstAddress) {
                const normalizedChain = normalizeChain(chain || detectChain(firstAddress));
                loadInitialGraph(normalizedChain, firstAddress);
            }
            return;
        }

        // Multi-seed bulk import (forensic UFDR / E01 unified-canvas path).
        // Format: seeds=CHAIN1:addr1,CHAIN2:addr2,... — chain is required per seed
        // because the addresses can span chains (mixed-chain extraction).
        if (seedsParam) {
            const parsedSeeds = seedsParam.split(',')
                .map(s => s.trim())
                .filter(Boolean)
                .map(s => {
                    const colonIdx = s.indexOf(':');
                    if (colonIdx <= 0) return null;
                    const chainPart = s.slice(0, colonIdx).trim();
                    const addrPart = normalizeAddressInput(s.slice(colonIdx + 1).trim());
                    if (!chainPart || !addrPart) return null;
                    return { chain: normalizeChain(chainPart), address: addrPart };
                })
                .filter((s): s is { chain: string; address: string } => s !== null);

            if (parsedSeeds.length > 0) {
                (async () => {
                    const [first, ...rest] = parsedSeeds;
                    await loadInitialGraph(first.chain, first.address);

                    for (const seed of rest) {
                        try {
                            const details = await addressService.getDetails(seed.chain, seed.address, caseIdForLoad || undefined);
                            const EVM = new Set(['ETH', 'BSC', 'BNB', 'MATIC', 'ARB', 'BASE', 'OP', 'AVAX', 'FTM', 'LINEA', 'BLAST', 'SCROLL', 'ZKSYNC', 'GNOSIS', 'CELO', 'MANTLE', 'MOONBEAM']);
                            const nodeAddr = EVM.has(seed.chain.toUpperCase())
                                ? (details.address || seed.address).toLowerCase()
                                : (details.address || seed.address);
                            await addNodesToCanvas([{
                                data: {
                                    id: nodeAddr,
                                    label: formatLabel(details.address || seed.address, details.entity_name),
                                    type: 'address',
                                    balance: details.balance,
                                    total_received: details.total_received,
                                    total_sent: details.total_sent,
                                    tx_count: details.tx_count,
                                    trc20_balances: details.trc20_balances || details.token_balances,
                                    first_seen: details.first_seen || details.create_time,
                                    last_active: details.last_active,
                                    is_contract: details.is_contract,
                                    entity_name: details.entity_name,
                                    entity_type: details.entity_type,
                                    entity_tags: normalizeStringArray((details as any).tags),
                                    node_labels: normalizeStringArray((details as any).node_labels).length > 0
                                        ? normalizeStringArray((details as any).node_labels)
                                        : normalizeStringArray(details.entity_name ? [details.entity_name] : []),
                                    intelligence_attributions: normalizeAttributions((details as any).intelligence_attributions),
                                    entity_country: details.entity_country,
                                    ens_name: (details as any).ens_name,
                                    name_service: (details as any).name_service,
                                    risk_score: details.risk_score,
                                    internal_txs: details.internal_txs,
                                    token_transfers: details.token_transfers,
                                    chain: seed.chain,
                                }
                            }], { chain: seed.chain, userAction: false, skipConnectionCheck: true });
                        } catch (err) {
                            console.error(`Failed to load seed ${seed.chain}:${seed.address}`, err);
                            toast.error(`Could not load ${seed.chain}:${seed.address.slice(0, 12)}…`);
                        }
                    }
                })();
            }
            return;
        }

        if (phoneParam) {
            toast.info(`Phone lookup is not yet graph-enabled: ${phoneParam}`);
            return;
        }

        // Priority LAST: No URL params at all — check for sessionStorage draft
        // (case_id alone is allowed: CaseDetail → Workbench entry).
        if (!chain && !address && !upiId && !demoId && !investigationId && !txHashParam && !queryParam && !addressesParam && !seedsParam) {
            try {
                const draft = readWorkbenchDraft();
                // A canvas that already came back from the persisted graph needs no draft offer.
                if (draft && useGraphStore.getState().elements.length === 0) {
                    // Never restore another case's draft onto this case URL.
                    if (caseIdForLoad && !draftMatchesCase(draft, caseIdForLoad)) {
                        /* leave draft for the owning case */
                    } else if (draft.elements?.length > 0) {
                        toast('Resume previous session?', {
                            duration: 8000,
                            action: {
                                label: 'Restore',
                                onClick: () => {
                                    isLoadingInvestigationRef.current = true;
                                    setElements(draft.elements);
                                    isLoadingInvestigationRef.current = false;
                                    isDirtyRef.current = false; // Restored draft — not dirty yet
                                    // Session context drives hasSession → action buttons visibility.
                                    // Fall back to first canvas node if draft lacks root_*.
                                    const firstDraftNode = draft.elements.find((el: any) => !el?.data?.source);
                                    setSessionChain(draft.rootChain || firstDraftNode?.data?.chain || null);
                                    setSessionAddress(draft.rootAddress || firstDraftNode?.data?.id || null);
                                    const node = draft.elements.find((el: any) => el.data?.id === draft.selectedNodeId && !el.data?.source);
                                    if (node) {
                                        setSelectedNode(node.data);
                                        setActiveTab('overview');
                                    }
                                    if (draft.caseId || draft.investigationId || caseIdForLoad) {
                                        useWorkbenchSessionStore.getState().setCaseContext(
                                            draft.caseId || caseIdForLoad || null,
                                            draft.investigationId || null,
                                        );
                                    }
                                    // Re-attach case/investigation ids to the URL when restoring
                                    // a bare /workbench visit that had a draft with case context.
                                    if (!caseIdForLoad && draft.caseId) {
                                        setSearchParams((prev) => {
                                            const next = new URLSearchParams(prev);
                                            next.set('case_id', draft.caseId!);
                                            if (draft.investigationId) {
                                                next.set('investigation_id', draft.investigationId);
                                            }
                                            return next;
                                        }, { replace: true });
                                    }
                                    clearWorkbenchDraft();
                                },
                            },
                            onDismiss: () => clearWorkbenchDraft(),
                        });
                    }
                }
            } catch { /* corrupt draft — ignore */ }
        }
    }, [chain, address, upiId, investigationId, caseIdForLoad, demoId, txHashParam, queryParam, addressesParam, seedsParam, phoneParam, freshParam, resumeParam, clearDraft, setSearchParams]);

    useEffect(() => {
        const loadCaseContext = async () => {
            if (!caseIdForLoad) {
                setCaseContext(null);
                setCaseName(null);
                return;
            }
            try {
                const c = await caseService.get(caseIdForLoad);
                setCaseContext({
                    firNo: c?.fir_number || '',
                    officerName: c?.officer_name || ''
                });
                setCaseName(c?.title || null);
                useWorkbenchSessionStore.getState().setCaseContext(
                    caseIdForLoad,
                    new URLSearchParams(window.location.search).get('investigation_id'),
                );
            } catch (e) {
                console.error('Failed to load case context', e);
            }
        };

        loadCaseContext();
    }, [caseIdForLoad]);

    // ── Forensic overlay: fetch once per case and merge onto canvas ─────────
    // Data uploaded into a case is visible to EVERY investigation under that case.
    // Nodes already on canvas get decorated in-place; forensic-only nodes are auto-added.
    // See india-le-platform/docs/architecture.md → "Forensic Record Ingestion".
    useEffect(() => {
        if (!caseIdForLoad) return;
        let cancelled = false;

        (async () => {
            const overlay = await useForensicOverlayStore.getState().fetch(caseIdForLoad);
            if (cancelled || !overlay || overlay.nodes.length === 0) return;

            // Decorate nodes already on canvas (adds KYC + forensic data property)
            setElements(prev => {
                const decorated = decorateExistingElementsWithOverlay(prev, overlay);
                const existingIds = new Set(decorated.filter(e => !e.data?.source).map(e => e.data?.id));
                const newElements = buildForensicElements(overlay, existingIds);
                if (newElements.length === 0) return decorated;
                // Bundle starbursts (CoinJoin / batch / mixer co-spends) into a
                // single cluster + bundle edge before they hit the canvas.
                // Centralized for any future on-chain or algorithm-emitted
                // multi-input data — see utils/bundleStarburstEdges.ts.
                const merged = [...decorated, ...newElements];
                const bundled = bundleStarburstEdges(merged);
                if (bundled.length !== merged.length) {
                    const clusters = bundled.filter((el: any) => el.data?.type === 'cluster').length;
                    console.log(`[forensic overlay] starburst bundling: ${merged.length} → ${bundled.length} elements (${clusters} cluster${clusters === 1 ? '' : 's'})`);
                }
                return bundled;
            });
        })();

        return () => { cancelled = true; };
    }, [caseIdForLoad]);

    // When auto-investigation completes: consolidate SSE edges + mark dirty
    useEffect(() => {
        if (!autoInvestigation.isComplete) return;

        // Post-process: consolidate SSE-added edges into one per (src→tgt) pair
        setElements(prev => {
            const hasUnconsolidated = prev.some(el =>
                el.data?.source && !el.data?.is_consolidated &&
                !['gold-path', 'dex-swap', 'p2p-link', 'inferred', 'gold-path-connector'].includes(el.data?.edgeType)
            );
            if (!hasUnconsolidated) return prev;

            const consolidated = consolidateExistingEdges(prev);
            console.log(`[Investigation complete] Consolidated ${prev.length} → ${consolidated.length} elements`);
            return consolidated;
        });

        if (isSavedInvestigation) markDirty();

        // Re-run layout after consolidation settles. Late SSE arrivals (counterparty
        // triage, multi-hop exposure, BFS terminals) get added to the canvas one at
        // a time — they end up stacking on the initial layout's empty slots and
        // overlapping settled nodes (observed in investigation
        // 73197ddc-7106-45a4-bd6f-c575eb08df82). One layout pass at the end of
        // the investigation lets ELK redistribute the final element set.
        // 300ms delay gives React time to flush the consolidate setElements +
        // GraphCanvas time to sync the new elements into Cytoscape before layout reads them.
        const layoutTimer = setTimeout(() => {
            graphRef.current?.rearrangeLayout();
        }, 300);
        return () => clearTimeout(layoutTimer);
    }, [autoInvestigation.isComplete]); // eslint-disable-line react-hooks/exhaustive-deps

    // Track investigation root for canvas tiering.
    // R-AUDIT: Activate 'showIntermediateNodes' by default on investigation complete.
    // This ensures Tier 2 nodes (counterparties) remain visible while still allowing
    // the tiering system to provide structural focus.
    useEffect(() => {
        if (autoInvestigation.isComplete && effectiveAddress) {
            const rootId = effectiveAddress.startsWith('0x') || effectiveAddress.startsWith('0X')
                ? effectiveAddress.toLowerCase() : effectiveAddress;
            setInvestigationRootId(rootId);
            // Ensure counterparties stay visible — user can still toggle via GraphFilterBar
            useFilterStore.getState().mergeGraphFilters({ showIntermediateNodes: true });
        }
    }, [autoInvestigation.isComplete, effectiveAddress]);

    // Clear investigation root when a new investigation starts (prevents stale tier filtering)
    useEffect(() => {
        if (autoInvestigation.isRunning) {
            setInvestigationRootId(null);
        }
    }, [autoInvestigation.isRunning]);

    // CANVAS_PLACEMENT_LANGUAGE §3.1/§3.3 — stamp the root node with
    // placement_role='subject' so GraphCanvas honours it as the canvas
    // anchor. Frontend-side because the root arrives via triage (omnibar
    // paste) BEFORE any backend SSE — the discovery emitter never sees it.
    //
    // Three subject sources, in order of priority:
    //   1. effectiveAddress  — fresh paste / tx-hash search
    //   2. investigationRootId — saved investigation load (effectiveAddress
    //      is null on URL ?investigation_id=…&case_id=…). Set by the
    //      complete-handler at the next ~10 lines above; we read it here.
    //   3. .manually-positioned classes — multi-seed Holistic, where each
    //      seed has been pinned by the orchestrator. Anchors are honoured
    //      as subjects so the LTR layout treats them as fixed columns.
    // Idempotent: re-runs are no-ops once the role is set. The setElements
    // call short-circuits when nothing changes so we don't trigger
    // unnecessary re-renders or relayouts.
    useEffect(() => {
        // Build the candidate-id set from all known subject sources.
        const candidates = new Set<string>();
        const addCandidate = (raw: string | null | undefined) => {
            if (!raw) return;
            candidates.add(raw);
            candidates.add(raw.toLowerCase()); // EVM is stored lowered
        };
        addCandidate(effectiveAddress);
        addCandidate(investigationRootId);
        if (candidates.size === 0) return;

        setElements(prev => {
            let changed = false;
            const next = prev.map(el => {
                if (!el.data) return el;
                const id = el.data.id;
                if (!id) return el;
                const matches = candidates.has(id) || candidates.has(id.toLowerCase());
                // Holistic seed fallback — manually-positioned class indicates
                // an orchestrator-anchored seed (multi-seed flow). We only
                // promote to 'subject' if no role is set yet to avoid
                // demoting an already-assigned higher-precedence role.
                const isHolisticSeed =
                    Array.isArray(el.classes)
                        ? el.classes.includes('manually-positioned')
                        : typeof el.classes === 'string' && el.classes.includes('manually-positioned');
                if (!matches && !isHolisticSeed) return el;
                if (el.data.placement_role === 'subject') return el;
                changed = true;
                return { ...el, data: { ...el.data, placement_role: 'subject' } };
            });
            return changed ? next : prev;
        });
    }, [effectiveAddress, investigationRootId, elements]);

    // Load UPI aliases when a node is selected
    useEffect(() => {
        if (selectedNode && selectedNode.id && selectedNode.type !== 'upi') {
            p2pService.getWalletLinkedUPIs(selectedNode.id).then(data => {
                setLinkedUPIs(data.linked_upis || []);
            }).catch(() => setLinkedUPIs([]));
        } else {
            setLinkedUPIs([]);
        }
        setUpiLinkInput('');
    }, [selectedNode?.id]);

    const handleLinkUPI = async () => {
        if (!selectedNode || !upiLinkInput.trim()) return;
        setUpiLinkLoading(true);
        try {
            await p2pService.linkUpiToWallet(upiLinkInput.trim(), selectedNode.id, 'manual_investigation');
            // Refresh linked UPIs
            const data = await p2pService.getWalletLinkedUPIs(selectedNode.id);
            setLinkedUPIs(data.linked_upis || []);
            setUpiLinkInput('');
            toast.success(`UPI ${upiLinkInput.trim()} linked to address`);
        } catch (err: any) {
            toast.error(err?.response?.data?.detail || 'Failed to link UPI');
        } finally {
            setUpiLinkLoading(false);
        }
    };

    const handleUnlinkUPI = async (upiId: string) => {
        if (!selectedNode) return;
        try {
            await p2pService.unlinkUpiFromWallet(upiId, selectedNode.id);
            setLinkedUPIs(prev => prev.filter(u => u.upi_id !== upiId));
            toast.success(`UPI ${upiId} unlinked`);
        } catch (err: any) {
            toast.error(err?.response?.data?.detail || 'Failed to unlink UPI');
        }
    };

    const loadInvestigation = async (invId: string, cId: string) => {
        isLoadingInvestigationRef.current = true;
        setPrivacyChainWarning(null);
        setAccessDenied(false);
        setLoading(true);
        try {
            const inv = await investigationService.get(invId, cId);
            if (inv && inv.graph_state) {
                // Restoration Logic
                const savedState = inv.graph_state;
                let restoredElements = [];

                if (Array.isArray(savedState.elements)) {
                    // Flat elements array (CSV import, batch creation)
                    restoredElements = savedState.elements;
                } else if (savedState.elements) {
                    // Standard Cytoscape JSON export { elements: { nodes: [], edges: [] }
                    if (savedState.elements.nodes) restoredElements.push(...savedState.elements.nodes);
                    if (savedState.elements.edges) restoredElements.push(...savedState.elements.edges);
                } else if (Array.isArray(savedState)) {
                    // Raw elements array
                    restoredElements = savedState;
                } else if (savedState.nodes || savedState.edges) {
                    // Direct object with nodes/edges arrays
                    if (savedState.nodes) restoredElements.push(...savedState.nodes);
                    if (savedState.edges) restoredElements.push(...savedState.edges);
                }

                // Rebuild edge labels from underlying value/token data so saved
                // snapshots that baked "Unknown"/"UNKNOWN" into the label string
                // get refreshed through the sanitizing formatters. Idempotent
                // and skips dex-swap / p2p-link / common-attribution edges that
                // carry custom multi-segment labels.
                restoredElements = rehydrateEdgeLabels(restoredElements);
                // Strip any transient _visibility (FTM dim, SSE flicker) that
                // leaked into the saved snapshot — see snapshotVisibility.ts.
                restoredElements = normalizeVisibilityOnLoad(restoredElements);

                setElements(restoredElements);
                setEdgeTxMap(new Map()); clearDraft(); // E1: restored graphs may have pre-consolidation edges
                lastSavedElementCount.current = restoredElements.length;

                const restoredNode = restoredElements.find((el: any) => !el?.data?.source)?.data;
                if (restoredNode?.id) {
                    setSelectedNode(restoredNode as GraphNodeData);
                    setActiveTab('overview');
                    setNodeDetailMinimized(false);
                }
                if (graphRef.current) {
                    // Wait for render cycle then fit using the cytoscape instance
                    setTimeout(() => {
                        const cy = graphRef.current?.getCy();
                        cy?.fit();
                        cy?.center();
                    }, 100);
                }

                // Hydrate workbench state.
                // Session context (address + chain) drives hasSession, which
                // controls whether Trace/Investigate/Links/Analyze buttons render.
                // Fall back to the first canvas node when root_* is missing
                // (older cases, CSV imports, multi-chain investigations).
                setInvestigationName(inv.name);
                const firstNode = restoredElements.find((el: any) => !el?.data?.source);
                const derivedAddress = firstNode?.data?.id || null;
                const derivedChain = firstNode?.data?.chain
                    || (firstNode?.data?.id ? detectChain(firstNode.data.id) : null);

                setSessionAddress(inv.root_address || derivedAddress);
                setSessionChain(inv.root_chain || derivedChain);

                console.log("Restored investigation:", inv.name);

                // Hydrate auto-investigation results if saved
                if (savedState.auto_investigation) {
                    autoInvestigation.hydrate(savedState.auto_investigation);
                    if (selectedNode) setActiveTab('analysis'); else setShowAutoInvestigateCard(true);
                }

                // Hydrate Follow-the-Money per-node cache if saved
                if (savedState.ftm_cache && typeof savedState.ftm_cache === 'object') {
                    useFtmStore.getState().hydrate(savedState.ftm_cache);
                } else {
                    useFtmStore.getState().clearAll();
                }
                if (savedState.investigation_cache && typeof savedState.investigation_cache === 'object') {
                    useInvestigationCacheStore.getState().hydrate(savedState.investigation_cache);
                } else {
                    useInvestigationCacheStore.getState().clearAll();
                }

                // Fetch list of report artifacts so AutoInvestigateCard can
                // surface a "View saved report" link for nodes that already
                // have an LE report archived (Issue A).  Best-effort —
                // failure here just means the link won't show.
                try {
                    const arts = await investigationService.listReportArtifacts(cId, invId);
                    setSavedReportArtifacts(Array.isArray(arts) ? arts : []);
                } catch (artErr) {
                    console.warn('Failed to load report artifacts list', artErr);
                    setSavedReportArtifacts([]);
                }

                // Record View
                searchService.recordView('investigation', inv.id, {
                    title: inv.name,
                    case_id: inv.case_id,
                    address: inv.root_address,
                    chain: inv.root_chain
                }).catch(console.error);
            }
        } catch (e) {
            console.error("Failed to restore investigation", e);
            if (isAuthorizationError(e)) {
                setAccessDenied(true);
                setElements([]);
                toast.error('You do not have access to this investigation.');
            } else if (!isAuthenticationError(e)) {
                toast.error('Failed to load saved investigation.');
            }
        } finally {
            setLoading(false);
            isLoadingInvestigationRef.current = false;
            isDirtyRef.current = false; // Just loaded — not dirty
        }
    };

    // Normalize timestamps: backend returns unix seconds (int) for ETH/BTC, ISO strings for TRX/TON
    const toSafeDate = (ts: any): Date | null => {
        if (!ts) return null;
        if (typeof ts === 'string') return new Date(ts);
        // Unix seconds (< 1e12) vs milliseconds (>= 1e12)
        return new Date(ts < 1e12 ? ts * 1000 : ts);
    };

    const formatLabel = (address: string, entityName?: string) => {
        if (entityName) return entityName;
        if (!address) return 'Unknown';
        return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
    };

    // Entity enrichment: Batch lookup all addresses and update node data
    const enrichGraphWithEntities = async (currentElements: any[], chainOverride?: string | null) => {
        try {
            // Extract all address nodes
            const addressNodes = currentElements.filter(el =>
                el.data?.type === 'address' && el.data?.id && !isPlaceholderId(el.data.id)
            );
            if (addressNodes.length === 0) return currentElements;

            // Prepare batch lookup request
            const addresses = addressNodes.map(node => ({
                address: node.data.id,
                // Mixed-chain CSV/forensic imports carry a chain on each node.
                // Preserve it instead of forcing the current Workbench chain.
                chain: node.data.chain || chainOverride || chain || (isPlaceholderId(node.data.id) ? undefined : detectChain(node.data.id))
            }));

            // Call batch lookup API
            const entityMap = await entityService.batchLookup(addresses);
            logEntityEnrich('batchLookup', addresses.length, entityMap || {});

            // Enrich nodes with entity data
            const enrichedElements = currentElements.map(el => {
                if (el.data?.type !== 'address') return el;

                const idLower = el.data.id.toLowerCase();
                const nodeChain = String(el.data.chain || chainOverride || chain || '').toUpperCase();
                const entity = entityMap[`${nodeChain}:${idLower}`] || entityMap[idLower];
                if (!entity) return el;

                // Update node data with entity information
                return {
                    ...el,
                    data: {
                        ...el.data,
                        entity_name: entity.name,
                        entity_type: entity.category,
                        entity_category: entity.category,
                        subcategory: (entity as any).subcategory || null,
                        ...riskFieldsFor(entity.risk_score, el.data.risk_score),
                        risk_level: entity.risk_level,
                        entity_tags: entity.tags,
                        node_labels: Array.from(new Set([
                            ...normalizeStringArray(el.data.user_label ? [el.data.user_label] : []),
                            ...(normalizeStringArray(entity.node_labels).length > 0
                                ? normalizeStringArray(entity.node_labels)
                                : normalizeStringArray(entity.name ? [entity.name] : [])),
                        ])),
                        intelligence_attributions: normalizeAttributions(entity.intelligence_attributions),
                        entity_source: entity.source,
                        label: el.data.user_label || formatLabel(el.data.id, entity.name)
                    }
                };
            });

            return enrichedElements;
        } catch (e) {
            console.error('Entity enrichment failed:', e);
            return currentElements;
        }
    };

    /**
     * addNodesToCanvas — unified entry point for adding nodes/edges to the canvas.
     *
     * ALL code paths that add elements to the canvas should call this instead of
     * raw setElements(). This ensures consistent behavior across:
     *   - Omnibar address/tx hash
     *   - CSV import
     *   - Expand node (counterparties)
     *   - Load investigation (restoration)
     *   - SSE discovery
     *
     * Steps (all idempotent):
     *   1. Undo snapshot (if user-initiated)
     *   2. Deduplicate against existing canvas
     *   3. Entity enrichment (batch lookup)
     *   4. Merge into canvas
     *   5. Sync transactions to store (if provided)
     *   6. Update session context (if first nodes)
     *   7. Background auto-connection check
     */
    const addNodesToCanvas = async (
        newElements: CytoscapeElement[],
        options: {
            /** Chain for entity enrichment. Auto-detected from elements if not provided. */
            chain?: string | null;
            /** Transactions to sync to store, keyed by node address. */
            transactions?: Map<string, any[]>;
            /** Whether this is a user action (enables undo snapshot). False for loads/SSE. */
            userAction?: boolean;
            /** Skip auto-connection check (e.g. during initial load or bulk import). */
            skipConnectionCheck?: boolean;
            /** Skip entity enrichment (elements already enriched by caller). */
            skipEnrichment?: boolean;
        } = {}
    ) => {
        const { chain: chainOverride, transactions, userAction = true, skipConnectionCheck = false, skipEnrichment = false } = options;

        if (newElements.length === 0) return;

        // 1. Undo snapshot
        if (userAction) {
            useGraphStore.getState().pushSnapshot();
        }

        // 2. Entity enrichment (skip if caller already enriched)
        const enrichedChain = chainOverride || effectiveChain || null;
        const enriched = skipEnrichment ? newElements : await enrichGraphWithEntities(newElements, enrichedChain);

        const newNodes = enriched.filter(e => !e.data?.source);
        const newEdges = enriched.filter(e => e.data?.source);
        logCanvasState(`addNodesToCanvas (+${newNodes.length} nodes, +${newEdges.length} edges)`, enriched);

        // 3. Merge into canvas via unified addToCanvas utility (R-AUDIT compliance)
        setElements(prev => addToCanvas(prev, enriched, {
            chain: enrichedChain || undefined,
            filterSpam: false // R-AUDIT: Show all discovered links for forensic sanctity
        }));

        // 4. Background entity enrichment for nodes still without attribution.
        // Honor skipEnrichment (the caller already enriched / opted out), and
        // never send synthetic placeholder ids (social:/cluster:/attr-hub:/ghost:)
        // or non-address node types (upi/social_handle) to the address-enrichment
        // API — they aren't on-chain addresses and would be looked up under a
        // bogus default chain. (Instagram OSINT value loop relies on this.)
        const unknownAddressNodes = skipEnrichment ? [] : newNodes
            .filter(n => !n.data?.entity_name || n.data.entity_name === 'Unknown')
            .filter(n => !isPlaceholderId(n.data?.id) && !NON_ADDRESS_NODE_TYPES.has(String(n.data?.type)))
            .map(n => ({
                address: n.data.id as string,
                chain: (n.data.chain || enrichedChain || 'ETH') as string,
            }));
        if (unknownAddressNodes.length > 0) {
            triggerEnrichment(unknownAddressNodes);
        }

        // 5. Sync transactions to store
        if (transactions) {
            for (const [nodeAddr, txs] of transactions.entries()) {
                if (txs.length > 0) {
                    useTransactionStore.getState().appendNodeTransactions(nodeAddr, txs);
                }
            }
        }

        // 6. Update session context if first nodes
        if (!sessionChain || !sessionAddress) {
            const firstNode = enriched.find(el => !el.data?.source);
            if (firstNode?.data) {
                if (!sessionChain && firstNode.data.chain) setSessionChain(firstNode.data.chain);
                if (!sessionAddress && firstNode.data.id) setSessionAddress(firstNode.data.id);
            }
        }

        // 7. Background auto-connection check (real address nodes only — never
        // synthetic placeholder ids or non-address types; same rationale as #4).
        if (!skipConnectionCheck && elements.length > 0) {
            const newAddressNodes = enriched.filter(el => !el.data?.source && el.data?.id
                && !isPlaceholderId(el.data.id) && !NON_ADDRESS_NODE_TYPES.has(String(el.data?.type)));
            // Bulk callers set skipConnectionCheck above 10 nodes. For an
            // accepted small import, check every imported address so the UI
            // promise ("10 or fewer") matches actual behavior.
            for (const node of newAddressNodes) {
                const nodeChain = node.data.chain || enrichedChain || 'ETH';
                checkAutoConnections(node.data.id, nodeChain).catch(() => {});
            }
        }

        // 8. Edge backfill — fill direct on-chain edges between any pair
        //    of on-canvas nodes that no individual algorithm explicitly
        //    emitted (debounced + per-chain). See architecture.md §"Edge
        //    Backfill" and the "floating node" diagnosis from May 2026.
        if (!skipConnectionCheck) {
            const newAddressNodes = enriched.filter(el => !el.data?.source && el.data?.type === 'address');
            const chainsTouched = new Set<string>();
            for (const n of newAddressNodes) {
                const ch = (n.data?.chain as string) || enrichedChain || '';
                if (ch) chainsTouched.add(ch);
            }
            for (const ch of chainsTouched) {
                scheduleEdgeBackfill(
                    ch,
                    (queryChain) => useGraphStore.getState().elements
                        .filter(e => !e.data?.source
                                  && e.data?.type === 'address'
                                  && (e.data?.chain || queryChain) === queryChain
                                  && e.data?.id)
                        .map(e => e.data.id as string),
                    (backfillElements) => {
                        setElements(prev => addToCanvas(prev, backfillElements, { chain: ch }));
                    },
                );
            }
        }
    };

    const loadInitialGraph = async (cParam: string, a: string) => {
        let c = normalizeChain(cParam);
        setInvestigationName(null);
        setTraceInput(a);  // Populate omnibar so it matches what was loaded
        setPrivacyChainWarning(null);
        setLoading(true);
        try {
            const modeAtLoad = demoDataModeRef.current;
            const skipLiveChainApis = isMockDataMode(modeAtLoad) || wantsCacheOnly(modeAtLoad);

            // ── Chain probe: disambiguate EVM family addresses ────────────
            // If the address could plausibly belong to multiple chains (EVM 0x...),
            // probe RPCs in parallel to find the chain with real activity. If the
            // declared chain has zero activity but another chain does, switch.
            // For deterministic formats (TRX, BTC, DOGE, LTC, etc.) this is a
            // 0ms no-op. For EVM addresses, ~500ms first time, instant after cache.
            let resolvedChain = c;
            if (!skipLiveChainApis) {
            try {
                const candidates = detectChainCandidates(a);
                // Unambiguous non-EVM formats (BTC, SOL, TRX, …) must not keep a
                // leftover ETH session chain — that is what made the address
                // panel say SOL/BTC while Node Inspector said Ethereum.
                if (candidates.length === 1 && candidates[0] !== c.toUpperCase()) {
                    resolvedChain = candidates[0];
                    c = candidates[0];
                }
                if (candidates.length > 1) {
                    const probe = await addressService.probeChain(a);
                    const declared = c.toUpperCase();
                    const isFormatDefault = declared === candidates[0];
                    // Same 0x key is a different entity per EVM chain (ETH
                    // Binance 48 vs MATIC OKX Polygon Hot). If the user or a
                    // test row named a non-default chain, do not steal it
                    // because ETH has a higher nonce.
                    if (!isFormatDefault) {
                        resolvedChain = declared;
                    } else {
                        const declaredActivity = probe.activity_by_chain?.[declared];
                        const declaredHasActivity = declaredActivity?.has_activity === true;
                        if (!declaredHasActivity && probe.chosen_chain && probe.chosen_chain !== declared) {
                            const chosenInfo = probe.activity_by_chain?.[probe.chosen_chain];
                            if (chosenInfo?.has_activity) {
                                resolvedChain = probe.chosen_chain;
                                console.info(
                                    `[chain-probe] switched ${declared}→${probe.chosen_chain} for ${a.slice(0,12)}…`,
                                    `(nonce=${chosenInfo.nonce}, bal=${chosenInfo.balance_wei})`,
                                );
                                toast.info(
                                    `Address has no activity on ${declared} — switched to ${probe.chosen_chain} ` +
                                    `(${chosenInfo.nonce} txs)`,
                                    { duration: 6000 },
                                );
                            }
                        }
                    }
                }
            } catch (probeErr) {
                // Probe failure must never block the main flow — fall through with c
                console.debug('[chain-probe] failed, using declared chain:', probeErr);
            }
            }
            // Reassign so the rest of the function uses the resolved chain
            c = resolvedChain;

            const EVM_CHAINS_SET = new Set(['ETH', 'BSC', 'BNB', 'OPBNB', 'MATIC', 'POLYGON', 'ARB', 'BASE', 'OP', 'AVAX', 'FTM', 'LINEA', 'BLAST', 'SCROLL', 'ZKSYNC', 'GNOSIS', 'CELO', 'MANTLE', 'MOONBEAM']);
            const provisionalAddress = EVM_CHAINS_SET.has(c.toUpperCase())
                ? a.toLowerCase()
                : a;

            // Paint a provisional root immediately so Overview / triage are not
            // blocked behind getDetails (cold path was 10–30s+). Enrich below.
            const provisionalNode = {
                data: {
                    id: provisionalAddress,
                    label: formatLabel(provisionalAddress),
                    type: 'address',
                    chain: c,
                    _visibility: 1.0,
                    _details_pending: true,
                },
            };
            setElements([provisionalNode]);
            setEdgeTxMap(new Map()); clearDraft();
            if (c) setSessionChain(c);
            if (provisionalAddress) setSessionAddress(provisionalAddress);
            setSelectedNode(provisionalNode.data as GraphNodeData);
            setActiveTab('overview');
            setNodeDetailMinimized(false);
            setLoading(false);

            if (skipLiveChainApis) {
                if (isMockDataMode(modeAtLoad)) {
                    toast.info(MOCK_MODE_TOAST, { duration: 5000 });
                }
                return;
            }

            const details = await addressService.getDetails(c, a, caseIdForLoad || undefined);

            // Fix 3: Normalize EVM root node address to lowercase so it matches
            // SSE-emitted addresses (which are always lowercase for EVM chains).
            // Without this, checksum-cased root (0xBC35f8D9...) and SSE lowercase
            // (0xbc35f8d9...) create duplicate nodes on the canvas.
            const rootAddress = EVM_CHAINS_SET.has(c.toUpperCase())
                ? (details.address || a).toLowerCase()
                : (details.address || a);

            // Create initial node
            let initialElements = [
                {
                    data: {
                        id: rootAddress,
                        label: formatLabel(details.address, details.entity_name),
                        type: 'address',
                        balance: details.balance,
                        total_received: details.total_received,
                        total_sent: details.total_sent,
                        tx_count: details.tx_count,
                        trc20_balances: details.trc20_balances || details.token_balances,
                        first_seen: details.first_seen || details.create_time,
                        last_active: details.last_active,
                        is_contract: details.is_contract,
                        entity_name: details.entity_name,
                        entity_type: details.entity_type,
                        entity_tags: normalizeStringArray((details as any).tags),
                        node_labels: normalizeStringArray((details as any).node_labels).length > 0
                            ? normalizeStringArray((details as any).node_labels)
                            : normalizeStringArray(details.entity_name ? [details.entity_name] : []),
                        intelligence_attributions: normalizeAttributions((details as any).intelligence_attributions),
                        entity_country: details.entity_country,
                        ens_name: (details as any).ens_name,
                        name_service: (details as any).name_service,
                        risk_score: details.risk_score,
                        internal_txs: details.internal_txs,
                        token_transfers: details.token_transfers,
                        chain: c,
                        _visibility: 1.0,
                    }
                }
            ];

            // Enrich with database entities
            initialElements = await enrichGraphWithEntities(initialElements, c);

            // Record search in history and handle auto-case creation
            await recordSearch('address', a, c, details, 1, (caseId, caseName) => {
                setAutoCaseNotification({
                    caseId,
                    caseName,
                    address: a,
                    riskScore: details.risk_score || 0
                });
            });

            setElements(initialElements);
            if (c) setSessionChain(c);
            if (rootAddress) setSessionAddress(rootAddress);
            if (initialElements[0]?.data) {
                setSelectedNode(initialElements[0].data as GraphNodeData);
            }

            // Record view for Recently Viewed
            searchService.recordView('address', a, {
                title: details.entity_name || a,
                address: a,
                chain: c,
                label: formatLabel(a, details.entity_name),
                risk_score: details.risk_score
            }).catch(console.error);

        } catch (error: any) {
            console.error("Failed to load graph", error);
            const msg = error?.response?.data?.detail || error?.message || 'Could not load address';
            toast.error(typeof msg === 'string' ? msg : 'Could not load address — check network and try again');
        } finally {
            setLoading(false);
        }
    };

    const loadDemoGraph = async (id: string, mode?: DemoDataMode) => {
        const demo = demoGraphs[id];
        if (!demo) {
            console.warn("Unknown demo id:", id);
            return;
        }

        // Determine effective mode (header pill / session wins over demo default)
        let effectiveMode = mode || readWorkbenchDataMode();
        if (isGuestDemo() && effectiveMode === 'live') {
            toast.info(GUEST_LIVE_BLOCKED_MESSAGE, { duration: 6000 });
            effectiveMode = 'mock';
        }
        let resolvedMode = effectiveMode;
        const nextMode = effectiveMode as WorkbenchDataMode;
        writeWorkbenchDataMode(nextMode);
        demoDataModeRef.current = nextMode;
        setDemoDataMode(nextMode);
        activeDemoIdRef.current = id;
        setActiveDemoId(id);

        setPrivacyChainWarning(null);
        setLoading(true);
        try {
            let demoElements: any[] = [];
            let effectiveChain = demo.chain || 'ETH';
            let effectiveAddress = demo.address;

            if (effectiveMode === 'cached') {
                // Load pre-fetched real data from backend cache
                try {
                    const cached = await demoService.getCached(id);
                    demoElements = cached.elements || [];
                    effectiveChain = cached.chain || effectiveChain;
                    effectiveAddress = cached.address || effectiveAddress;
                    toast.success(`Loaded cached real data (${cached.element_count} elements)`);
                } catch {
                    toast.error('No cached data available — falling back to mock');
                    demoElements = demo.elements || [];
                    resolvedMode = 'mock';
                    writeWorkbenchDataMode('mock');
                    demoDataModeRef.current = 'mock';
                    setDemoDataMode('mock');
                }
            } else if (effectiveMode === 'live') {
                // Live mode: load the seed node with a real address, user expands manually.
                // UPI-only / teaching demos without a wallet seed keep their mock graph —
                // an empty address produces a broken empty-id node.
                const realAddr = demo.realAddress || demo.address || '';
                const realCh = demo.realChain || demo.chain || 'ETH';
                effectiveChain = realCh;
                if (!realAddr) {
                    demoElements = demo.elements || [];
                    effectiveAddress = demo.upi || undefined;
                    toast.info(
                        demo.upi
                            ? 'This demo is UPI-led — showing the teaching graph. Click a linked wallet to expand live chain data.'
                            : 'No live wallet seed for this demo — showing the teaching graph.',
                    );
                } else {
                    effectiveAddress = realAddr;
                    demoElements = [{
                        data: {
                            id: realAddr,
                            label: demo.title.replace(/[🇮🇳 ]/g, '').trim(),
                            type: 'address',
                            chain: realCh,
                            entity_name: demo.realSource?.split('—')[0]?.trim() || demo.title,
                            ...riskFieldsFor(undefined)
                        }
                    }];
                    toast.info(`Live mode — expand the node to fetch real-time data from ${realCh}`);
                }
            } else {
                // Mock mode — use pre-built elements
                demoElements = demo.elements || [];
            }

            const enrichedElements = resolvedMode === 'mock'
                ? demoElements
                : await enrichGraphWithEntities(demoElements, effectiveChain || null);
            setElements(enrichedElements);
            setEdgeTxMap(new Map()); clearDraft(); // E1: demo graphs use individual edges

            // Set session context so hasSession=true → action buttons visible
            if (effectiveChain) setSessionChain(effectiveChain);
            if (effectiveAddress) setSessionAddress(effectiveAddress);

            // Show demo guide if the scenario has instructions
            if (demo.howTo && demo.howTo.length > 0) {
                setDemoGuideVisible(true);
                setDemoGuideMinimized(false);
            }

            setTriageData(null);
            const focusId = effectiveAddress || demo.upi || enrichedElements.find(el => el.data?.type === 'address')?.data?.id;
            const focusNode = enrichedElements.find(el => el.data?.id === focusId)?.data;
            if (focusNode) {
                if (resolvedMode === 'mock' && (focusNode as any)._triage) {
                    setTriageData((focusNode as any)._triage as TriagePayload);
                }
                // Pre-populate expanded_transactions from demo graph edges so Txs tab is filled immediately
                const demoEdgeTxs = enrichedElements
                    .filter(el => el.data?.source && el.data?.target &&
                        (el.data.source === focusNode.id || el.data.target === focusNode.id))
                    .map(el => {
                        const isSender = el.data.source === focusNode.id;
                        const counterparty = isSender ? el.data.target : el.data.source;
                        const cpNode = enrichedElements.find(n => n.data?.id === counterparty && !n.data?.source);
                        return {
                            tx_hash: el.data.tx_hash || el.data.id,
                            from_address: el.data.source,
                            to_address: el.data.target,
                            value: typeof el.data.value === 'number' ? el.data.value : parseFloat(String(el.data.label || '0').replace(/[^0-9.]/g, '')) || 0,
                            fee: el.data.fee,
                            timestamp: el.data.timestamp || null,
                            block_height: el.data.block_height,
                            direction: isSender ? 'outgoing' : 'incoming',
                            counterparty,
                            counterparty_entity: cpNode?.data?.entity_name || null,
                            counterparty_type: cpNode?.data?.entity_type || null,
                            counterparty_risk: cpNode?.data?.risk_score,
                            token_symbol: el.data.token_symbol || null,
                        };
                    });
                const enrichedFocusNode = demoEdgeTxs.length > 0
                    ? { ...focusNode, expanded_transactions: demoEdgeTxs }
                    : focusNode;
                setSelectedNode(enrichedFocusNode);
                // Seed txStore so Transfers tab shows mock edges without clicking Load
                if (demoEdgeTxs.length > 0 && focusNode?.id) {
                    useTransactionStore.getState().setNodeTransactions(focusNode.id, demoEdgeTxs as any);
                }
                // Auto-expand real transactions only for live/cached modes or real addresses
                // Skip auto-expand for mock-only demos with synthetic addresses to avoid NOTOK errors
                const hasFakeAddress = !focusNode?.id || focusNode.id.includes('_') || focusNode.id.includes('@') || focusNode.id.length < 20;
                const shouldAutoExpand = resolvedMode !== 'mock' && !hasFakeAddress;

                if (shouldAutoExpand) {
                    const tryExpand = (attempt = 1) => {
                        if (focusNode?.id) {
                            expandNode(enrichedFocusNode);
                            toast.info('Demo loaded (auto-expanded)');
                            return;
                        }
                        if (attempt < 4) {
                            setTimeout(() => tryExpand(attempt + 1), 200 * attempt);
                        }
                    };
                    setTimeout(() => tryExpand(), 200);
                } else {
                    toast.info('Demo loaded — click "Expand" to load live transactions');
                }
            }
        } catch (error) {
            console.error("Failed to load demo graph", error);
        } finally {
            setLoading(false);
        }
    };

    // Handle demo data mode change from DemoGuide toggle
    const handleDemoModeChange = useCallback((mode: DemoDataMode) => {
        if (isGuestDemo() && mode === 'live') {
            toast.info(GUEST_LIVE_BLOCKED_MESSAGE, { duration: 6000 });
            return;
        }
        if (activeDemoId) {
            loadDemoGraph(activeDemoId, mode);
        }
    }, [activeDemoId]);

    const handleWorkbenchDataModeChange = useCallback((mode: WorkbenchDataMode) => {
        if (isGuestDemo() && mode === 'live') {
            toast.info(GUEST_LIVE_BLOCKED_MESSAGE, { duration: 6000 });
            return;
        }
        writeWorkbenchDataMode(mode);
        demoDataModeRef.current = mode;
        setDemoDataMode(mode);
        if (activeDemoId) {
            loadDemoGraph(activeDemoId, mode);
        }
    }, [activeDemoId]);

    const loadInitialUPI = async (id: string) => {
        setLoading(true);
        try {
            const profile = await p2pService.getUPIProfile(id);

            const initialElements: any[] = [
                // UPI Node
                {
                    data: {
                        id: profile.upi_id,
                        label: profile.upi_id,
                        type: 'upi',
                        risk_score: profile.risk_score,
                        reports_count: profile.reports_count
                    }
                }
            ];

            // Add linked addresses
            if (profile.linked_addresses) {
                profile.linked_addresses.forEach((link: any) => {
                    // Handle both string (legacy) and object (new) formats
                    const addr = typeof link === 'string' ? link : link.address;
                    const type = typeof link === 'string' ? 'ownership' : (link.type || 'ownership');

                    initialElements.push({
                        data: {
                            id: addr,
                            label: formatLabel(addr),
                            type: 'address',
                            ...riskFieldsFor(undefined) // never assessed here: no placeholder score
                        }
                    });

                    // Edge from Address -> UPI
                    // We use a special property 'edgeType' to style it differently in GraphCanvas
                    initialElements.push({
                        data: {
                            id: `${addr}-${profile.upi_id}`,
                            source: profile.upi_id, // UPI is the center, so source=UPI makes sense hierarchically, or bidirectional
                            target: addr,
                            label: type === 'ownership' ? 'LINKED WALLET' : 'INTERACTION',
                            edgeType: type === 'ownership' ? 'p2p-link' : 'transaction',
                            lineStyle: type === 'ownership' ? 'dashed' : 'solid',
                            color: type === 'ownership' ? '#8b5cf6' : '#94a3b8' // Purple for links
                        }
                    });
                });
            }

            // Record search in history
            await recordSearch('upi', id, null, profile, profile.linked_addresses?.length || 0);

            setElements(initialElements);
            setEdgeTxMap(new Map()); clearDraft(); // E1: clear stale edge tx map

            // Set session context so hasSession=true → action buttons visible
            const firstLinkedAddr = profile.linked_addresses?.[0];
            const firstAddr = typeof firstLinkedAddr === 'string' ? firstLinkedAddr : firstLinkedAddr?.address;
            if (firstAddr) {
                setSessionChain('TRON'); // UPI links are TRC-20
                setSessionAddress(firstAddr);
            }
        } catch (error) {
            console.error("Failed to load UPI graph", error);
        } finally {
            setLoading(false);
        }
    };

    const loadTransactionHash = async (txHash: string, preferredChain?: string | null) => {
        const sanitizedTxHash = txHash.trim();
        if (!sanitizedTxHash) return;

        setLoading(true);
        let txGraph = null;
        let resolvedChain = preferredChain ? normalizeChain(preferredChain) : null;
        logTracePath('loadTransactionHash START', { txHash: sanitizedTxHash, preferredChain: resolvedChain });
        recordUserAction('search_tx_hash', sanitizedTxHash, 'omnibar', `chain=${resolvedChain || 'auto'}`);

        try {
            try {
                const txDetails = await transactionService.searchTransaction(sanitizedTxHash, resolvedChain || undefined);
                if (txDetails?.is_value_transfer === false) {
                    // Real Tron tx but a resource/staking/governance op (e.g.
                    // Un/DelegateResource, FreezeBalance, VoteWitness) — no funds
                    // move, so there's no transfer edge to render. Tell the
                    // officer explicitly instead of 404-ing or drawing a phantom edge.
                    toast.info('Resource operation — no value transfer', {
                        description: `${txDetails.operation_label || txDetails.contract_type || 'Tron resource operation'}. This transaction does not move funds (no amount or counterparty).`,
                    });
                    logTracePath('loadTransactionHash resource-op', { txHash: sanitizedTxHash, contractType: txDetails.contract_type });
                    return;
                }
                if (txDetails?.chain) {
                    resolvedChain = normalizeChain(txDetails.chain);
                    txGraph = await transactionService.getTransactionGraph(resolvedChain, sanitizedTxHash);
                }
            } catch (searchError: any) {
                console.error('Transaction search failed:', searchError);
                toast.error("Transaction Not Found", {
                    description: searchError.response?.status === 404 
                        ? "Could not locate this transaction on any integrated ledger."
                        : "Failed to query the blockchain indexer."
                });
                return;
            }

            if (txGraph && txGraph.nodes && txGraph.nodes.length > 0) {
                logTracePath('txGraph received', { chain: txGraph.chain, nodes: txGraph.nodes?.length, edges: txGraph.edges?.length, nodeIds: txGraph.nodes?.map((n: any) => n.id?.slice(0, 12)) });
                // Privacy chain debug: log full graph response
                if (txGraph.is_privacy_chain || txGraph.privacy_chain_warning) {
                    console.log('[PRIVACY-CHAIN] Full txGraph response:', JSON.stringify({
                        chain: txGraph.chain,
                        is_privacy_chain: txGraph.is_privacy_chain,
                        privacy_chain_tier: txGraph.privacy_chain_tier,
                        has_warning: !!txGraph.privacy_chain_warning,
                        warning_title: txGraph.privacy_chain_warning?.title,
                        node_count: txGraph.nodes?.length,
                        nodes: txGraph.nodes?.map((n: any) => ({
                            id: n.id, type: n.type, chain: n.chain,
                            block_height: n.block_height, timestamp: n.timestamp,
                            fee: n.fee, ring_size: n.ring_size,
                            num_inputs: n.num_inputs, num_outputs: n.num_outputs,
                            is_privacy_chain: n.is_privacy_chain,
                            data_sources: n.data_sources,
                            addresses_hidden: n.addresses_hidden,
                        })),
                    }, null, 2));
                }
                // Use chain from graph response if available (backend now returns it)
                if (txGraph.chain) resolvedChain = normalizeChain(txGraph.chain);
                const txElements: CytoscapeElement[] = [];

                txGraph.nodes.forEach((node: any) => {
                    // Destructure known Cytoscape-reserved fields out of node,
                    // spread remaining metadata (privacy chain fields, etc.) safely
                    const { source: _s, target: _t, parent: _p, position: _pos, ...safeNodeFields } = node;
                    const elementData = {
                        id: node.id,
                        label: node.label || `${node.id?.substring(0, 8) || 'node'}...`,
                        type: node.type || 'address',
                        chain: node.chain || resolvedChain || undefined,
                        entity_name: node.entity_name,
                        entity_type: node.entity_type,
                        risk_score: node.risk_score,
                        _visibility: 1.0,
                        ...safeNodeFields,  // Spread AFTER explicit fields — can't overwrite Cytoscape props
                    };
                    // Privacy chain debug: log element data being pushed to canvas
                    if (elementData.is_privacy_chain) {
                        console.log('[PRIVACY-CHAIN] Canvas element data:', JSON.stringify({
                            id: elementData.id, type: elementData.type,
                            block_height: elementData.block_height, timestamp: elementData.timestamp,
                            fee: elementData.fee, ring_size: elementData.ring_size,
                            num_inputs: elementData.num_inputs, num_outputs: elementData.num_outputs,
                            data_sources: elementData.data_sources,
                            addresses_hidden: elementData.addresses_hidden,
                            has_warning: !!elementData.privacy_chain_warning,
                            all_keys: Object.keys(elementData).filter(k => elementData[k] != null).sort(),
                        }, null, 2));
                    }
                    txElements.push({ data: elementData });
                });

                // Collect individual txs for edgeTxMap
                const initialTxMapEntries = new Map<string, any[]>();
                txGraph.edges.forEach((edge: any) => {
                    const tokenSymbol = cleanTokenSymbol(edge.token_symbol) || resolvedChain || '';
                    const txHash = edge.tx_hash || edge.id || sanitizedTxHash;
                    const consKey = `${edge.source}->${edge.target}`;
                    
                    const txEntry = {
                        tx_hash: txHash,
                        from_address: edge.source,
                        to_address: edge.target,
                        value: edge.value,
                        timestamp: edge.timestamp,
                        token_symbol: tokenSymbol,
                        fee: edge.fee,
                        block_height: edge.block_height,
                    };
                    
                    const edgeId = `${edge.source}-${edge.target}-${txHash}`;
                    
                    if (!initialTxMapEntries.has(consKey)) initialTxMapEntries.set(consKey, []);
                    initialTxMapEntries.get(consKey)!.push(txEntry);

                    txElements.push({
                        data: {
                            id: edgeId,
                            source: edge.source,
                            target: edge.target,
                            label: (() => { const v = typeof edge.value === 'number' ? edge.value : parseFloat(edge.value) || 0; const tok = cleanTokenSymbol(tokenSymbol); if (v === 0) return tok; const fmt = v >= 1_000_000 ? `${(v/1e6).toFixed(1)}M` : v >= 1_000 ? `${(v/1e3).toFixed(1)}K` : v >= 1 ? v.toFixed(2) : v >= 0.0001 ? v.toFixed(4) : v.toExponential(2); return tok ? `${fmt} ${tok}` : fmt; })(),
                            tx_hash: txHash,
                            value: edge.value,
                            timestamp: edge.timestamp,
                            token_symbol: tokenSymbol,
                            fee: edge.fee,
                            block_height: edge.block_height,
                            _visibility: 1.0,
                        }
                    });
                });

                // Update edgeTxMap for initial load
                setEdgeTxMap(prev => {
                    const next = new Map(prev);
                    initialTxMapEntries.forEach((txs, key) => {
                        next.set(key, txs);
                    });
                    return next;
                });

                // Use unified addNodesToCanvas for consistent behavior
                // This will handle the initial replacement OR additive load correctly.
                await addNodesToCanvas(txElements, {
                    chain: resolvedChain,
                    userAction: true,
                    skipEnrichment: false,
                });
                
                clearDraft(); // clear stale draft state

                const firstNode = txElements.find((el: any) => !el.data?.source)?.data;
                if (firstNode?.id) {
                    // Pre-populate expanded_transactions so the Transfers tab isn't empty upon initial tx load
                    const edgeTxs = txElements
                        .filter((el: any) => el.data?.source && el.data?.target &&
                            (el.data.source === firstNode.id || el.data.target === firstNode.id))
                        .map((el: any) => {
                            const isSender = el.data.source === firstNode.id;
                            const counterparty = isSender ? el.data.target : el.data.source;
                            const cpNode = txElements.find((n: any) => n.data?.id === counterparty && !n.data?.source);
                            return {
                                tx_hash: el.data.tx_hash || el.data.id,
                                from_address: el.data.source,
                                to_address: el.data.target,
                                value: typeof el.data.value === 'number' ? el.data.value : parseFloat(String(el.data.label || '0').replace(/[^0-9.]/g, '')) || 0,
                                fee: el.data.fee,
                                timestamp: el.data.timestamp || null,
                                block_height: el.data.block_height,
                                direction: isSender ? 'outgoing' : 'incoming',
                                counterparty,
                                counterparty_entity: cpNode?.data?.entity_name || null,
                                counterparty_type: cpNode?.data?.entity_type || null,
                                counterparty_risk: cpNode?.data?.risk_score,
                                token_symbol: el.data.token_symbol || null,
                            };
                        });

                    let enrichedFirstNode: any;
                    if (edgeTxs.length > 0) {
                        enrichedFirstNode = { ...firstNode, expanded_transactions: edgeTxs };
                    } else if (firstNode.is_privacy_chain && firstNode.type === 'transaction') {
                        // Privacy chain TX node: create a synthetic TX entry from the node's
                        // own metadata so the Transfers tab has something to display.
                        // The node IS the transaction — just with hidden sender/receiver.
                        const syntheticTx = {
                            tx_hash: firstNode.tx_hash || sanitizedTxHash,
                            from_address: null,
                            to_address: null,
                            value: null,
                            fee: firstNode.fee,
                            timestamp: firstNode.timestamp,
                            block_height: firstNode.block_height,
                            direction: undefined,
                            token_symbol: firstNode.chain,
                            // All privacy/forensic fields from the node
                            chain: firstNode.chain,
                            ring_size: firstNode.ring_size,
                            num_inputs: firstNode.num_inputs,
                            num_outputs: firstNode.num_outputs,
                            tx_size_bytes: firstNode.tx_size_bytes,
                            is_privacy_chain: true,
                            privacy_chain_tier: firstNode.privacy_chain_tier,
                            protocol_era: firstNode.protocol_era,
                            key_images: firstNode.key_images,
                            ring_members: firstNode.ring_members,
                            rct_type: firstNode.rct_type,
                            tx_version: firstNode.tx_version,
                            unlock_time: firstNode.unlock_time,
                            confirmations: firstNode.confirmations,
                            is_coinbase: firstNode.is_coinbase,
                            data_source: firstNode.data_source,
                            // ZEC/DASH fields
                            is_unshielding: firstNode.is_unshielding,
                            exit_amount: firstNode.exit_amount,
                            is_coinjoin: firstNode.is_coinjoin,
                            mixing_depth: firstNode.mixing_depth,
                            all_inputs: firstNode.all_inputs,
                            all_outputs: firstNode.all_outputs,
                        };
                        enrichedFirstNode = { ...firstNode, expanded_transactions: [syntheticTx] };
                        // Also write to transaction store so TransfersTab reads it
                        useTransactionStore.getState().setNodeTransactions(
                            firstNode.id,
                            [syntheticTx].map(t => toExpandedTx(t, firstNode.id))
                        );
                    } else {
                        enrichedFirstNode = firstNode;
                    }

                    setSelectedNode(enrichedFirstNode as GraphNodeData);
                    // Privacy chain TX nodes → Overview tab (has metadata panel)
                    // Regular address nodes → Transfers tab (transaction list)
                    setActiveTab(firstNode.is_privacy_chain && firstNode.type === 'transaction' ? 'overview' : 'transfers');
                    setNodeDetailMinimized(false);
                }

                // Set session context so hasSession=true → action buttons visible.
                // We do NOT navigate (which would re-trigger loadInitialGraph via useEffect).
                if (resolvedChain) setSessionChain(resolvedChain);
                if (firstNode?.id) setSessionAddress(firstNode.id);

                // Reset investigation state so Auto-Investigate button appears fresh
                autoInvestigation.reset();
                useInvestigationStore.getState().setInvestigationRootId(null);
                useInvestigationStore.getState().setShowAutoInvestigateCard(false);

                const chainInfo = resolvedChain ? ` on ${resolvedChain}` : '';
                const nodeCount = txGraph.nodes?.length || 0;
                const edgeCount = txGraph.edges?.length || 0;
                toast.success(`Transaction loaded${chainInfo}`, {
                    description: `${sanitizedTxHash.substring(0, 20)}... — ${nodeCount} addresses, ${edgeCount} transfers`,
                });

                // Surface privacy chain warning if present
                if ((txGraph as any).privacy_chain_warning) {
                    console.log('[PRIVACY-CHAIN] Setting warning banner:', JSON.stringify((txGraph as any).privacy_chain_warning, null, 2));
                    setPrivacyChainWarning((txGraph as any).privacy_chain_warning);
                } else {
                    console.log('[PRIVACY-CHAIN] No privacy_chain_warning on txGraph');
                    setPrivacyChainWarning(null);
                }

                // Log selected node data for Node Inspector
                const firstEl = txElements[0];
                if (firstEl?.data?.is_privacy_chain) {
                    console.log('[PRIVACY-CHAIN] Selected node for Inspector:', JSON.stringify({
                        id: firstEl.data.id, type: firstEl.data.type,
                        block_height: firstEl.data.block_height,
                        timestamp: firstEl.data.timestamp,
                        fee: firstEl.data.fee,
                        ring_size: firstEl.data.ring_size,
                        is_privacy_chain: firstEl.data.is_privacy_chain,
                        privacy_chain_tier: firstEl.data.privacy_chain_tier,
                    }, null, 2));
                }
            } else {
                setPrivacyChainWarning(null);
                toast.error('Transaction not found on any supported chain');
            }
        } catch (e: any) {
            console.error('Transaction graph build failed', e);
            toast.error(`Transaction lookup failed: ${e.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    // Replace a starburst-cluster placeholder with its bundled co-spender
    // nodes + edges on the canvas. Extracted from the previous left-click
    // handler so both right-click "Expand on canvas" and the legacy click
    // path can reach it. Mutates canvas — wrap in pushSnapshot for undo.
    const expandClusterOnCanvas = useCallback((nodeData: any) => {
        if (!nodeData || nodeData.type !== 'cluster') return;
        // Read live position from cytoscape so the bundled co-spenders are
        // seeded around where the cluster icon was — otherwise they
        // materialise at (0,0) off-screen and the user can't tell anything
        // happened. Fallback chain:
        //   1. cluster's current position
        //   2. convergence node's position
        //   3. viewport centre (cy.extent() midpoint)
        const cy = graphRef.current?.getCy?.();
        let center: { x: number; y: number } | undefined;
        if (cy) {
            const clusterEle = cy.getElementById(nodeData.id);
            if (clusterEle && !clusterEle.empty()) {
                const p = clusterEle.position();
                if (p) center = { x: p.x, y: p.y };
            }
            if (!center && nodeData.convergence_node_id) {
                const conv = cy.getElementById(nodeData.convergence_node_id);
                if (conv && !conv.empty()) {
                    const p = conv.position();
                    if (p) center = { x: p.x, y: p.y };
                }
            }
            if (!center && typeof cy.extent === 'function') {
                try {
                    const ext = cy.extent();
                    if (ext && isFinite(ext.x1) && isFinite(ext.y1) && isFinite(ext.x2) && isFinite(ext.y2)) {
                        center = { x: (ext.x1 + ext.x2) / 2, y: (ext.y1 + ext.y2) / 2 };
                    }
                } catch { /* defensive */ }
            }
        }

        useGraphStore.getState().pushSnapshot();
        const { elements: expanded, expandedNodeIds } =
            expandStarburstClusterDetailed(elements, nodeData.id, center);
        setElements(expanded);
        const cnt = nodeData.cospender_count ?? nodeData.bundled_node_ids?.length ?? '?';
        const txShort = (nodeData.tx_hash || '').slice(0, 10) + '...';

        toast.info(`Expanded ${cnt} co-${nodeData.cluster_kind === 'in' ? 'inputs' : 'outputs'} from tx ${txShort}.`, {
            duration: 8000,
            action: {
                label: 'Collapse',
                onClick: () => {
                    useGraphStore.getState().pushSnapshot();
                    setElements(prev => bundleStarburstEdges(prev));
                },
            },
        });

        if (cy && expandedNodeIds.length > 0) {
            setTimeout(() => {
                try {
                    const sel = expandedNodeIds.map(id => `#${CSS.escape(id)}`).join(', ');
                    const convId = nodeData.convergence_node_id;
                    const fitTargets = convId
                        ? cy.elements(`${sel}, #${CSS.escape(convId)}`)
                        : cy.elements(sel);
                    if (!fitTargets.empty()) cy.animate({ fit: { eles: fitTargets, padding: 80 } }, { duration: 350 });
                } catch { /* defensive: bad selector → no fit */ }
            }, 60);
        }
    }, [elements, setElements]);

    // Open a starburst cluster inside the Transfers tab without mutating
    // canvas: select the convergence node, switch to Activity, and signal
    // TransfersTab to auto-expand the matching tx_hash group inline. This is
    // the new default left-click behavior — the canvas-mutating expansion
    // moved to the right-click "Expand on canvas" entry.
    const openClusterInTransfers = useCallback((nodeData: any) => {
        if (!nodeData || nodeData.type !== 'cluster') return;
        const convId = nodeData.convergence_node_id;
        const convEl = convId ? elements.find(el => el?.data?.id === convId && !el?.data?.source) : null;
        if (convEl?.data) {
            setSelectedNode(convEl.data);
        }
        // Defer the tab switch to the next macrotask. NodeInspector has a
        // useEffect that auto-routes forensic-counterparty selections to the
        // Forensic tab — that effect runs on commit, AFTER the synchronous
        // setActiveTab call here would otherwise apply, and clobbers it.
        // Pushing past one render guarantees our 'transfers' choice wins.
        setAutoExpandTxHash(null);
        setTimeout(() => {
            setActiveTab('transfers');
            setAutoExpandTxHash(nodeData.tx_hash || null);
        }, 0);
        const cnt = nodeData.cospender_count ?? nodeData.bundled_node_ids?.length ?? '?';
        const kind = nodeData.cluster_kind === 'in' ? 'co-inputs' : 'co-outputs';
        toast.info(`Showing ${cnt} ${kind} in Transfers — canvas unchanged. Right-click the cluster to expand on canvas.`);
    }, [elements, setActiveTab]);

    const handleNodeClick = useCallback(async (nodeData: any) => {
        // Clicking a wallet is a request to inspect that wallet, so the
        // canvas-scoped Graph Summary yields the panel slot. The summary itself
        // is kept in the store — the tray button reopens it unchanged.
        if (usePanelStore.getState().graphSummaryOpen) {
            usePanelStore.getState().setGraphSummaryOpen(false);
        }

        // Left-click on a starburst cluster opens its members in the Transfers
        // tab without touching the canvas. Canvas expansion is now opt-in via
        // right-click → "Expand on canvas".
        if (nodeData.type === 'cluster') {
            openClusterInTransfers(nodeData);
            return;
        }

        // If clicking a synthetic grouped node, redirect to the parent wallet
        if (nodeData.is_synthetic || nodeData.type === 'synthetic-group') {
            const hub = String(nodeData.central_node_id || '').toLowerCase();
            const parentNode = elements.find(n =>
                !n.data.source && String(n.data.id || '').toLowerCase() === hub
            );
            if (parentNode) {
                setSelectedNode(parentNode.data);
                const addr = nodeData.central_node_id || '';
                const shortAddr = addr.length > 10 ? addr.slice(0, 6) + '...' + addr.slice(-4) : addr;
                const count = nodeData.hidden_count || '?';
                toast.info(`Showing all counterparties for ${shortAddr} — ${count} hidden from canvas, 50 shown.`);
            }
            return;
        }

        // If node already has expanded_transactions, use it as-is.
        // Otherwise, build expanded_transactions from existing graph edges connected to this node.
        const storeTxCount = useTransactionStore.getState().getNodeTransactions(nodeData.id)?.length || 0;
        recordUserAction('click_node', nodeData.id, 'canvas', `entity=${nodeData.entity_name || 'none'}`);
        logInspector('node selected', nodeData.id, {
            entity_name: nodeData.entity_name || null,
            entity_type: nodeData.entity_type || null,
            chain: nodeData.chain || null,
            expanded_transactions: nodeData.expanded_transactions?.length || 0,
            txStoreCount: storeTxCount,
            _visibility: nodeData._visibility ?? 1,
        });
        let enrichedNode = nodeData;
        if (!nodeData.expanded_transactions || nodeData.expanded_transactions.length === 0) {
            const connectedEdges = elements
                .filter(el => el.data?.source && el.data?.target &&
                    (el.data.source === nodeData.id || el.data.target === nodeData.id));

            // E1: Handle consolidated, gold-path (trace_transactions), and individual edges
            const edgeTxs = connectedEdges.flatMap(el => {
                const isSender = el.data.source === nodeData.id;
                const counterparty = isSender ? el.data.target : el.data.source;
                const cpNode = elements.find(n => n.data?.id === counterparty && !n.data?.source);

                // Gold-path edges carry trace_transactions[] from backend enrichment
                if (Array.isArray(el.data.trace_transactions) && el.data.trace_transactions.length > 0) {
                    return el.data.trace_transactions.map((tx: any) => ({
                        tx_hash: tx.tx_hash || el.data.tx_hash,
                        from_address: el.data.source,
                        to_address: el.data.target,
                        value: tx.value ?? el.data.value ?? 0,
                        fee: tx.fee,
                        timestamp: tx.timestamp || el.data.timestamp || null,
                        block_height: tx.block_height,
                        direction: isSender ? 'outgoing' as const : 'incoming' as const,
                        counterparty,
                        counterparty_entity: cpNode?.data?.entity_name || null,
                        counterparty_type: cpNode?.data?.entity_type || null,
                        counterparty_risk: cpNode?.data?.risk_score,
                        token_symbol: tx.token_symbol || el.data.token_symbol || null,
                        _group: el.data.edgeType === 'gold-path' ? 'gold-path' : undefined,
                    }));
                }

                if (el.data.is_consolidated) {
                    // Pull individual txs from edgeTxMap
                    const key = `${el.data.source}->${el.data.target}`;
                    const txs = edgeTxMap.get(key) || [];
                    return txs.map((tx: any) => {
                        const txIsSender = tx.from_address === nodeData.id;
                        const txCounterparty = txIsSender ? tx.to_address : tx.from_address;
                        const txCpNode = elements.find(n => n.data?.id === txCounterparty && !n.data?.source);
                        return {
                            tx_hash: tx.tx_hash,
                            from_address: tx.from_address,
                            to_address: tx.to_address,
                            value: tx.value,
                            fee: tx.fee,
                            timestamp: tx.timestamp || null,
                            block_height: tx.block_height,
                            direction: txIsSender ? 'outgoing' as const : 'incoming' as const,
                            counterparty: txCounterparty,
                            counterparty_entity: txCpNode?.data?.entity_name || null,
                            counterparty_type: txCpNode?.data?.entity_type || null,
                            counterparty_risk: txCpNode?.data?.risk_score,
                            token_symbol: tx.token_symbol || null,
                        };
                    });
                }
                // Individual edge (dex-swap, p2p-link, etc.)
                return [{
                    tx_hash: el.data.tx_hash || el.data.id,
                    from_address: el.data.source,
                    to_address: el.data.target,
                    value: typeof el.data.value === 'number' ? el.data.value : parseFloat(String(el.data.label || '0').replace(/[^0-9.]/g, '')) || 0,
                    fee: el.data.fee,
                    timestamp: el.data.timestamp || null,
                    block_height: el.data.block_height,
                    direction: isSender ? 'outgoing' as const : 'incoming' as const,
                    counterparty,
                    counterparty_entity: cpNode?.data?.entity_name || null,
                    counterparty_type: cpNode?.data?.entity_type || null,
                    counterparty_risk: cpNode?.data?.risk_score,
                    token_symbol: el.data.token_symbol || null,
                }];
            });
            if (edgeTxs.length > 0) {
                enrichedNode = { ...nodeData, expanded_transactions: edgeTxs };
            }
        }
        setSelectedNode(enrichedNode);
        // Edge tx hash / edge pair selection cleared on node click
        if (graphRef.current) {
            graphRef.current.clearEdgeSelection();
            graphRef.current.clearEdgeFocus();
        }
        setSelectedEdgeIds([]);
        setSelectedEdgePair(null);
        setEdgeFocusActive(false);
        setActiveTab('overview'); // Reset to overview tab when selecting a new node
        setPanelMode('details');
        setNodeDetailMinimized(false); // Expand panel for new node
    }, [elements, edgeTxMap, openClusterInTransfers]); // Memoized to prevent recreation on every render

    // Animation Playback Functions
    const startAnimation = () => {
        if (transactions.length === 0) return;
        setIsAnimating(true);
        // Resume from current step when paused mid-run; restart only at end/start.
        if (animationStep <= 0 || animationStep >= transactions.length) {
            setAnimationStep(0);
        }
    };

    const pauseAnimation = () => {
        setIsAnimating(false);
        if (animationTimerRef.current) {
            window.clearTimeout(animationTimerRef.current);
        }
    };

    const restartAnimation = () => {
        setAnimationStep(0);
        setIsAnimating(true);
    };

    const stepPlayback = (delta: number) => {
        if (transactions.length === 0) return;
        setIsAnimating(false);
        if (animationTimerRef.current) {
            window.clearTimeout(animationTimerRef.current);
        }
        setAnimationStep((prev) => {
            const next = Math.max(0, Math.min(transactions.length - 1, prev + delta));
            return next;
        });
    };

    const seekPlayback = (index: number) => {
        if (transactions.length === 0) return;
        setIsAnimating(false);
        if (animationTimerRef.current) {
            window.clearTimeout(animationTimerRef.current);
        }
        setAnimationStep(Math.max(0, Math.min(transactions.length - 1, index)));
    };

    const changeAnimationSpeed = (speed: number) => {
        setAnimationSpeed(speed);
    };

    // Animation loop effect
    useEffect(() => {
        if (!isAnimating || animationStep >= transactions.length) {
            if (animationStep >= transactions.length) {
                setIsAnimating(false);
            }
            return;
        }

        const delay = 1000 / animationSpeed; // Base delay 1 second, adjusted by speed
        animationTimerRef.current = window.setTimeout(() => {
            // Highlight current edge
            const currentTx = transactions[animationStep];
            if (currentTx.edgeId && graphRef.current) {
                graphRef.current.highlightEdge(currentTx.edgeId);
            }

            setAnimationStep(prev => prev + 1);
        }, delay);

        return () => {
            if (animationTimerRef.current) {
                window.clearTimeout(animationTimerRef.current);
            }
        };
    }, [isAnimating, animationStep, animationSpeed, transactions]);

    // Extract and sort transactions from graph edges
    const extractTransactions = (graphElements: any[]) => {
        const txList: Array<{
            timestamp: Date;
            from: string;
            to: string;
            amount: number;
            currency: string; // Token/chain symbol (e.g., ETH, USDT, TRX)
            txHash: string;
            edgeId: string;
        }> = [];

        graphElements.forEach(elem => {
            // Only process edges
            if (elem.data.source && elem.data.target) {
                // Prefer explicit tx_hash if present
                const txHash = elem.data.tx_hash || elem.data.id;

                // Try to find timestamp from selected node's expanded_transactions
                let timestamp: Date | null = null;
                if (elem.data.timestamp) {
                    timestamp = toSafeDate(elem.data.timestamp);
                } else if (selectedNode?.expanded_transactions) {
                    const matchingTx = selectedNode.expanded_transactions.find(
                        (tx: any) => tx.tx_hash === txHash
                    );
                    if (matchingTx && matchingTx.timestamp) {
                        timestamp = toSafeDate(matchingTx.timestamp);
                    }
                }

                // Skip edges with no timestamp for timeline (they'd skew the animation)
                if (!timestamp) return;

                // Extract amount and currency from metadata first, fallback to label
                const metaAmount = typeof elem.data.value === 'number' ? elem.data.value : undefined;
                const labelMatch = elem.data.label?.match(/([\d,]+(?:\.\d+)?)\s*(\w+)?/);
                const amount = metaAmount ?? (labelMatch ? parseFloat(labelMatch[1].replace(/,/g, '')) : 0);
                // Extract currency symbol from metadata or label, or infer from source address
                let currency = elem.data.token_symbol || labelMatch?.[2] || '';
                if (!currency) {
                    // Infer from address format
                    const sourceAddr = elem.data.source || '';
                    if (sourceAddr.startsWith('T')) currency = 'TRX';
                    else if (sourceAddr.startsWith('0x')) currency = 'ETH';
                    else if (sourceAddr.startsWith('1') || sourceAddr.startsWith('3') || sourceAddr.startsWith('bc1')) currency = 'BTC';
                    else currency = 'Crypto';
                }

                txList.push({
                    timestamp,
                    from: elem.data.source,
                    to: elem.data.target,
                    amount,
                    currency,
                    txHash,
                    edgeId: elem.data.id
                });
            }
        });

        // Sort by timestamp
        return txList.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    };

    const toggleTxSelection = (tx: any) => {
        if (!tx?.tx_hash) return;
        setSelectedTxs(prev => {
            const exists = prev.some((t: any) => t.tx_hash === tx.tx_hash);
            if (exists) {
                return prev.filter((t: any) => t.tx_hash !== tx.tx_hash);
            }
            return [...prev, tx];
        });
    };

    const applyManualLabel = async () => {
        if (!selectedNode?.id) return;
        const existingLabels = normalizeStringArray(selectedNode.node_labels);
        const input = await prompt({
            title: 'Add custom label(s)',
            message: 'Enter one or more labels (comma-separated).',
            defaultValue: existingLabels.length > 0 ? existingLabels.join(', ') : (selectedNode.entity_name || ''),
            placeholder: 'Binance deposit, Exchange candidate',
            confirmText: 'Save Labels',
        });
        if (!input) return;

        const labels = normalizeStringArray(input.split(',').map((l: string) => l.trim()));
        if (labels.length === 0) {
            toast.error('Please provide at least one non-empty label.');
            return;
        }
        const primaryLabel = labels[0];

        const nodeChain = chain || detectChain(selectedNode.id);
        try {
            const entity = await entityService.upsertManualLabel({
                address: selectedNode.id,
                chain: nodeChain,
                label: primaryLabel,
                labels,
            });

            const updatedNodeLabels = normalizeStringArray(entity?.node_labels).length > 0
                ? normalizeStringArray(entity?.node_labels)
                : labels;
            const updatedTags = normalizeStringArray(entity?.tags);
            const updatedAttributions = normalizeAttributions(entity?.intelligence_attributions);

            const updatedElements = elements.map(el => {
                if (el.data?.id !== selectedNode.id) return el;
                return {
                    ...el,
                    data: {
                        ...el.data,
                        entity_name: entity?.name || primaryLabel,
                        entity_type: entity?.category || 'custom',
                        entity_source: entity?.source || 'manual',
                        entity_tags: updatedTags,
                        node_labels: updatedNodeLabels,
                        intelligence_attributions: updatedAttributions,
                        label: formatLabel(selectedNode.id, entity?.name || primaryLabel)
                    }
                };
            });

            setElements(updatedElements);
            setSelectedNode({
                ...selectedNode,
                entity_name: entity?.name || primaryLabel,
                entity_type: entity?.category || 'custom',
                entity_source: entity?.source || 'manual',
                entity_tags: updatedTags,
                node_labels: updatedNodeLabels,
                intelligence_attributions: updatedAttributions,
                label: formatLabel(selectedNode.id, entity?.name || primaryLabel)
            });

            toast.success(`Saved ${updatedNodeLabels.length} label${updatedNodeLabels.length > 1 ? 's' : ''}`);
        } catch (e) {
            console.error('Failed to save label', e);
            const message = (e as any)?.response?.data?.detail || (e as any)?.message || 'Failed to save label';
            toast.error(message);
        }
    };

    // Update transactions when elements change
    useEffect(() => {
        const txs = extractTransactions(elements);
        setTransactions(txs);
    }, [elements]);

    useEffect(() => {
        if (!graphRef.current) return;
        const shouldFocus = selectedEdgeIds.length > 0;
        if (edgeFocusActive !== shouldFocus) {
            setEdgeFocusActive(shouldFocus);
        }
        if (shouldFocus) {
            graphRef.current.applyEdgeFocus(selectedEdgeIds);
        } else {
            graphRef.current.clearEdgeFocus();
        }
    }, [edgeFocusActive, selectedEdgeIds, elements]);

    // Bidirectional sync: selectedTxs checkboxes → canvas edge highlight + zoom/center
    useEffect(() => {
        if (!graphRef.current) return;
        if (selectedTxs.length === 0) {
            graphRef.current.clearTxHighlight();
            return;
        }
        const hashes = new Set(selectedTxs.map(t => t.tx_hash));
        graphRef.current.highlightTxHashes(hashes);
    }, [selectedTxs]);

    const buildAnalysisGraph = () => {
        const nodes = elements.filter(e => !e.data?.source).map(n => ({
            id: n.data.id,
            label: n.data.label,
            entity_name: n.data.entity_name,
            entity_type: n.data.entity_type,
            entity_category: n.data.entity_category,
            entity_tags: n.data.entity_tags,
            node_labels: n.data.node_labels,
            intelligence_attributions: n.data.intelligence_attributions,
            risk_score: n.data.risk_score,
            chain: n.data.chain || chain || detectChain(n.data.id),
            is_contract: n.data.is_contract,
            first_seen: n.data.first_seen,
            last_seen: n.data.last_seen,
            last_active: n.data.last_active,
            dormant_days: n.data.dormant_days,
        }));

        const edges = elements.filter(e => e.data?.source).map(e => {
            const rawValue = typeof e.data.value === 'number'
                ? e.data.value
                : parseFloat(String(e.data.label || '').replace(/[^0-9.]/g, '') || '0');

            return {
                from: e.data.source,
                to: e.data.target,
                value: Number.isFinite(rawValue) ? rawValue : 0,
                amount: Number.isFinite(rawValue) ? rawValue : 0,
                value_usd: e.data.value_usd,
                token_symbol: e.data.token_symbol,
                timestamp: e.data.timestamp,
                tx_hash: e.data.tx_hash,
                direction: e.data.direction,
                from_entity_name: e.data.from_entity_name,
                from_entity_type: e.data.from_entity_type,
                to_entity_name: e.data.to_entity_name,
                to_entity_type: e.data.to_entity_type,
            };
        });

        const values = edges.map(e => e.value).filter(v => typeof v === 'number' && v > 0);
        const totalValue = values.reduce((a, b) => a + b, 0);
        const maxValue = values.length ? Math.max(...values) : 0;
        const uniqueCounterparties = new Set(edges.flatMap(e => [e.from, e.to])).size;

        // Per-token volume breakdown (prevents AI from conflating TRX + USDT)
        const tokenVolumes: Record<string, number> = {};
        for (const e of edges) {
            const token = cleanTokenSymbol(e.token_symbol) || 'unspecified';
            const val = typeof e.value === 'number' && e.value > 0 ? e.value : 0;
            if (val > 0) tokenVolumes[token] = (tokenVolumes[token] || 0) + val;
        }

        const summary = {
            total_value: totalValue,
            max_value: maxValue,
            edge_count: edges.length,
            node_count: nodes.length,
            unique_counterparties: uniqueCounterparties,
            token_volumes: tokenVolumes,
        };

        return { nodes, edges, summary, chain };
    };

    const expandNode = async (nodeOverride?: any, direction?: 'in' | 'out', batchSize?: number) => {
        let nodeToExpand = nodeOverride?.id ? nodeOverride : selectedNode;
        if (!nodeToExpand?.id) {
            console.log("Cannot expand: no node selected");
            return;
        }
        recordUserAction('expand_node', nodeToExpand.id, 'workbench', `direction=${direction || 'all'}`);

        if (txStartDate && txEndDate && txStartDate > txEndDate) {
            toast.error('Start date must be earlier than or equal to end date.');
            return;
        }

        // Hot wallet / omnibus commingled funds guardrail
        if (isHotWalletNode(nodeToExpand) && !(nodeToExpand as any)._bypass_hot_warning) {
            setHotWalletModalNode(nodeToExpand);
            setPendingExpandAction(() => async () => {
                const bypassNode = { ...nodeToExpand, _bypass_hot_warning: true };
                await expandNode(bypassNode, direction, batchSize);
            });
            return;
        }

        // Synthesized placeholders (forensic ghosts, BTC coinbase / OP_RETURN /
        // contract-creation stand-ins) have no real address and cannot be
        // expanded. The shared check beats the per-flag inline match below
        // and works even if a producer forgot to set `type='placeholder'`.
        if (isPlaceholderNode(nodeToExpand)) {
            if ((nodeToExpand as any).forensic_ghost || String(nodeToExpand.id).startsWith('ghost:')) {
                toast.info('Counterparty was not disclosed in the subpoena — there is no real address to expand. The placeholder will be replaced if an on-chain match is found later.');
            } else {
                toast.info('Placeholder counterparty (coinbase / contract creation / OP_RETURN) — nothing to expand.');
            }
            return;
        }

        // Non-blockchain node types (UPI, phone, entity, synthetic) cannot be expanded via address API
        const nodeType = nodeToExpand.type || nodeToExpand.data?.type;
        if (nodeType && nodeType !== 'address') {
            if (nodeType === 'synthetic-group') {
                toast.info('Click this node to view all counterparties in the Node Inspector.');
            } else if (nodeType === 'transaction') {
                if ((nodeToExpand as any).is_privacy_chain) {
                    const chainName = nodeToExpand.chain === 'XMR' ? 'Monero' : nodeToExpand.chain === 'ZEC' ? 'Zcash' : nodeToExpand.chain === 'DASH' ? 'Dash' : nodeToExpand.chain || 'Privacy chain';
                    toast.info(`${chainName} transaction — sender and receiver are hidden. Use subpoena panel or view key input in Overview tab.`);
                } else {
                    toast.info('This is a UTXO transaction node. Expand the connected address nodes instead.');
                }
            } else if (nodeType === 'placeholder') {
                if ((nodeToExpand as any).forensic_ghost || String(nodeToExpand.id).startsWith('ghost:')) {
                    toast.info('Counterparty was not disclosed in the subpoena — there is no real address to expand. The placeholder will be replaced if an on-chain match is found later.');
                } else {
                    toast.info('Placeholder counterparty (coinbase / contract creation / OP_RETURN) — nothing to expand.');
                }
            }
            console.log("Skipping expand for non-address node type:", nodeType, nodeToExpand.id);
            return;
        }

        // Prefer the node's own chain. Skip placeholder labels ("Unknown") so
        // Instagram/CSV leads don't call `/addresses/UNKNOWN/…`. detectChain()
        // defaults unrecognized 0x to ETH — prefer detectChainStrict first.
        const expandAddress = normalizeAddressInput(String(nodeToExpand.id || ''));
        let nodeChain = resolveUsableChain(
            expandAddress,
            nodeToExpand.chain,
            chain,
            detectChainStrict(expandAddress),
        );

        console.log("Expanding node:", expandAddress, "on chain:", nodeChain, "direction:", direction || 'all');
        if (isMockDataMode(demoDataMode) && !(nodeToExpand as any)._mock_expansion) {
            toast.info(MOCK_DEMO_TX_TOAST, { duration: 5000 });
            return;
        }
        setLoading(true);
        try {
            // Lean first paint: transfers only (no patterns/exposure/CP accel).
            // Accel + 100 txs routinely exceeds gateway idle limits on heavy /
            // Instagram-handoff wallets across TRX/ETH/BTC — Transfers stays empty.
            // Align with CLAUDE gotcha #10 / TxLoadBar "Load by Count (20)".
            const DEFAULT_TX_LOAD = 20;
            // `batchSize` is set by the Transfers tab "Load Next 100" button, so that the button loads what it says.
            const requestedLimit = (txStartDate || txEndDate) ? 500 : (batchSize && batchSize > 0 ? batchSize : DEFAULT_TX_LOAD);
            const fetchLimit = hubExpandLimit(requestedLimit, nodeToExpand);
            // For date-filtered or directional: always offset 0 (rely on existingIds dedup).
            // For undirected pagination: offset by already-loaded count.
            const currentOffset = (txStartDate || txEndDate || direction) ? 0 : (nodeToExpand.expanded_transactions?.length || 0);
            const mockExpansion = (nodeToExpand as any)._mock_expansion;
            // Expand transfers must paint txs before Railway's ~100s proxy drop.
            // Acceleration (sanctions x counterparties + exposure) used to run
            // on the same request for non-hub wallets and routinely exceeded the
            // proxy budget -> axios Network Error, empty Transfers tab.
            // Always lean on expand; retry once at a smaller limit on drop.
            const txOpts = {
                includeAcceleration: false,
                cacheOnly: wantsCacheOnly(demoDataMode),
            };
            let usedLimit = fetchLimit;
            let response = mockExpansion;
            if (!response) {
                try {
                    response = await addressService.getTransactions(
                        nodeChain,
                        expandAddress,
                        fetchLimit,
                        currentOffset,
                        txStartDate || undefined,
                        txEndDate || undefined,
                        debugMode || undefined,
                        caseIdForLoad || undefined,
                        txOpts,
                    );
                } catch (firstErr) {
                    // Gateway kill (Network Error / 502–504) — one lean retry at ≤20.
                    const retryLimit = Math.min(fetchLimit, TX_LOAD_RETRY_LIMIT);
                    if (!isTxLoadNetworkFailure(firstErr)) {
                        throw firstErr;
                    }
                    toast.info(
                        `Heavy wallet — retrying with ${retryLimit} transfers…`,
                        { duration: 3500 },
                    );
                    usedLimit = retryLimit;
                    response = await addressService.getTransactions(
                        nodeChain,
                        expandAddress,
                        retryLimit,
                        0,
                        txStartDate || undefined,
                        txEndDate || undefined,
                        debugMode || undefined,
                        caseIdForLoad || undefined,
                        { includeAcceleration: false, cacheOnly: wantsCacheOnly(demoDataMode) },
                    );
                }
            }
            console.log("Got transactions:", response);

            // Backend auto chain-probe may return txs from a different EVM chain
            // when the declared chain had zero activity (graph_service.switched_chain).
            if (response?.switched_chain && response?.actual_chain) {
                const fromChain = response.original_chain || nodeChain;
                nodeChain = String(response.actual_chain);
                toast.info(
                    `No transactions on ${fromChain} — loaded from ${nodeChain}`,
                    { duration: 6000 },
                );
                setElements(prev => prev.map(el =>
                    el.data?.id === nodeToExpand.id
                        ? { ...el, data: { ...el.data, chain: nodeChain } }
                        : el
                ));
                if (selectedNode?.id === nodeToExpand.id) {
                    setSelectedNode(prev => prev ? { ...prev, chain: nodeChain } as any : prev);
                }
                if (urlAddress === nodeToExpand.id || address === nodeToExpand.id) {
                    setSearchParams(prev => {
                        const next = new URLSearchParams(prev);
                        next.set('chain', nodeChain);
                        return next;
                    }, { replace: true });
                }
            }

            // If debug mode, capture trace info. Accumulate across calls so a
            // no-op endpoint (0 calls, 19ms) doesn't wipe the prior rich trace.
            if (debugMode && response._trace) {
                setLastTrace((prev: any) => mergeApiTrace(prev, response._trace));
                setShowTracePanel(true);
                console.log('[DEBUG TRACE]', response._trace);
            }

            const txs = response.transactions || [];
            if (wantsCacheOnly(demoDataMode) && (response.cache_miss || txs.length === 0)) {
                toast.info(CACHED_MISS_TOAST, { duration: 5000 });
                return;
            }
            const retargetTo = typeof response.retargeted_to === 'string' ? response.retargeted_to.toLowerCase() : '';
            const retargetFrom = typeof response.retargeted_from === 'string' ? response.retargeted_from.toLowerCase() : '';
            if (retargetTo && retargetTo !== String(nodeToExpand.id || '').toLowerCase()) {
                toast.info(
                    `Optimism CREATE2 slot is Uniswap UnsupportedProtocol — loaded live Universal Router ${retargetTo.slice(0, 10)}…`,
                    { duration: 8000 },
                );
                nodeToExpand = { ...nodeToExpand, id: retargetTo, address: retargetTo };
            }
            if (!mockExpansion && txs.length === 0) {
                if (response.empty_reason === 'unsupported_protocol_stub') {
                    const hint = response.suggested_chain
                        ? ` Same address is active on ${response.suggested_chain}.`
                        : '';
                    toast.warning(
                        `No swaps on ${nodeChain}: Uniswap UnsupportedProtocol stub.${hint}`,
                        { duration: 8000 },
                    );
                } else {
                    const evmHint = ['ETH', 'BSC', 'BNB', 'MATIC', 'ARB', 'BASE', 'OP', 'AVAX'].includes(
                        String(nodeChain || '').toUpperCase(),
                    );
                    toast.warning(
                        `No transactions returned for ${expandAddress.slice(0, 10)}… on ${nodeChain}.` +
                        (evmHint
                            ? ' If this is an EVM address, use Re-probe chain in Overview.'
                            : nodeChain === 'TRX'
                                ? ' Confirm the address on TronScan, or retry in a moment if TronGrid is rate-limiting.'
                                : ''),
                        { duration: 7000 },
                    );
                }
            }
            if (isHubCapNotice(response, usedLimit, requestedLimit)) {
                // The backend flag (response.hub_capped) covers hubs the name regex does not recognise.
                const loadedNow = response.fetched_count ?? txs.length;
                toast.info(`Hub address — loaded latest ${response.hub_capped ? loadedNow : usedLimit} transfers, not full history.`, { duration: 5000 });
            }
            const accelerationCounterparties = Array.isArray(response?.acceleration?.counterparties)
                ? response.acceleration.counterparties
                : [];
            const accelerationMap = new Map<string, any>(
                accelerationCounterparties
                    .filter((cp: any) => cp?.address)
                    .map((cp: any) => [String(cp.address).toLowerCase(), cp])
            );

            const newElements = [...elements];
            const existingIds = new Set(elements.map(e => e.data.id));
            if (retargetTo && retargetFrom && retargetFrom !== retargetTo) {
                for (const el of newElements) {
                    if (!el?.data) continue;
                    if (!el.data.source && String(el.data.id).toLowerCase() === retargetFrom) {
                        existingIds.delete(el.data.id);
                        el.data = { ...el.data, id: retargetTo, address: retargetTo };
                        existingIds.add(retargetTo);
                    } else if (el.data.source) {
                        const src = String(el.data.source).toLowerCase() === retargetFrom ? retargetTo : el.data.source;
                        const tgt = String(el.data.target).toLowerCase() === retargetFrom ? retargetTo : el.data.target;
                        if (src !== el.data.source || tgt !== el.data.target) {
                            el.data = { ...el.data, source: src, target: tgt };
                        }
                    }
                }
            }
            // Also track existing edge pairs (source→target) to prevent duplicates from different ID formats
            const existingEdgePairs = new Set(
                elements.filter(e => e.data.source && e.data.target)
                    .map(e => `${e.data.source}→${e.data.target}`)
            );

            // Track new transactions to add to selectedNode
            const expandedTransactions: any[] = [];
            const zeroValueAggregates = new Map<string, {
                sourceId: string;
                targetId: string;
                tokenSymbol: string;
                count: number;
                latestTimestamp: any;
            }>();

            // E1: Consolidation — aggregate edges per (source -> target) direction pair
            const goldPathPairs = new Set<string>();
            elements.forEach(el => {
                if (el.data?.edgeType === 'gold-path' && el.data.source && el.data.target) {
                    goldPathPairs.add(`${el.data.source}->${el.data.target}`);
                    goldPathPairs.add(`${el.data.target}->${el.data.source}`);
                }
            });
            const consolidatedAggs = new Map<string, {
                fromAddr: string; toAddr: string; totalValue: number; txCount: number;
                tokens: Map<string, number>; firstTimestamp: any; lastTimestamp: any;
                elementIndex: number; // -1 = new, >=0 = update existing in newElements
            }>();
            // Seed from existing consolidated edges already on canvas
            newElements.forEach((el, idx) => {
                if (el.data?.is_consolidated) {
                    const key = `${el.data.source}->${el.data.target}`;
                    if (goldPathPairs.has(key)) return;
                    const tokens = new Map<string, number>();
                    if (el.data.token_symbol) {
                        el.data.token_symbol.split(', ').forEach((t: string) => tokens.set(t, 0));
                    }
                    consolidatedAggs.set(key, {
                        fromAddr: el.data.source!, toAddr: el.data.target!,
                        totalValue: el.data.total_value || 0, txCount: el.data.tx_count || 0,
                        tokens, firstTimestamp: el.data.first_timestamp,
                        lastTimestamp: el.data.last_timestamp, elementIndex: idx,
                    });
                }
            });
            const newTxEntries = new Map<string, TxEntry[]>();
            // Also absorb existing individual (non-gold-path) edges into consolidation
            newElements.forEach((el) => {
                if (el.data?.source && el.data?.target && !el.data.is_consolidated &&
                    el.data.edgeType !== 'gold-path' && !el.data.is_zero_value_aggregate) {
                    const key = `${el.data.source}->${el.data.target}`;
                    if (goldPathPairs.has(key)) return;
                    const txValue = Number(el.data.value || 0);
                    const usdValue = Number(el.data.historical_usd || 0);
                    // Don't skip zero-value txs if they have USD value (MEV, internal transfers)
                    if (txValue === 0 && usdValue === 0) return;
                    const effectiveValue = txValue > 0 ? txValue : usdValue;
                    const tokenSymbol = txValue > 0 ? (el.data.token_symbol || nodeChain) : 'USD';
                    const existing = consolidatedAggs.get(key);
                    if (existing) {
                        existing.totalValue += effectiveValue;
                        existing.txCount += 1;
                        existing.tokens.set(tokenSymbol, (existing.tokens.get(tokenSymbol) || 0) + effectiveValue);
                    } else {
                        consolidatedAggs.set(key, {
                            fromAddr: el.data.source, toAddr: el.data.target,
                            totalValue: effectiveValue, txCount: 1,
                            tokens: new Map([[tokenSymbol, effectiveValue]]),
                            firstTimestamp: el.data.timestamp, lastTimestamp: el.data.timestamp,
                            elementIndex: -1,
                        });
                    }
                    // Also seed edgeTxMap entry for the individual edge's transaction
                    if (el.data.tx_hash) {
                        const consKey = consolidationKey(el.data.source, el.data.target);
                        if (!newTxEntries.has(consKey)) newTxEntries.set(consKey, []);
                        newTxEntries.get(consKey)!.push({
                            tx_hash: el.data.tx_hash, from_address: el.data.source,
                            to_address: el.data.target, value: txValue, fee: el.data.fee,
                            timestamp: el.data.timestamp, block_height: el.data.block_height,
                            token_symbol: tokenSymbol,
                        });
                    }
                }
            });

            // Helper to check if an address matches the selected node (case-insensitive)
            const isSelectedNode = (addr?: string) =>
                !!addr && !!nodeToExpand?.id && addr.toLowerCase() === nodeToExpand.id.toLowerCase();

            // Helper to ensure a node exists, returns the normalized ID
            const ensureNodeExists = (addr: string, entityName?: string, entityType?: string, riskScore?: number) => {
                // Use selectedNode.id if this address matches (preserves original casing)
                const evmSet = new Set(['ETH', 'BSC', 'BNB', 'MATIC', 'ARB', 'BASE', 'OP', 'AVAX', 'FTM', 'LINEA', 'BLAST', 'SCROLL', 'ZKSYNC', 'GNOSIS', 'CELO', 'MANTLE', 'MOONBEAM']);
                let normalizedId = isSelectedNode(addr) ? nodeToExpand.id : addr;
                if (evmSet.has((nodeChain || '').toUpperCase()) && typeof normalizedId === 'string' && normalizedId.startsWith('0x')) {
                    normalizedId = normalizedId.toLowerCase();
                }

                const existingMatch = [...existingIds].find(
                    (id) => String(id).toLowerCase() === String(normalizedId).toLowerCase()
                );
                if (existingMatch) return existingMatch as string;

                if (!existingIds.has(normalizedId)) {
                    // Placeholder counterparties (coinbase / contract creation /
                    // OP_RETURN) get type 'placeholder' so the existing
                    // "non-address node types cannot be expanded" guard at the
                    // top of expandNode blocks accidental clicks on them.
                    const isPlaceholder = typeof normalizedId === 'string' && normalizedId.startsWith('__');
                    newElements.push({
                        data: {
                            id: normalizedId,
                            label: formatLabel(normalizedId, entityName),
                            type: isPlaceholder ? 'placeholder' : 'address',
                            balance: 0,
                            entity_name: entityName,
                            entity_type: entityType,
                            risk_score: riskScore,
                            is_placeholder: isPlaceholder || undefined,
                            chain: nodeChain,
                        }
                    });
                    existingIds.add(normalizedId);
                }
                return normalizedId;
            };

            // Bucket counters for one summary log per expand call (instead of
            // a console.warn per dropped tx, which spams the console for BTC
            // coinbase / EVM contract-creation / OP_RETURN outputs).
            const skipBuckets = {
                coinbase: 0,           // BTC block reward — no from_address
                contract_creation: 0,  // EVM new contract — no to_address
                op_return: 0,          // BTC OP_RETURN — no destination address
                malformed: 0,          // truly missing both, no tx_hash
                no_hash: 0,            // missing tx_hash entirely
            };

            // Synthesize a labelled placeholder for legitimate-but-missing sides
            // so the activity still shows on the canvas. Stable per-tx so we
            // don't fan-out the canvas with 100 separate "coinbase" nodes.
            const placeholderId = (kind: 'coinbase' | 'contract_creation' | 'op_return') =>
                `__${kind}__:${nodeChain.toLowerCase()}`;

            txs.forEach((tx: any) => {
                // Truly malformed — no addresses AND no tx_hash. Nothing to
                // anchor it to. Drop silently, count for the summary.
                if (!tx.tx_hash && !tx.from_address && !tx.to_address) {
                    skipBuckets.malformed += 1;
                    return;
                }
                if (!tx.tx_hash) {
                    skipBuckets.no_hash += 1;
                    return;
                }

                // Recognize and label known patterns instead of dropping.
                const chainUpper = (nodeChain || '').toUpperCase();
                if (tx.from_address && /^(coinbase|null|none)$/i.test(String(tx.from_address).trim())) {
                    tx.from_address = '';
                }
                if (!tx.from_address) {
                    if (chainUpper === 'BTC' || chainUpper === 'LTC' || chainUpper === 'BCH' || chainUpper === 'DOGE'
                        || /^(coinbase)$/i.test(String(tx.from_address || 'coinbase'))) {
                        tx.from_address = placeholderId('coinbase');
                        tx.from_entity_name = 'Coinbase (block reward)';
                    } else {
                        // Unknown chain pattern — drop and count
                        skipBuckets.malformed += 1;
                        return;
                    }
                }
                if (!tx.to_address) {
                    const evmChains = new Set(['ETH', 'BNB', 'MATIC', 'ARB', 'BASE', 'OP', 'AVAX',
                        'FTM', 'LINEA', 'BLAST', 'SCROLL', 'GNOSIS', 'CELO', 'MANTLE', 'MOONBEAM']);
                    if (evmChains.has(chainUpper)) {
                        tx.to_address = placeholderId('contract_creation');
                        tx.to_entity_name = 'Contract creation';
                    } else if (chainUpper === 'BTC' || chainUpper === 'LTC' || chainUpper === 'BCH' || chainUpper === 'DOGE') {
                        tx.to_address = placeholderId('op_return');
                        tx.to_entity_name = 'OP_RETURN / non-standard';
                    } else {
                        skipBuckets.malformed += 1;
                        return;
                    }
                }

                // Update bucket counters for the synthesized cases (so the
                // summary still surfaces them — they just don't drop on the canvas).
                if (tx.from_address?.startsWith('__coinbase__:')) skipBuckets.coinbase += 1;
                if (tx.to_address?.startsWith('__contract_creation__:')) skipBuckets.contract_creation += 1;
                if (tx.to_address?.startsWith('__op_return__:')) skipBuckets.op_return += 1;

                // Determine direction and neighbor
                const isSender = isSelectedNode(tx.from_address);
                const neighborAddr = isSender ? tx.to_address : tx.from_address;

                // Directional filter: skip txs that don't match the requested direction.
                // 'out' = only outgoing (node is sender), 'in' = only incoming (node is receiver)
                if (direction === 'out' && !isSender) return;
                if (direction === 'in' && isSender) return;

                // Store transaction for raw data display
                expandedTransactions.push({
                    tx_hash: tx.tx_hash,
                    from_address: tx.from_address,
                    to_address: tx.to_address,
                    value: tx.value,
                    fee: tx.fee,
                    timestamp: tx.timestamp,
                    block_height: tx.block_height,
                    token_symbol: tx.token_symbol,
                    historical_usd: tx.historical_usd,
                    direction: isSender ? 'outgoing' : 'incoming',
                    counterparty: neighborAddr,
                    counterparty_entity: tx.other_address_entity_name,
                    counterparty_type: tx.other_address_entity_type,
                    counterparty_risk: tx.other_address_risk_score,
                    from_entity_name: tx.from_entity_name,
                    from_entity_type: tx.from_entity_type,
                    to_entity_name: tx.to_entity_name,
                    to_entity_type: tx.to_entity_type,
                });

                // Ensure both source and target nodes exist before creating edge
                const sourceId = ensureNodeExists(
                    tx.from_address || nodeToExpand.id,
                    isSender ? nodeToExpand.entity_name : tx.other_address_entity_name,
                    isSender ? nodeToExpand.entity_type : tx.other_address_entity_type,
                    isSender ? nodeToExpand.risk_score : tx.other_address_risk_score
                );
                const targetId = ensureNodeExists(
                    tx.to_address || nodeToExpand.id,
                    !isSender ? nodeToExpand.entity_name : tx.other_address_entity_name,
                    !isSender ? nodeToExpand.entity_type : tx.other_address_entity_type,
                    !isSender ? nodeToExpand.risk_score : tx.other_address_risk_score
                );

                const txValue = Number(tx.value || 0);
                const usdValue = Number(tx.historical_usd || 0);
                const tokenSymbol = txValue > 0 ? (tx.token_symbol || (nodeChain === 'TRX' ? 'TRX' : nodeChain)) : (usdValue > 0 ? 'USD' : (tx.token_symbol || nodeChain));

                // Zero-value with no USD: aggregate into dotted "inferred" edges
                if (txValue === 0 && usdValue === 0) {
                    const aggKey = `${sourceId}->${targetId}:${tokenSymbol}`;
                    const existingAgg = zeroValueAggregates.get(aggKey);
                    if (existingAgg) {
                        existingAgg.count += 1;
                        existingAgg.latestTimestamp = tx.timestamp || existingAgg.latestTimestamp;
                    } else {
                        zeroValueAggregates.set(aggKey, {
                            sourceId, targetId, tokenSymbol,
                            count: 1, latestTimestamp: tx.timestamp,
                        });
                    }
                    return;
                }

                // E1: Consolidate non-zero-value edges by (source -> target) pair
                const consKey = consolidationKey(sourceId, targetId);
                const txEntry: TxEntry = {
                    tx_hash: tx.tx_hash, from_address: tx.from_address, to_address: tx.to_address,
                    value: txValue, fee: tx.fee, timestamp: tx.timestamp,
                    block_height: tx.block_height, token_symbol: tokenSymbol,
                };
                if (!newTxEntries.has(consKey)) newTxEntries.set(consKey, []);
                newTxEntries.get(consKey)!.push(txEntry);

                // For gold-path pairs, keep individual edges (don't consolidate)
                if (goldPathPairs.has(consKey)) {
                    const edgeId = `${sourceId}-${targetId}-${tx.tx_hash}`;
                    if (!existingIds.has(edgeId)) {
                        const _tok = cleanTokenSymbol(tx.token_symbol);
                        const valueLabel = _tok
                            ? `${(tx.value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${_tok}`
                            : formatCurrency(tx.value || 0, nodeChain);
                        newElements.push({
                            data: {
                                id: edgeId, source: sourceId, target: targetId,
                                label: valueLabel, value: tx.value || 0, token_symbol: tokenSymbol,
                                timestamp: tx.timestamp, tx_hash: tx.tx_hash,
                                direction: isSender ? 'outgoing' : 'incoming',
                                _visibility: 1.0,
                                // Bridge fields propagated from backend enrichment.
                                // The effect further up in this file promotes edgeType
                                // to "bridge-hop" when is_bridge_tx is true.
                                is_bridge_tx: tx.is_bridge_tx,
                                bridge_family: tx.bridge_family,
                                bridge_name: tx.bridge_name,
                                bridge_target_chain: tx.bridge_target_chain,
                                // Privacy / mixer fields propagated from backend
                                // enrichment (analysis/privacy_detection.py). The
                                // privacy-edge-tagging effect promotes edgeType to
                                // mixer-deposit / mixer-withdrawal / coinjoin-pattern
                                // when is_privacy_tx is true.
                                is_privacy_tx: tx.is_privacy_tx,
                                privacy_family: tx.privacy_family,
                                privacy_name: tx.privacy_name,
                                privacy_kind: tx.privacy_kind,
                                privacy_direction: tx.privacy_direction,
                                privacy_sanctioned: tx.privacy_sanctioned,
                                privacy_pool_size: tx.privacy_pool_size,
                                // Stablecoin-swap fields (STABLECOIN-SWAP-1). Tagging
                                // effect promotes edgeType to "stablecoin-swap".
                                is_stablecoin_swap: tx.is_stablecoin_swap,
                                swap_protocol: tx.swap_protocol,
                                swap_pair_hint: tx.swap_pair_hint,
                                swap_direction: tx.swap_direction,
                                swap_detection_source: tx.swap_detection_source,
                            }
                        });
                        existingIds.add(edgeId);
                    }
                    return;
                }

                // Accumulate into consolidation aggregate (use USD fallback for zero-value txs)
                const effectiveValue = txValue > 0 ? txValue : usdValue;
                const existing = consolidatedAggs.get(consKey);
                if (existing) {
                    existing.totalValue += effectiveValue;
                    existing.txCount += 1;
                    existing.tokens.set(tokenSymbol, (existing.tokens.get(tokenSymbol) || 0) + effectiveValue);
                    if (tx.timestamp) {
                        const ts = tsToMillis(tx.timestamp);
                        if (!isNaN(ts)) {
                            const existFirst = tsToMillis(existing.firstTimestamp);
                            const existLast = tsToMillis(existing.lastTimestamp);
                            if (isNaN(existFirst) || ts < existFirst) existing.firstTimestamp = tx.timestamp;
                            if (isNaN(existLast) || ts > existLast) existing.lastTimestamp = tx.timestamp;
                        }
                    }
                } else {
                    consolidatedAggs.set(consKey, {
                        fromAddr: sourceId, toAddr: targetId,
                        totalValue: effectiveValue, txCount: 1,
                        tokens: new Map([[tokenSymbol, effectiveValue]]),
                        firstTimestamp: tx.timestamp, lastTimestamp: tx.timestamp,
                        elementIndex: -1,
                    });
                }
            });

            // One summary log per expand call instead of one warn per dropped tx.
            // Coinbase / contract creation / OP_RETURN are normal patterns — they
            // get rendered as labelled placeholder counterparties (not dropped).
            // Only `malformed` and `no_hash` actually disappear from the canvas.
            const totalSkipped = skipBuckets.malformed + skipBuckets.no_hash;
            const totalSynthesized = skipBuckets.coinbase + skipBuckets.contract_creation + skipBuckets.op_return;
            if (totalSkipped > 0 || totalSynthesized > 0) {
                console.info(
                    `[expandNode] tx normalization: synthesized=${totalSynthesized} ` +
                    `(coinbase=${skipBuckets.coinbase} contract_creation=${skipBuckets.contract_creation} ` +
                    `op_return=${skipBuckets.op_return}) dropped=${totalSkipped} ` +
                    `(malformed=${skipBuckets.malformed} no_hash=${skipBuckets.no_hash}) ` +
                    `out_of=${txs.length}`
                );
            }

            // E1: Emit consolidated edges for all accumulated (source -> target) pairs
            consolidatedAggs.forEach((agg, key) => {
                const label = formatConsolidatedLabel(agg.txCount, agg.totalValue, agg.tokens, agg.lastTimestamp);
                const tokenNames = Array.from(agg.tokens.keys());

                const edgeData: any = {
                    id: key,
                    source: agg.fromAddr,
                    target: agg.toAddr,
                    label,
                    value: agg.totalValue,
                    total_value: agg.totalValue,
                    tx_count: agg.txCount,
                    token_symbol: tokenNames.join(', '),
                    is_consolidated: true,
                    timestamp: agg.lastTimestamp,
                    first_timestamp: agg.firstTimestamp,
                    last_timestamp: agg.lastTimestamp,
                    _visibility: 1.0,
                };
                if (isDustEdge(agg.totalValue, agg.tokens)) edgeData._hideLabel = true;

                logEdge({
                    path: 'expandNode',
                    edgeId: key,
                    source: agg.fromAddr,
                    target: agg.toAddr,
                    direction: agg.fromAddr === nodeToExpand.id ? 'outgoing' : 'incoming',
                    rootNodeId: nodeToExpand.id,
                    value: agg.totalValue,
                    tokenSymbol: tokenNames.join(', '),
                    isConsolidated: true,
                    txCount: agg.txCount,
                });

                const pairKey = `${agg.fromAddr}→${agg.toAddr}`;
                if (agg.elementIndex >= 0) {
                    // Update existing consolidated edge in-place
                    newElements[agg.elementIndex] = { data: edgeData };
                } else if (!existingIds.has(key) && !existingEdgePairs.has(pairKey)) {
                    newElements.push({ data: edgeData });
                    existingIds.add(key);
                    existingEdgePairs.add(pairKey);
                }
            });

            // Add one dotted aggregate edge per zero-value relationship (industry-standard de-noising)
            zeroValueAggregates.forEach((agg, key) => {
                const aggEdgeId = `inferred-zero-${key}`;
                if (existingIds.has(aggEdgeId)) return;

                const _aggTok = cleanTokenSymbol(agg.tokenSymbol);
                const countLabel = _aggTok ? `${agg.count}× 0-value ${_aggTok}` : `${agg.count}× 0-value tx`;
                newElements.push({
                    data: {
                        id: aggEdgeId,
                        source: agg.sourceId,
                        target: agg.targetId,
                        label: countLabel,
                        value: 0,
                        token_symbol: agg.tokenSymbol,
                        timestamp: agg.latestTimestamp,
                        direction: 'inferred',
                        edgeType: 'inferred',
                        is_zero_value_aggregate: true,
                        zero_value_count: agg.count,
                    }
                });
                existingIds.add(aggEdgeId);
            });

            // Update the selectedNode with the new transaction data (deduplicated)
            const existingTxHashes = new Set(
                (nodeToExpand.expanded_transactions || []).map((t: any) => t.tx_hash)
            );
            const dedupedNewTxs = expandedTransactions.filter(t => !existingTxHashes.has(t.tx_hash));
            const enrichedExpandedTransactions = dedupedNewTxs.map((tx: any) => {
                const cp: any = accelerationMap.get(String(tx.counterparty || '').toLowerCase());
                if (!cp) return tx;
                return {
                    ...tx,
                    counterparty_entity: cp.entity_name || tx.counterparty_entity,
                    counterparty_type: cp.entity_type || tx.counterparty_type,
                    counterparty_risk: typeof cp.risk_score === 'number'
                        ? cp.risk_score
                        : (cp.sanctioned ? 1.0 : tx.counterparty_risk),
                };
            });

            const updatedNode = {
                ...nodeToExpand,
                expanded_transactions: [
                    ...(nodeToExpand.expanded_transactions || []),
                    ...enrichedExpandedTransactions
                ],
                last_expanded: new Date().toISOString(),
                total_expanded_txs: (nodeToExpand.expanded_transactions?.length || 0) + enrichedExpandedTransactions.length,
                // tx_count is the real on-chain count when the server had one, else it keeps the triage count;
                // it is never the page size. has_more / truncated / incomplete come from the fetcher.
                ...coverageAfterLoad(response, nodeToExpand),
                data_source: response.data_source || nodeToExpand.data_source,
                empty_reason: response.empty_reason || nodeToExpand.empty_reason,
                suggested_chain: response.suggested_chain || nodeToExpand.suggested_chain,
                hub_capped: response.hub_capped || nodeToExpand.hub_capped,
            };

            const enrichedExpandedElements = mockExpansion
                ? newElements
                : await enrichGraphWithEntities(newElements, nodeChain);
            const accelerationEnrichedElements = enrichedExpandedElements.map((el: any) => {
                if (el?.data?.source || !el?.data?.id) return el;
                const cp: any = accelerationMap.get(String(el.data.id).toLowerCase());
                if (!cp) return el;

                const cpRisk = typeof cp.risk_score === 'number'
                    ? cp.risk_score
                    : (cp.sanctioned ? 1.0 : el.data.risk_score);
                const cpEntityName = cp.entity_name || el.data.entity_name;

                return {
                    ...el,
                    data: {
                        ...el.data,
                        entity_name: cpEntityName,
                        entity_type: cp.entity_type || el.data.entity_type,
                        risk_score: cpRisk,
                        entity_category: cp.sanctioned ? 'sanction' : el.data.entity_category,
                        label: formatLabel(el.data.id, cpEntityName),
                    }
                };
            });

            // Use functional updater to avoid race conditions with concurrent expansions
            setElements(prevElements => {
                const merged = [...prevElements];
                const mergedIds = new Set(merged.map(e => e.data.id));

                // Collect all node IDs (existing + new) to validate edges
                const allNodeIds = new Set<string>();
                const remember = (id?: string) => {
                    if (!id) return;
                    allNodeIds.add(id);
                    allNodeIds.add(id.toLowerCase());
                };
                merged.filter(e => !e.data.source).forEach(e => remember(e.data.id));
                newElements.filter(e => !e.data.source).forEach(e => remember(e.data.id));
                accelerationEnrichedElements.filter(e => !e.data.source).forEach(e => remember(e.data.id));

                if (retargetTo && retargetFrom && retargetFrom !== retargetTo) {
                    for (const el of merged) {
                        if (!el?.data) continue;
                        if (!el.data.source && String(el.data.id).toLowerCase() === retargetFrom) {
                            mergedIds.delete(el.data.id);
                            el.data = { ...el.data, id: retargetTo, address: retargetTo };
                            mergedIds.add(retargetTo);
                            remember(retargetTo);
                        } else if (el.data.source) {
                            const src = String(el.data.source).toLowerCase() === retargetFrom ? retargetTo : el.data.source;
                            const tgt = String(el.data.target || '').toLowerCase() === retargetFrom ? retargetTo : el.data.target;
                            if (src !== el.data.source || tgt !== el.data.target) {
                                el.data = { ...el.data, source: src, target: tgt };
                            }
                        }
                    }
                }

                // E1: Collect consolidated edge keys to absorb individual duplicates
                const consolidatedKeys = new Set<string>();
                accelerationEnrichedElements.forEach(el => {
                    if (el.data.is_consolidated && el.data.source && el.data.target) {
                        consolidatedKeys.add(`${el.data.source}->${el.data.target}`);
                    }
                });

                // Remove pre-existing individual edges that are now covered by consolidated edges
                if (consolidatedKeys.size > 0) {
                    for (let i = merged.length - 1; i >= 0; i--) {
                        const el = merged[i];
                        if (el.data?.source && el.data?.target && !el.data.is_consolidated &&
                            el.data.edgeType !== 'gold-path' && !el.data.is_zero_value_aggregate) {
                            const pairKey = `${el.data.source}->${el.data.target}`;
                            if (consolidatedKeys.has(pairKey)) {
                                merged.splice(i, 1);
                                mergedIds.delete(el.data.id);
                            }
                        }
                    }
                }

                // Add only NEW elements that don't already exist, skipping orphan edges
                accelerationEnrichedElements.forEach(el => {
                    if (mergedIds.has(el.data.id)) {
                        // E1: Update existing consolidated edges in-place
                        if (el.data.is_consolidated) {
                            const idx = merged.findIndex(e => e.data.id === el.data.id);
                            if (idx >= 0) merged[idx] = el;
                        }
                        return;
                    }
                    // For edges, verify both source and target nodes exist
                    const srcOk = !el.data.source || allNodeIds.has(el.data.source) || allNodeIds.has(String(el.data.source).toLowerCase());
                    const tgtOk = !el.data.target || allNodeIds.has(el.data.target) || allNodeIds.has(String(el.data.target).toLowerCase());
                    if (el.data.source && (!srcOk || !tgtOk)) {
                        console.warn('Skipping orphan edge:', el.data.id, 'source:', el.data.source, 'target:', el.data.target);
                        return;
                    }
                    merged.push(el);
                    mergedIds.add(el.data.id);
                });

                // Update node data (without leaking expanded_transactions into cytoscape)
                const liveId = String(nodeToExpand.id).toLowerCase();
                const nodeIdx = merged.findIndex(e => !e.data.source && String(e.data.id).toLowerCase() === liveId);
                if (nodeIdx >= 0) {
                    merged[nodeIdx] = {
                        ...merged[nodeIdx],
                        data: {
                            ...merged[nodeIdx].data,
                            last_expanded: updatedNode.last_expanded,
                            total_expanded_txs: updatedNode.total_expanded_txs,
                            empty_reason: updatedNode.empty_reason,
                            suggested_chain: updatedNode.suggested_chain,
                            hub_capped: updatedNode.hub_capped,
                            tx_total_real: updatedNode.tx_total_real,
                            tx_has_more: updatedNode.tx_has_more,
                            tx_truncated: updatedNode.tx_truncated,
                            tx_incomplete: updatedNode.tx_incomplete,
                        }
                    };
                }

                return merged;
            });

            setSelectedNode(updatedNode);
            // Also write to useTransactionStore (primary source for Node Inspector)
            if (updatedNode.expanded_transactions?.length > 0) {
                txStore.setNodeTransactions(nodeToExpand.id, updatedNode.expanded_transactions);
            }
            flushSummary(`expandNode(${nodeToExpand.id.slice(0, 12)}…)`);

            // E1: Populate edgeTxMap for consolidated edge drill-down
            if (newTxEntries.size > 0) {
                setEdgeTxMap(prev => {
                    const next = new Map(prev);
                    newTxEntries.forEach((txs, key) => {
                        const existing = next.get(key) || [];
                        const existingHashes = new Set(existing.map(t => t.tx_hash));
                        const deduped = txs.filter(t => !existingHashes.has(t.tx_hash));
                        next.set(key, [...existing, ...deduped]);
                    });
                    return next;
                });
            }

            if (response?.acceleration?.badges?.sanctioned_counterparties > 0) {
                toast.warning(`⚠️ ${response.acceleration.badges.sanctioned_counterparties} sanctioned counterparties in expansion`, { duration: 5000 });
            }

            // Show data source indicator — show NEW count (after dedup) not total fetched
            const dataSource = response.data_source || (txs[0]?.data_source) || 'unknown';
            const newCount = dedupedNewTxs.length;
            if (newCount === 0) {
                if (response.empty_reason !== 'unsupported_protocol_stub') {
                    toast.info('No new transactions found (all already loaded)', { duration: 3000 });
                }
            } else if (dataSource === 'mock') {
                toast.warning(`Loaded ${newCount} new DEMO transactions (mock data — API unavailable)`, { duration: 5000 });
            } else if (dataSource === 'live') {
                toast.success(`Loaded ${newCount} new LIVE transactions from blockchain`, { duration: 3000 });
            } else {
                toast.info(`Loaded ${newCount} new transactions`);
            }

            if ((txStartDate || txEndDate) && txs.length >= fetchLimit) {
                toast.info(`Loaded first ${fetchLimit} transactions in date range. Narrow the range for more precise results.`, { duration: 5000 });
            }
        } catch (error: any) {
            console.error("Failed to expand node", error);
            toast.error(txLoadFailureToast(error, nodeToExpand), { duration: 7000 });
        } finally {
            setLoading(false);
        }
    };

    // ── Add counterparty address to canvas from Transfers tab ──
    const handleAddCounterpartyToCanvas = React.useCallback((address: string, entityName?: string, entityType?: string) => {
        useGraphStore.getState().pushSnapshot();
        // Check if already on canvas (read current elements)
        setElements(prev => {
            const existingIds = new Set(prev.map(e => e.data.id));
            if (existingIds.has(address)) {
                // Node already on canvas — check for non-gold edge between them
                if (selectedNode) {
                    const nodeId = selectedNode.id;
                    // Find edges between these two nodes, separating gold-path from regular
                    const edgesBetween = prev.filter(e => e.data?.source &&
                        ((e.data.source === nodeId && e.data.target === address) ||
                         (e.data.source === address && e.data.target === nodeId)));
                    const goldEdge = edgesBetween.find(e => e.data.edgeType === 'gold-path');
                    const regularEdge = edgesBetween.find(e => e.data.edgeType !== 'gold-path');

                    if (regularEdge) {
                        // Regular edge exists — toggle its visibility
                        if (regularEdge.data._visibility === 0) {
                            const cy = graphRef.current?.getCy();
                            if (cy) {
                                cy.batch(() => {
                                    const cyEdge = cy.getElementById(regularEdge.data.id);
                                    if (cyEdge.length) cyEdge.data('_visibility', 1.0);
                                    const cyNode = cy.getElementById(address);
                                    if (cyNode.length && cyNode.data('_visibility') === 0) cyNode.data('_visibility', 1.0);
                                });
                            }
                            toast.success(`Shown edge to ${address.substring(0, 10)}…`);
                            return prev.map(el => {
                                if (el.data.id === regularEdge.data.id || (el.data.id === address && el.data._visibility === 0)) {
                                    return { ...el, data: { ...el.data, _visibility: 1.0 } };
                                }
                                return el;
                            });
                        }
                        // Regular edge visible — if there's also a gold edge, this pair is fully covered
                        toast.info('Already visible on canvas');
                        return prev;
                    }

                    // No regular edge — check if we should create one
                    // (gold-path edge may exist but covers only algorithm-selected txs)
                    const storeTxs2 = useTransactionStore.getState().getNodeTransactions(selectedNode.id);
                    const txs = storeTxs2?.length ? storeTxs2 : (selectedNode.expanded_transactions || []);
                    const addrLow = address.toLowerCase();
                    const evm = address.startsWith('0x') || address.startsWith('0X');
                    const matchAddr = (a?: string) => a ? (a === address || (evm && a.toLowerCase() === addrLow)) : false;
                    const relatedTxs = txs.filter((tx: any) =>
                        matchAddr(tx.counterparty) || matchAddr(tx.from_address) || matchAddr(tx.to_address)
                    );

                    if (relatedTxs.length > 0) {
                        // If gold-path edge exists AND covers all transactions, no need for a grey edge
                        if (goldEdge && !goldEdge.data.is_consolidated) {
                            // Gold edge is single-tx — check if there are additional txs it doesn't cover
                            const goldTxCount = goldEdge.data.tx_count || 1;
                            if (relatedTxs.length <= goldTxCount) {
                                toast.info('Already visible on canvas (gold path)');
                                return prev;
                            }
                        }

                        // Build consolidated grey edge(s) for non-gold transactions
                        const newEdges: CytoscapeElement[] = [];
                        const outgoing = relatedTxs.filter((tx: any) => {
                            const from = tx.from_address || '';
                            return from === nodeId || (evm && from.toLowerCase() === nodeId.toLowerCase());
                        });
                        const incoming = relatedTxs.filter((tx: any) => {
                            const to = tx.to_address || '';
                            return to === nodeId || (evm && to.toLowerCase() === nodeId.toLowerCase());
                        });
                        // Only add if no edge (gold or regular) exists in this direction
                        if (outgoing.length > 0 && !prev.some(e => e.data?.source === nodeId && e.data?.target === address && e.data.edgeType !== 'gold-path')) {
                            const totalValue = outgoing.reduce((sum: number, tx: any) => sum + (Number(tx.value) || 0), 0);
                            newEdges.push({
                                data: {
                                    id: `${nodeId}->${address}-all`,
                                    source: nodeId, target: address,
                                    label: `${outgoing.length} tx${outgoing.length > 1 ? 's' : ''}`,
                                    is_consolidated: outgoing.length > 1, tx_count: outgoing.length,
                                    total_value: totalValue,
                                }
                            });
                        }
                        if (incoming.length > 0 && !prev.some(e => e.data?.source === address && e.data?.target === nodeId && e.data.edgeType !== 'gold-path')) {
                            const totalValue = incoming.reduce((sum: number, tx: any) => sum + (Number(tx.value) || 0), 0);
                            newEdges.push({
                                data: {
                                    id: `${address}->${nodeId}-all`,
                                    source: address, target: nodeId,
                                    label: `${incoming.length} tx${incoming.length > 1 ? 's' : ''}`,
                                    is_consolidated: incoming.length > 1, tx_count: incoming.length,
                                    total_value: totalValue,
                                }
                            });
                        }
                        if (newEdges.length > 0) {
                            toast.success(`Added ${newEdges.length} edge(s) to canvas`);
                            return [...prev, ...newEdges];
                        }
                    }

                    // No regular edge and no transactions to add — gold path covers it
                    if (goldEdge) {
                        toast.info('Already visible on canvas (gold path)');
                        return prev;
                    }
                }
                toast.info('Address already on canvas');
                return prev;
            }

            const newElements: CytoscapeElement[] = [];

            // Infer placement_role from edge direction vs selected subject (R24 §4.2).
            let counterpartyRole: ReturnType<typeof inferRoleFromEdgeDirection> = 'context';
            if (selectedNode) {
                const storeTxsPeek = useTransactionStore.getState().getNodeTransactions(selectedNode.id);
                const txsPeek = storeTxsPeek?.length ? storeTxsPeek : (selectedNode.expanded_transactions || []);
                const addrLow = address.toLowerCase();
                const evmPeek = address.startsWith('0x') || address.startsWith('0X');
                const matchPeek = (a?: string) => a ? (a === address || (evmPeek && a.toLowerCase() === addrLow)) : false;
                const relatedPeek = txsPeek.filter((tx: any) =>
                    matchPeek(tx.counterparty) || matchPeek(tx.from_address) || matchPeek(tx.to_address)
                );
                const hasOut = relatedPeek.some((tx: any) => {
                    const from = tx.from_address || '';
                    return from === selectedNode.id || (evmPeek && from.toLowerCase() === selectedNode.id.toLowerCase());
                });
                const hasIn = relatedPeek.some((tx: any) => {
                    const to = tx.to_address || '';
                    return to === selectedNode.id || (evmPeek && to.toLowerCase() === selectedNode.id.toLowerCase());
                });
                counterpartyRole = inferRoleFromEdgeDirection(
                    hasOut && hasIn ? 'both' : hasOut ? 'outgoing' : hasIn ? 'incoming' : 'unknown',
                );
            }

            // Add the counterparty node
            newElements.push({
                data: {
                    id: address,
                    label: formatLabel(address, entityName),
                    type: 'address',
                    balance: 0,
                    entity_name: entityName,
                    entity_type: entityType,
                    placement_role: counterpartyRole,
                    _visibility: 1.0,
                }
            });

            // Add consolidated edge(s) with correct direction if selected node is on canvas
            if (selectedNode && existingIds.has(selectedNode.id)) {
                // Read from transaction store (append-only, survives re-selection) first, fallback to element data
                const storeTxs = useTransactionStore.getState().getNodeTransactions(selectedNode.id);
                const txs = storeTxs?.length ? storeTxs : (selectedNode.expanded_transactions || []);
                const nodeId = selectedNode.id;
                // Case-insensitive compare for EVM addresses (R1: normalize)
                const addrLow = address.toLowerCase();
                const evm = address.startsWith('0x') || address.startsWith('0X');
                const match = (a?: string) => a ? (a === address || (evm && a.toLowerCase() === addrLow)) : false;
                const relatedTxs = txs.filter(tx =>
                    match(tx.counterparty) ||
                    match(tx.from_address) ||
                    match(tx.to_address)
                );
                if (relatedTxs.length > 0) {
                    // Split by direction: outgoing (root→counterparty) vs incoming (counterparty→root)
                    const outgoing = relatedTxs.filter(tx => {
                        const from = tx.from_address || '';
                        return from === nodeId || (evm && from.toLowerCase() === nodeId.toLowerCase());
                    });
                    const incoming = relatedTxs.filter(tx => {
                        const to = tx.to_address || '';
                        return to === nodeId || (evm && to.toLowerCase() === nodeId.toLowerCase());
                    });

                    if (outgoing.length > 0) {
                        const totalValue = outgoing.reduce((sum, tx) => sum + (Number(tx.value) || 0), 0);
                        const totalUsd = outgoing.reduce((sum, tx) => sum + (Number(tx.historical_usd) || 0), 0);
                        // Pick dominant token for label
                        const tokenCounts = new Map<string, number>();
                        outgoing.forEach(tx => {
                            const tok = tx.token_symbol || 'native';
                            tokenCounts.set(tok, (tokenCounts.get(tok) || 0) + (Number(tx.value) || 0));
                        });
                        const dominantToken = [...tokenCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || '';
                        // Use native value if available, fallback to USD for zero-value txs (MEV, internal transfers)
                        const displayValue = totalValue > 0 ? totalValue : totalUsd;
                        const displayUnit = totalValue > 0 ? dominantToken : (totalUsd > 0 ? 'USD' : dominantToken);
                        const fmtVal = displayValue >= 1_000_000 ? `${(displayValue / 1_000_000).toFixed(1)}M` : displayValue >= 1_000 ? `${(displayValue / 1_000).toFixed(1)}K` : displayValue >= 1 ? displayValue.toFixed(2) : displayValue > 0 ? displayValue.toFixed(4) : '0';
                        const prefix = totalValue === 0 && totalUsd > 0 ? '$' : '';
                        const edgeLabel = outgoing.length === 1 ? `${prefix}${fmtVal} ${displayUnit}` : `${outgoing.length} txs · ${prefix}${fmtVal} ${displayUnit}`;
                        logEdge({
                            path: 'handleAddCounterparty',
                            edgeId: `${nodeId}->${address}`,
                            source: nodeId, target: address,
                            direction: 'outgoing', rootNodeId: nodeId,
                            value: totalValue, isConsolidated: true, txCount: outgoing.length,
                        });
                        newElements.push({
                            data: {
                                id: `${nodeId}->${address}`,
                                source: nodeId,
                                target: address,
                                label: edgeLabel,
                                is_consolidated: true,
                                tx_count: outgoing.length,
                                total_value: totalValue,
                                value: totalValue,
                                token_symbol: dominantToken,
                                _visibility: 1.0,
                            }
                        });
                    }

                    if (incoming.length > 0) {
                        const totalValue = incoming.reduce((sum, tx) => sum + (Number(tx.value) || 0), 0);
                        const totalUsd = incoming.reduce((sum, tx) => sum + (Number(tx.historical_usd) || 0), 0);
                        const tokenCounts = new Map<string, number>();
                        incoming.forEach(tx => {
                            const tok = tx.token_symbol || 'native';
                            tokenCounts.set(tok, (tokenCounts.get(tok) || 0) + (Number(tx.value) || 0));
                        });
                        const dominantToken = [...tokenCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || '';
                        const displayValue = totalValue > 0 ? totalValue : totalUsd;
                        const displayUnit = totalValue > 0 ? dominantToken : (totalUsd > 0 ? 'USD' : dominantToken);
                        const fmtVal = displayValue >= 1_000_000 ? `${(displayValue / 1_000_000).toFixed(1)}M` : displayValue >= 1_000 ? `${(displayValue / 1_000).toFixed(1)}K` : displayValue >= 1 ? displayValue.toFixed(2) : displayValue > 0 ? displayValue.toFixed(4) : '0';
                        const prefix = totalValue === 0 && totalUsd > 0 ? '$' : '';
                        const edgeLabel = incoming.length === 1 ? `${prefix}${fmtVal} ${displayUnit}` : `${incoming.length} txs · ${prefix}${fmtVal} ${displayUnit}`;
                        logEdge({
                            path: 'handleAddCounterparty',
                            edgeId: `${address}->${nodeId}`,
                            source: address, target: nodeId,
                            direction: 'incoming', rootNodeId: nodeId,
                            value: totalValue, isConsolidated: true, txCount: incoming.length,
                        });
                        newElements.push({
                            data: {
                                id: `${address}->${nodeId}`,
                                source: address,
                                target: nodeId,
                                label: edgeLabel,
                                is_consolidated: true,
                                tx_count: incoming.length,
                                total_value: totalValue,
                                value: totalValue,
                                token_symbol: dominantToken,
                                _visibility: 1.0,
                            }
                        });
                    }
                }
            }

            toast.success(`Added ${entityName || address.substring(0, 10) + '…'} to canvas`);
            return [...prev, ...newElements];
        });

        // If we added this node without an entity name, kick off background enrichment
        // so the canvas label updates when OKLink/TronScan/etc. resolves it.
        if (!entityName) {
            const addrChain = (selectedNode?.chain || effectiveChain || detectChain(address)) as string;
            triggerEnrichment([{ address, chain: addrChain }]);
        }

        // Register edge txs in edgeTxMap outside the state updater (side-effect safe)
        if (selectedNode) {
            const txs = selectedNode.expanded_transactions || [];
            const nodeId = selectedNode.id;
            // Case-insensitive compare for EVM addresses (R1: normalize)
            const addrLower = address.toLowerCase();
            const isEvm = address.startsWith('0x') || address.startsWith('0X');
            const matchAddr = (a?: string) => {
                if (!a) return false;
                if (a === address) return true;
                return isEvm && a.toLowerCase() === addrLower;
            };
            const relatedTxs = txs.filter(tx =>
                matchAddr(tx.counterparty) ||
                matchAddr(tx.from_address) ||
                matchAddr(tx.to_address)
            );
            if (relatedTxs.length > 0) {
                // Split by direction for correct edgeTxMap keys
                const matchNode = (a?: string) => {
                    if (!a) return false;
                    if (a === nodeId) return true;
                    return isEvm && a.toLowerCase() === nodeId.toLowerCase();
                };
                const outgoing = relatedTxs.filter(tx => matchNode(tx.from_address));
                const incoming = relatedTxs.filter(tx => matchNode(tx.to_address));
                setEdgeTxMap(m => {
                    const next = new Map(m);
                    if (outgoing.length > 0) next.set(`${nodeId}->${address}`, outgoing);
                    if (incoming.length > 0) next.set(`${address}->${nodeId}`, incoming);
                    return next;
                });
            }
        }
    }, [formatLabel, selectedNode]);

    const handleRemoveFromCanvas = React.useCallback((address: string) => {
        // v5.3: stash the node (its edges are auto-stashed as orphans). The
        // controller handles the undo snapshot and both the store + cytoscape
        // stay in sync. Case-insensitive match for EVM addresses.
        const isEvm = address.startsWith('0x') || address.startsWith('0X');
        const addrLow = isEvm ? address.toLowerCase() : null;
        const match = useGraphStore.getState().elements.find(el =>
            !el.data.source && el.data.id && (
                el.data.id === address || (addrLow && el.data.id.toLowerCase() === addrLow)
            )
        );
        if (!match?.data.id) return; // not on canvas
        canvasController.stash([match.data.id], 'user-hide', 'user');
        toast.success(`Stashed ${address.substring(0, 10)}… — restore from Tray`);
    }, []);

    // ── Add Transaction Hash to canvas ──
    const handleAddTxHash = React.useCallback(async (hash: string) => {
        const input = hash.trim();
        if (!input) return;

        // Detect if input is a wallet address (not a tx hash)
        const isAddress = input.startsWith('T') && input.length === 34  // TRX
            || input.startsWith('0x') && input.length === 42           // EVM
            || input.startsWith('bc1') || (input.length >= 25 && input.length <= 35 && /^[13][a-km-zA-HJ-NP-Z1-9]+$/.test(input)); // BTC

        if (isAddress) {
            // Add as a node — detect chain, load triage, add to canvas
            const isEvm = input.startsWith('0x');
            const isTrx = input.startsWith('T') && input.length === 34;
            const addrChain = isTrx ? 'TRX' : isEvm ? (chain || 'ETH') : 'BTC';

            // Check if already on canvas
            const existing = elements.find(el => !el.data?.source && (
                el.data.id === input || el.data.id?.toLowerCase() === input.toLowerCase()
            ));
            if (existing) {
                const cy = graphRef.current?.getCy() || (window as any).__cy;
                if (cy) {
                    const node = cy.getElementById(existing.data.id);
                    if (node.length) {
                        cy.elements().unselect();
                        node.select();
                        cy.animate({ center: { eles: node }, duration: 300 });
                    }
                }
                toast.info('Address already on canvas — highlighted');
                return;
            }

            // Add node to canvas — snapshot first so Ctrl+Z can revert this user-initiated add
            // (bypasses addNodesToCanvas because handleAddTxHash predates that helper; the
            //  snapshot here matches what addNodesToCanvas does at line 1907).
            useGraphStore.getState().pushSnapshot();
            setElements(prev => [...prev, {
                data: {
                    id: input,
                    label: `${input.slice(0, 6)}...${input.slice(-4)}`,
                    type: 'address',
                    chain: addrChain,
                    _visibility: 1.0,
                }
            }]);
            toast.success(`Added ${input.slice(0, 10)}... to canvas (${addrChain})`);
            return;
        }

        // Check if tx hash is already on canvas — highlight it instead of re-adding
        const existingEdge = elements.find(el =>
            el.data?.source && (el.data.tx_hash === hash ||
                (el.data.is_consolidated && edgeTxMap.get(`${el.data.source}->${el.data.target}`)?.some((tx: any) => tx.tx_hash === hash)))
        );
        if (existingEdge) {
            // Highlight the edge on canvas
            const cy = graphRef.current?.getCy();
            if (cy) {
                const cyEdge = cy.getElementById(existingEdge.data.id);
                if (cyEdge.length) {
                    cy.elements().unselect();
                    cyEdge.select();
                    cy.animate({ center: { eles: cyEdge }, duration: 300 });
                }
            }
            toast.info('Transaction already on canvas — highlighted');
            if (!addedTxHashes.includes(hash)) {
                setAddedTxHashes(prev => [...prev, hash]);
            }
            return;
        }

        setAddedTxHashes(prev => [...prev, hash]);

        // Load tx graph and MERGE into existing canvas (don't replace)
        const sanitizedHash = hash.trim();
        const resolvedChain = chain ? normalizeChain(chain) : null;
        try {
            let txGraph = null;
            let txChain = resolvedChain;
            try {
                const txDetails = await transactionService.searchTransaction(sanitizedHash, txChain || undefined);
                if (txDetails?.is_value_transfer === false) {
                    // Resource/staking/governance op — no funds move, no edge to
                    // add. Surface it and undo the optimistic addedTxHashes push
                    // so the canvas isn't marked as containing a phantom edge.
                    toast.info('Resource operation — no value transfer', {
                        description: `${txDetails.operation_label || txDetails.contract_type || 'Tron resource operation'}. This transaction does not move funds (no amount or counterparty).`,
                    });
                    setAddedTxHashes(prev => prev.filter(h => h !== hash));
                    return;
                }
                if (txDetails?.chain) {
                    txChain = normalizeChain(txDetails.chain);
                    txGraph = await transactionService.getTransactionGraph(txChain, sanitizedHash);
                }
            } catch { /* fallback below */ }

            if (!txGraph || !txGraph.nodes?.length) {
                const candidates = txChain
                    ? [txChain, 'BTC', 'TRX', 'ETH', 'SOL', 'MATIC', 'XRP', 'TON']
                    : ['BTC', 'TRX', 'ETH', 'SOL', 'MATIC', 'XRP', 'TON'];
                for (const c of candidates) {
                    try {
                        const g = await transactionService.getTransactionGraph(c, sanitizedHash);
                        if (g?.nodes?.length) { txGraph = g; txChain = c; break; }
                    } catch { continue; }
                }
            }

            if (txGraph?.nodes?.length) {
                if (txGraph.chain) txChain = normalizeChain(txGraph.chain);
                const newEls: CytoscapeElement[] = [];

                txGraph.nodes.forEach((node: any) => {
                    const { source: _s2, target: _t2, parent: _p2, position: _pos2, ...safeFields2 } = node;
                    newEls.push({ data: { id: node.id, label: node.label || `${node.id?.substring(0, 8)}...`, type: node.type || 'address', chain: node.chain || txChain || undefined, entity_name: node.entity_name, entity_type: node.entity_type, risk_score: node.risk_score, _visibility: 1.0, ...safeFields2 } });
                });
                txGraph.edges.forEach((edge: any) => {
                    const tok = cleanTokenSymbol(edge.token_symbol) || txChain || '';
                    const rawVal = typeof edge.value === 'number' ? edge.value : parseFloat(edge.value) || 0;
                    const fmtEdgeVal = rawVal === 0 ? '' : rawVal >= 1_000_000 ? `${(rawVal / 1_000_000).toFixed(1)}M` : rawVal >= 1_000 ? `${(rawVal / 1_000).toFixed(1)}K` : rawVal >= 1 ? rawVal.toFixed(2) : rawVal >= 0.0001 ? rawVal.toFixed(4) : rawVal.toExponential(2);
                    const edgeLabel = fmtEdgeVal && tok ? `${fmtEdgeVal} ${tok}` : (fmtEdgeVal || tok || '');
                    // Use consistent edge ID format: source-target-txhash (matches consolidation)
                    const txHash = edge.tx_hash || edge.id || '';
                    const edgeId = edge.source && edge.target ? `${edge.source}-${edge.target}-${txHash}` : edge.id;
                    newEls.push({ data: { id: edgeId, source: edge.source, target: edge.target, label: edgeLabel, tx_hash: txHash, value: edge.value, timestamp: edge.timestamp, token_symbol: tok, fee: edge.fee, block_height: edge.block_height, _visibility: 1.0 } });
                });

                const enriched = await enrichGraphWithEntities(newEls, txChain || null);

                // Merge into existing elements (not replace) — dedup by ID and source+target pair
                setElements(prev => {
                    const merged = [...prev];
                    const existingIds = new Set(merged.map(e => e.data.id));
                    const existingEdgePairs = new Set(
                        merged.filter(e => e.data.source && e.data.target)
                            .map(e => `${e.data.source}→${e.data.target}`)
                    );
                    enriched.forEach(el => {
                        if (existingIds.has(el.data.id)) return;
                        // For edges, also check if an edge with same source→target already exists
                        if (el.data.source && el.data.target) {
                            const pairKey = `${el.data.source}→${el.data.target}`;
                            if (existingEdgePairs.has(pairKey)) return;
                            existingEdgePairs.add(pairKey);
                        }
                        merged.push(el);
                        existingIds.add(el.data.id);
                    });
                    return merged;
                });

                toast.success(`Transaction added to canvas: ${sanitizedHash.substring(0, 16)}...`);
            } else {
                toast.error('Transaction not found on any supported chain');
                setAddedTxHashes(prev => prev.filter(h => h !== hash));
            }
        } catch (err: any) {
            toast.error(`Failed to load transaction: ${err.message || 'Unknown error'}`);
            setAddedTxHashes(prev => prev.filter(h => h !== hash));
        }
    }, [elements, edgeTxMap, addedTxHashes, chain]);

    const handleRemoveTxHash = React.useCallback((hash: string) => {
        setAddedTxHashes(prev => prev.filter(h => h !== hash));
    }, []);

    // ── Dossier entity enrichment callback ──
    // When NodeInspector's dossier discovers an entity name for a node,
    // propagate the label back to canvas elements so it renders on the graph.
    const handleEntityEnriched = React.useCallback((address: string, entityName: string, entityType?: string) => {
        // Skip placeholders entirely — they don't have real addresses and any
        // entity attribution would be garbage. The placeholder detection lives
        // in the shared utility so any future enrichment path picks it up.
        if (isPlaceholderNode({ id: address })) return;
        setElements(prev => {
            let changed = false;
            const next = prev.map(el => {
                if (el.data?.source || !el.data?.id) return el;
                // Case-insensitive match for EVM addresses
                const match = el.data.id === address ||
                    ((el.data.id.startsWith('0x') || el.data.id.startsWith('0X')) && el.data.id.toLowerCase() === address.toLowerCase());
                if (!match) return el;
                // Don't overwrite existing entity_name
                if (el.data.entity_name && el.data.entity_name !== 'Unknown') return el;
                // Defense in depth: if the matched node IS a placeholder
                // (e.g. it carries forensic_ghost flags), short-circuit so we
                // never overwrite the placeholder marker fields.
                if (isPlaceholderNode(el)) return el;
                changed = true;
                // preservePlaceholderFields is a no-op for non-placeholders;
                // included so that any future merge here automatically keeps
                // placeholder semantics intact if a placeholder slips through.
                const merged = preservePlaceholderFields(el.data, {
                    ...el.data,
                    entity_name: entityName,
                    entity_type: entityType || el.data.entity_type,
                    label: formatLabel(el.data.id, entityName),
                });
                return { ...el, data: merged };
            });
            return changed ? next : prev;
        });
    }, [formatLabel]);

    // ── Background entity enrichment (TronScan, GoPlus, Etherscan labels) ──
    // Triggered after addNodesToCanvas for nodes with no entity attribution.
    // Results are propagated back to canvas via handleEntityEnriched.
    const { enrichmentStatus, triggerEnrichment, cancelEnrichment } = useEntityEnrichment(handleEntityEnriched);

    // B1 cutoff: tie entity-enrichment polling to the investigation lifecycle.
    // The polling loop runs independently (60 × 2.5s = 2.5 min ceiling) and
    // writes entity labels back onto canvas nodes. After 'complete' the user
    // has the report; labels trickling in mid-review is one of the known
    // sources of "canvas keeps changing 2-10 min later." Also cancel on a
    // new run so stale labels from the previous session don't land on the
    // new canvas.
    useEffect(() => {
        if (autoInvestigation.isComplete) {
            cancelEnrichment();
        }
    }, [autoInvestigation.isComplete, cancelEnrichment]);
    useEffect(() => {
        if (autoInvestigation.isRunning) {
            cancelEnrichment();
        }
    }, [autoInvestigation.isRunning, cancelEnrichment]);

    // ── Dossier contract detection callback ──
    // When NodeInspector's dossier profile reveals is_contract for any node
    // (including nodes added via SSE / auto-trace that didn't go through the
    // initial address-details load), propagate the flag back to canvas so the
    // dashed-blue-border Cytoscape style is applied correctly.
    const handleContractDetected = React.useCallback((address: string, isContract: boolean) => {
        setElements(prev => {
            let changed = false;
            const next = prev.map(el => {
                if (el.data?.source || !el.data?.id) return el;
                const match = el.data.id === address ||
                    ((el.data.id.startsWith('0x') || el.data.id.startsWith('0X')) && el.data.id.toLowerCase() === address.toLowerCase());
                if (!match) return el;
                if (el.data.is_contract === isContract) return el; // already correct
                changed = true;
                return { ...el, data: { ...el.data, is_contract: isContract } };
            });
            return changed ? next : prev;
        });
    }, []);

    // ── Transaction Loading: Count Mode ──
    const loadTransactionsByCount = async (count: number) => {
        const nodeToLoad = selectedNode;
        if (!nodeToLoad?.id) return;
        // Hot wallet / omnibus commingled funds guardrail
        if (isHotWalletNode(nodeToLoad) && !(nodeToLoad as any)._bypass_hot_warning) {
            setHotWalletModalNode(nodeToLoad);
            setPendingExpandAction(() => async () => {
                const bypassNode = { ...nodeToLoad, _bypass_hot_warning: true };
                setSelectedNode(bypassNode as any);
                await loadTransactionsByCount(count);
            });
            return;
        }
        // Privacy chain TX nodes are not addresses — skip transaction fetch
        if ((nodeToLoad as any).is_privacy_chain && (nodeToLoad as any).type === 'transaction') return;
        if (isMockDataMode(demoDataModeRef.current)) {
            const existing = nodeToLoad.expanded_transactions?.length
                || useTransactionStore.getState().getNodeTransactions(nodeToLoad.id)?.length
                || 0;
            toast.info(
                existing > 0
                    ? MOCK_DEMO_TX_TOAST
                    : 'Mock demo — no live txs. Switch Data Mode to Live to fetch from the blockchain.',
                { duration: 5000 },
            );
            return;
        }
        const loadAddr = normalizeAddressInput(nodeToLoad.id);
        let nodeChain = resolveUsableChain(
            loadAddr,
            (nodeToLoad as any).chain,
            chain,
        );
        setLoading(true);
        setDateRangeAllLoaded(false);
        try {
            const fetchLimit = hubExpandLimit(count, nodeToLoad);
            if (fetchLimit < count) {
                toast.info(`Hub wallet capped at ${fetchLimit} txs per load (requested ${count}).`, { duration: 4000 });
            }
            let usedLimit = fetchLimit;
            let response;
            try {
                response = await addressService.getTransactions(
                    nodeChain, loadAddr, fetchLimit, 0,
                    undefined, undefined, debugMode || undefined, caseIdForLoad || undefined,
                    { cacheOnly: wantsCacheOnly(demoDataModeRef.current) },
                );
            } catch (firstErr) {
                const retryLimit = Math.min(fetchLimit, TX_LOAD_RETRY_LIMIT);
                if (!isTxLoadNetworkFailure(firstErr)) {
                    throw firstErr;
                }
                toast.info(
                    `Heavy wallet — retrying with ${retryLimit} transfers…`,
                    { duration: 3500 },
                );
                usedLimit = retryLimit;
                response = await addressService.getTransactions(
                    nodeChain, loadAddr, retryLimit, 0,
                    undefined, undefined, debugMode || undefined, caseIdForLoad || undefined,
                    { cacheOnly: wantsCacheOnly(demoDataModeRef.current) },
                );
            }
            if (response?.switched_chain && response?.actual_chain) {
                const fromChain = response.original_chain || nodeChain;
                nodeChain = String(response.actual_chain);
                toast.info(`No transactions on ${fromChain} — loaded from ${nodeChain}`, { duration: 6000 });
                setElements(prev => prev.map(el =>
                    el.data?.id === nodeToLoad.id
                        ? { ...el, data: { ...el.data, chain: nodeChain } }
                        : el
                ));
                if (urlAddress === nodeToLoad.id || address === nodeToLoad.id) {
                    setSearchParams(prev => {
                        const next = new URLSearchParams(prev);
                        next.set('chain', nodeChain);
                        return next;
                    }, { replace: true });
                }
            }
            const txs = response.transactions || [];
            if (wantsCacheOnly(demoDataModeRef.current) && (response.cache_miss || txs.length === 0)) {
                toast.info(CACHED_MISS_TOAST, { duration: 5000 });
                return;
            }
            if (txs.length === 0) {
                const evmHint = ['ETH', 'BSC', 'BNB', 'MATIC', 'ARB', 'BASE', 'OP', 'AVAX'].includes(
                    String(nodeChain || '').toUpperCase(),
                );
                toast.warning(
                    `No transactions on ${nodeChain}.` +
                    (evmHint
                        ? ' If this is an EVM address, use Re-probe chain in Overview.'
                        : nodeChain === 'TRX'
                            ? ' Confirm the address on TronScan, or retry if TronGrid is rate-limiting.'
                            : ''),
                    { duration: 7000 },
                );
            }
            const expandedTxs = txs.map((tx: any) => {
                const isSender = !!tx.from_address && tx.from_address.toLowerCase() === nodeToLoad.id.toLowerCase();
                return {
                    tx_hash: tx.tx_hash,
                    from_address: tx.from_address,
                    to_address: tx.to_address,
                    value: tx.value,
                    fee: tx.fee,
                    timestamp: tx.timestamp,
                    block_height: tx.block_height,
                    token_symbol: tx.token_symbol,
                    historical_usd: tx.historical_usd,
                    direction: isSender ? 'outgoing' : 'incoming',
                    counterparty: isSender ? tx.to_address : tx.from_address,
                    counterparty_entity: tx.other_address_entity_name,
                    counterparty_type: tx.other_address_entity_type,
                    counterparty_risk: tx.other_address_risk_score,
                    from_entity_name: tx.from_entity_name,
                    from_entity_type: tx.from_entity_type,
                    to_entity_name: tx.to_entity_name,
                    to_entity_type: tx.to_entity_type,
                };
            });
            // Merge into expanded_transactions (additive, deduped by tx_hash)
            if (selectedNode?.id === nodeToLoad.id) {
                setSelectedNode(prev => {
                    if (!prev || prev.id !== nodeToLoad.id) return prev;
                    const existing = prev.expanded_transactions || [];
                    const existingHashes = new Set(existing.map((t: any) => t.tx_hash));
                    const deduped = expandedTxs.filter((t: any) => t.tx_hash && !existingHashes.has(t.tx_hash));
                    const merged = [...existing, ...deduped];
                    return {
                        ...prev,
                        chain: nodeChain,
                        expanded_transactions: merged,
                        last_expanded: new Date().toISOString(),
                        total_expanded_txs: merged.length,
                        ...coverageAfterLoad(response, prev),
                        hub_capped: response.hub_capped || prev.hub_capped,
                        data_source: response.data_source || prev.data_source,
                    };
                });
                addTransactionsToGraph(txs, nodeChain);
                if (response?.hub_capped && !(fetchLimit < count)) {
                    // The server clamped a hub the frontend name check did not recognise.
                    toast.info(`Hub wallet capped at ${response.fetched_count ?? txs.length} txs per load (requested ${count}).`, { duration: 4000 });
                }
                if (expandedTxs.length > 0) {
                    toast.success(`Loaded ${expandedTxs.length} transactions (last ${usedLimit})`);
                }
            }
        } catch (error: any) {
            toast.error(txLoadFailureToast(error, nodeToLoad), { duration: 7000 });
        } finally {
            setLoading(false);
        }
    };

    // ── Transaction Loading: Date Range Mode (batched) ──
    const loadTransactionsByDateRange = async (
        startDate: string, endDate: string,
        onProgress: (loaded: number, batch: number) => void
    ) => {
        const nodeToLoad = selectedNode;
        if (!nodeToLoad?.id) return;
        if ((nodeToLoad as any).is_privacy_chain && (nodeToLoad as any).type === 'transaction') return;
        if (isMockDataMode(demoDataModeRef.current)) {
            toast.info(MOCK_DEMO_TX_TOAST, { duration: 5000 });
            return;
        }
        const nodeChain = resolveUsableChain(
            nodeToLoad.id,
            (nodeToLoad as any).chain,
            chain,
        );
        const BATCH = hubExpandLimit(1000, nodeToLoad);
        let offset = 0;
        let allTxs: any[] = [];
        let allExpandedTxs: any[] = [];
        // "All loaded" only when the server said the history of the window ended. A short batch is not
        // that: a hub clamp, a merged slice or a fetcher that cannot say all return fewer rows.
        let rangeExhausted = false;
        let rangeIncomplete = false;
        const seenRangeKeys = new Set<string>();
        const MAX_RANGE_BATCHES = 200;
        let rangeBatches = 0;
        cancelDateRangeRef.current = false;
        setLoading(true);
        setDateRangeAllLoaded(false);

        try {
            while (!cancelDateRangeRef.current) {
                // Abort if user changed node selection
                if (selectedNode?.id !== nodeToLoad.id) break;

                const response = await addressService.getTransactions(
                    nodeChain, nodeToLoad.id, BATCH, offset,
                    startDate, endDate, debugMode || undefined, undefined,
                    { cacheOnly: wantsCacheOnly(demoDataModeRef.current) },
                );
                rangeBatches += 1;
                const batchTxsAll = response.transactions || [];
                // Rows already seen in an earlier batch (an offset the server did not honour) do not count.
                const batchTxs = batchTxsAll.filter((tx: any) => {
                    const k = `${tx.tx_hash}|${tx.from_address}|${tx.to_address}|${tx.token_symbol ?? ''}`;
                    if (seenRangeKeys.has(k)) return false;
                    seenRangeKeys.add(k);
                    return true;
                });
                rangeExhausted = isExhausted(response);
                rangeIncomplete = response.incomplete === true;
                allTxs = [...allTxs, ...batchTxs];

                const batchExpanded = batchTxs.map((tx: any) => {
                    const isSender = !!tx.from_address && tx.from_address.toLowerCase() === nodeToLoad.id.toLowerCase();
                    return {
                        tx_hash: tx.tx_hash,
                        from_address: tx.from_address,
                        to_address: tx.to_address,
                        value: tx.value,
                        fee: tx.fee,
                        timestamp: tx.timestamp,
                        block_height: tx.block_height,
                        token_symbol: tx.token_symbol,
                        historical_usd: tx.historical_usd,
                        direction: isSender ? 'outgoing' : 'incoming',
                        counterparty: isSender ? tx.to_address : tx.from_address,
                        counterparty_entity: tx.other_address_entity_name,
                        counterparty_type: tx.other_address_entity_type,
                        counterparty_risk: tx.other_address_risk_score,
                    };
                });
                allExpandedTxs = [...allExpandedTxs, ...batchExpanded];

                const batchNum = Math.floor(offset / BATCH) + 1;
                onProgress(allExpandedTxs.length, batchNum);

                if (rangeExhausted || rangeIncomplete) break;           // the server said the window ended, or a read failed
                if (batchTxs.length === 0) break;                       // nothing new came back: stop, but not "all loaded"
                if (rangeBatches >= MAX_RANGE_BATCHES) break;
                offset += batchTxsAll.length;                           // rows the server returned, not the batch size asked for
                await new Promise(r => setTimeout(r, 250)); // Rate limit safety
            }

            // Merge into expanded_transactions (additive, deduped by tx_hash)
            if (selectedNode?.id === nodeToLoad.id) {
                setSelectedNode(prev => {
                    if (!prev || prev.id !== nodeToLoad.id) return prev;
                    const existing = prev.expanded_transactions || [];
                    const existingHashes = new Set(existing.map((t: any) => t.tx_hash));
                    const deduped = allExpandedTxs.filter((t: any) => t.tx_hash && !existingHashes.has(t.tx_hash));
                    const merged = [...existing, ...deduped];
                    return {
                        ...prev,
                        expanded_transactions: merged,
                        last_expanded: new Date().toISOString(),
                        total_expanded_txs: merged.length,
                    };
                });
                addTransactionsToGraph(allTxs, nodeChain);
                setDateRangeAllLoaded(!cancelDateRangeRef.current && rangeExhausted);

                if (cancelDateRangeRef.current) {
                    toast.info(`Cancelled — kept ${allExpandedTxs.length} txs loaded so far`);
                } else if (rangeExhausted) {
                    toast.success(`Loaded ${allExpandedTxs.length} txs (${startDate} to ${endDate}), complete`);
                } else if (rangeIncomplete) {
                    toast.warning(`Loaded ${allExpandedTxs.length} txs (${startDate} to ${endDate}) — a part of the read failed, the window may hold more. Load again to retry.`, { duration: 8000 });
                } else {
                    toast.success(`Loaded ${allExpandedTxs.length} txs (${startDate} to ${endDate}), more may exist`);
                }
            }
        } catch (error: any) {
            toast.error(txLoadFailureToast(error, nodeToLoad), { duration: 7000 });
            setDateRangeAllLoaded(false);
        } finally {
            setLoading(false);
        }
    };

    // E1: Edge consolidation — aggregate all txs between same (from, to) pair into one edge
    const addTransactionsToGraph = (txs: any[], nodeChain: string) => {
        useGraphStore.getState().pushSnapshot();
        if (!txs || txs.length === 0) return;

        // Collect individual txs per consolidated edge key for edgeTxMap update
        const newTxEntries = new Map<string, any[]>();

        // Use functional updater to avoid race condition with concurrent expandNode calls
        setElements(prevElements => {
            const merged = [...prevElements];
            const existingIds = new Set(merged.map(e => e.data.id));

            // Identify gold-path edge pairs — NEVER consolidate over these
            const goldPathPairs = new Set<string>();
            merged.forEach(el => {
                if (el.data?.edgeType === 'gold-path' && el.data.source && el.data.target) {
                    goldPathPairs.add(`${el.data.source}->${el.data.target}`);
                    goldPathPairs.add(`${el.data.target}->${el.data.source}`);
                }
            });

            // Track consolidated edge aggregates — seed from existing consolidated edges
            const consolidatedAggs = new Map<string, {
                fromAddr: string;
                toAddr: string;
                totalValue: number;
                txCount: number;
                tokens: Map<string, number>; // token -> volume for that token
                firstTimestamp: any;
                lastTimestamp: any;
                elementIndex: number; // -1 = new, >=0 = update in-place
            }>();
            merged.forEach((el, idx) => {
                if (el.data?.is_consolidated) {
                    const key = `${el.data.source}->${el.data.target}`;
                    // Skip pairs that have a gold-path edge — don't overwrite them
                    if (goldPathPairs.has(key)) return;
                    const tokens = new Map<string, number>();
                    // Reconstruct token volumes from existing label (best-effort)
                    if (el.data.token_symbol) {
                        el.data.token_symbol.split(', ').forEach((t: string) => tokens.set(t, 0));
                    }
                    consolidatedAggs.set(key, {
                        fromAddr: el.data.source!,
                        toAddr: el.data.target!,
                        totalValue: el.data.total_value || 0,
                        txCount: el.data.tx_count || 0,
                        tokens,
                        firstTimestamp: el.data.first_timestamp,
                        lastTimestamp: el.data.last_timestamp,
                        elementIndex: idx,
                    });
                }
            });

            const zeroValueAggregates = new Map<string, {
                fromAddr: string;
                toAddr: string;
                tokenSymbol: string;
                count: number;
                latestTimestamp: any;
            }>();

            txs.forEach((tx: any) => {
                const fromAddr = tx.from_address || tx.from;
                const toAddr = tx.to_address || tx.to;
                if (!fromAddr || !toAddr) return;

                // Add nodes if missing
                if (!existingIds.has(fromAddr)) {
                    merged.push({ data: { id: fromAddr, label: formatLabel(fromAddr), type: 'address', _visibility: 1.0 } });
                    existingIds.add(fromAddr);
                }
                if (!existingIds.has(toAddr)) {
                    merged.push({ data: { id: toAddr, label: formatLabel(toAddr), type: 'address', _visibility: 1.0 } });
                    existingIds.add(toAddr);
                }

                const txValue = Number(tx.value || 0);
                const tokenSymbol = cleanTokenSymbol(tx.token_symbol) || nodeChain || '';

                // Zero-value: keep existing inferred-edge aggregation
                if (txValue === 0) {
                    const aggKey = `${fromAddr}->${toAddr}:${tokenSymbol}`;
                    const existingAgg = zeroValueAggregates.get(aggKey);
                    if (existingAgg) {
                        existingAgg.count += 1;
                        existingAgg.latestTimestamp = tx.timestamp || existingAgg.latestTimestamp;
                    } else {
                        zeroValueAggregates.set(aggKey, {
                            fromAddr, toAddr, tokenSymbol,
                            count: 1, latestTimestamp: tx.timestamp,
                        });
                    }
                    return;
                }

                // Non-zero: consolidate by (from -> to) pair
                const consKey = `${fromAddr}->${toAddr}`;

                // Store individual tx for edgeTxMap (even for gold-path pairs — supports drill-down)
                const txHash = tx.tx_hash || tx.id;
                const txEntry = {
                    tx_hash: txHash,
                    from_address: fromAddr,
                    to_address: toAddr,
                    value: txValue,
                    fee: tx.fee,
                    timestamp: tx.timestamp,
                    block_height: tx.block_height,
                    token_symbol: tokenSymbol,
                };
                if (!newTxEntries.has(consKey)) newTxEntries.set(consKey, []);
                // Dedup by tx_hash to prevent double-counting same tx from different code paths
                const existingTxs = newTxEntries.get(consKey)!;
                if (txHash && existingTxs.some(t => t.tx_hash === txHash)) return;
                existingTxs.push(txEntry);

                // Skip consolidation for gold-path pairs — preserve the gold edge as-is
                if (goldPathPairs.has(consKey)) return;

                const existing = consolidatedAggs.get(consKey);
                if (existing) {
                    // Dedup: don't add value if this tx_hash was already counted
                    const seenKey = `_seen_${consKey}`;
                    if (!(existing as any)[seenKey]) (existing as any)[seenKey] = new Set<string>();
                    if (txHash && (existing as any)[seenKey].has(txHash)) return;
                    if (txHash) (existing as any)[seenKey].add(txHash);
                    existing.totalValue += txValue;
                    existing.txCount += 1;
                    existing.tokens.set(tokenSymbol, (existing.tokens.get(tokenSymbol) || 0) + txValue);
                    if (tx.timestamp) {
                        const ts = tsToMillis(tx.timestamp);
                        if (!isNaN(ts)) {
                            const existFirst = tsToMillis(existing.firstTimestamp);
                            const existLast = tsToMillis(existing.lastTimestamp);
                            if (isNaN(existFirst) || ts < existFirst) existing.firstTimestamp = tx.timestamp;
                            if (isNaN(existLast) || ts > existLast) existing.lastTimestamp = tx.timestamp;
                        }
                    }
                } else {
                    consolidatedAggs.set(consKey, {
                        fromAddr, toAddr,
                        totalValue: txValue,
                        txCount: 1,
                        tokens: new Map([[tokenSymbol, txValue]]),
                        firstTimestamp: tx.timestamp,
                        lastTimestamp: tx.timestamp,
                        elementIndex: -1,
                    });
                }
            });

            // Emit consolidated edges
            consolidatedAggs.forEach((agg, key) => {
                const tokenNames = Array.from(agg.tokens.keys());
                const dominantToken = tokenNames.length === 1 ? tokenNames[0] : `${tokenNames.length} tokens`;
                const fmtValue = agg.totalValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
                const label = agg.txCount === 1
                    ? `${fmtValue} ${dominantToken}`
                    : `${agg.txCount} txs · ${fmtValue} ${dominantToken}`;

                const isSenderRoot = selectedNode?.id ? (
                    agg.fromAddr === selectedNode.id ||
                    agg.fromAddr.toLowerCase() === selectedNode.id.toLowerCase()
                ) : false;
                const dir = isSenderRoot ? 'outgoing' : 'incoming';

                logEdge({
                    path: 'addTransactionsToGraph',
                    edgeId: key,
                    source: agg.fromAddr,
                    target: agg.toAddr,
                    direction: dir,
                    rootNodeId: selectedNode?.id || '?',
                    value: agg.totalValue,
                    tokenSymbol: dominantToken,
                    isConsolidated: true,
                    txCount: agg.txCount,
                });

                const edgeData = {
                    id: key,
                    source: agg.fromAddr,
                    target: agg.toAddr,
                    label,
                    value: agg.totalValue, // for dust-filter fallback when value_usd is missing
                    total_value: agg.totalValue,
                    tx_count: agg.txCount,
                    token_symbol: tokenNames.join(', '),
                    is_consolidated: true,
                    timestamp: agg.lastTimestamp, // for filter compatibility (date range)
                    first_timestamp: agg.firstTimestamp,
                    last_timestamp: agg.lastTimestamp,
                    _visibility: 1.0,
                };

                if (agg.elementIndex >= 0) {
                    merged[agg.elementIndex] = { data: edgeData };
                } else {
                    merged.push({ data: edgeData });
                    existingIds.add(key);
                }
            });

            // Zero-value aggregates (unchanged)
            zeroValueAggregates.forEach((agg, key) => {
                const aggEdgeId = `inferred-zero-${key}`;
                if (existingIds.has(aggEdgeId)) return;
                const _aggTok2 = cleanTokenSymbol(agg.tokenSymbol);
                merged.push({
                    data: {
                        id: aggEdgeId,
                        source: agg.fromAddr,
                        target: agg.toAddr,
                        label: _aggTok2 ? `${agg.count}× 0-value ${_aggTok2}` : `${agg.count}× 0-value tx`,
                        value: 0,
                        token_symbol: _aggTok2,
                        timestamp: agg.latestTimestamp,
                        edgeType: 'inferred',
                        is_zero_value_aggregate: true,
                        zero_value_count: agg.count,
                    }
                });
                existingIds.add(aggEdgeId);
            });

            return merged;
        });

        // Sync individual txs into edgeTxMap (deduped by tx_hash)
        if (newTxEntries.size > 0) {
            setEdgeTxMap(prev => {
                const next = new Map(prev);
                newTxEntries.forEach((txsList, key) => {
                    const existing = next.get(key) || [];
                    const existingHashes = new Set(existing.map((t: any) => t.tx_hash));
                    const deduped = txsList.filter(t => t.tx_hash && !existingHashes.has(t.tx_hash));
                    if (deduped.length > 0) next.set(key, [...existing, ...deduped]);
                });
                return next;
            });
        }
        flushSummary(`addTransactionsToGraph(${txs.length} txs)`);
    };

    const loadTransactionContext = async (txHash: string, contextBefore = 5, contextAfter = 5) => {
        if (!transactionService.isTransactionHash(txHash)) {
            toast.info('Transaction context is available only for real on-chain transaction hashes.');
            return;
        }

        const nodeChain = selectedNode?.chain || chain || detectChain(selectedNode?.id || '') || 'ETH';
        setLoading(true);
        try {
            const context = await transactionService.getTransactionContext(nodeChain, txHash, contextBefore, contextAfter);
            const fromContext = context?.from_address_context || [];
            const toContext = context?.to_address_context || [];
            addTransactionsToGraph([...fromContext, ...toContext], nodeChain);
            toast.success('Loaded transaction context');
        } catch (e: any) {
            console.error('Failed to load transaction context', e);
            toast.error('Failed to load transaction context');
        } finally {
            setLoading(false);
        }
    };

    // Export investigation to CSV for court documentation
    const exportToCSV = () => {
        const nodes = elements.filter(e => !e.data.source); // Only nodes, not edges

        // CSV-safe value escaper
        const esc = (v: any): string => {
            if (v == null || v === '') return '';
            const s = String(v);
            if (s.includes(',') || s.includes('"') || s.includes('\n')) {
                return `"${s.replace(/"/g, '""')}"`;
            }
            return s;
        };

        let csv = 'CRYPTO FORENSICS INVESTIGATION REPORT\n';
        csv += `Generated,${new Date().toISOString()}\n`;
        csv += `Primary Address,${esc(address || 'N/A')}\n`;
        csv += `Chain,${esc(chain || 'Unknown')}\n`;
        csv += `Total Nodes on Canvas,${nodes.length}\n\n`;

        // Section 1: Addresses (canvas nodes)
        csv += 'ADDRESSES ON CANVAS\n';
        csv += 'Address,Chain,Entity Name,Entity Type,Risk Score\n';
        nodes.forEach(n => {
            csv += [
                esc(n.data.id),
                esc(n.data.chain || chain || ''),
                esc(n.data.entity_name),
                esc(n.data.entity_type),
                esc(n.data.risk_score ?? ''),
            ].join(',') + '\n';
        });

        // Section 2: All transactions from the transaction store (data layer)
        const allTxs: { nodeAddr: string; tx: TransactionEvidence }[] = [];
        const seenHashes = new Set<string>();
        nodes.forEach(n => {
            const nodeTxs = txStore.getNodeTransactions(n.data.id);
            nodeTxs.forEach(tx => {
                if (tx.tx_hash && !seenHashes.has(tx.tx_hash)) {
                    seenHashes.add(tx.tx_hash);
                    allTxs.push({ nodeAddr: n.data.id, tx });
                }
            });
        });

        csv += `\nTRANSACTIONS (${allTxs.length} unique)\n`;
        csv += TX_CSV_HEADER + '\n';
        const evidenceCtx = {
            caseId: searchParams.get('case_id'),
            investigationId: searchParams.get('investigation_id'),
        };
        allTxs.forEach(({ nodeAddr, tx }) => {
            const node = nodes.find(n => n.data.id === nodeAddr);
            const txChain = (tx as any).chain || node?.data?.chain || chain || '';
            csv += buildTxCsvRow(tx as any, txChain, nodeAddr, evidenceCtx) + '\n';
        });

        // Download
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `investigation-${address?.substring(0, 10) || 'report'}-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Same rows as the CSV, as an Excel workbook (SpreadsheetML). Sheet #5.
    const exportToExcel = () => {
        const nodes = elements.filter(e => !e.data.source);
        const addressRows: unknown[][] = [
            ['Address', 'Chain', 'Entity Name', 'Entity Type', 'Risk Score'],
            ...nodes.map(n => [
                n.data.id,
                n.data.chain || chain || '',
                n.data.entity_name ?? '',
                n.data.entity_type ?? '',
                n.data.risk_score ?? '',
            ]),
        ];
        const seenHashes = new Set<string>();
        const evidenceCtx = {
            caseId: searchParams.get('case_id'),
            investigationId: searchParams.get('investigation_id'),
        };
        const txRows: unknown[][] = [TX_CSV_HEADER.split(',')];
        nodes.forEach(n => {
            txStore.getNodeTransactions(n.data.id).forEach(tx => {
                if (!tx.tx_hash || seenHashes.has(tx.tx_hash)) return;
                seenHashes.add(tx.tx_hash);
                const txChain = (tx as any).chain || n.data.chain || chain || '';
                txRows.push(txExportCells(tx as any, txChain, n.data.id, evidenceCtx));
            });
        });
        const xml = spreadsheetMl([
            { name: 'Addresses', rows: addressRows },
            { name: 'Transactions', rows: txRows },
        ]);
        // BOM so Excel reads non-ASCII entity names as UTF-8.
        const blob = new Blob(['\uFEFF', xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `investigation-${address?.substring(0, 10) || 'report'}-${new Date().toISOString().slice(0, 10)}.xls`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };



    // handleAIAssistant — Now delegated to AnalyzeGraphCard

    const handleSaveState = async (options?: { namePrefix?: string; notes?: string; defaultName?: string; skipPrompt?: boolean; resumeAfterModal?: boolean }): Promise<{ caseId: string; investigationId: string } | null> => {
        let caseId = searchParams.get('case_id');
        let invId = searchParams.get('investigation_id');

        // Every step of the save hits an authenticated endpoint. Fail loudly up
        // front instead of opening a case picker that can only 401.
        if (!hasActiveSession()) {
            toast.error(isGuestDemo() ? GUEST_SIGN_IN_MESSAGE : SESSION_ENDED_MESSAGE, { duration: 7000 });
            return null;
        }

        if (!caseId) {
            setPendingSaveOptions(options || {});
            setIsCaseModalOpen(true);
            // Legacy callers (snapshot buttons) keep the old fire-and-forget
            // behaviour: the save resumes from the modal callbacks independently.
            // Opt-in callers (report persistence) await the modal result so they
            // can archive after the user actually picks a case.
            if (options?.resumeAfterModal) {
                // Flush any already-pending resolver first (defensive): a second
                // opt-in save while the modal is still open would otherwise orphan
                // the first awaiter and hang it. Not reachable via the current UI
                // (the picker is a blocking overlay), but cheap insurance.
                resolvePendingSave(null);
                return new Promise((resolve) => { pendingSaveResolverRef.current = resolve; });
            }
            return null;
        }

        return executeSave(caseId, invId, options);
    };

    const handleCreateNewCaseFromSave = async () => {
        setIsCaseModalOpen(false);
        const caseTitle = await prompt({
            title: 'Name new case',
            defaultValue: '',
            placeholder: `e.g. Investigation - ${address || 'General'}`,
            confirmText: 'Create',
        });
        if (!caseTitle) { resolvePendingSave(null); return; }

        let newCase: any;
        try {
            newCase = await caseService.create({
                title: caseTitle,
                notes: 'Auto-created from Workbench Save'
            });
        } catch (err) {
            console.error('Failed to create case for investigation save', err);

            // api.ts owns 401 handling and redirects to /login?expired=1.
            // Do not race that redirect with a misleading case-creation toast.
            if (isAuthenticationError(err)) {
                resolvePendingSave(null);
                return;
            }

            toast.error(caseCreationErrorMessage(err), { duration: 7000 });
            // Keep the pending save alive and restore the picker so the officer
            // can retry or select an existing case without rerunning analysis.
            setIsCaseModalOpen(true);
            return;
        }

        // Case creation and investigation persistence are separate operations.
        // executeSave reports its own failure; never mislabel that as a case
        // creation failure (the case may already exist successfully).
        const result = await executeSave(newCase.id, null, pendingSaveOptions || undefined);
        resolvePendingSave(result);
    };

    const handleSelectExistingCaseFromSave = async (caseId: string) => {
        setIsCaseModalOpen(false);
        const result = await executeSave(caseId, null, pendingSaveOptions || undefined);
        resolvePendingSave(result);
    };

    const executeSave = async (caseId: string, existingInvId: string | null, options?: { namePrefix?: string; notes?: string; defaultName?: string; skipPrompt?: boolean }): Promise<{ caseId: string; investigationId: string } | null> => {

        let nameToSave = investigationName || options?.defaultName || `${options?.namePrefix || 'Snapshot'} ${new Date().toLocaleTimeString()}`;
        let shouldOverwrite = false;

        // Auto-save (skipPrompt) should always update the existing investigation, not create a new one
        if (existingInvId && options?.skipPrompt) {
            shouldOverwrite = true;
            nameToSave = investigationName || nameToSave;
        }

        // If we have an existing ID, ask user if they want to update it or save a new one
        if (existingInvId && !options?.skipPrompt) {
            const saveAction = await confirm({
                title: 'Save Investigation',
                message: `You are editing an existing investigation ("${investigationName}").\n\nDo you want to update the existing record or save as a new snapshot?`,
                confirmText: 'Update Existing',
                cancelText: 'Save as New'
            });
            if (saveAction === null) return null;
            shouldOverwrite = saveAction;

            if (!shouldOverwrite) {
                // They chose "Save as New", clear the existing ID so it creates a new record
                existingInvId = null;
                // Prepend "Copy of " or just generate a new timestamp suffix
                nameToSave = `${options?.namePrefix || 'Snapshot'} ${new Date().toLocaleTimeString()}`;
            }
        }

        if (!options?.skipPrompt && !shouldOverwrite) {
            const promptedName = await prompt({
                title: 'Save investigation snapshot',
                defaultValue: nameToSave,
                placeholder: 'Snapshot name',
                confirmText: 'Save',
            });
            if (!promptedName) return null;
            nameToSave = promptedName;
        }

        if (!graphRef.current) return null;
        const cy = graphRef.current.getCy();
        if (!cy) return null;

        // ── Capture Screenshot ───────────────────────────────────────
        let screenshot: string | undefined;
        try {
            screenshot = cy.png({ output: 'base64uri', bg: '#0f172a', full: false, scale: 1.5 });
        } catch (e) {
            console.warn('Screenshot capture failed, continuing without it.', e);
        }

        // ── Build enriched node_intelligence map ─────────────────────
        const node_intelligence: Record<string, object> = {};
        cy.nodes().forEach((n: any) => {
            const d = n.data();
            if (d?.id && d.id.length > 20) {
                node_intelligence[d.id] = {
                    chain: d.chain || chain,
                    risk_score: d.risk_score,
                    entity_name: d.entity_name,
                    entity_type: d.entity_type,
                    entity_source: d.entity_source,
                    balance: d.balance,
                };
            }
        });

        // ── Build action log from state ───────────────────────────────
        const action_log: string[] = [];
        const nodeCount = cy.nodes().length;
        const edgeCount = cy.edges().length;
        action_log.push(`Graph saved with ${nodeCount} nodes, ${edgeCount} edges`);
        if (address) action_log.push(`Root address: ${address} (${chain || 'unknown chain'})`);
        if (inlineAiSummary) action_log.push('AI Intelligence Note generated');
        if (inlinePatterns?.length) action_log.push(`Pattern scan completed: ${inlinePatterns.length} typologies detected`);
        if (options?.notes) action_log.push(`Notes: ${options.notes.slice(0, 200)}`);

        // ── Auto-investigation results (persisted with Investigation) ──
        const autoInvestigationData = autoInvestigation.exportForSave();
        if (autoInvestigationData) {
            action_log.push(`Auto-investigation: ${Object.keys(autoInvestigationData).length} sections completed`);
            const vel = autoInvestigationData.velocity;
            if (vel?.velocity_profile) action_log.push(`velocity=${vel.velocity_profile}`);
            const sig = autoInvestigationData.signal_assessment;
            if (sig?.escalation_recommendation) action_log.push(`escalation=${sig.escalation_recommendation}`);
        }

        // ── Compose enriched graph_state ─────────────────────────────
        // Clear transient investigation classes before snapshot so they
        // don't persist in the saved investigation (C1 fix).
        cy.elements('.discovering').removeClass('discovering');
        cy.elements('.pruned').removeClass('pruned');

        const cyJson = cy.json() as any;
        // Strip transient _visibility (FTM dim, SSE flicker) from the
        // serialised clone before persist — leaves the live canvas
        // untouched so the user sees no visual flash. See
        // snapshotVisibility.ts for the why and a real-data example.
        normalizeVisibilityForSerialize(cyJson.elements);
        // Strip data-layer tx payloads (expanded_transactions / token_transfers
        // / internal_txs) before persist — R12. Without this the snapshot bloats
        // ~35x (3.7 MB of txs in one 5.4 MB snapshot) and the reload GET aborts
        // on the axios timeout, leaving a blank canvas on reopen. Symmetric with
        // useGraphStore's sessionStorage partialize (same shared util).
        stripDataLayerInPlace(cyJson.elements);
        const graphState = {
            elements: cyJson.elements,   // { nodes: [...], edges: [...] }
            layout: { zoom: cy.zoom(), pan: cy.pan() },
            screenshot,
            intelligence_summary: inlineAiSummary || undefined,
            node_intelligence,
            pattern_scans: inlinePatterns?.length ? { typologies: inlinePatterns } : undefined,
            action_log,
            auto_investigation: autoInvestigationData || undefined,
            auto_investigation_meta: autoInvestigationData ? {
                ran_at: new Date().toISOString(),
                chain: chain || undefined,
                address: address || undefined,
            } : undefined,
            // Per-node Follow-the-Money cache — re-hydrated on load so FTM
            // summaries and report access survive across sessions.
            ftm_cache: useFtmStore.getState().exportForSave(),
            investigation_cache: useInvestigationCacheStore.getState().exportForSave(),
        } as any;

        try {
            let savedInvId = null;

            if (shouldOverwrite && existingInvId) {
                const updatedInv = await investigationService.update(caseId, existingInvId, {
                    name: nameToSave,
                    notes: options?.notes || '',
                    graph_state: graphState
                });
                savedInvId = updatedInv?.id;
                setInvestigationName(updatedInv?.name || nameToSave);
                toast.success('Investigation updated successfully.');
            } else {
                const createdInv = await investigationService.create(caseId, nameToSave, graphState, options?.notes || '', address || '', chain || '');
                savedInvId = createdInv?.id;
                setInvestigationName(createdInv?.name || nameToSave);
                toast.success('Investigation saved as a new snapshot.');
            }

            if (caseId && savedInvId) {
                // Update React Router's searchParams (not window.history) so isSavedInvestigation stays in sync
                const newParams = new URLSearchParams(searchParams);
                newParams.set('case_id', String(caseId));
                newParams.set('investigation_id', String(savedInvId));
                justSavedRef.current = true; // Prevent the load effect from re-fetching
                setSearchParams(newParams, { replace: true });
            }

            // Mark the current snapshot as fully saved so we don't prompt again unless it changes
            lastSavedElementCount.current = elements.length;
            isDirtyRef.current = false;
            clearDraft(); // Clear sessionStorage draft since we just saved to backend

            if (!caseName && caseId) {
                caseService.get(caseId).then(c => setCaseName(c?.title || null)).catch(() => { });
            }

            return savedInvId ? { caseId: String(caseId), investigationId: String(savedInvId) } : null;
        } catch (e) {
            console.error('Save failed', e);
            toast.error('Failed to save investigation.');
            return null;
        }
    };


    const handleSaveAnalyticsRun = async () => {
        if (!selectedNode?.id) {
            toast.error('Select a node before saving an analytics run.');
            return;
        }

        const summaryLines: string[] = [];
        summaryLines.push('Analytics Run Snapshot');
        summaryLines.push(`Node: ${selectedNode.id}`);
        summaryLines.push(`Section: ${analyticsSection}`);
        summaryLines.push(`Captured At: ${new Date().toISOString()}`);

        const graphSnapshot = buildAnalysisGraph();
        summaryLines.push(`Graph: ${graphSnapshot.summary.node_count} nodes, ${graphSnapshot.summary.edge_count} edges, total_value=${graphSnapshot.summary.total_value}`);

        if (inlinePatterns) summaryLines.push(`Patterns Found: ${inlinePatterns.length}`);
        if (nodeCompliance) summaryLines.push(`Node Compliance: ${nodeCompliance.sanctioned ? 'SANCTIONED' : 'clear'}`);
        if (complianceHits.length > 0) summaryLines.push(`Graph Sanctions Hits: ${complianceHits.length}`);
        if (inlineAiSummary) summaryLines.push(`AI Summary (excerpt): ${inlineAiSummary.slice(0, 500)}`);

        const notes = summaryLines.join('\n');
        await handleSaveState({
            namePrefix: 'Analytics Run',
            defaultName: `Analytics Run ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
            notes,
        });
    };

    const handleTraceTx = async (txHash: string, txChain: string) => {
        const normalized = normalizeAddressInput(txHash);
        if (!transactionService.isTransactionHash(normalized)) {
            toast.error('Invalid transaction hash format.');
            return;
        }
        await loadTransactionHash(normalized, txChain);
    };

    // Prompt user to save before navigating away from unsaved work
    const promptSaveBeforeNavigate = async (targetLabel: string): Promise<boolean> => {
        if (elements.length === 0) return true; // no work to lose

        if (!hasUnsavedWork) {
            // Clear stale badge since we're navigating to a new context
            setCaseName(null);
            setInvestigationName(null);
            isDirtyRef.current = false;
            return true; // proceed without prompting
        }

        const shouldSave = await confirm({
            title: 'Unsaved investigation',
            message: 'Save current investigation before starting a new one?',
            confirmText: 'Save & Continue',
            cancelText: 'Discard',
            dismissText: 'Stay on Workbench',
        });
        if (shouldSave === null) return false;
        if (shouldSave) {
            await handleSaveState({ defaultName: `Auto-save before ${targetLabel}` });
        }
        // Clear stale badge — starting fresh context
        setCaseName(null);
        setInvestigationName(null);
        isDirtyRef.current = false;
        return true; // always continue (user chose save or discard)
    };

    // ── Navigation blocker effect: intercept cross-route navigation ─────
    // NOTE: `blocker` is captured by the async closure as a snapshot. After an
    // await, `blocker.state` still reads the stale value from render time, even
    // if the router has internally transitioned to 'unblocked' (e.g., because
    // our handler cleared `isDirtyRef`, which flips `hasUnsavedWork` false and
    // causes react-router to unblock). Guarding on `blocker.state` is therefore
    // unreliable post-await — we must wrap proceed()/reset() in try/catch so
    // the stale-closure crash is swallowed instead of bubbling as an unhandled
    // error. See: "Invalid blocker state transition: unblocked -> proceeding".
    const safeProceed = useCallback(() => {
        try { blocker.proceed?.(); } catch (e) {
            console.debug('blocker.proceed skipped (already unblocked):', e);
        }
    }, [blocker]);
    const safeReset = useCallback(() => {
        try { blocker.reset?.(); } catch (e) {
            console.debug('blocker.reset skipped (already unblocked):', e);
        }
    }, [blocker]);

    useEffect(() => {
        if (blocker.state !== 'blocked' || isHandlingBlockerRef.current) return;

        const handleBlock = async () => {
            try {
                isHandlingBlockerRef.current = true;

                if (isSavedInvestigation) {
                    // Scenario 1: Already saved — auto-save silently, then proceed
                    try {
                        await executeSave(
                            searchParams.get('case_id')!,
                            searchParams.get('investigation_id')!,
                            { skipPrompt: true }
                        );
                        leaveHandledRef.current = true;
                    } catch (e) {
                        console.error('Auto-save failed on navigate-away', e);
                        // The work is leaving unsaved: keep a draft so the next visit can offer it.
                        writeDraftToSessionStorage();
                        leaveHandledRef.current = true;
                    }
                    isDirtyRef.current = false;
                    safeProceed();
                } else {
                    // Scenario 2: Not saved — ask user
                    const shouldSave = await confirm({
                        title: 'Unsaved investigation',
                        message: 'You have unsaved work. Would you like to save before leaving?',
                        confirmText: 'Save',
                        cancelText: 'Leave without saving',
                        dismissText: 'Stay on Workbench',
                    });

                    if (shouldSave === null) {
                        // User dismissed — stay on workbench
                        safeReset();
                    } else if (shouldSave) {
                        const result = await handleSaveState({ skipPrompt: false });
                        if (result) {
                            leaveHandledRef.current = true;
                            safeProceed();
                        } else {
                            // User cancelled the save dialog — stay on workbench
                            safeReset();
                        }
                    } else {
                        // User chose "Leave without saving": discard everything, so the canvas
                        // does not reappear from the persisted stores on the next visit.
                        leaveHandledRef.current = true;
                        clearDraft();
                        isDirtyRef.current = false;
                        discardWorkbenchSession();
                        autoInvestigation.reset();
                        clearCanvasView();
                        safeProceed();
                    }
                }
            } finally {
                isHandlingBlockerRef.current = false;
            }
        };

        handleBlock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blocker.state]);

    // ── beforeunload: save draft on tab close / hard refresh ────────
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (!hasUnsavedWork) return;
            writeDraftToSessionStorage();
            // The draft is safe in sessionStorage, so never fight an app-driven
            // redirect (session expiry): cancelling it would strand the officer
            // on a signed-out Workbench where every save fails.
            if (isAppNavigating()) return;
            e.preventDefault(); // triggers browser's native "Leave site?" prompt
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasUnsavedWork, writeDraftToSessionStorage]);

    /** Background auto-connection check: find paths between a new address and existing canvas nodes. */
    const checkAutoConnections = async (newAddress: string, newChain: string) => {
        const existingNodes = elements
            .filter(el => !el.data?.source && el.data?.id && el.data.id.toLowerCase() !== newAddress.toLowerCase())
            .map(el => el.data.id);
        if (existingNodes.length === 0) return;

        // Only check same-chain nodes (cross-chain path finding not supported)
        const sameChainNodes = existingNodes.filter(id => {
            const node = elements.find(el => el.data?.id === id && !el.data?.source);
            const nodeChain = node?.data?.chain || effectiveChain;
            return nodeChain?.toUpperCase() === newChain.toUpperCase();
        });
        if (sameChainNodes.length === 0) return;

        // Check up to 5 existing nodes (don't spam API)
        const toCheck = sameChainNodes.slice(0, 5);
        for (const existingId of toCheck) {
            try {
                const path = await addressService.tracePath(newChain, existingId, newAddress);
                if (path && path.length > 0) {
                    // Path found — add edges to canvas as gold path
                    const pathElements: CytoscapeElement[] = [];
                    const existingIds = new Set(elements.map(e => e.data.id));
                    path.forEach((edge: any) => {
                        [edge.from_address, edge.to_address].forEach((addr: string) => {
                            if (!existingIds.has(addr)) {
                                pathElements.push({
                                    data: { id: addr, label: formatLabel(addr), type: 'address', chain: newChain, _visibility: 1.0 }
                                });
                                existingIds.add(addr);
                            }
                        });
                        const edgeId = `${edge.from_address}-${edge.to_address}-${edge.tx_hash}`;
                        if (!existingIds.has(edgeId)) {
                            const _gpTok = cleanTokenSymbol(edge.token_symbol) || newChain || '';
                            pathElements.push({
                                data: {
                                    id: edgeId, source: edge.from_address, target: edge.to_address,
                                    label: _gpTok ? `${edge.value} ${_gpTok}` : `${edge.value}`,
                                    value: edge.value, token_symbol: _gpTok,
                                    timestamp: edge.timestamp, tx_hash: edge.tx_hash,
                                    edgeType: 'gold-path', _visibility: 1.0,
                                }
                            });
                            existingIds.add(edgeId);
                        }
                    });
                    if (pathElements.length > 0) {
                        setElements(prev => [...prev, ...pathElements.filter(el => !prev.some(p => p.data.id === el.data.id))]);
                        const hops = path.length;
                        toast.success(`Connection found: ${hops} hop${hops !== 1 ? 's' : ''} between addresses`, { duration: 5000 });
                    }
                    return; // Found one connection, that's enough
                }
            } catch { /* no path — continue to next node */ }
        }
    };

    /**
     * Check connections between ALL address nodes on canvas (or selected subset).
     * Runs pairwise tracePath queries in background, adds gold-path edges for discovered connections.
     */
    const handleCheckConnections = async () => {
        // If user has 2+ nodes selected, check only those pairs (no cap).
        // Otherwise check all address nodes on canvas with smart ordering.
        const isSelectionMode = multiSelect.count >= 2;

        const sourceNodes = isSelectionMode
            ? multiSelect.getSelectedArray()
                .filter(n => n.type === 'address')
                .map(n => ({ id: n.id, chain: (n.chain || effectiveChain || 'ETH') }))
            : elements
                .filter(el => !el.data?.source && el.data?.id && el.data?.type === 'address')
                .map(el => ({ id: el.data.id, chain: el.data.chain || effectiveChain || 'ETH' }));

        if (sourceNodes.length < 2) {
            toast.info(isSelectionMode
                ? 'Select 2+ address nodes to find links between them'
                : 'Need at least 2 addresses on canvas to check connections');
            return;
        }

        setIsCheckingConnections(true);
        useGraphStore.getState().pushSnapshot();

        // Compute node degrees (how connected each node currently is).
        // Used for smart ordering: hanging (degree 0) nodes get priority.
        const degree = new Map<string, number>();
        for (const el of elements) {
            if (el.data?.source && el.data?.target) {
                degree.set(el.data.source, (degree.get(el.data.source) || 0) + 1);
                degree.set(el.data.target, (degree.get(el.data.target) || 0) + 1);
            }
        }
        const nodeDegree = (id: string) => degree.get(id) || 0;

        // Group nodes by chain (cross-chain path finding not supported)
        const byChain = new Map<string, string[]>();
        for (const n of sourceNodes) {
            const c = n.chain.toUpperCase();
            if (!byChain.has(c)) byChain.set(c, []);
            byChain.get(c)!.push(n.id);
        }

        let connectionsFound = 0;
        let failedPairs = 0;
        const existingEdgePairs = new Set(
            elements.filter(e => e.data?.source && e.data?.target)
                .map(e => `${e.data.source}→${e.data.target}`)
        );

        // Per-click cap. In selection mode we respect user intent fully.
        // Otherwise raise to 50 — most hits are instant Neo4j lookups.
        const PER_CHAIN_CAP = isSelectionMode ? Number.POSITIVE_INFINITY : 50;

        try {
            for (const [chainKey, nodeIds] of byChain.entries()) {
                if (nodeIds.length < 2) continue;

                // Generate unique pairs (N*(N-1)/2). A pair is skipped when:
                //   1. A direct edge already exists between the two endpoints
                //      (any direction), OR
                //   2. We've already attempted tracePath for this pair in this
                //      session — regardless of whether it found a path or not.
                //      The "no-path" attempts are the costly ones to re-fire,
                //      since each runs a full BFS + blockchain fetch (~3s).
                const pairs: [string, string][] = [];
                let skippedAlreadyAttempted = 0;
                for (let i = 0; i < nodeIds.length; i++) {
                    for (let j = i + 1; j < nodeIds.length; j++) {
                        const fwd = `${nodeIds[i]}→${nodeIds[j]}`;
                        const rev = `${nodeIds[j]}→${nodeIds[i]}`;
                        if (existingEdgePairs.has(fwd) || existingEdgePairs.has(rev)) continue;
                        // Canonical memo key: chain + sorted(lo|hi) so (A,B) and
                        // (B,A) collide on a single entry.
                        const [lo, hi] = nodeIds[i] < nodeIds[j]
                            ? [nodeIds[i], nodeIds[j]]
                            : [nodeIds[j], nodeIds[i]];
                        const memoKey = `${chainKey}:${lo}|${hi}`;
                        if (linksAttemptsRef.current.has(memoKey)) {
                            skippedAlreadyAttempted++;
                            continue;
                        }
                        pairs.push([nodeIds[i], nodeIds[j]]);
                    }
                }

                if (pairs.length === 0) {
                    if (skippedAlreadyAttempted > 0) {
                        toast.info(
                            `All ${skippedAlreadyAttempted} ${chainKey} pair${skippedAlreadyAttempted !== 1 ? 's' : ''} already checked — no new links to find.`,
                            { duration: 4000 }
                        );
                    }
                    continue;
                }

                // Smart ordering (only when not in selection mode):
                //   Phase 1: both endpoints hanging (degree 0)
                //   Phase 2: one endpoint hanging
                //   Phase 3: both low-degree — sort by sum of degrees asc
                // Pressing Links repeatedly advances naturally through phases
                // because each successful check adds edges and raises degrees.
                if (!isSelectionMode) {
                    pairs.sort(([a1, a2], [b1, b2]) => {
                        const aScore = nodeDegree(a1) + nodeDegree(a2);
                        const bScore = nodeDegree(b1) + nodeDegree(b2);
                        return aScore - bScore;
                    });
                }

                const toCheck = pairs.slice(0, PER_CHAIN_CAP);
                const skipped = pairs.length - toCheck.length;
                const modeLabel = isSelectionMode ? 'selected' : 'prioritized';
                toast.info(
                    `Checking ${toCheck.length} ${modeLabel} pair${toCheck.length !== 1 ? 's' : ''} on ${chainKey}${skipped > 0 ? ` (${skipped} queued for next click)` : ''}...`,
                    { duration: 3000 }
                );

                for (const [src, tgt] of toCheck) {
                    // Compute the canonical memo key once so both the success
                    // and the no-path branches record the result identically.
                    const [pmLo, pmHi] = src < tgt ? [src, tgt] : [tgt, src];
                    const memoKey = `${chainKey}:${pmLo}|${pmHi}`;
                    try {
                        const path = await addressService.tracePath(chainKey, src, tgt);
                        if (path && path.length > 0) {
                            linksAttemptsRef.current.set(memoKey, 'has-path');
                            const pathElements: CytoscapeElement[] = [];
                            const existingIds = new Set(elements.map(e => e.data.id));

                            path.forEach((edge: any) => {
                                // Add intermediate hop nodes if not on canvas
                                [edge.from_address, edge.to_address].forEach((addr: string) => {
                                    if (!existingIds.has(addr)) {
                                        pathElements.push({
                                            data: {
                                                id: addr,
                                                label: formatLabel(addr),
                                                type: 'address',
                                                chain: chainKey,
                                                placement_role: 'relay',
                                                _visibility: 1.0,
                                            }
                                        });
                                        existingIds.add(addr);
                                    }
                                });
                                const edgeId = `${edge.from_address}-${edge.to_address}-${edge.tx_hash}`;
                                if (!existingIds.has(edgeId)) {
                                    const _gpTok2 = cleanTokenSymbol(edge.token_symbol) || chainKey || '';
                                    pathElements.push({
                                        data: {
                                            id: edgeId, source: edge.from_address, target: edge.to_address,
                                            label: _gpTok2 ? `${edge.value} ${_gpTok2}` : `${edge.value}`,
                                            value: edge.value, token_symbol: _gpTok2,
                                            timestamp: edge.timestamp, tx_hash: edge.tx_hash,
                                            edgeType: 'gold-path', _visibility: 1.0,
                                        }
                                    });
                                    existingIds.add(edgeId);
                                }
                            });

                            if (pathElements.length > 0) {
                                await addNodesToCanvas(pathElements, {
                                    chain: chainKey,
                                    userAction: false, // snapshot already taken above
                                    skipConnectionCheck: true,
                                    skipEnrichment: false,
                                });
                                connectionsFound++;
                            }
                        } else {
                            // tracePath returned empty — no path exists between
                            // this pair. Record so the next Links click skips it.
                            linksAttemptsRef.current.set(memoKey, 'no-path');
                        }
                    } catch {
                        // Transient failure (timeout / network / 5xx) — do NOT
                        // memoize as 'no-path'. The pair was never actually
                        // checked, so leaving it unrecorded lets the next Links
                        // click retry instead of permanently skipping it (the
                        // old behaviour made the button look "dead" after one
                        // slow/cold attempt).
                        failedPairs++;
                    }
                }
            }

            // Second pass: common-attribution links.  For pairs where no
            // direct on-chain path was found, check whether the addresses
            // share the same entity attribution (e.g. multiple Binance
            // deposit addresses).  If so, draw a dashed "common-attribution"
            // edge via a virtual entity hub so the user sees they're linked
            // through the same VASP — even without a direct tx connection.
            //
            // Data source: node.data.entity_name / entity_category, populated
            // by triage warm-up on import + any subsequent triage run.  No
            // new API call required.
            const attributionEdges: CytoscapeElement[] = [];
            const attributionHubs: CytoscapeElement[] = [];
            const currentEdgePairs = new Set(
                elements.filter(e => e.data?.source && e.data?.target)
                    .map(e => `${e.data.source}→${e.data.target}`)
            );
            // Group address nodes by normalized entity_name (only meaningful names)
            const byEntity = new Map<string, Array<{ id: string; chain: string; category?: string }>>();
            for (const n of sourceNodes) {
                const el = elements.find(e => !e.data?.source && e.data?.id === n.id);
                const name = (el?.data?.entity_name || '').trim();
                const category = (el?.data?.entity_category || el?.data?.entity_type || '').toLowerCase();
                if (!name) continue;
                // Only group for meaningful attributions — don't link "unknown" addresses
                if (category === 'unknown' || category === 'other' || category === '') continue;
                const key = name.toLowerCase();
                if (!byEntity.has(key)) byEntity.set(key, []);
                byEntity.get(key)!.push({ id: n.id, chain: n.chain, category });
            }
            let attributionLinked = 0;
            byEntity.forEach((members, entityKey) => {
                if (members.length < 2) return;
                // Virtual hub node representing the entity
                const sample = members[0];
                const displayName = elements.find(e => !e.data?.source && e.data?.id === sample.id)?.data?.entity_name || entityKey;
                const hubId = `attr-hub:${entityKey}:${sample.chain}`;
                if (!elements.some(e => e.data?.id === hubId)) {
                    attributionHubs.push({
                        data: {
                            id: hubId,
                            label: displayName,
                            type: 'entity',
                            entity_name: displayName,
                            entity_type: sample.category,
                            entity_category: sample.category,
                            is_attribution_hub: true,
                            _visibility: 1.0,
                        }
                    });
                }
                // Dashed pink edge from each member to the hub
                members.forEach(m => {
                    const edgeId = `attr:${m.id}→${hubId}`;
                    const fwd = `${m.id}→${hubId}`;
                    if (currentEdgePairs.has(fwd)) return;
                    attributionEdges.push({
                        data: {
                            id: edgeId,
                            source: m.id,
                            target: hubId,
                            label: `via ${displayName}`,
                            edgeType: 'common-attribution',
                            attribution_entity_name: displayName,
                            attribution_entity_category: m.category,
                            attribution_member_count: members.length,
                            _visibility: 1.0,
                        }
                    });
                    currentEdgePairs.add(fwd);
                    attributionLinked++;
                });
            });
            if (attributionHubs.length > 0 || attributionEdges.length > 0) {
                await addNodesToCanvas([...attributionHubs, ...attributionEdges], {
                    userAction: false,
                    skipConnectionCheck: true,
                    skipEnrichment: true,
                });
            }

            if (connectionsFound > 0 && attributionLinked > 0) {
                toast.success(`Found ${connectionsFound} on-chain connection${connectionsFound !== 1 ? 's' : ''} + ${attributionLinked} shared-attribution link${attributionLinked !== 1 ? 's' : ''}`, { duration: 6000 });
            } else if (connectionsFound > 0) {
                toast.success(`Found ${connectionsFound} connection${connectionsFound !== 1 ? 's' : ''} between addresses`, { duration: 5000 });
            } else if (attributionLinked > 0) {
                toast.info(`No on-chain paths, but ${attributionLinked} address${attributionLinked !== 1 ? 'es' : ''} share common entity attribution`, { duration: 5000 });
            } else if (failedPairs === 0) {
                toast.info('No connections found between addresses on canvas', { duration: 4000 });
            }
            if (failedPairs > 0) {
                toast.warning(
                    `${failedPairs} pair${failedPairs !== 1 ? 's' : ''} couldn't be checked (timeout/network) — press Links again to retry.`,
                    { duration: 5000 },
                );
            }
        } catch (e: any) {
            toast.error(`Connection check failed: ${e.message || 'Unknown error'}`);
        } finally {
            const { investigationId: invId, version } = linksMemoMetaRef.current;
            if (invId && version) {
                saveLinksMemo(invId, version, linksAttemptsRef.current);
            }
            setIsCheckingConnections(false);
        }
    };

    const handleTraceAddress = async (targetAddress: string, activeChain: string) => {
        const normalized = normalizeAddressInput(targetAddress);

        if (normalized.includes('@')) {
            if (elements.length === 0) {
                navigate(`/workbench?upi=${encodeURIComponent(normalized)}`);
            } else {
                const canProceed = await promptSaveBeforeNavigate(normalized);
                if (!canProceed) return;
                navigate(`/workbench?upi=${encodeURIComponent(normalized)}`);
            }
            return;
        }

        // Empty canvas: load immediately (don't rely on URL effect alone — it won't
        // re-fire when params already match after Clear Canvas, and navigate-only
        // was a silent no-op in that case). Sync URL for shareability.
        if (elements.length === 0) {
            const resolvedChain = normalizeChain(activeChain);
            const loadKey = `${resolvedChain}:${normalized}`;
            pendingOmnibarLoadRef.current = loadKey;

            const cid = searchParams.get('case_id');
            const urlChain = searchParams.get('chain');
            const urlAddr = searchParams.get('address');
            const paramsMatch =
                urlChain && urlAddr &&
                normalizeChain(urlChain) === resolvedChain &&
                normalizeAddressInput(urlAddr) === normalized;

            if (!paramsMatch) {
                const next = new URLSearchParams();
                next.set('chain', resolvedChain);
                next.set('address', normalized);
                if (cid) next.set('case_id', cid);
                setSearchParams(next, { replace: true });
            }

            try {
                await loadInitialGraph(resolvedChain, normalized);
            } finally {
                if (pendingOmnibarLoadRef.current === loadKey) {
                    pendingOmnibarLoadRef.current = null;
                }
            }
            return;
        }

        // Canvas has elements — ADDITIVE mode: add the new address to existing canvas

        // Check if already on canvas
        const existing = elements.find(el => !el.data?.source && (
            el.data.id === normalized || el.data.id?.toLowerCase() === normalized.toLowerCase()
        ));
        if (existing) {
            const existingChain = existing.data.chain ? normalizeChain(existing.data.chain) : '';
            const wantedChain = activeChain ? normalizeChain(activeChain) : '';
            if (wantedChain && existingChain !== wantedChain) {
                const nodeId = existing.data.id;
                setElements(prev => retargetAddressChain(prev, normalized, wantedChain));
                const cy = graphRef.current?.getCy() || (window as any).__cy;
                if (cy) {
                    const node = cy.getElementById(nodeId);
                    if (node.length) {
                        node.data('chain', wantedChain);
                        cy.elements().unselect();
                        node.select();
                        cy.animate({ center: { eles: node }, duration: 300 });
                    }
                }
                setSelectedNode(prev => {
                    if (!prev) return prev;
                    const pid = prev.id;
                    if (pid === nodeId || (typeof pid === 'string' && pid.toLowerCase() === normalized.toLowerCase())) {
                        return { ...prev, chain: wantedChain };
                    }
                    return prev;
                });
                const rootAddr = (address || sessionAddress || '').toLowerCase();
                if (!rootAddr || rootAddr === normalized.toLowerCase()) {
                    setSessionChain(wantedChain);
                    const next = new URLSearchParams(searchParams);
                    next.set('chain', wantedChain);
                    next.set('address', normalized);
                    const cid = searchParams.get('case_id');
                    if (cid) next.set('case_id', cid);
                    setSearchParams(next, { replace: true });
                }
                toast.success(`Retargeted address to ${wantedChain}`);
                return;
            }
            const cy = graphRef.current?.getCy() || (window as any).__cy;
            if (cy) {
                const node = cy.getElementById(existing.data.id);
                if (node.length) {
                    cy.elements().unselect();
                    node.select();
                    cy.animate({ center: { eles: node }, duration: 300 });
                }
            }
            toast.info('Address already on canvas — highlighted');
            return;
        }

        // Unified add: entity enrichment + dedup + session context + auto-connections
        await addNodesToCanvas([{
            data: {
                id: normalized,
                label: formatLabel(normalized),
                type: 'address',
                chain: activeChain,
                _visibility: 1.0,
            }
        }], { chain: activeChain });

        toast.success(`Added ${normalized.slice(0, 10)}... to canvas (${activeChain})`);
    };

    // Bridge-hop edge tagging — scans elements for txs that the backend flagged
    // as is_bridge_tx (set by analysis/bridge_detection.py) and sets
    // edgeType="bridge-hop" so the canvas style picks up amber/dashed. Runs
    // whenever elements change; idempotent (skips already-tagged edges).
    useEffect(() => {
        let changed = false;
        const updatedElements = elements.map(el => {
            if (!el.data?.source || !el.data?.target) return el;
            // Don't overwrite higher-priority edge types
            if (['gold-path', 'p2p-link', 'dex-swap', 'stablecoin-swap', 'common-attribution'].includes(el.data.edgeType)) return el;
            if (el.data.edgeType === 'bridge-hop') return el;  // already tagged
            if (!el.data.is_bridge_tx) return el;
            changed = true;
            return {
                ...el,
                data: {
                    ...el.data,
                    edgeType: 'bridge-hop',
                    bridge_family: el.data.bridge_family,
                    bridge_target_chain: el.data.bridge_target_chain,
                },
            };
        });
        if (changed) setElements(updatedElements);
    }, [elements, setElements]);

    // Privacy / mixer edge tagging — parallel to bridge-hop above. Maps
    // privacy_direction into the specific edgeType variant ("mixer-deposit",
    // "mixer-withdrawal", "coinjoin-pattern") so the canvas style selector
    // picks the right visual. Backend primitive: analysis/privacy_detection.py.
    useEffect(() => {
        let changed = false;
        const updated = elements.map(el => {
            if (!el.data?.source || !el.data?.target) return el;
            // Don't overwrite higher-priority edge types (bridge beats mixer
            // because a tx going THROUGH a bridge to a mixer is still a bridge
            // hop from the graph's perspective).
            if (['gold-path', 'p2p-link', 'dex-swap', 'stablecoin-swap', 'common-attribution', 'bridge-hop'].includes(el.data.edgeType)) return el;
            if (['mixer-deposit', 'mixer-withdrawal', 'coinjoin-pattern'].includes(el.data.edgeType)) return el;
            if (!el.data.is_privacy_tx) return el;

            // Route to specific edgeType by privacy_kind + privacy_direction.
            let newType = 'mixer-deposit';
            if (el.data.privacy_kind === 'coinjoin') newType = 'coinjoin-pattern';
            else if (el.data.privacy_direction === 'withdrawal') newType = 'mixer-withdrawal';

            changed = true;
            return {
                ...el,
                data: {
                    ...el.data,
                    edgeType: newType,
                    privacy_family: el.data.privacy_family,
                    privacy_kind: el.data.privacy_kind,
                    privacy_direction: el.data.privacy_direction,
                    privacy_sanctioned: el.data.privacy_sanctioned,
                    privacy_pool_size: el.data.privacy_pool_size,
                },
            };
        });
        if (changed) setElements(updated);
    }, [elements, setElements]);

    // Stablecoin-swap edge tagging — parallel to bridge/privacy. Backend sets
    // is_stablecoin_swap; frontend maps to edgeType="stablecoin-swap" (teal).
    useEffect(() => {
        let changed = false;
        const updated = elements.map(el => {
            if (!el.data?.source || !el.data?.target) return el;
            if (['gold-path', 'p2p-link', 'dex-swap', 'common-attribution', 'bridge-hop',
                 'mixer-deposit', 'mixer-withdrawal', 'coinjoin-pattern'].includes(el.data.edgeType)) {
                return el;
            }
            if (el.data.edgeType === 'stablecoin-swap') return el;
            if (!el.data.is_stablecoin_swap) return el;
            changed = true;
            return {
                ...el,
                data: {
                    ...el.data,
                    edgeType: 'stablecoin-swap',
                    swap_protocol: el.data.swap_protocol,
                    swap_pair_hint: el.data.swap_pair_hint,
                    swap_direction: el.data.swap_direction,
                },
            };
        });
        if (changed) setElements(updated);
    }, [elements, setElements]);

    // DEX swap edge labeling — when IntelligenceTab loads DEX data, update matching graph edges
    const handleDexSwapsLoaded = useCallback((swaps: any[], nodeId: string) => {
        if (!selectedNode || selectedNode.id !== nodeId) return;

        const swapByHash = new Map<string, any>();
        for (const swap of swaps) {
            if (swap.tx_hash) swapByHash.set(swap.tx_hash.toLowerCase(), swap);
        }
        if (swapByHash.size === 0) return;

        let changed = false;
        const updatedElements = elements.map(el => {
            if (!el.data?.source || !el.data?.tx_hash) return el;
            // Don't overwrite gold-path, p2p-link, inferred edges
            if (el.data.edgeType && el.data.edgeType !== 'dex-swap') return el;

            const swap = swapByHash.get(el.data.tx_hash.toLowerCase());
            if (!swap) return el;

            changed = true;
            const amtIn = swap.amount_in != null
                ? Number(swap.amount_in).toLocaleString(undefined, { maximumFractionDigits: 4 }) : '?';
            const amtOut = swap.amount_out != null
                ? Number(swap.amount_out).toLocaleString(undefined, { maximumFractionDigits: 4 }) : '?';
            const label = `${amtIn} ${swap.token_in_symbol || '?'} → ${amtOut} ${swap.token_out_symbol || '?'}\nvia ${swap.dex || 'DEX'}`;

            return { ...el, data: { ...el.data, edgeType: 'dex-swap', label } };
        });

        if (changed) setElements(updatedElements);
    }, [elements, selectedNode]);

    /** Unified helper: add any trace path (auto/forward/backward) to graph as gold-path nodes/edges.
     *  All trace actions route through this single function for consistent Canvas ↔ Node Inspector sync. */
    const addTracePathToGraph = useCallback((response: any, direction: 'forward' | 'backward' | 'auto', traceChain?: string, skipSnapshot?: boolean) => {
        if (!skipSnapshot) useGraphStore.getState().pushSnapshot();
        const paths = response?.paths || response?.traces || [];
        if (!Array.isArray(paths) || paths.length === 0) return 0;

        const resolvedChain = response?.chain || traceChain || '';

        // Build a node map from response.nodes for entity enrichment
        const responseNodeMap: Record<string, any> = {};
        if (Array.isArray(response?.nodes)) {
            for (const n of response.nodes) {
                if (n?.id) responseNodeMap[n.id] = n;
            }
        }
        // Also pull hop_details from all paths
        for (const p of paths) {
            if (Array.isArray(p?.hop_details)) {
                for (const hd of p.hop_details) {
                    if (hd?.address && !responseNodeMap[hd.address]) {
                        responseNodeMap[hd.address] = hd;
                    }
                }
            }
        }

        // Format value for edge labels: 80538.13 → "80.5K", 378.2 → "378.2"
        const fmtVal = (v: number) => {
            const abs = Math.abs(v);
            if (abs >= 1_000_000) return `${(abs / 1_000_000).toFixed(1)}M`;
            if (abs >= 10_000) return `${(abs / 1_000).toFixed(1)}K`;
            if (abs >= 1_000) return `${(abs / 1_000).toFixed(1)}K`;
            return abs.toLocaleString(undefined, { maximumFractionDigits: 1 });
        };

        const newElements: any[] = [];
        let totalHops = 0;

        // Render ALL paths, not just the first one
        for (const pathObj of paths) {
            const pathAddresses: string[] = pathObj?.addresses || pathObj?.path || [];
            const targetEntity = pathObj?.target_entity || pathObj?.source;
            if (!Array.isArray(pathAddresses) || pathAddresses.length < 2) continue;

            totalHops = Math.max(totalHops, pathAddresses.length - 1);

            // R-CANVAS-INTENT: Determine if this is a high-danger path (mixer, sanctions, scam)
            const targetType = (targetEntity?.type || targetEntity?.entity_type || '').toLowerCase();
            const isDangerPath = ['sanctions', 'mixer', 'scam', 'hacker', 'terrorism_financing', 'child_exploitation', 'stolen_coins'].includes(targetType);

            // Add nodes for this path
            pathAddresses.forEach((addr: string, index: number) => {
                if (!addr) return;
                const isRoot = index === 0;
                const isTerminal = index === pathAddresses.length - 1;
                const nodeInfo = responseNodeMap[addr];
                
                let intent: string = CanvasNodeIntent.HEAVY_CONTEXT;
                if (isRoot) intent = CanvasNodeIntent.TARGET_ROOT;
                else if (isTerminal) intent = isDangerPath ? CanvasNodeIntent.DANGER : CanvasNodeIntent.CRITICAL_ENDPOINT;

                newElements.push({
                    data: {
                        id: addr,
                        label: isTerminal && targetEntity?.name ? targetEntity.name : (nodeInfo?.entity_name || formatLabel(addr)),
                        type: 'address',
                        chain: nodeInfo?.chain || resolvedChain,
                        balance: 0,
                        is_root: isRoot,
                        isGoldPath: true,
                        canvas_intent: intent,
                        _visibility: 1.0,
                        ...(nodeInfo?.entity_type ? { entity_type: nodeInfo.entity_type } : {}),
                        ...(nodeInfo?.entity_name ? { entity_name: nodeInfo.entity_name } : {}),
                        ...(isTerminal && targetEntity ? {
                            entity_name: targetEntity.name,
                            entity_type: targetEntity.type || targetEntity.entity_type || undefined,
                        } : {})
                    }
                });
            });

            // Add edges for this path
            //
            // Direction: gold-path edges MUST always be in money-flow direction
            // (sender → receiver), matching the SSE `edge_discovered` events
            // emitted by the backend. Otherwise the gold-path edge from this
            // function and the SSE-emitted edge for the same hop end up as TWO
            // separate Cytoscape edges (opposite directions are different
            // dirKeys, so addToCanvas can't dedupe them) — the user sees the
            // same Bybit edge twice, one at _visibility=1.0 (this code) and
            // one at _visibility=0.55 (SSE).
            //
            // For backward trace, `pathAddresses` is in path-traversal order
            // ([root, hop1, …, origin]) which is REVERSED money flow. The
            // backend's `pathEdges[i]` already has from/to in money-flow
            // direction (see `_build_paths_from_bfs` at graph_service.py:2733
            // — for direction='incoming' it stores from=path[i+1] sender,
            // to=path[i] receiver). Prefer those when available; fall back to
            // a position-based swap for backward traces if `pe` is missing.
            const pathEdges: any[] = pathObj?.edges || [];
            for (let i = 0; i < pathAddresses.length - 1; i++) {
                const posSrc = pathAddresses[i];
                const posTgt = pathAddresses[i + 1];
                if (!posSrc || !posTgt) continue;
                const pe = pathEdges[i];
                const edgeValue = pe?.total_value ?? pe?.value ??
                    (Array.isArray(pe?.all_transactions) && pe.all_transactions.length > 0
                        ? pe.all_transactions.reduce((sum: number, tx: any) => sum + (Number(tx.value) || 0), 0)
                        : undefined);
                const tokenSym = pe?.token_symbol ?? undefined;
                const txCount = pe?.tx_count ?? pe?.all_transactions?.length ?? 1;

                // Resolve money-flow source/target. Backend `pe.from`/`pe.to`
                // are authoritative; otherwise reverse positional order for
                // backward traces.
                let src: string;
                let tgt: string;
                if (pe?.from && pe?.to) {
                    src = pe.from;
                    tgt = pe.to;
                } else if (direction === 'backward') {
                    src = posTgt;
                    tgt = posSrc;
                } else {
                    src = posSrc;
                    tgt = posTgt;
                }

                const edgeId = `gold-${src}-${tgt}`;
                let edgeLabel = `Hop ${i + 1}`;
                if (edgeValue != null && edgeValue !== 0) {
                    const tokMap = new Map<string, number>();
                    if (tokenSym) tokMap.set(tokenSym, edgeValue);
                    edgeLabel = formatConsolidatedLabel(txCount, edgeValue, tokMap, pe?.timestamp ?? null);
                }
                logEdge({
                    path: 'addTracePath',
                    edgeId,
                    source: src,
                    target: tgt,
                    direction: direction === 'backward' ? 'incoming' : 'outgoing',
                    value: edgeValue ?? 0,
                    tokenSymbol: tokenSym,
                    edgeType: 'gold-path',
                    txCount,
                    reason: `${direction} trace hop ${i + 1}/${pathAddresses.length - 1}`,
                });
                newElements.push({
                    data: {
                        id: edgeId,
                        source: src,
                        target: tgt,
                        label: edgeLabel,
                        edgeType: 'gold-path',
                        canvas_intent: isDangerPath ? CanvasEdgeIntent.DANGER_TRACE : CanvasEdgeIntent.PRIMARY_TRACE,
                        value: edgeValue,
                        token_symbol: tokenSym,
                        tx_hash: pe?.tx_hash ?? null,
                        timestamp: pe?.timestamp ?? null,
                        tx_count: txCount,
                        total_value: edgeValue,
                        _visibility: 1.0,
                        ...(txCount > 1 ? { is_consolidated: true } : {}),
                        trace_hop: i + 1,
                        trace_total_hops: pathAddresses.length - 1,
                        trace_target_name: targetEntity?.name || 'Exchange',
                        trace_target_type: targetEntity?.type || targetEntity?.entity_type || 'exchange',
                        trace_direction: direction,
                        trace_transactions: pe?.all_transactions || (pe ? [{ tx_hash: pe.tx_hash, value: pe.value, timestamp: pe.timestamp, token_symbol: pe.token_symbol }] : []),
                    }
                });
            }
        } // end for-each path

        setElements(prev => addToCanvas(prev, newElements, {
            chain: resolvedChain || undefined,
            applyGoldPathClass: true,
        }));

        // Populate edgeTxMap for ALL paths' edges
        // AND write to transaction store keyed per-address so Node Inspector
        // shows data when ANY discovered node is selected (not just the root).
        setEdgeTxMap(prev => {
            const next = new Map(prev);
            const txsByAddr = new Map<string, any[]>();

            for (const pathObj of paths) {
                const pa: string[] = pathObj?.addresses || pathObj?.path || [];
                const pe: any[] = pathObj?.edges || [];
                for (let i = 0; i < pa.length - 1; i++) {
                    const src = pa[i];
                    const tgt = pa[i + 1];
                    const edge = pe[i];
                    if (!src || !tgt) continue;

                    const key = `${src}->${tgt}`;
                    const existing = next.get(key) || [];
                    const existingHashes = new Set(existing.map((t: any) => t.tx_hash));
                    const txsToAdd = edge?.all_transactions || (edge?.tx_hash ? [edge] : []);

                    for (const tx of txsToAdd) {
                        if (tx.tx_hash && !existingHashes.has(tx.tx_hash)) {
                            const normalizedTx = {
                                tx_hash: tx.tx_hash,
                                from_address: tx.from_address || src,
                                to_address: tx.to_address || tgt,
                                value: tx.value ?? 0,
                                timestamp: tx.timestamp ?? null,
                                token_symbol: tx.token_symbol ?? null,
                                is_trace: true,
                            };
                            existing.push(normalizedTx);
                            existingHashes.add(tx.tx_hash);

                            // File under both addresses for store sync
                            for (const addr of [src, tgt]) {
                                const arr = txsByAddr.get(addr) || [];
                                if (!arr.some(t => t.tx_hash === tx.tx_hash)) {
                                    arr.push({
                                        ...normalizedTx,
                                        direction: addr === src ? 'outgoing' : 'incoming',
                                        counterparty: addr === src ? tgt : src,
                                    });
                                    txsByAddr.set(addr, arr);
                                }
                            }
                        }
                    }
                    next.set(key, existing);
                }
            }

            // Flush to central store
            for (const [addr, addrTxs] of txsByAddr.entries()) {
                useTransactionStore.getState().appendNodeTransactions(addr, addrTxs);
            }

            return next;
        });

        // Merge trace transactions from ALL paths into selectedNode's expanded_transactions
        setSelectedNode(prev => {
            if (!prev?.id) return prev;
            const existingTxs = prev.expanded_transactions || [];
            const existingHashSet = new Set(existingTxs.map((t: any) => t.tx_hash));
            const traceTxsToMerge: any[] = [];
            const enrichMap = new Map<string, { counterparty_entity: string | null; counterparty_type: string | null }>();

            for (const pathObj of paths) {
                const pa: string[] = pathObj?.addresses || pathObj?.path || [];
                const pe: any[] = pathObj?.edges || [];
                for (let i = 0; i < pa.length - 1; i++) {
                    const src = pa[i];
                    const tgt = pa[i + 1];
                    if (src !== prev.id && tgt !== prev.id) continue;
                    const edge = pe[i];
                    const txList = edge?.all_transactions || (edge?.tx_hash ? [edge] : []);
                    const isSender = src === prev.id;
                    const counterparty = isSender ? tgt : src;
                    const cpNode = newElements.find((n: any) => n.data?.id === counterparty && !n.data?.source);
                    const cpEntityName = cpNode?.data?.entity_name || null;
                    const cpEntityType = cpNode?.data?.entity_type || null;

                    for (const tx of txList) {
                        if (!tx.tx_hash) continue;
                        if (existingHashSet.has(tx.tx_hash)) {
                            enrichMap.set(tx.tx_hash, { counterparty_entity: cpEntityName, counterparty_type: cpEntityType });
                            continue;
                        }
                        traceTxsToMerge.push({
                            tx_hash: tx.tx_hash,
                            from_address: src,
                            to_address: tgt,
                            value: tx.value ?? 0,
                            timestamp: tx.timestamp ?? null,
                            token_symbol: tx.token_symbol ?? null,
                            direction: isSender ? 'outgoing' : 'incoming',
                            counterparty,
                            counterparty_entity: cpEntityName,
                            counterparty_type: cpEntityType,
                            is_trace: true,
                        });
                        existingHashSet.add(tx.tx_hash);
                    }
                }
            }

            if (enrichMap.size === 0 && traceTxsToMerge.length === 0) return prev;
            const enrichedExisting = enrichMap.size > 0
                ? existingTxs.map((t: any) => {
                    const e = t.tx_hash ? enrichMap.get(t.tx_hash) : null;
                    if (!e) return t;
                    return { ...t, is_trace: true, counterparty_entity: t.counterparty_entity || e.counterparty_entity, counterparty_type: t.counterparty_type || e.counterparty_type };
                })
                : existingTxs;
            return { ...prev, expanded_transactions: [...enrichedExisting, ...traceTxsToMerge] };
        });

        setSelectedEdgePair(null);
        // Preserve user's viewport — they may be following a trace and
        // don't want the camera yanked.  Fit button is always available.
        return totalHops;
    }, []);

    /** Merge raw transactions from auto-trace fallback into selectedNode's expanded_transactions.
     *  Returns the count of NEW transactions added + top counterparties for canvas. */
    const mergeAutoTraceTransactions = useCallback((txs: any[], nodeId: string, traceChain: string): { added: number; topCounterparties: Array<{ address: string; outVolume: number; inVolume: number; txCount: number; primaryToken: string | null }> } => {
        if (!txs || txs.length === 0) return { added: 0, topCounterparties: [] };
        let addedCount = 0;

        // Group by counterparty for canvas neighborhood
        const cpMap = new Map<string, { outVolume: number; inVolume: number; txCount: number; tokenCounts: Map<string, number> }>();
        for (const tx of txs) {
            const fromAddr = tx.from_address || tx.from || '';
            const toAddr = tx.to_address || tx.to || '';
            const isSender = fromAddr.toLowerCase() === nodeId.toLowerCase() || fromAddr === nodeId;
            const cp = isSender ? toAddr : fromAddr;
            if (!cp || cp.toLowerCase() === nodeId.toLowerCase() || cp === nodeId) continue;
            const val = Number(tx.value) || 0;
            const entry = cpMap.get(cp) || { outVolume: 0, inVolume: 0, txCount: 0, tokenCounts: new Map() };
            if (isSender) entry.outVolume += val; else entry.inVolume += val;
            entry.txCount += 1;
            const tok = tx.token_symbol || 'native';
            entry.tokenCounts.set(tok, (entry.tokenCounts.get(tok) || 0) + val);
            cpMap.set(cp, entry);
        }

        const topCounterparties = [...cpMap.entries()]
            .sort((a, b) => (b[1].outVolume + b[1].inVolume) - (a[1].outVolume + a[1].inVolume))
            .slice(0, 5)
            .map(([address, stats]) => {
                let primaryToken: string | null = null;
                let maxTokenVol = 0;
                stats.tokenCounts.forEach((vol, tok) => {
                    if (vol > maxTokenVol) { maxTokenVol = vol; primaryToken = tok === 'native' ? null : tok; }
                });
                return { address, outVolume: stats.outVolume, inVolume: stats.inVolume, txCount: stats.txCount, primaryToken };
            });

        // Merge into expanded_transactions for the selected node
        setSelectedNode(prev => {
            if (!prev?.id || prev.id !== nodeId) return prev;
            const existing = prev.expanded_transactions || [];
            const existingHashes = new Set(existing.map((t: any) => t.tx_hash));
            const newTxs: any[] = [];
            for (const tx of txs) {
                if (!tx.tx_hash || existingHashes.has(tx.tx_hash)) continue;
                const fromAddr = tx.from_address || tx.from || '';
                const toAddr = tx.to_address || tx.to || '';
                const isSender = fromAddr.toLowerCase() === nodeId.toLowerCase() || fromAddr === nodeId;
                newTxs.push({
                    tx_hash: tx.tx_hash,
                    from_address: fromAddr,
                    to_address: toAddr,
                    value: tx.value ?? 0,
                    timestamp: tx.timestamp ?? null,
                    token_symbol: tx.token_symbol ?? null,
                    block_height: tx.block_height ?? null,
                    direction: isSender ? 'outgoing' : 'incoming',
                    counterparty: isSender ? toAddr : fromAddr,
                    counterparty_entity: tx.counterparty_entity ?? null,
                    counterparty_type: tx.counterparty_type ?? null,
                });
                existingHashes.add(tx.tx_hash);
            }
            addedCount = newTxs.length;
            if (newTxs.length === 0) return prev;
            return { ...prev, expanded_transactions: [...existing, ...newTxs] };
        });

        // Also write to transaction store keyed per-address so Node Inspector
        // shows data when ANY discovered node is selected (not just the root).
        const txsByAddr = new Map<string, any[]>();
        for (const tx of txs) {
            const fromAddr = tx.from_address || tx.from || '';
            const toAddr = tx.to_address || tx.to || '';
            // File under both addresses so clicking either shows the tx
            for (const addr of [fromAddr, toAddr]) {
                if (!addr) continue;
                const isSender = addr.toLowerCase() === fromAddr.toLowerCase();
                const arr = txsByAddr.get(addr) || [];
                arr.push({
                    tx_hash: tx.tx_hash,
                    from_address: fromAddr,
                    to_address: toAddr,
                    value: tx.value ?? 0,
                    timestamp: tx.timestamp ?? null,
                    token_symbol: tx.token_symbol ?? null,
                    block_height: tx.block_height ?? null,
                    direction: isSender ? 'outgoing' : 'incoming',
                    counterparty: isSender ? toAddr : fromAddr,
                });
                txsByAddr.set(addr, arr);
            }
        }
        for (const [addr, addrTxs] of txsByAddr.entries()) {
            useTransactionStore.getState().appendNodeTransactions(addr, addrTxs);
        }

        return { added: addedCount, topCounterparties };
    }, []);

    /** Add top counterparties as a neighborhood graph on canvas (no gold path).
     *  Fires async entity enrichment to resolve labels. */
    const addNeighborhoodToCanvas = useCallback((
        counterparties: Array<{ address: string; outVolume: number; inVolume: number; txCount: number; primaryToken: string | null }>,
        nodeId: string,
        nodeChain: string,
    ) => {
        if (counterparties.length === 0) return;
        const newElements: any[] = [];
        for (const cp of counterparties) {
            // Counterparty node
            newElements.push({
                data: { id: cp.address, label: formatLabel(cp.address), type: 'address', chain: nodeChain, balance: 0, _visibility: 1.0 }
            });
            // Edge: direction follows dominant fund flow
            const totalVol = cp.outVolume + cp.inVolume;
            const isOutbound = cp.outVolume >= cp.inVolume;
            const src = isOutbound ? nodeId : cp.address;
            const tgt = isOutbound ? cp.address : nodeId;
            const valStr = totalVol.toLocaleString(undefined, { maximumFractionDigits: 2 });
            let edgeLabel = cp.primaryToken ? `${valStr} ${cp.primaryToken}` : valStr;
            if (cp.txCount > 1) edgeLabel += ` (${cp.txCount} txs)`;
            newElements.push({
                data: {
                    id: `${src}->${tgt}`,
                    source: src,
                    target: tgt,
                    label: edgeLabel,
                    value: totalVol,
                }
            });
        }
        setElements(prev => addToCanvas(prev, newElements, { chain: nodeChain }));

        // Async entity enrichment (best-effort) — resolve labels + entity_type
        const addrs = counterparties.map(cp => ({ address: cp.address, chain: nodeChain }));
        entityService.batchLookup(addrs).then((entityMap: any) => {
            if (!entityMap || typeof entityMap !== 'object') return;
            setElements(prev => prev.map(el => {
                if (el.data?.source) return el; // skip edges
                const entity = entityMap[el.data.id?.toLowerCase?.()] || entityMap[el.data.id];
                if (!entity?.name) return el;
                return {
                    ...el,
                    data: {
                        ...el.data,
                        label: entity.name,
                        entity_name: entity.name,
                        entity_type: entity.category,
                        entity_source: entity.source,
                    }
                };
            }));
            // Also enrich expanded_transactions counterparty_entity for grouping
            setSelectedNode(prev => {
                if (!prev?.id) return prev;
                const txs = prev.expanded_transactions;
                if (!txs?.length) return prev;
                let changed = false;
                const enriched = txs.map((t: any) => {
                    if (t.counterparty_entity) return t;
                    const cpAddr = t.counterparty;
                    if (!cpAddr) return t;
                    const entity = entityMap[cpAddr.toLowerCase?.()] || entityMap[cpAddr];
                    if (!entity?.name) return t;
                    changed = true;
                    return { ...t, counterparty_entity: entity.name, counterparty_type: entity.category || null };
                });
                return changed ? { ...prev, expanded_transactions: enriched } : prev;
            });
        }).catch(() => {}); // best-effort

        // Preserve user's viewport on expansion — don't yank the camera.
    }, []);

    // ── Granular action runner ─────────────────────────────────────────────────
    const handleRunGranularAction = useCallback(async (action: import('../components/NodeInspector/GranularActionsRow').GranularAction) => {
        const nodeId = selectedNode?.id ?? effectiveAddress;
        const nodeChainRaw = selectedNode?.chain ?? (nodeId ? detectChain(nodeId) : null) ?? effectiveChain;
        const nodeChainMaybeNull = nodeChainRaw ? normalizeChain(nodeChainRaw) : null;
        if (!nodeId || !nodeChainMaybeNull) { toast.error('No address selected'); return; }
        const nodeChain: string = nodeChainMaybeNull;

        const setStatus = (s: import('../components/NodeInspector/GranularActionsRow').ActionStatus) =>
            usePanelStore.getState().updateActionStatus(action, s);

        const addFinding = (finding: Omit<import('../components/NodeInspector/FindingsStrip').Finding, 'id' | 'time'>) => {
            const now = new Date();
            const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
            const prev = usePanelStore.getState().findings;
            usePanelStore.getState().setFindings([{ ...finding, id: `${action}-${Date.now()}`, time } as any, ...prev].slice(0, 10));
        };

        // Helper: patch both canvas element data AND selectedNode in one call
        const patchNode = (patch: Record<string, any>) => {
            // 1. Update canvas element
            setElements(prev => {
                let changed = false;
                const next = prev.map(el => {
                    if (el.data?.source || !el.data?.id) return el;
                    const match = el.data.id === nodeId ||
                        ((el.data.id.startsWith('0x') || el.data.id.startsWith('0X')) && el.data.id.toLowerCase() === nodeId.toLowerCase());
                    if (!match) return el;
                    changed = true;
                    return { ...el, data: { ...el.data, ...patch } };
                });
                return changed ? next : prev;
            });
            // 2. Update selectedNode (drives NodeInspector re-render)
            useSelectionStore.getState().updateSelectedNode(patch);
        };

        setStatus('running');
        try {
            if (action === 'entity_lookup') {
                // User-triggered → walk every provider (Arkham included) and
                // bypass the negative cache so newly-attributed addresses
                // surface even if a previous fast-path call cached a miss.
                const result = await entityService.batchLookup(
                    [{ address: nodeId, chain: nodeChain }],
                    { deep: true },
                );
                const entity = result?.[nodeId] || result?.[nodeId.toLowerCase()];
                const name = entity?.entity_name || entity?.name || 'Unknown';
                const eType = entity?.entity_type || entity?.type;
                const eTags = entity?.tags || entity?.entity_tags;
                const eLabels = entity?.node_labels;
                if (name !== 'Unknown') {
                    const patch: Record<string, any> = { entity_name: name, label: formatLabel(nodeId, name) };
                    if (eType) patch.entity_type = eType;
                    if (Array.isArray(eTags) && eTags.length > 0) patch.entity_tags = eTags;
                    if (Array.isArray(eLabels) && eLabels.length > 0) patch.node_labels = eLabels;
                    patchNode(patch);
                }
                setStatus('done');
                addFinding({ action, emoji: '🏷', label: 'Entity', summary: name, variant: name === 'Unknown' ? 'info' : 'success', tabLink: 'overview' });

            } else if (action === 'sanctions') {
                const result = await addressService.checkSanctions(nodeChain, nodeId);
                const hit = result?.sanctioned || result?.hit;
                if (hit) {
                    patchNode({ entity_category: 'sanctioned', sanctioned: true });
                }
                setStatus('done');
                const designation = result?.hit?.typology ? ` — ${result.hit.typology}` : '';
                addFinding({ action, emoji: hit ? '🚨' : '✅', label: 'Sanctions', summary: hit ? `HIT: ${result?.hit?.program || result?.entity || 'OFAC'}${designation}` : 'Clear', variant: hit ? 'error' : 'success', tabLink: 'intelligence' });

            } else if (action === 'exchange_detection') {
                const result = await investigationAnalysisService.detectExchange(nodeChain, nodeId);
                const name = result?.exchange_name || result?.entity_name;
                const isEx = result?.is_exchange || !!name;
                if (isEx && name) {
                    patchNode({
                        entity_name: name,
                        entity_type: 'Exchange',
                        label: formatLabel(nodeId, name),
                    });
                }
                setStatus('done');
                addFinding({ action, emoji: '🏦', label: 'Exchange', summary: isEx ? `${name || 'Exchange'} · ${Math.round((result?.confidence || 0) * 100)}%` : 'Not an exchange', variant: isEx ? 'success' : 'info', tabLink: 'overview' });

            } else if (action === 'velocity') {
                const result = await investigationAnalysisService.velocity(nodeChain, nodeId);
                const metrics = result?.data?.metrics || result?.metrics || result;
                const profile = result?.velocity_profile || result?.profile_type || metrics?.profile_type;
                const ftr = metrics?.fund_through_rate ?? result?.fund_through_rate;
                const summary = profile
                    ? `${profile}${ftr != null ? ` · FTR ${(ftr * 100).toFixed(0)}%` : ''}`
                    : 'Profile computed';
                setStatus('done');
                addFinding({ action, emoji: '⚡', label: 'Velocity', summary, variant: 'info' });

            } else if (action === 'smart_contract') {
                const result = await addressService.getContractInfo(nodeChain, nodeId);
                const isContract = result?.is_contract;
                if (isContract !== undefined) {
                    patchNode({ is_contract: isContract });
                }
                setStatus('done');
                addFinding({ action, emoji: '📄', label: 'Contract', summary: isContract ? `Contract · ${result?.verified ? 'Verified' : 'Unverified'}` : 'Not a contract', variant: isContract ? 'info' : 'success', tabLink: 'intelligence' });

            } else if (action === 'risk_score') {
                const result = await addressService.getRisk(nodeChain, nodeId);
                const score = result?.risk_score ?? result?.score;
                const pct = score != null ? `${Math.round(score * 100)}%` : '—';
                const safeScore = score ?? 0;
                const tier = safeScore > 0.7 ? 'High' : safeScore > 0.3 ? 'Medium' : 'Low';
                if (score != null) {
                    patchNode({ risk_score: score });
                }
                setStatus('done');
                addFinding({ action, emoji: '📊', label: 'Risk', summary: `${pct} · ${tier}`, variant: safeScore > 0.7 ? 'error' : safeScore > 0.3 ? 'warning' : 'success', tabLink: 'overview' });

            } else if (action === 'forward_trace') {
                if (selectedNode) setActiveTab('analysis'); else setShowAutoInvestigateCard(true);
                toast.info('Forward Trace — use Investigate card, or right-click the node');
                setStatus('idle');

            } else if (action === 'follow_money') {
                // Follow the Money: auto-trace to exchange → gold-path → dim non-trail → select trail txs
                // Uses auto_trace_to_exchange which leverages Neo4j graph paths (instant when
                // graph is populated) with BFS fallback. Much faster than raw BFS trace.
                // Uses SSE stream so BFS phase emits progress events + keeps Railway proxy alive.

                // Warn if tracing from a non-root node
                const rootAddr = effectiveAddress;
                if (rootAddr && nodeId !== rootAddr) {
                    const displayName = (selectedNode?.entity_name && selectedNode.entity_name !== 'Unknown')
                        ? selectedNode.entity_name
                        : nodeId.slice(0, 10) + '…';
                    toast.info(`Tracing from ${displayName} (not the root address)`, { duration: 4000 });
                }

                toast.info('Follow the Money — tracing to exchange…');

                // ── Process the completed auto-trace response (same logic for SSE and REST fallback) ──
                const handleAutoTraceResult = (response: any) => {
                    const paths = response?.paths || response?.traces || [];

                    // Warn if node had no outgoing txs locally but trace found paths via graph
                    if (paths.length > 0) {
                        const nodeTxs = useTransactionStore.getState().getNodeTransactions(nodeId) || selectedNode?.expanded_transactions || [];
                        const outgoingCount = nodeTxs.filter((tx: any) => {
                            const from = tx.from_address || '';
                            return from === nodeId || (nodeId.startsWith('0x') && from.toLowerCase() === nodeId.toLowerCase());
                        }).length;
                        if (outgoingCount === 0 && nodeTxs.length > 0) {
                            toast.info('Paths found via graph data. Load outgoing transactions in Transfers tab for full detail.', { duration: 5000 });
                        }
                    }

                    if (paths.length > 0) {
                        // Remove .discovering class from SSE-streamed preview nodes/edges
                        try {
                            graphRef.current?.getCy()?.elements('.discovering').removeClass('discovering');
                        } catch { /* cy not ready */ }

                        // skipSnapshot=true: FTM SSE path already pushed a snapshot before
                        // opening the EventSource, so a single undo removes both preview
                        // nodes and the final gold path.
                        const hops = addTracePathToGraph(response, 'forward', nodeChain, true);

                        // Connect root node to first hop if path starts from a counterparty
                        const connectedFirstHops = new Set<string>();
                        for (const p of paths) {
                            const pAddrs: string[] = p?.addresses || p?.path || [];
                            if (pAddrs.length > 0 && pAddrs[0] !== nodeId) {
                                const firstHop = pAddrs[0];
                                if (!connectedFirstHops.has(firstHop)) {
                                    connectedFirstHops.add(firstHop);
                                    const connEdgeId = `${nodeId}-${firstHop}-connector`;
                                    setElements(prev => {
                                        if (prev.some(el => el.data.id === connEdgeId)) return prev;
                                        return [...prev, {
                                            data: {
                                                id: connEdgeId,
                                                source: nodeId,
                                                target: firstHop,
                                                label: '',
                                                edgeType: 'gold-path-connector',
                                                _visibility: 1.0,
                                            }
                                        }];
                                    });
                                }
                            }
                        }

                        const primary = paths[0];
                        const targetName = primary?.target_entity?.name || 'destination';

                        // Select trail txs as evidence (checked in Transfers tab)
                        const pathAddresses: string[] = primary?.addresses || primary?.path || [];
                        const pathEdges: any[] = primary?.edges || [];
                        const trailTxs: any[] = [];
                        for (let i = 0; i < pathEdges.length; i++) {
                            const pe = pathEdges[i];
                            if (!pe) continue;
                            const src = pathAddresses[i] || '';
                            const tgt = pathAddresses[i + 1] || '';
                            const txs = pe.all_transactions || [pe];
                            for (const tx of txs) {
                                if (!tx?.tx_hash) continue;
                                trailTxs.push({
                                    tx_hash: tx.tx_hash,
                                    from_address: src,
                                    to_address: tgt,
                                    value: tx.value ?? 0,
                                    timestamp: tx.timestamp ?? null,
                                    token_symbol: tx.token_symbol ?? null,
                                    direction: 'outgoing',
                                    counterparty: tgt,
                                });
                            }
                        }
                        if (trailTxs.length > 0) {
                            setSelectedTxs(prev => {
                                const existing = new Set(prev.map(t => t.tx_hash));
                                return [...prev, ...trailTxs.filter(t => !existing.has(t.tx_hash))];
                            });
                        }

                        lastFtmResultRef.current = { chain: nodeChain, address: nodeId, response };
                        // Per-node cache so the Analysis tab can show a summary and
                        // the user can revisit any node's FTM result later in the session.
                        useFtmStore.getState().setEntry(nodeChain, nodeId, response);

                        // Auto-archive FTM HTML into the case file (Analysis tab)
                        // without requiring the officer to click "View Report".
                        void (async () => {
                            try {
                                const mod = await import('../components/workbench/FollowTheMoneyReport');
                                const allTxs: any[] = [];
                                const els = useGraphStore.getState().elements || [];
                                els.forEach((el: any) => {
                                    if (el.data?.expanded_transactions) {
                                        allTxs.push(...el.data.expanded_transactions);
                                    }
                                });
                                const cy = graphRef.current?.getCy?.();
                                const screenshot = cy
                                    ? cy.png({ output: 'base64uri', full: true, scale: 1.5 })
                                    : undefined;
                                const html = mod.generateFollowTheMoneyReportHtml(
                                    nodeChain,
                                    nodeId,
                                    { ...response, direction: 'forward' },
                                    screenshot,
                                    allTxs.length > 0 ? allTxs : undefined,
                                );
                                const persist = persistFtmReportRef.current;
                                if (!persist) return;
                                await persist({
                                    title: buildReportTitle(REPORT_TYPES.followTheMoney, {
                                        chain: nodeChain,
                                        address: nodeId,
                                    }),
                                    htmlContent: html,
                                    metadata: {
                                        chain: nodeChain,
                                        address: nodeId,
                                        archive_source: 'auto_complete',
                                        silent_toast: true,
                                        paths_count: (response?.paths || response?.traces || []).length,
                                    },
                                });
                                useFtmStore.getState().markArchived(nodeChain, nodeId);
                                useGraphStore.getState().markNodeReported(nodeId, 'follow_the_money');
                            } catch (archErr) {
                                console.warn('Auto-archive FTM report failed', archErr);
                            }
                        })();

                        setStatus('done');
                        addFinding({ action, emoji: '💰', label: 'Follow Money', summary: `${hops} hops → ${targetName}`, variant: 'success', tabLink: 'transfers' });
                        toast.success(`Trail: ${hops} hops → ${targetName}`, { duration: 7000 });

                        // ── Background: enrich path nodes with full transaction context ──
                        (async () => {
                            try {
                                const allPathAddrs = new Set<string>();
                                for (const p of paths) {
                                    for (const addr of (p.addresses || p.path || [])) {
                                        if (addr) allPathAddrs.add(addr);
                                    }
                                }
                                await enrichExpandedTransactions({
                                    chain: nodeChain,
                                    setElements,
                                    addresses: [...allPathAddrs],
                                    txLimit: 50,
                                    skipIfExists: true,
                                });
                            } catch (bgErr) {
                                console.error('Follow Money background enrich failed:', bgErr);
                            }
                            setFollowMoneyMode(true);
                            setTimeout(() => graphRef.current?.toggleFollowMoney(true), 600);
                        })();
                    } else {
                        setStatus('done');
                        addFinding({ action, emoji: '💰', label: 'Follow Money', summary: 'No forward path found', variant: 'info' });
                        toast.info('No forward trail found from this address.');
                    }
                };

                // ── SSE stream (primary path) ──
                // Snapshot before any canvas mutation so the entire FTM action (preview
                // nodes + final gold path) can be undone in one step. addTracePathToGraph
                // also calls pushSnapshot(), but by then the SSE preview nodes have
                // already been added — so the user-facing snapshot boundary must be here.
                useGraphStore.getState().pushSnapshot();

                let ftmCleanup: (() => void) | null = null;
                let ftmDone = false;
                let ftmReceivedAnyEvent = false;

                // If no event arrives within 8s, assume SSE is broken and fall back to REST.
                const ftmFallbackTimer = setTimeout(async () => {
                    if (ftmDone || ftmReceivedAnyEvent) return;
                    ftmDone = true;
                    ftmCleanup?.();
                    try {
                        const response = await addressService.autoTrace(nodeChain, nodeId);
                        handleAutoTraceResult(response);
                    } catch (traceErr: any) {
                        setStatus('error');
                        toast.error(`Follow Money failed: ${traceErr?.message || 'Unknown error'}`);
                    }
                }, 8000);

                try {
                    ftmCleanup = addressService.autoTraceStream(nodeChain, nodeId, (evtType, data) => {
                        if (ftmDone) return;
                        ftmReceivedAnyEvent = true;
                        clearTimeout(ftmFallbackTimer);
                        if (evtType === 'phase' || evtType === 'progress') {
                            // Progress events keep the connection alive — no UI action needed
                        } else if (evtType === 'node_discovered') {
                            // Add preview node with .discovering class so officers see live progress
                            if (data.id) {
                                setElements(prev => addToCanvas(prev, [{
                                    data: {
                                        id: data.id,
                                        label: data.entity_name || undefined,
                                        type: 'address',
                                        chain: data.chain || nodeChain,
                                        isGoldPath: true,
                                        _visibility: 1.0,
                                        ...(data.entity_type ? { entity_type: data.entity_type } : {}),
                                        ...(data.entity_name ? { entity_name: data.entity_name } : {}),
                                    },
                                    classes: 'discovering',
                                }], { chain: nodeChain, applyGoldPathClass: false }));
                            }
                        } else if (evtType === 'edge_discovered') {
                            if (data.source && data.target) {
                                setElements(prev => addToCanvas(prev, [{
                                    data: {
                                        id: `gold-${data.source}-${data.target}`,
                                        source: data.source,
                                        target: data.target,
                                        label: data.hop ? `Hop ${data.hop}` : '',
                                        edgeType: 'gold-path',
                                        value: data.value ?? 0,
                                        token_symbol: data.token_symbol ?? null,
                                        tx_hash: data.tx_hash ?? null,
                                        _visibility: 1.0,
                                    },
                                    classes: 'discovering gold-path',
                                }], { chain: nodeChain, applyGoldPathClass: true }));
                            }
                        } else if (evtType === 'complete') {
                            ftmDone = true;
                            ftmCleanup?.();
                            handleAutoTraceResult(data);
                        } else if (evtType === 'error') {
                            if (!ftmDone) {
                                ftmDone = true;
                                ftmCleanup?.();
                                setStatus('error');
                                toast.error(`Follow Money failed: ${data?.error || 'Unknown error'}`);
                            }
                        }
                    });
                } catch (streamErr: any) {
                    // EventSource constructor threw — fall back to REST immediately
                    clearTimeout(ftmFallbackTimer);
                    ftmDone = true;
                    try {
                        const response = await addressService.autoTrace(nodeChain, nodeId);
                        handleAutoTraceResult(response);
                    } catch (traceErr: any) {
                        setStatus('error');
                        toast.error(`Follow Money failed: ${traceErr?.message || 'Unknown error'}`);
                    }
                }

            } else if (action === 'backward_trace') {
                if (selectedNode) setActiveTab('analysis'); else setShowAutoInvestigateCard(true);
                toast.info('Backward Trace — use Investigate card, or right-click the node');
                setStatus('idle');

            } else if (action === 'counterparty') {
                if (selectedNode) setActiveTab('analysis'); else setShowAutoInvestigateCard(true);
                toast.info('Counterparty Analysis — running via Investigate card');
                setStatus('idle');

            } else if (action === 'multi_hop') {
                if (selectedNode) setActiveTab('analysis'); else setShowAutoInvestigateCard(true);
                toast.info('Multi-Hop Exposure — running via Investigate card');
                setStatus('idle');

            } else if (action === 'compliant_exchange') {
                const nodeChain = selectedNode?.chain || effectiveChain;
                const nodeId = selectedNode?.id || effectiveAddress;
                if (!nodeId || !nodeChain) return;

                let cleanup: (() => void) | null = null;
                setStatus('running');
                addFinding({
                    action,
                    emoji: '🛡️',
                    label: 'Compliant Exit',
                    summary: `Probing up to 10 hops from ${nodeId.slice(0, 8)}...`,
                    variant: 'info',
                });

                cleanup = addressService.findCompliantExchangeStream(nodeChain, nodeId, (evt, data) => {
                    // Mirror the autoTraceStream pattern above — discovered
                    // nodes/edges get promoted to the canvas as gold-path
                    // elements with the `discovering` class so officers see
                    // the trace materialising live.
                    if (evt === 'node_discovered') {
                        if (data?.id) {
                            setElements(prev => addToCanvas(prev, [{
                                data: {
                                    id: data.id,
                                    label: data.entity_name || undefined,
                                    type: 'address',
                                    chain: data.chain || nodeChain,
                                    isGoldPath: true,
                                    _visibility: 1.0,
                                    ...(data.entity_type ? { entity_type: data.entity_type } : {}),
                                    ...(data.entity_name ? { entity_name: data.entity_name } : {}),
                                    ...(data.is_compliant ? { is_compliant: true } : {}),
                                },
                                classes: 'discovering',
                            }], { chain: nodeChain, applyGoldPathClass: false }));
                        }
                    } else if (evt === 'edge_discovered') {
                        if (data?.source && data?.target) {
                            setElements(prev => addToCanvas(prev, [{
                                data: {
                                    id: `compliant-${data.source}-${data.target}`,
                                    source: data.source,
                                    target: data.target,
                                    label: data.hop ? `Hop ${data.hop}` : '',
                                    edgeType: 'gold-path',
                                    value: data.value ?? 0,
                                    token_symbol: data.token_symbol ?? null,
                                    tx_hash: data.tx_hash ?? null,
                                    _visibility: 1.0,
                                },
                                classes: 'discovering gold-path',
                            }], { chain: nodeChain, applyGoldPathClass: true }));
                        }
                    } else if (evt === 'complete') {
                        setStatus('done');
                        if (data?.found) {
                            toast.success(`Found ${data.paths?.length || ''} legal pathway(s) to compliant exchange`);
                        } else {
                            // WB09: say what was searched, not that the world was.
                            const cov = data?.coverage;
                            toast.info(
                                cov && typeof cov.addresses_explored === 'number'
                                    ? `None found in the ${cov.addresses_explored} addresses loaded (${cov.hops_available ?? 0} hop(s) deep). Load more transactions to search further.`
                                    : 'None found in the addresses loaded. Load more transactions to search further.',
                            );
                        }
                    } else if (evt === 'error') {
                        setStatus('error');
                        toast.error('Compliant exchange trace failed');
                    }
                });
            }
        } catch (err: any) {
            setStatus('error');
            toast.error(`${action.replace(/_/g, ' ')} failed: ${err?.message || 'Unknown error'}`);
        }
    }, [selectedNode, effectiveAddress, effectiveChain]);

    // ── Auto-trace (node-aware: uses selectedNode when present) ───────────────
    const handleAutoTrace = async () => {
        // Prefer the selected canvas node over the session address
        const targetChain = selectedNode?.chain ? normalizeChain(selectedNode.chain) || effectiveChain : effectiveChain;
        const targetAddress = selectedNode?.id ?? effectiveAddress;
        if (!targetChain || !targetAddress) return;
        if (isMockDataMode(demoDataModeRef.current)) {
            toast.info(MOCK_MODE_TOAST, { duration: 5000 });
            return;
        }
        setLoading(true);
        try {
            const response = await addressService.autoTrace(targetChain, targetAddress);

            // Merge any fetched transactions into Node Inspector tabs
            let topCounterparties: Array<{ address: string; outVolume: number; inVolume: number; txCount: number; primaryToken: string | null }> = [];
            if (response.transactions?.length > 0) {
                const result = mergeAutoTraceTransactions(response.transactions, targetAddress, targetChain);
                topCounterparties = result.topCounterparties;
                if (result.added > 0) toast.success(`Loaded ${result.added} transactions`, { duration: 4000 });
            }

            if (response.found && response.paths?.length > 0) {
                const hops = addTracePathToGraph(response, 'auto', targetChain);
                const targetName = response.paths[0]?.target_entity?.name || 'Exchange';
                toast.success(`Found path to ${targetName}: ${hops} hops`, { duration: 7000 });
            } else {
                toast.info(`No exchange found within ${response.depth_searched} hops.`);
                // Show neighborhood graph: top counterparties on canvas
                if (topCounterparties.length > 0) {
                    addNeighborhoodToCanvas(topCounterparties, targetAddress, targetChain);
                }
            }
        } catch (e: any) {
            console.error('Auto-Trace failed', e);
            toast.error('Auto-Trace failed.');
        } finally {
            setLoading(false);
        }
    };

    const holisticRunRef = useRef<{ id: number; cleanup: (() => void) | null }>({ id: 0, cleanup: null });
    const runHolisticTrace = useCallback((chain: string, addresses: string[]) => {
        if (!chain || addresses.length < 2) {
            toast.error('Holistic Trace requires at least 2 selected addresses.');
            return;
        }

        // Cancel any in-flight holistic run and bump the session id so a
        // previous run's late SSE events can't bleed into this run's store /
        // canvas (R8 stale-response guard — the holistic store is global).
        try { holisticRunRef.current.cleanup?.(); } catch { /* already closed */ }
        const myRunId = ++holisticRunRef.current.id;
        const isStale = () => holisticRunRef.current.id !== myRunId;

        const store = useHolisticStore.getState();
        store.setRunning(chain, addresses);
        setLoading(true);

        let cleanup: (() => void) | null = null;
        let isDone = false;

        cleanup = addressService.holisticTraceStream(chain, addresses, 2, (evtType, data) => {
            if (isDone || isStale()) return;

            if (evtType === 'step_start') {
                useHolisticStore.getState().setStep(data.step ?? '');
            } else if (evtType === 'step_result' && data.step && data.data) {
                useHolisticStore.getState().mergeStepResult(data.step, data.data);
            } else if (evtType === 'node_discovered' && data.id) {
                // Pass through every optional field §3 lists for node_discovered.
                // `risk_score` drives overlay glow intensity, `confidence`
                // drives label suppression (<0.3 hidden), `hop` and
                // `discovered_via` are read by the inspector trace context.
                setElements(prev => addToCanvas(prev, [{
                    data: {
                        id: data.id,
                        label: data.entity_name || undefined,
                        type: 'address',
                        chain: data.chain || chain,
                        _visibility: 1.0,
                        ...(data.entity_type ? { entity_type: data.entity_type } : {}),
                        ...(data.entity_name ? { entity_name: data.entity_name } : {}),
                        ...(typeof data.risk_score === 'number' ? { risk_score: data.risk_score } : {}),
                        ...(typeof data.confidence === 'number' ? { confidence: data.confidence } : {}),
                        ...(typeof data.hop === 'number' ? { hop: data.hop } : {}),
                        ...(data.discovered_via ? { discovered_via: data.discovered_via } : {}),
                        // CANVAS_PLACEMENT_LANGUAGE §3.3: spatial-role channel.
                        // Drives analyzeGraph override + focus-set picker in
                        // GraphCanvas. Backend declares; layout owns x/y.
                        ...(data.placement_role ? { placement_role: data.placement_role } : {}),
                    },
                    classes: 'discovering',
                }], { chain, applyGoldPathClass: false }));
            } else if (evtType === 'edge_discovered' && data.source && data.target) {
                // Holistic emits two edge taxonomies via the orchestrator:
                //   • edge_type === 'cross_link'  — direct money movement BETWEEN
                //     two seed addresses. This is the headline finding the
                //     report flags ("Cross-Links Found: 12"). Render gold.
                //   • edge_type === 'trace' (or anything else) — BFS shallow-trace
                //     CONTEXT around each seed. These are evidence for shared-
                //     entity computation, not the trace finding itself. Painting
                //     them gold drowns the cross-link signal (the v18 dump had
                //     66/66 amber edges, palette signal lost). Render as plain
                //     money_flow grey — the §10.5 width tier still encodes
                //     magnitude so investigators can see relative volume.
                const isCrossLink = data.edge_type === 'cross_link';
                const value = data.value ?? 0;
                const tokenSymbol = data.token_symbol ?? null;
                const txHash = data.tx_hash ?? null;
                const timestamp = data.timestamp ?? null;
                const txCount = data.tx_count ?? undefined;

                // Prefer "value + token (N txs)" label over "Hop N" — investigators
                // need to see amounts on edges directly (canvas readability).
                const valueLabel = computeEdgeLabel(value, tokenSymbol, txCount);
                const edgeLabel = valueLabel || (data.hop ? `Hop ${data.hop}` : undefined);

                // §10.5 magnitude tier — gives the stylesheet `[_widthTier=...]`
                // selectors something to bind to so trace-context edges aren't
                // all rendered at the same default width. consolidateExistingEdges
                // would normally write this, but cross_link / gold-path edges
                // are PROTECTED in the consolidator (kept as-is post-SSE) so we
                // need to seed it here at mint time.
                const widthTier = magnitudeTier(
                    typeof value === 'number' ? value : parseFloat(String(value || 0)) || 0,
                    txCount ?? 1,
                    tokenSymbol || undefined,
                );

                // Canonical edge id `${src}->${tgt}` matches the convention
                // addToCanvas uses for non-gold-path edges. The legacy
                // `gold-${src}-${tgt}` / `cross-${src}-${tgt}` prefixes minted
                // a parallel edge whenever later SSE on the same pair came
                // through the canonical path, leaving an orphan that never
                // deduped. Suffix with tx_hash when present so distinct
                // single-tx cross-link records between the same pair stay
                // distinct (the cross_link discovery path emits one event per
                // underlying transaction).
                const baseId = `${data.source}->${data.target}`;
                const edgeId = txHash ? `${baseId}#${txHash}` : baseId;

                // Data-layer-first (R28 + CANVAS_DATA_DISPLAY_SEPARATION §5.6,
                // ALGORITHM_FRONTEND_CONTRACT.md §4): when the backend includes
                // per-tx records on the edge event, write them to
                // useTransactionStore for BOTH endpoints so Node Inspector →
                // Transfers tab can show dates / values / tokens.
                //
                // Fallback: if `transactions[]` is empty but `tx_hash` is set,
                // synthesize EXACTLY ONE record from the event fields. This is
                // the only legal synthesis pattern per §4 — a one-to-one
                // repackaging of fields the backend already sent (tx_hash,
                // value, timestamp, token_symbol), not invented data. Covers
                // single-tx trace edges whose underlying graph_service caller
                // doesn't pass `transactions=` to the emitter.
                const txArray: any[] = Array.isArray(data.transactions) ? data.transactions : [];
                if (txArray.length > 0) {
                    useTransactionStore.getState().appendNodeTransactions(data.source, txArray.map(t => toExpandedTx(t, data.source)));
                    useTransactionStore.getState().appendNodeTransactions(data.target, txArray.map(t => toExpandedTx(t, data.target)));
                } else if (txHash) {
                    const repackaged = {
                        tx_hash: txHash,
                        from_address: data.source,
                        to_address: data.target,
                        value: typeof value === 'number' ? value : parseFloat(String(value)) || 0,
                        timestamp,
                        token_symbol: tokenSymbol,
                    };
                    useTransactionStore.getState().appendNodeTransactions(data.source, [toExpandedTx(repackaged, data.source)]);
                    useTransactionStore.getState().appendNodeTransactions(data.target, [toExpandedTx(repackaged, data.target)]);
                }

                setElements(prev => addToCanvas(prev, [{
                    data: {
                        id: edgeId,
                        source: data.source,
                        target: data.target,
                        label: edgeLabel,
                        // Cross-link gets gold-path. Trace-context edges get NO
                        // edgeType so the default money_flow stylesheet wins
                        // (grey stroke, width driven by _widthTier).
                        ...(isCrossLink ? { edgeType: 'gold-path' } : {}),
                        value,
                        token_symbol: tokenSymbol,
                        tx_hash: txHash,
                        timestamp,
                        tx_count: txCount,
                        // Directional volumes — passed through when the backend
                        // emits them (counterparty-shaped edges). Inspector
                        // tooltip + edge-info panel read these.
                        ...(typeof data.volume_in === 'number' ? { volume_in: data.volume_in } : {}),
                        ...(typeof data.volume_out === 'number' ? { volume_out: data.volume_out } : {}),
                        ...(data.direction ? { direction: data.direction } : {}),
                        _widthTier: widthTier,
                        _visibility: 1.0,
                    },
                    classes: isCrossLink ? 'discovering gold-path' : 'discovering',
                }], { chain, applyGoldPathClass: isCrossLink }));
            } else if (evtType === 'complete') {
                isDone = true;
                cleanup?.();
                useHolisticStore.getState().setComplete(data.all_results ?? {});
                setLoading(false);
                toast.success('Holistic Trace completed.');
                setTimeout(() => {
                    try { graphRef.current?.getCy()?.elements('.discovering').removeClass('discovering'); }
                    catch { /* cy not ready */ }
                }, 500);
            } else if (evtType === 'error') {
                if (!isDone) {
                    isDone = true;
                    cleanup?.();
                    const msg = data?.error || data?.message || 'Unknown error';
                    useHolisticStore.getState().setError(msg);
                    setLoading(false);
                    toast.error(`Holistic Trace failed: ${msg}`);
                    setTimeout(() => {
                        try { graphRef.current?.getCy()?.elements('.discovering').removeClass('discovering'); }
                        catch { /* cy not ready */ }
                    }, 500);
                }
            }
        });
        // Store cleanup so the NEXT run (or unmount) can cancel this stream.
        holisticRunRef.current.cleanup = cleanup;
    }, []);

    // runGraphComplianceSweep + runInlinePathTrace — Now handled by AnalyzeGraphCard

    const handleBatchStash = async (nodeIds: string[]) => {
        if (!nodeIds.length) return;
        const before = elements;
        const nodesToStash = before.filter(el => !el.data?.source && nodeIds.includes(el.data.id));
        // Controller stashes nodes (and auto-stashes orphan edges) in one call.
        canvasController.stash(nodeIds, 'manual-stash', 'user');
        if (selectedNode?.id && nodeIds.includes(selectedNode.id)) {
            setSelectedNode(null);
        }
        const edgeCount = before.filter(el => el.data?.source && (nodeIds.includes(el.data.source!) || nodeIds.includes(el.data.target!))).length;
        toast.success(`Stashed ${nodesToStash.length} nodes and ${edgeCount} connected edges.`);
    };

    const loadTriageForNode = useCallback(async (nodeAddress: string, nodeChain: string, options?: { fast?: boolean }) => {
        if (!nodeAddress) {
            setTriageData(null);
            setTriageError(null);
            return;
        }
        // Privacy chain TX nodes (id starts with "tx:") are not addresses — skip triage + tx fetch
        if (nodeAddress.startsWith('tx:')) {
            setTriageData(null);
            setTriageError(null);
            return;
        }
        if (isMockDataMode(demoDataModeRef.current)) {
            setTriageLoading(false);
            setTriageError(null);
            return;
        }

        setTriageLoading(true);
        setTriageError(null);
        try {
            const triage = await addressService.getTriage(nodeChain, nodeAddress, 1, { fast: !!options?.fast });
            logTriage(nodeAddress, triage);
            setTriageData(triage);

            // Auto-populate transaction store for the root node (triage caches txs in DB,
            // so this is a fast cache hit). Cap hubs — 500 on WazirX routinely Network-Errors.
            try {
                const focusEl = useGraphStore.getState().elements.find(
                    (el: any) => el.data?.id === nodeAddress && !el.data?.source,
                );
                const fetchLimit = hubExpandLimit(20, focusEl?.data as any);
                const txResp = await addressService.getTransactions(
                    nodeChain, nodeAddress, fetchLimit, 0,
                    undefined, undefined, undefined, undefined,
                    { cacheOnly: wantsCacheOnly(demoDataModeRef.current) },
                );
                const txs = txResp?.transactions || [];
                logTxStore(`triage auto-load (source=${txResp?.data_source || '?'})`, nodeAddress, txs.length, txs);
                if (txs.length > 0) {
                    txStore.setNodeTransactions(nodeAddress, toExpandedTxs(txs, nodeAddress));
                }
            } catch { /* best-effort — triage is the priority */ }

            // Back-fill entity data onto canvas node if triage discovered it
            // (entity batch-lookup may have missed it on the first pass)
            const triageEntity = triage?.summary?.entity_name;
            if (triageEntity && nodeAddress) {
                const currentEls = useGraphStore.getState().elements;
                const nodeEl = currentEls.find((el: any) => el.data?.id === nodeAddress && !el.data?.source);
                if (nodeEl && !nodeEl.data?.entity_name) {
                    const updated = currentEls.map((el: any) =>
                        el.data?.id === nodeAddress && !el.data?.source
                            ? { ...el, data: { ...el.data, entity_name: triageEntity, label: `${triageEntity}\n${nodeAddress.substring(0, 8)}...` } }
                            : el
                    );
                    useGraphStore.getState().setElements(updated);
                }
            }

            // UX hints for contract addresses and zero-activity EVM addresses
            const txCount = triage?.transactions?.total ?? triage?.summary?.tx_count ?? (triage as any)?.details?.tx_count;
            const isContract = (triage as any)?.details?.is_contract;
            if (isContract) {
                toast('This appears to be a token contract address.', { duration: 4000, icon: 'ℹ️' });
            } else if (txCount === 0 && ['ETH', 'MATIC', 'BNB', 'ARB', 'BASE'].includes(nodeChain)) {
                // Only show if triage truly found no transactions (not just missing field)
                if (triage?.transactions !== undefined) {
                    toast('No activity on ' + nodeChain + '. Try another EVM chain via the chain selector.', { duration: 5000, icon: '💡' });
                }
            }

        } catch (e: any) {
            console.error('Failed to load triage', e);
            setTriageError(toErrorMessage(e, 'Triage unavailable'));
            setTriageData(null);

        } finally {
            setTriageLoading(false);
        }
    }, [txStore]);

    useEffect(() => {
        const nodeId = selectedNode?.id || effectiveAddress;
        const nodeType = selectedNode?.type;

        if (triageDelayTimerRef.current) {
            window.clearTimeout(triageDelayTimerRef.current);
            triageDelayTimerRef.current = null;
        }

        if (!nodeId || (nodeType && nodeType !== 'address')) {
            setTriageData(null);
            setTriageError(null);
            return;
        }

        const selectedNodeChain = !isPlaceholderChain(selectedNode?.chain)
            ? normalizeChain(selectedNode!.chain)
            : null;
        const queryChain = !isPlaceholderChain(effectiveChain)
            ? normalizeChain(effectiveChain)
            : null;
        const strictDetectedChain = detectChainStrict(nodeId);
        const nodeChain = selectedNodeChain || queryChain || strictDetectedChain;

        if (!nodeChain || isPlaceholderChain(nodeChain)) {
            setTriageData(null);
            setTriageError('Unsupported or unknown chain for triage');

            return;
        }

        const isRootNode = !!(effectiveAddress && nodeId.toLowerCase() === effectiveAddress.toLowerCase());

        // Stagger initial root triage until after graph/details render completes.
        if (isRootNode && loading) {
            return;
        }

        // Mock demos already ship sample edges — do not hit Triage / TronGrid hubs.
        if (isMockDataMode(demoDataModeRef.current)) {
            setTriageLoading(false);
            setTriageError(null);
            if ((selectedNode as any)?._triage) {
                setTriageData((selectedNode as any)._triage as TriagePayload);
            }
            return;
        }

        // R8: stale-response guard — prevent Node A data overwriting Node B
        let cancelled = false;

        const triageDelayMs = 0;

        const wrappedRun = async () => {
            setTriageLoading(true);
            setTriageError(null);
            try {
                const triage = await addressService.getTriage(nodeChain, nodeId, 1, { fast: isRootNode });
                if (cancelled) return; // R8: node changed while in-flight
                setTriageData(triage);

                const txCount = triage?.transactions?.total ?? triage?.summary?.tx_count ?? (triage as any)?.details?.tx_count;
                const isContract = (triage as any)?.details?.is_contract;
                if (isContract) {
                    toast('This appears to be a token contract address.', { duration: 4000, icon: 'ℹ️' });
                } else if (txCount === 0 && ['ETH', 'MATIC', 'BNB', 'ARB', 'BASE'].includes(nodeChain)) {
                    // Only show if triage truly found no transactions (not just missing field)
                    if (triage?.transactions !== undefined) {
                        toast('No activity on ' + nodeChain + '. Try another EVM chain via the chain selector.', { duration: 5000, icon: '💡' });
                    }
                }

                // Auto-populate transaction store for this node (cache hit, fast)
                // Cap hubs (WazirX etc.) — 500 on a CEX hot wallet routinely Network-Errors.
                if (!txStore.hasTransactions(nodeId)) {
                    try {
                        const fetchLimit = hubExpandLimit(20, selectedNode as any);
                        const txResp = await addressService.getTransactions(
                            nodeChain, nodeId, fetchLimit, 0,
                            undefined, undefined, undefined, undefined,
                            { cacheOnly: wantsCacheOnly(demoDataModeRef.current) },
                        );
                        const txs = txResp?.transactions || [];
                        if (txs.length > 0 && !cancelled) {
                            txStore.setNodeTransactions(nodeId, toExpandedTxs(txs, nodeId));
                        }
                    } catch { /* best-effort */ }
                }
            } catch (e: any) {
                if (cancelled) return;
                console.error('Failed to load triage', e);
                setTriageError(toErrorMessage(e, 'Triage unavailable'));
                setTriageData(null);
            } finally {
                if (!cancelled) setTriageLoading(false);
            }
        };

        if (triageDelayMs > 0) {
            triageDelayTimerRef.current = window.setTimeout(wrappedRun, triageDelayMs);
        } else {
            wrappedRun();
        }

        return () => {
            cancelled = true; // R8: mark stale
            if (triageDelayTimerRef.current) {
                window.clearTimeout(triageDelayTimerRef.current);
                triageDelayTimerRef.current = null;
            }
        };
    }, [selectedNode?.id, selectedNode?.type, effectiveChain, effectiveAddress, loading, activeDemoId, demoDataMode]);

    useEffect(() => {
        if (!selectedNode?.id || !isSelectedNodeTriageMatch || !triageData?.summary) return;

        const triageRisk = typeof triageData.summary.risk_score === 'number' ? triageData.summary.risk_score : undefined;
        const fallbackEntityName = triageData.summary.entity_name;
        const fallbackEntityType = triageData.summary.entity_type;
        const fallbackEntitySource = (triageData as any)?.details?.entity?.source;
        const triageIsSanctioned = !!triageData.summary.sanctioned;
        // Auto-flag tags computed by the backend at the triage chokepoint
        // (addresses_common.py _build_triage_payload). The backend appends
        // 'auto_flagged' to entity_tags and '🚩 Flagged' to node_labels
        // whenever risk_score >= 0.7, sanctioned, or mixer_entity. We
        // mirror those into the canvas node so the §9b-slot-1 flag SVG
        // renders, the Stash tray's Flagged-for-review section picks it
        // up, and the ActionsTab Flag button shows the right state.
        const triageEntityTags: string[] = Array.isArray((triageData as any).entity_tags) ? (triageData as any).entity_tags : [];
        const triageNodeLabels: string[] = Array.isArray((triageData as any).node_labels) ? (triageData as any).node_labels : [];
        const triageAutoFlagged: boolean = !!triageData.summary.auto_flagged;
        const selectedId = selectedNode.id;

        const mergeTags = (existing: any, incoming: string[]): { merged: string[]; changed: boolean } => {
            const base: string[] = Array.isArray(existing) ? existing.filter((s: any) => typeof s === 'string') : [];
            let changed = false;
            const merged = [...base];
            for (const t of incoming) {
                if (!merged.includes(t)) { merged.push(t); changed = true; }
            }
            return { merged, changed };
        };

        setSelectedNode(prev => {
            if (!prev || prev.id.toLowerCase() !== selectedId.toLowerCase()) return prev;

            let changed = false;
            const next: GraphNodeData = { ...prev };

            if (typeof triageRisk === 'number' && (next.risk_score === undefined || triageRisk > next.risk_score)) {
                next.risk_score = triageRisk;
                changed = true;
            }
            if (!next.entity_name && fallbackEntityName) {
                next.entity_name = fallbackEntityName;
                changed = true;
            }
            if (!next.entity_type && fallbackEntityType) {
                next.entity_type = fallbackEntityType;
                changed = true;
            }
            if (!next.entity_source && fallbackEntitySource) {
                next.entity_source = fallbackEntitySource;
                changed = true;
            }
            if (triageIsSanctioned && !next.entity_category) {
                next.entity_category = 'sanction';
                changed = true;
            }
            if (triageAutoFlagged) {
                const tagResult = mergeTags(next.entity_tags, triageEntityTags);
                if (tagResult.changed) { next.entity_tags = tagResult.merged; changed = true; }
                const labelResult = mergeTags(next.node_labels, triageNodeLabels);
                if (labelResult.changed) { next.node_labels = labelResult.merged; changed = true; }
            }

            return changed ? next : prev;
        });

        setElements(prevElements => {
            let changedAny = false;
            const nextElements = prevElements.map(el => {
                if (el.data?.source || !el.data?.id || el.data.id.toLowerCase() !== selectedId.toLowerCase()) return el;

                const updatedData = { ...el.data } as any;
                let changed = false;

                if (typeof triageRisk === 'number' && (updatedData.risk_score === undefined || triageRisk > updatedData.risk_score)) {
                    updatedData.risk_score = triageRisk;
                    changed = true;
                }
                if (!updatedData.entity_name && fallbackEntityName) {
                    updatedData.entity_name = fallbackEntityName;
                    changed = true;
                }
                if (!updatedData.entity_type && fallbackEntityType) {
                    updatedData.entity_type = fallbackEntityType;
                    changed = true;
                }
                if (!updatedData.entity_source && fallbackEntitySource) {
                    updatedData.entity_source = fallbackEntitySource;
                    changed = true;
                }
                if (triageIsSanctioned && !updatedData.entity_category) {
                    updatedData.entity_category = 'sanction';
                    changed = true;
                }
                if (triageAutoFlagged) {
                    const tagResult = mergeTags(updatedData.entity_tags, triageEntityTags);
                    if (tagResult.changed) { updatedData.entity_tags = tagResult.merged; changed = true; }
                    const labelResult = mergeTags(updatedData.node_labels, triageNodeLabels);
                    if (labelResult.changed) { updatedData.node_labels = labelResult.merged; changed = true; }
                }

                if (!changed) return el;
                changedAny = true;
                updatedData.label = formatLabel(updatedData.id, updatedData.entity_name);
                return { ...el, data: updatedData };
            });

            return changedAny ? nextElements : prevElements;
        });
    }, [selectedNode?.id, isSelectedNodeTriageMatch, triageData]);

    // Auto-trigger investigation on high-risk triage (respects localStorage preference)
    useEffect(() => {
        if (!triageData?.summary?.risk_score || !address || !chain) return;
        if (autoInvestigation.isRunning || autoInvestigation.isComplete) return;

        // TODO: Make auto-investigate preference configurable in Settings page
        // Options: 'always' | 'high-risk' | 'never' (stored in localStorage)
        const pref = localStorage.getItem('auto-investigate-on-triage') || 'never';
        if (pref === 'never') return;

        const riskScore = triageData.summary.risk_score;
        const shouldTrigger = pref === 'always' || (pref === 'high-risk' && riskScore >= 0.45);

        if (shouldTrigger) {
            if (selectedNode) setActiveTab('analysis'); else setShowAutoInvestigateCard(true);
            autoInvestigation.run(effectiveChain!, effectiveAddress!);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triageData?.summary?.risk_score, effectiveAddress, effectiveChain]);

    const handleRunAnalysis = async (type: MacroAnalysisType): Promise<boolean> => {
        if (elements.length === 0) {
            toast.error('No elements to analyze');
            return false;
        }
        setAnalyzeLoading(type);
        try {
            if (type === 'ai-summary') {
                if (!elements.length) return false;
                setInlineAiLoading(true);
                try {
                    const graphSnapshot = buildAnalysisGraph();
                    const primaryNode = elements.find(e => e.data.id === selectedNode?.id) || elements.find(e => e.data.id === address);
                    const behavioralFlags: string[] = [];
                    if (graphSnapshot.summary.total_value > 1000) behavioralFlags.push('high_volume');
                    if (graphSnapshot.summary.unique_counterparties > 10) behavioralFlags.push('many_counterparties');

                    const riskContext = {
                        overall_risk_score: primaryNode?.data?.risk_score || 0.5,
                        entity_name: primaryNode?.data?.entity_name,
                        entity_type: primaryNode?.data?.entity_type,
                        behavioral_flags: behavioralFlags
                    };

                    const result = await addressService.explainInvestigation(graphSnapshot, riskContext, chain || undefined, {
                        caseId: caseIdForLoad,
                        focusAddress: (primaryNode?.data?.id as string | undefined) || address || null,
                    });
                    // P1-07: an unavailable AI answer (analysis null) is a muted
                    // notice — never a summary, never a success toast, and the
                    // auto-archive below is never reached for it.
                    if (isAiUnavailable(result, 'analysis')) {
                        const reason = aiErrorText(result, 'analysis');
                        setMacroAiResults(prev => ({ ...prev, aiSummaryUnavailable: { error: reason } }));
                        toast.warning(aiUnavailableText(reason));
                        return false;
                    }
                    const analysisText: string = result.analysis;
                    setMacroAiResults(prev => ({ ...prev, aiSummaryUnavailable: null, aiSummaryMeta: readAiMeta(result) }));
                    setInlineAiSummary(analysisText);
                    setAnalyzeResultCounts({ ...usePanelStore.getState().analyzeResultCounts, 'ai-summary': 1 });

                    // Auto-archive printable AI Graph Summary into the case file
                    const subjectAddress =
                        (primaryNode?.data?.id as string | undefined)
                        || address
                        || null;
                    const subjectChain =
                        (primaryNode?.data?.chain as string | undefined)
                        || chain
                        || graphSnapshot.chain
                        || null;
                    const nodeCount = graphSnapshot.summary?.node_count
                        ?? elements.filter((e) => !e.data?.source).length;
                    const edgeCount = graphSnapshot.summary?.edge_count
                        ?? elements.filter((e) => e.data?.source).length;
                    const archiveKey = `${subjectAddress || 'graph'}|${subjectChain || ''}|${analysisText.length}|${nodeCount}|${edgeCount}`;
                    if (aiSummaryArchivedKeyRef.current !== archiveKey) {
                        aiSummaryArchivedKeyRef.current = archiveKey;
                        void (async () => {
                            try {
                                const mod = await import('../components/workbench/AiGraphSummaryReport');
                                let screenshot: string | undefined;
                                try {
                                    const cy = graphRef.current?.getCy?.();
                                    if (cy?.png) {
                                        screenshot = cy.png({
                                            output: 'base64uri',
                                            full: true,
                                            scale: 1.5,
                                            bg: '#ffffff',
                                        });
                                    }
                                } catch { /* optional */ }
                                const html = mod.generateAiGraphSummaryReportHtml({
                                    chain: subjectChain,
                                    address: subjectAddress,
                                    markdown: analysisText,
                                    nodeCount,
                                    edgeCount,
                                    canvasScreenshot: screenshot,
                                });
                                const persist = persistAiSummaryReportRef.current;
                                if (!persist) return;
                                await persist({
                                    title: buildReportTitle(REPORT_TYPES.aiGraphSummary, {
                                        chain: subjectChain,
                                        address: subjectAddress,
                                    }),
                                    htmlContent: html,
                                    metadata: {
                                        chain: subjectChain,
                                        address: subjectAddress,
                                        node_count: nodeCount,
                                        edge_count: edgeCount,
                                        generated_at: new Date().toISOString(),
                                        archive_source: 'auto_complete',
                                        silent_toast: true,
                                    },
                                });
                            } catch (archErr) {
                                console.warn('Auto-archive AI Graph Summary failed', archErr);
                                aiSummaryArchivedKeyRef.current = null;
                            }
                        })();
                    }
                    return true;
                } catch (error) {
                    console.error('Inline AI analysis failed', error);
                    toast.error('Failed to generate inline AI summary');
                    return false;
                } finally {
                    setInlineAiLoading(false);
                }
            } else if (type === 'pattern-scan') {
                if (!elements.length) return false;
                setInlinePatternsLoading(true);
                setInlinePatterns(null);
                try {
                    const analysisGraph = buildAnalysisGraph();
                    const result = await analyticsService.scanPatterns(
                        { nodes: analysisGraph.nodes, edges: analysisGraph.edges },
                        analysisGraph.chain || undefined
                    );
                    const findings = result.typologies || [];
                    setInlinePatterns(findings);
                    setAnalyzeResultCounts({
                        ...usePanelStore.getState().analyzeResultCounts,
                        'pattern-scan': findings.length,
                    });
                    toast.success(`Pattern scan complete (${findings.length} findings)`);
                    return true;
                } catch (e) {
                    console.error('Inline pattern scan failed:', e);
                    toast.error('Pattern scan failed');
                    return false;
                } finally {
                    setInlinePatternsLoading(false);
                }
            } else if (['pagerank', 'betweenness', 'communities'].includes(type)) {
                const addressNodes = elements.filter(el => !el.data?.source && el.data?.id);
                if (addressNodes.length === 0) return false;

                const scores = type === 'pagerank'
                    ? calculatePageRank(elements)
                    : type === 'betweenness'
                        ? calculateBetweenness(elements)
                        : calculateCommunities(elements);

                if (type === 'communities') {
                    applyCommunityOverlay(scores);
                    const communityCount = new Set(scores.map((score: GraphScore) => score.clusterId)).size;
                    setAnalyzeResultCounts({ ...usePanelStore.getState().analyzeResultCounts, communities: communityCount });
                } else {
                    applyScoreOverlay(type as 'pagerank' | 'betweenness', scores);
                    setAnalyzeResultCounts({ ...usePanelStore.getState().analyzeResultCounts, [type]: addressNodes.length });
                }
                toast.success(`${type} analysis complete on ${addressNodes.length} nodes`);
                return true;
            } else if (type === 'syndicate-corridor') {
                const targetAddr = selectedNode?.id || address;
                const targetChain = selectedNode?.chain || chain || 'TRX';
                if (!targetAddr) {
                    toast.info('Select a node or enter an address first');
                    return false;
                }
                if (!caseIdForLoad) {
                    toast.info(`${CASE_REQUIRED_REASON}: the corridor review is case-scoped.`);
                    return false;
                }
                toast.loading('Reviewing upstream feeders and drafting a playbook…', { id: 'syndicate-toast' });
                const corridorBase = { address: targetAddr, chain: targetChain, playbook: null, candidateFunderCount: 0, aiMeta: null };
                try {
                    const res = await investigationAnalysisService.analyzeSyndicateCorridor(targetChain, targetAddr, { caseId: caseIdForLoad });
                    toast.dismiss('syndicate-toast');
                    const status = corridorStatus(res);
                    const clusters = res?.syndicate_data?.syndicate_clusters;
                    // The playbook gets its own slot in the Macro results panel —
                    // it no longer overwrites the AI Summary (inlineAiSummary).
                    setMacroAiResults(prev => ({
                        ...prev,
                        corridor: {
                            ...corridorBase,
                            status,
                            unavailable: status === 'unavailable',
                            error: status === 'unavailable' ? aiErrorText(res, 'playbook') : null,
                            message: status === 'no_evidence' ? corridorNoEvidenceMessage(res) : null,
                            aiMeta: readAiMeta(res),
                            playbook: status === 'success' ? String(res.playbook) : null,
                            candidateFunderCount: Array.isArray(clusters) ? clusters.length : 0,
                        },
                    }));
                    setAnalyzeResultCounts({ ...usePanelStore.getState().analyzeResultCounts, 'syndicate-corridor': 1 });
                    if (status === 'unavailable') {
                        toast.warning(`Corridor data loaded. AI playbook unavailable: ${aiErrorText(res, 'playbook')}`);
                    } else if (status === 'no_evidence') {
                        toast.info(`No corridor evidence for this address: ${corridorNoEvidenceMessage(res)}`);
                    } else {
                        toast.info('Corridor review complete. The playbook is an AI draft for human review.');
                    }
                    return true;
                } catch (err: any) {
                    toast.dismiss('syndicate-toast');
                    const n = caseScopedErrorNotice(err, 'The corridor review');
                    setMacroAiResults(prev => ({
                        ...prev,
                        corridor: {
                            ...corridorBase,
                            status: n.kind === 'disabled' ? 'disabled' : n.kind === 'no_case' ? 'no_case' : 'error',
                            unavailable: n.kind !== 'error',
                            error: n.message,
                        },
                    }));
                    if (n.kind === 'error') toast.error(n.message); else toast.warning(n.message);
                    return false;
                }
            } else if (type === 'ai-trace') {
                const targetAddr = selectedNode?.id || address;
                const targetChain = selectedNode?.chain || chain || 'TRX';
                if (!targetAddr) {
                    toast.info('Select a node or enter an address first');
                    return false;
                }
                if (!caseIdForLoad) {
                    toast.info(`${CASE_REQUIRED_REASON}: the AI-assisted trace is case-scoped.`);
                    return false;
                }
                toast.loading('Running the AI-assisted trace…', { id: 'aitrace-toast' });
                const baseOutcome = {
                    address: targetAddr, chain: targetChain, nodeCount: 0, edgeCount: 0,
                    promotedNodes: 0, promotedEdges: 0, selectionMethods: {}, edges: [],
                };
                try {
                    const res = await investigationAnalysisService.aiTrace(targetChain, targetAddr, {
                        caseId: caseIdForLoad, maxForwardDepth: 2, maxBackwardDepth: 2,
                    });
                    toast.dismiss('aitrace-toast');
                    const aiMeta = readAiMeta(res);
                    const trace = res?.trace ?? null;
                    const built = buildAiTraceElements(trace, targetChain, aiMeta);

                    // Promote only NEW nodes: addToCanvas merges data onto an
                    // existing node, which would overwrite its label/attribution.
                    const existingIds = new Set(useGraphStore.getState().elements.map(e => String(e.data?.id)));
                    const newNodes = built.elements.filter(el => !el.data.source && !existingIds.has(String(el.data.id)));
                    const newEdges = built.elements.filter(el => !!el.data.source && !existingIds.has(String(el.data.id)));

                    // R12 data layer first: one repackaged record per edge that
                    // carries a tx hash (no invented data), for both endpoints.
                    for (const e of built.edges) {
                        if (!e.tx_hash) continue;
                        const repackaged = {
                            tx_hash: e.tx_hash,
                            from_address: e.source,
                            to_address: e.target,
                            value: typeof e.value === 'number' ? e.value : 0,
                            timestamp: null,
                            token_symbol: e.token_symbol || null,
                        };
                        useTransactionStore.getState().appendNodeTransactions(e.source, [toExpandedTx(repackaged, e.source)]);
                        useTransactionStore.getState().appendNodeTransactions(e.target, [toExpandedTx(repackaged, e.target)]);
                    }

                    // Gotcha 28: the single canvas entry point (undo snapshot,
                    // dedup, enrichment). edge_type 'trace' — the stylesheet owns
                    // presentation (R23); layout owns coordinates (R24).
                    const toPromote = [...newNodes, ...newEdges] as CytoscapeElement[];
                    if (toPromote.length > 0) {
                        await addNodesToCanvas(toPromote, { chain: targetChain, userAction: true, skipConnectionCheck: true });
                    }

                    const summary = trace?.summary;
                    const summaryText = summary && typeof summary === 'object' && summary.primary_root_supplier
                        ? `Candidate shared funder: ${String(summary.primary_root_supplier)} (lead; corroboration required).`
                        : null;
                    setMacroAiResults(prev => ({
                        ...prev,
                        aiTrace: {
                            ...baseOutcome,
                            status: 'ok',
                            unavailable: false,
                            aiMeta,
                            nodeCount: built.nodeCount,
                            edgeCount: built.edgeCount,
                            promotedNodes: newNodes.length,
                            promotedEdges: newEdges.length,
                            selectionMethods: built.selectionMethods,
                            edges: built.edges,
                            summaryText,
                        },
                    }));
                    setAnalyzeResultCounts({ ...usePanelStore.getState().analyzeResultCounts, 'ai-trace': built.edgeCount });
                    toast.info(`AI-assisted trace: ${newNodes.length} new address${newNodes.length === 1 ? '' : 'es'} added. Branches are leads for verification.`);
                    return true;
                } catch (err: any) {
                    toast.dismiss('aitrace-toast');
                    const n = caseScopedErrorNotice(err, 'The AI-assisted trace');
                    const status = n.kind === 'disabled' ? 'disabled' : n.kind === 'no_case' ? 'no_case' : 'error';
                    setMacroAiResults(prev => ({ ...prev, aiTrace: { ...baseOutcome, status, unavailable: n.kind !== 'error', error: n.message } }));
                    if (n.kind === 'error') toast.error(n.message); else toast.warning(n.message);
                    return false;
                }
            }
            return false;
        } catch (e: any) {
            console.error(`Analysis failed for ${type}`, e);
            toast.error(`Analysis failed: ${e.message}`);
            return false;
        } finally {
            setAnalyzeLoading(null);
        }
    };

    /**
     * Canvas tray → Graph Summary panel. The panel opens first so the officer
     * watches the summary being written in the slot where they will read it,
     * rather than waiting on a toast and then being asked to go find the result.
     */
    const runGraphSummary = async () => {
        const els = useGraphStore.getState().elements;
        if (els.length === 0) {
            toast.info('Add an address to the canvas first');
            return;
        }
        if (usePanelStore.getState().inlineAiLoading) return;

        const ok = await handleRunAnalysis('ai-summary');
        if (!ok) return;

        const current = useGraphStore.getState().elements;
        usePanelStore.getState().setGraphSummaryMeta({
            nodeCount: current.filter(el => !el.data?.source).length,
            edgeCount: current.filter(el => !!el.data?.source).length,
            generatedAt: Date.now(),
        });
    };

    const handleOpenGraphSummary = () => {
        if (elements.length === 0) {
            toast.info('Add an address to the canvas first');
            return;
        }
        usePanelStore.getState().setGraphSummaryOpen(true);
        useSelectionStore.getState().setNodeDetailMinimized(false);
        // Reopening from the tray shows the cached summary; Regenerate re-runs it.
        if (!usePanelStore.getState().inlineAiSummary.trim()) {
            void runGraphSummary();
        }
    };

    // ── Clustering handler ─────────────────────────────────────────────
    const CLUSTER_COLORS = ['#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#84cc16', '#f97316'];
    const handleCluster = useCallback(async () => {
        if (!selectedNode?.id || !chain) return;
        setClusterLoading(true);
        try {
            const result = await analyticsService.clusterAddress(chain, selectedNode.id);
            const clusters = result?.clusters || [];
            if (clusters.length === 0) {
                toast.info('No clusters found for this address');
                return;
            }

            // Apply cluster colors to existing graph nodes + inject new nodes
            const cy = graphRef.current?.getCy();
            let newNodeCount = 0;
            if (cy) {
                const allNewEls: any[] = [];
                clusters.forEach((cluster: any, idx: number) => {
                    const color = CLUSTER_COLORS[idx % CLUSTER_COLORS.length];
                    const addresses: string[] = cluster.addresses || [];
                    addresses.forEach((addr: string) => {
                        const node = cy.getElementById(addr);
                        if (node && node.length > 0) {
                            node.style({
                                'border-width': 4,
                                'border-color': color,
                                'border-opacity': 0.9,
                            });
                            node.data('cluster_id', cluster.cluster_id);
                            node.data('cluster_label', cluster.label);
                        } else {
                            // Inject cluster address not yet on canvas
                            allNewEls.push({ data: {
                                id: addr,
                                label: `${addr.slice(0, 6)}...${addr.slice(-4)}`,
                                type: 'address',
                                cluster_id: cluster.cluster_id,
                                cluster_label: cluster.label,
                            }});
                            allNewEls.push({ data: {
                                id: `cluster-${selectedNode.id}-${addr}`,
                                source: selectedNode.id,
                                target: addr,
                                type: 'cluster-link',
                            }});
                            newNodeCount++;
                        }
                    });
                });
                // Add new elements to graph
                if (allNewEls.length > 0) {
                    setElements(prev => {
                        const merged = [...prev];
                        const existingIds = new Set(prev.map(e => e.data.id));
                        const allNodeIds = new Set(prev.filter(e => !e.data.source).map(e => e.data.id));
                        for (const el of allNewEls) {
                            if (el.data.source) continue;
                            if (!existingIds.has(el.data.id)) { merged.push(el); existingIds.add(el.data.id); allNodeIds.add(el.data.id); }
                        }
                        for (const el of allNewEls) {
                            if (!el.data.source) continue;
                            if (existingIds.has(el.data.id)) continue;
                            if (allNodeIds.has(el.data.source) && allNodeIds.has(el.data.target)) { merged.push(el); existingIds.add(el.data.id); }
                        }
                        return merged;
                    });
                    // Re-apply cluster colors to newly added nodes after render
                    setTimeout(() => {
                        clusters.forEach((cluster: any, idx: number) => {
                            const color = CLUSTER_COLORS[idx % CLUSTER_COLORS.length];
                            (cluster.addresses || []).forEach((addr: string) => {
                                const node = cy.getElementById(addr);
                                if (node && node.length > 0) {
                                    node.style({ 'border-width': 4, 'border-color': color, 'border-opacity': 0.9 });
                                }
                            });
                        });
                    }, 200);
                }
            }

            const totalAddrs = clusters.reduce((n: number, c: any) => n + (c.address_count || 0), 0);
            toast.success(`Found ${clusters.length} cluster(s) with ${totalAddrs} addresses${newNodeCount > 0 ? ` (${newNodeCount} new added to canvas)` : ''}`);
        } catch (e: any) {
            toast.error(e?.response?.data?.detail || 'Clustering failed');
        } finally {
            setClusterLoading(false);
        }
    }, [selectedNode?.id, chain]);

    // ── Auto-Investigate card callbacks ──
    const lastAddToGraphToastRef = useRef<number>(0);
    const pendingAddToGraphToastRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const handleAddTraceToGraph = useCallback((newElements: any[]) => {
        setElements(prev => {
            const result = addToCanvas(prev, newElements, {
                chain: effectiveChain || undefined,
            });
            return result === prev ? prev : result;
        });
        // Note: previously auto-fit when >5 nodes arrived via SSE.  Removed
        // so the user's viewport is preserved during live discovery.  If
        // nodes land outside the current viewport, user can click Fit.
        // Throttle toast: show at most once per 3s during rapid SSE updates
        const now = Date.now();
        if (now - lastAddToGraphToastRef.current > 3000) {
            lastAddToGraphToastRef.current = now;
            toast.success(`Added trace data to graph`);
        } else if (!pendingAddToGraphToastRef.current) {
            // Schedule a trailing toast so the final batch still gets feedback
            pendingAddToGraphToastRef.current = setTimeout(() => {
                lastAddToGraphToastRef.current = Date.now();
                pendingAddToGraphToastRef.current = null;
                toast.success(`Added trace data to graph`);
            }, 3000);
        }
    }, []);

    // Update existing element data in React state (survives react-cytoscapejs re-renders)
    const handleEnrichElements = useCallback((updates: Array<{ id: string; data: Record<string, any> }>) => {
        setElements(prev => {
            let changed = false;
            const next = prev.map(el => {
                const upd = updates.find(u => u.id === el.data.id);
                if (!upd) return el;
                changed = true;
                return { ...el, data: { ...el.data, ...upd.data } };
            });
            return changed ? next : prev;
        });
    }, []);

    const handleInvestigateNotice = useCallback((exchangeName?: string) => {
        setNoticeExchangeOverride(exchangeName?.trim() || null);
        setNoticeModalOpen(true);
    }, [setNoticeModalOpen]);

    const handleApplyFilterPreset = useCallback((partial: Partial<GraphFilters>) => {
        setGraphFilters({ ...useFilterStore.getState().graphFilters, ...partial });
        setShowFilterBar(true);
    }, []);

    // Case wallets list: select the wallet on the canvas, or add it from the
    // case-graph payload the list already holds. No request is made here.
    const focusCanvasNode = (nodeId: string, nodeData: any) => {
        const cy = graphRef.current?.getCy();
        if (cy && !cy.destroyed()) {
            const node = cy.getElementById(nodeId);
            if (node.length) {
                cy.elements().unselect();
                node.select();
                cy.animate({ center: { eles: node }, duration: 300 });
            }
        }
        void handleNodeClick(nodeData);
    };
    const handleSelectCaseWallet = async (row: CaseWalletRow) => {
        if (!caseIdForLoad) return;
        const current = useGraphStore.getState().elements;
        const plan = planCaseWalletSelect(current, row, caseIdForLoad);
        if (plan.kind === 'select') {
            const existing = current.find((el) => !el.data?.source && el.data?.id === plan.nodeId);
            focusCanvasNode(plan.nodeId, existing?.data || { id: plan.nodeId });
            return;
        }
        await addNodesToCanvas([plan.element as CytoscapeElement], {
            chain: row.chain,
            userAction: true,
            skipEnrichment: true,
            skipConnectionCheck: true,
        });
        // GraphCanvas mounts the node after the store commit and layout.
        setTimeout(() => focusCanvasNode(plan.element.data.id, plan.element.data), 350);
    };

    const handleInvestigateWatchlist = useCallback(async () => {
        if (!address) return;
        try {
            await addToWatchlistForCase(
                address,
                chain || detectChain(address),
                address,
                caseIdForLoad,
            );
            toast.success('Added to watchlist');
        } catch (e: any) {
            toast.error(e?.response?.data?.detail || 'Failed to add to watchlist');
        }
    }, [address, chain, caseIdForLoad]);

    const handlePersistAutoInvestigateReport = useCallback(async (payload: {
        title: string;
        htmlContent: string;
        metadata?: Record<string, any>;
    }) => {
        const urlParams = new URL(window.location.href).searchParams;
        let activeCaseId = urlParams.get('case_id');
        let activeInvestigationId = urlParams.get('investigation_id');

        if (!activeCaseId || !activeInvestigationId) {
            const saved = await handleSaveState({
                defaultName: `Auto-Investigation Snapshot ${new Date().toLocaleString()}`,
                notes: 'Auto-saved before LE report generation',
                skipPrompt: true,
                resumeAfterModal: true,
            });
            if (!saved) {
                // Report still opens — just can't auto-archive it without a case context
                toast.info('Report opened. Open from a Case to auto-archive it.');
                return;
            }
            activeCaseId = saved.caseId;
            activeInvestigationId = saved.investigationId;
        }

        // Retry up to 2 times — Railway can intermittently drop large POST bodies
        for (let attempt = 1; attempt <= 2; attempt++) {
            try {
                const created = await investigationService.createReportArtifact(activeCaseId, activeInvestigationId, {
                    title: payload.title,
                    html_content: payload.htmlContent,
                    report_type: REPORT_TYPES.autoInvestigate,
                    report_format: 'html',
                    metadata: payload.metadata,
                });
                // Update the local artifacts list so the "View saved report"
                // link surfaces immediately without waiting for a reload.
                if (created && (created as any).id) {
                    setSavedReportArtifacts(prev => [{
                        id: (created as any).id,
                        title: payload.title,
                        report_type: REPORT_TYPES.autoInvestigate,
                        metadata: payload.metadata,
                        created_at: (created as any).created_at || new Date().toISOString(),
                    }, ...prev]);
                }
                const silent = Boolean(payload.metadata?.silent_toast);
                if (!silent) {
                    toast.success('Targeted Node Intelligence report saved to the case file (Analysis tab).');
                } else {
                    toast.success('Investigation report saved to case file.', { duration: 3500 });
                }
                return; // success
            } catch (e) {
                if (attempt < 2) {
                    console.warn(`Report persist attempt ${attempt} failed, retrying...`, e);
                    await new Promise(r => setTimeout(r, 1500));
                } else {
                    console.error('Failed to persist LE report artifact after retries', e);
                    toast.warning('Could not archive report to case file. Retry via View LE Report or Case page.');
                }
            }
        }
    }, [handleSaveState]);

    // Find the latest archived auto_investigate_html report artifact for a
    // given address.  Used by AutoInvestigateCard to surface a "View saved
    // report" link when the user navigates to a node that already has one.
    const savedAutoInvestigateReportFor = useCallback((address: string | null) => {
        if (!address) return null;
        const lower = address.toLowerCase();
        const matches = savedReportArtifacts.filter(a =>
            a.report_type === REPORT_TYPES.autoInvestigate &&
            (a.metadata?.address || '').toLowerCase() === lower
        );
        if (matches.length === 0) return null;
        // Sort by created_at desc — most recent wins
        matches.sort((a, b) => {
            const ad = a.created_at ? Date.parse(a.created_at) : 0;
            const bd = b.created_at ? Date.parse(b.created_at) : 0;
            return bd - ad;
        });
        const latest = matches[0];
        return {
            id: String(latest.id),
            title: latest.title,
            generatedAt: latest.created_at || null,
        };
    }, [savedReportArtifacts]);

    // Open a previously-archived report artifact in a new tab.  Fetches the
    // full HTML body via the artifact-detail endpoint (the list endpoint
    // returns metadata only).
    const handleOpenSavedAutoInvestigateReport = useCallback(async (artifactId: string) => {
        const urlParams = new URL(window.location.href).searchParams;
        const cid = urlParams.get('case_id');
        const invId = urlParams.get('investigation_id');
        if (!cid || !invId) {
            toast.error('Cannot open saved report — case context lost.');
            return;
        }
        try {
            const detail = await investigationService.getReportArtifact(cid, invId, artifactId);
            const html = (detail as any)?.html_content || (detail as any)?.html || '';
            if (!html) {
                toast.error('Saved report is empty.');
                return;
            }
            // Reuse the same opener used by freshly-generated reports.
            const { openReportHtmlInNewTab } = await import('../components/workbench/AutoInvestigateReport');
            openReportHtmlInNewTab(html);
        } catch (err) {
            console.error('Failed to open saved report', err);
            toast.error('Could not open saved report. Please try again.');
        }
    }, []);

    // Archive a rendered FTM report HTML under the current case/investigation,
    // same pattern as handlePersistAutoInvestigateReport but tagged as FTM.
    const handlePersistFtmReport = useCallback(async (payload: {
        title: string;
        htmlContent: string;
        metadata?: Record<string, any>;
    }) => {
        const urlParams = new URL(window.location.href).searchParams;
        let activeCaseId = urlParams.get('case_id');
        let activeInvestigationId = urlParams.get('investigation_id');

        if (!activeCaseId || !activeInvestigationId) {
            const saved = await handleSaveState({
                defaultName: `FTM Snapshot ${new Date().toLocaleString()}`,
                notes: 'Auto-saved before FTM report generation',
                skipPrompt: true,
                resumeAfterModal: true,
            });
            if (!saved) {
                toast.info('Report opened. Open from a Case to auto-archive it.');
                return;
            }
            activeCaseId = saved.caseId;
            activeInvestigationId = saved.investigationId;
        }

        for (let attempt = 1; attempt <= 2; attempt++) {
            try {
                const created = await investigationService.createReportArtifact(activeCaseId, activeInvestigationId, {
                    title: payload.title,
                    html_content: payload.htmlContent,
                    report_type: REPORT_TYPES.followTheMoney,
                    report_format: 'html',
                    metadata: payload.metadata,
                });
                if (created && (created as any).id) {
                    setSavedReportArtifacts(prev => [{
                        id: (created as any).id,
                        title: payload.title,
                        report_type: REPORT_TYPES.followTheMoney,
                        metadata: payload.metadata,
                        created_at: (created as any).created_at || new Date().toISOString(),
                    }, ...prev]);
                }
                if (!payload.metadata?.silent_toast) {
                    toast.success('Follow-the-Money report saved to the case file (Analysis tab).');
                } else {
                    toast.success('Follow-the-Money report saved to case file.', { duration: 3500 });
                }
                return;
            } catch (e) {
                if (attempt < 2) {
                    console.warn(`FTM report persist attempt ${attempt} failed, retrying...`, e);
                    await new Promise(r => setTimeout(r, 1500));
                } else {
                    console.error('Failed to persist FTM report artifact after retries', e);
                    toast.warning('Could not archive FTM report to case file. Retry via View Report or Case page.');
                }
            }
        }
    }, [handleSaveState]);
    persistFtmReportRef.current = handlePersistFtmReport;

    // Archive a printable AI Graph Summary under the current case/investigation.
    const handlePersistAiGraphSummaryReport = useCallback(async (payload: {
        title: string;
        htmlContent: string;
        metadata?: Record<string, any>;
    }) => {
        const urlParams = new URL(window.location.href).searchParams;
        let activeCaseId = urlParams.get('case_id');
        let activeInvestigationId = urlParams.get('investigation_id');

        if (!activeCaseId || !activeInvestigationId) {
            const saved = await handleSaveState({
                defaultName: `AI Summary Snapshot ${new Date().toLocaleString()}`,
                notes: 'Auto-saved before AI Graph Summary archive',
                skipPrompt: true,
                resumeAfterModal: true,
            });
            if (!saved) {
                toast.info('AI Summary ready. Open from a Case to auto-archive it.');
                return;
            }
            activeCaseId = saved.caseId;
            activeInvestigationId = saved.investigationId;
        }

        for (let attempt = 1; attempt <= 2; attempt++) {
            try {
                const created = await investigationService.createReportArtifact(activeCaseId, activeInvestigationId, {
                    title: payload.title,
                    html_content: payload.htmlContent,
                    report_type: REPORT_TYPES.aiGraphSummary,
                    report_format: 'html',
                    metadata: payload.metadata,
                });
                if (created && (created as any).id) {
                    setSavedReportArtifacts(prev => [{
                        id: (created as any).id,
                        title: payload.title,
                        report_type: REPORT_TYPES.aiGraphSummary,
                        metadata: payload.metadata,
                        created_at: (created as any).created_at || new Date().toISOString(),
                    }, ...prev]);
                }
                if (!payload.metadata?.silent_toast) {
                    toast.success('AI Graph Summary saved to the case file (Analysis tab).');
                } else {
                    toast.success('AI Graph Summary saved to case file.', { duration: 3500 });
                }
                return;
            } catch (e) {
                if (attempt < 2) {
                    console.warn(`AI Summary report persist attempt ${attempt} failed, retrying...`, e);
                    await new Promise(r => setTimeout(r, 1500));
                } else {
                    console.error('Failed to persist AI Graph Summary after retries', e);
                    toast.warning('Could not archive AI Graph Summary to case file.');
                }
            }
        }
    }, [handleSaveState]);
    persistAiSummaryReportRef.current = handlePersistAiGraphSummaryReport;

    // Archive a rendered Holistic Trace report under the current case/investigation,
    // same pattern as handlePersistFtmReport but tagged as holistic. Without this,
    // Holistic reports opened in a new tab but were never persisted to the Case
    // module, so other members could not re-open them (ISSUE-02).
    const handlePersistHolisticReport = useCallback(async (payload: {
        title: string;
        htmlContent: string;
        metadata?: Record<string, any>;
    }) => {
        const urlParams = new URL(window.location.href).searchParams;
        let activeCaseId = urlParams.get('case_id');
        let activeInvestigationId = urlParams.get('investigation_id');

        if (!activeCaseId || !activeInvestigationId) {
            const saved = await handleSaveState({
                defaultName: `Holistic Trace Snapshot ${new Date().toLocaleString()}`,
                notes: 'Auto-saved before Holistic Trace report generation',
                skipPrompt: true,
                resumeAfterModal: true,
            });
            if (!saved) {
                toast.info('Report opened. Open from a Case to auto-archive it.');
                return;
            }
            activeCaseId = saved.caseId;
            activeInvestigationId = saved.investigationId;
        }

        for (let attempt = 1; attempt <= 2; attempt++) {
            try {
                const created = await investigationService.createReportArtifact(activeCaseId, activeInvestigationId, {
                    title: payload.title,
                    html_content: payload.htmlContent,
                    report_type: REPORT_TYPES.holistic,
                    report_format: 'html',
                    metadata: payload.metadata,
                });
                if (created && (created as any).id) {
                    setSavedReportArtifacts(prev => [{
                        id: (created as any).id,
                        title: payload.title,
                        report_type: REPORT_TYPES.holistic,
                        metadata: payload.metadata,
                        created_at: (created as any).created_at || new Date().toISOString(),
                    }, ...prev]);
                }
                if (!payload.metadata?.silent_toast) {
                    toast.success('Holistic Trace report saved to the case file (Analysis tab).');
                } else {
                    toast.success('Holistic Trace report saved to case file.', { duration: 3500 });
                }
                return;
            } catch (e) {
                if (attempt < 2) {
                    console.warn(`Holistic report persist attempt ${attempt} failed, retrying...`, e);
                    await new Promise(r => setTimeout(r, 1500));
                } else {
                    console.error('Failed to persist Holistic report artifact after retries', e);
                    toast.warning('Could not archive Holistic report to case file. Retry via View LE Report or Case page.');
                }
            }
        }
    }, [handleSaveState]);

    // ── Case-file auto-archive (Analysis tab evidence tray) ─────────────────
    // Lives on Workbench (not card UI) so completion persists even when the
    // Analysis accordion is collapsed / card unmounted. Hydrate does not re-run.
    const leAutoArchivedKeyRef = useRef<string | null>(null);
    const leRanThisSessionRef = useRef(false);
    const holisticAutoArchivedKeyRef = useRef<string | null>(null);
    const holisticSawRunningRef = useRef(false);

    useEffect(() => {
        if (autoInvestigation.isRunning) leRanThisSessionRef.current = true;
        if (!autoInvestigation.isRunning && !autoInvestigation.isComplete) {
            leRanThisSessionRef.current = false;
            leAutoArchivedKeyRef.current = null;
        }
    }, [autoInvestigation.isRunning, autoInvestigation.isComplete]);

    useEffect(() => {
        if (!autoInvestigation.isComplete || autoInvestigation.isRunning) return;
        if (!leRanThisSessionRef.current) return;
        const targetAddress = autoInvestigation.investigatedAddress;
        if (!targetAddress) return;
        const sections = autoInvestigation.sections;
        if (sections?.signal_assessment?.status === 'loading') return;

        const loaded = Object.entries(sections || {})
            .filter(([, s]) => (s as any)?.status === 'loaded')
            .map(([k]) => k)
            .join(',');
        const runKey = `${targetAddress}|${loaded}|${sections?.signal_assessment?.status || ''}`;
        if (leAutoArchivedKeyRef.current === runKey) return;
        leAutoArchivedKeyRef.current = runKey;

        let cancelled = false;
        (async () => {
            try {
                const { generateInvestigationReportHtml, generateOnePageSummaryHtml } =
                    await import('../components/workbench/AutoInvestigateReport');
                const CRITICAL = new Set([
                    'velocity', 'counterparty_triage', 'backward_trace', 'signal_assessment',
                ]);
                const errored = Object.entries(sections || {})
                    .filter(([, s]) => (s as any)?.status === 'error')
                    .map(([k]) => k);
                const degraded = Object.entries(sections || {})
                    .filter(([, s]) => (s as any)?.health === 'degraded')
                    .map(([k]) => k);
                const limited = errored.some(k => CRITICAL.has(k));
                const targetChain =
                    effectiveChain
                    || (targetAddress ? detectChain(targetAddress) : null);
                const triage = useTriageStore.getState().triageData;
                const dataMode = demoDataMode;
                const entityCount = sections?.exchange_detection?.status === 'loaded'
                    ? sections.exchange_detection.data?.entity_count
                    : undefined;
                const html = limited
                    ? generateOnePageSummaryHtml(targetChain, targetAddress, sections as any, {
                        ...(triage || {}),
                        summary: {
                            ...((triage as any)?.summary || {}),
                            risk_level: `${(triage as any)?.summary?.risk_level || 'UNKNOWN'} (DEGRADED EVIDENCE)`,
                        },
                    })
                    : generateInvestigationReportHtml(
                        targetChain,
                        targetAddress,
                        sections as any,
                        triage,
                        dataMode,
                        entityCount,
                        autoInvestigation.circulation,
                    );
                if (cancelled) return;
                await handlePersistAutoInvestigateReport({
                    title: buildReportTitle(REPORT_TYPES.autoInvestigate, {
                        chain: targetChain,
                        address: targetAddress,
                    }),
                    htmlContent: html,
                    metadata: {
                        chain: targetChain,
                        address: targetAddress,
                        generated_at: new Date().toISOString(),
                        data_mode: dataMode,
                        report_mode: limited ? 'limited' : 'full',
                        degraded_sections: degraded,
                        error_sections: errored,
                        sections_loaded: Object.entries(sections || {})
                            .filter(([, s]) => (s as any)?.status === 'loaded')
                            .map(([k]) => k),
                        archive_source: 'auto_complete',
                        silent_toast: true,
                    },
                });
                if (!cancelled) {
                    useGraphStore.getState().markNodeReported(targetAddress, 'auto_investigate');
                }
            } catch (e) {
                console.error('Workbench auto-archive LE report failed', e);
                if (!cancelled) leAutoArchivedKeyRef.current = null;
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional narrow complete-trigger set
    }, [
        autoInvestigation.isComplete,
        autoInvestigation.isRunning,
        autoInvestigation.investigatedAddress,
        autoInvestigation.sections,
        autoInvestigation.circulation,
        handlePersistAutoInvestigateReport,
        effectiveChain,
        activeDemoId,
        demoDataMode,
    ]);

    const holisticStatus = useHolisticStore(s => s.status);
    const holisticChain = useHolisticStore(s => s.chain);
    const holisticAddresses = useHolisticStore(s => s.addresses);
    const holisticResults = useHolisticStore(s => s.results);

    useEffect(() => {
        if (holisticStatus === 'running') holisticSawRunningRef.current = true;
        if (holisticStatus === 'idle' || holisticStatus === 'error') {
            holisticSawRunningRef.current = false;
            holisticAutoArchivedKeyRef.current = null;
        }
    }, [holisticStatus]);

    useEffect(() => {
        if (holisticStatus !== 'complete' || !holisticAddresses?.length) return;
        if (!holisticSawRunningRef.current) return;
        const key = `${holisticChain || ''}|${holisticAddresses.join(',')}|${holisticResults?.cross_link_discovery?.cross_links_found ?? 0}`;
        if (holisticAutoArchivedKeyRef.current === key) return;
        holisticAutoArchivedKeyRef.current = key;

        let cancelled = false;
        (async () => {
            try {
                const { generateHolisticTraceReportHtml } =
                    await import('../components/workbench/HolisticTraceReport');
                const elements = useGraphStore.getState().elements || [];
                const labels: Record<string, string> = {};
                for (const addr of holisticAddresses) {
                    const el = elements.find((e: any) => !e.data?.source && e.data?.id === addr);
                    const name = el?.data?.entity_name || el?.data?.label;
                    if (name && typeof name === 'string') labels[addr] = name;
                }
                let screenshot: string | undefined;
                try {
                    const cy = graphRef.current?.getCy?.() || (window as any).__cy;
                    if (cy?.png) screenshot = cy.png({ output: 'base64uri', full: true, scale: 1.5, bg: '#ffffff' });
                } catch { /* optional snapshot */ }
                const html = generateHolisticTraceReportHtml(
                    holisticChain,
                    holisticAddresses,
                    holisticResults,
                    undefined,
                    labels,
                    screenshot,
                );
                if (cancelled) return;
                await handlePersistHolisticReport({
                    title: buildReportTitle(REPORT_TYPES.holistic, {
                        chain: holisticChain,
                        addresses: holisticAddresses,
                    }),
                    htmlContent: html,
                    metadata: {
                        report_kind: 'holistic',
                        chain: holisticChain,
                        addresses: holisticAddresses,
                        address: holisticAddresses[0],
                        archive_source: 'auto_complete',
                        silent_toast: true,
                    },
                });
                if (!cancelled) {
                    holisticAddresses.forEach(a => useGraphStore.getState().markNodeReported(a, 'holistic'));
                }
            } catch (e) {
                console.error('Workbench auto-archive Holistic report failed', e);
                if (!cancelled) holisticAutoArchivedKeyRef.current = null;
            }
        })();
        return () => { cancelled = true; };
    }, [
        holisticStatus,
        holisticChain,
        holisticAddresses,
        holisticResults,
        handlePersistHolisticReport,
    ]);

    // Open the FTM report modal using the node's cached FTM result.
    // The report component builds the HTML, then we archive it via
    // handlePersistFtmReport so it shows up alongside Auto-Investigate
    // reports in the case's Artifacts list.
    const handleOpenFollowMoneyReport = useCallback(() => {
        const activeChain = selectedNode?.chain
            ? (normalizeChain(selectedNode.chain) || selectedNode.chain)
            : effectiveChain;
        const activeAddress = selectedNode?.id || effectiveAddress;
        if (!activeChain || !activeAddress) {
            toast.info('Select a node to view its Follow-the-Money report.');
            return;
        }
        const entry = useFtmStore.getState().getEntry(activeChain, activeAddress);
        if (!entry) {
            toast.info('Run Follow the Money on this node first.');
            return;
        }
        import('../components/workbench/FollowTheMoneyReport').then(mod => {
            const allTxs: any[] = [];
            elements.forEach(el => {
                if (el.data?.expanded_transactions) {
                    allTxs.push(...el.data.expanded_transactions);
                }
            });
            const cy = graphRef.current?.getCy?.();
            const screenshot = cy ? cy.png({ output: 'base64uri', full: true, scale: 1.5 }) : undefined;
            const openFn: any = mod.openFollowTheMoneyReport;
            // Some versions of the report accept an onPersist callback; pass it
            // when supported so the archived HTML matches exactly what the user sees.
            openFn(
                entry.chain,
                entry.address,
                { ...entry.response, direction: 'forward' },
                screenshot,
                allTxs.length > 0 ? allTxs : undefined,
                {
                    onPersist: (html: string) => {
                        handlePersistFtmReport({
                            title: buildReportTitle(REPORT_TYPES.followTheMoney, {
                                chain: entry.chain,
                                address: entry.address,
                            }),
                            htmlContent: html,
                            metadata: {
                                chain: entry.chain,
                                address: entry.address,
                                ran_at: entry.ranAt,
                                paths_count: (entry.response?.paths || entry.response?.traces || []).length,
                            },
                        }).then(() => {
                            useFtmStore.getState().markArchived(entry.chain, entry.address);
                        });
                    },
                },
            );
            // C5/F4: mark the node as reported (Follow-the-Money).
            useGraphStore.getState().markNodeReported(activeAddress, 'follow_the_money');
        });
    }, [selectedNode, effectiveAddress, effectiveChain, elements, handlePersistFtmReport]);

    // Track whether FTM is actively running for the currently-selected node.
    // We don't have a dedicated status field yet, so reuse the existing
    // `loading` flag gated by node identity.
    const isFollowMoneyRunningForSelected = loading && !!selectedNode;

    // Step 5 (CANVAS_PLACEMENT_LANGUAGE §3.6) — detect whether the subject
    // is islanded (on canvas but with zero connecting edges). If so, the
    // banner below renders "no on-chain connections found" copy and counts
    // any other connected components for the footer link.
    const subjectIsland = useMemo(() =>
        detectSubjectIsland(elements, [effectiveAddress, investigationRootId]),
        [elements, effectiveAddress, investigationRootId]
    );
    // User can dismiss the banner. We track dismissed subject ids in a Set
    // — the banner re-appears on a different investigation but stays
    // dismissed within the same one. Set form rather than bare boolean +
    // reset-on-subject-change useEffect avoids the React render-ordering
    // race where the reset would un-dismiss on the same render.
    const [dismissedIslandIds, setDismissedIslandIds] = useState<Set<string>>(() => new Set());
    const islandBannerDismissed =
        !!subjectIsland.subjectId && dismissedIslandIds.has(subjectIsland.subjectId);

    const styledElements = useMemo(() => {
        // Phase 2: Prune excessive leaves before rendering (Max 50 counterparties per node direction)
        const prunedElements = pruneExcessiveLeaves(elements);
        
        if (!activeOverlay) return prunedElements;
        
        return prunedElements.map((el: any) => {
            if (!el.data || !el.data.id) return el;
            const color = getNodeColor(el.data.id);
            const sizeMult = getNodeSize(el.data.id);
            if (!color && sizeMult === 1) return el;
            return {
                ...el,
                data: {
                    ...el.data,
                    overlayColor: color || undefined,
                    overlayWidth: color ? 20 * sizeMult : undefined,
                }
            };
        });
    }, [elements, activeOverlay, getNodeColor, getNodeSize]);

    // Shared "blow away the workbench back to a fresh state" routine. Used by
    // both "New Investigation" and "Clear Canvas" — the two buttons have
    // different confirmation UX and telemetry around them, but the actual
    // wipe is identical.
    //
    // Anything that represents per-investigation state belongs here:
    //   - global stores (graph / stash / transactions / triage / filters /
    //     panel / ftm / bridges / layout / investigation / selection)
    //   - workbench-local state (edgeTxMap, trace panel, tx date range,
    //     UPI links, deep check result, context menus, tooltips, playback)
    //
    // Stores NOT cleared: theme, sidebar collapse, demo mode preference —
    // these are UI preferences, not investigation state.
    // Canvas edge click (also used by Find-in-graph for an edge match).
    // Synthetic link edges have no real transactions — ignore.
    // Every other edge, consolidated, gold-path or plain, selects
    // its own endpoint pair so the Transfers tab never keeps an
    // unrelated list after an edge click (package 8).
    const handleCanvasEdgeClick = (edge: any) => {
        const sel = resolveEdgeClickSelection(edge);
        if (!sel) return;
        setSelectedEdgePair(sel.pair);
        if (sel.clearEdgeIds) setSelectedEdgeIds([]);
        setPanelMode('details');
        setActiveTab('transfers');
    };
    const getCanvasCy = useCallback(() => graphRef.current?.getCy() ?? null, []);
    const handleFindSelectNode = useCallback((_nodeId: string, data: any) => {
        void handleNodeClick(data);
    }, [handleNodeClick]);

    const resetWorkbenchToFresh = useCallback((options: { preserveGraphHistory?: boolean } = {}) => {
        // Global stores ─────────────────────────────────────────────
        beginControlledWrite();
        try { useGraphStore.getState().setElements([]); } finally { endControlledWrite(); }
        if (!options.preserveGraphHistory) {
            useGraphStore.getState().clearHistory();
        }
        useStashStore.getState().clearAll();
        useSelectionStore.getState().clearSelection();
        useTransactionStore.getState().clearAll();
        useTriageStore.getState().clearTriage();
        useFilterStore.getState().resetFilters();
        usePanelStore.getState().clearFindings();
        usePanelStore.getState().resetOnNodeChange();
        // The graph summary describes a canvas that no longer exists.
        usePanelStore.getState().setGraphSummaryOpen(false);
        usePanelStore.getState().setGraphSummaryMeta(null);
        usePanelStore.getState().setInlineAiSummary('');
        setMacroAiResults({});
        useFtmStore.getState().clearAll();
        useInvestigationCacheStore.getState().clearAll();
        useBridgeInsightStore.getState().clearAll();
        usePendingLayoutStore.getState().clear();
        useInvestigationStore.getState().setInvestigationRootId(null);
        useInvestigationStore.getState().setShowAutoInvestigateCard(false);
        autoInvestigation.reset();

        // Workbench-local state ─────────────────────────────────────
        setEdgeTxMap(new Map());
        clearDraft();
        setSessionChain(null);
        setSessionAddress(null);
        setTraceInput('');
        useWorkbenchSessionStore.getState().setSession(null, null);
        useWorkbenchSessionStore.getState().setSelectedNodeId(null);
        useWorkbenchSessionStore.getState().setOmnibarText('');
        clearCanvasView();
        setCaseName(null);
        setInvestigationName(null);
        setLastTrace(null);
        setShowTracePanel(false);
        setAddedTxHashes([]);
        setTxStartDate('');
        setTxEndDate('');
        setDateRangeAllLoaded(false);
        setPrivacyChainWarning(null);
        setDeepCheckResult(null);
        setDeepCheckLoading(false);
        setContextMenuNode(null);
        setContextMenuEdge(null);
        setHoverNodeData(null);
        setHoverNodeBBox(null);
        setSelectedTxForNotice(null);
        setLinkedUPIs([]);
        setUpiLinkInput('');
        setEdgeFocusActive(false);
        setIsAnimating(false);
        setShowPlayback(false);
        setAnimationStep(0);
        setIsCheckingConnections(false);
        // Reset Links memo so a fresh canvas re-checks every pair instead of
        // inheriting stale "no-path" verdicts from the previous session.
        const prevMemo = linksMemoMetaRef.current;
        if (prevMemo.investigationId) {
            clearLinksMemoForInvestigation(prevMemo.investigationId);
        }
        linksAttemptsRef.current.clear();
        linksMemoMetaRef.current = { investigationId: '', version: '' };

        // URL + backend ─────────────────────────────────────────────
        setSearchParams({}, { replace: true });
        adminService.clearSession().catch(() => { /* best-effort */ });
    }, [
        autoInvestigation, clearDraft, setEdgeTxMap, setSearchParams,
    ]);

    const handleNewInvestigation = useCallback(() => {
        if (hasUnsavedWork && !window.confirm('You have unsaved changes. Start a new investigation?')) return;
        isDirtyRef.current = false; // Prevent blocker race on URL clear
        useGraphStore.getState().pushSnapshot();
        resetWorkbenchToFresh();
        toast.success('Canvas cleared — ready for new investigation');
    }, [hasUnsavedWork, resetWorkbenchToFresh]);

    // Keep shortcut action refs current without re-binding window listeners.
    workbenchShortcutActionsRef.current = {
        save: () => {
            const els = useGraphStore.getState().elements;
            if (els.length === 0) {
                toast.info('Nothing to save — add an address to the canvas first');
                return;
            }
            const params = new URLSearchParams(window.location.search);
            const isSaved = params.has('investigation_id') && params.has('case_id');
            const hasUnsaved = !isSaved || isDirtyRef.current;
            if (!hasUnsaved) {
                toast.info('No unsaved changes');
                return;
            }
            void handleSaveState();
        },
        newInvestigation: handleNewInvestigation,
        trace: () => {
            const sel = useSelectionStore.getState().selectedNode;
            const params = new URLSearchParams(window.location.search);
            const urlChain = params.get('chain');
            const urlAddress = params.get('address');
            const hasTarget = !!(sel?.id || (sessionChain && sessionAddress) || (urlChain && urlAddress));
            if (!hasTarget) {
                toast.info('Select an address on the canvas (or start a session) to Trace');
                return;
            }
            void handleAutoTrace();
        },
        links: () => {
            void handleCheckConnections();
        },
        toggleFilters: () => {
            const els = useGraphStore.getState().elements;
            if (els.length === 0) {
                toast.info('Add addresses to the canvas before filtering');
                return;
            }
            const store = useFilterStore.getState();
            store.setShowFilterBar(!store.showFilterBar);
        },
    };

    // File / Tools shortcuts: Ctrl/Cmd+S save, Ctrl/Cmd+Alt+N new, T/L/F bare keys.
    // Ctrl/Cmd+N is browser-reserved ("new window") and never reliably reaches
    // the page, so New Investigation uses the browser-safe Alt chord.
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const action = getWorkbenchShortcutAction(e);
            if (!action) return;
            e.preventDefault();
            workbenchShortcutActionsRef.current[action]();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    return (
        // Fill Layout <main className="… relative"> so GraphCanvas (absolute inset-0)
        // has a non-zero box. Pair with main's min-h-0 overflow-hidden.
        <div className="absolute inset-0 flex flex-col min-h-0 overflow-hidden">
            <div className="shrink-0 relative z-40">
            <WorkbenchHeader
                caseId={caseIdForLoad}
                onOpenFusion={() => openInFusion(navigate, caseIdForLoad, { address: selectedNode?.id || urlAddress })}
                investigationId={investigationId}
                officerName={caseContext?.officerName || null}
                caseName={caseName}
                investigationName={investigationName}
                elementsCount={elements.length}
                traceInput={traceInput}
                onTraceInputChange={setTraceInput}
                sessionChain={chain || sessionChain}
                onPrimaryAction={(_type, detectedChain, isTx) => {
                    if (_type === 'holistic') {
                        const selectedArr = multiSelect.getSelectedArray();
                        const selectedIds = selectedArr.map((n: any) => n.id);
                        const nodeChain = (selectedArr[0] as any)?.chain || chain || 'ETH';
                        useHolisticStore.getState().openModal(nodeChain, selectedIds);
                        return;
                    }

                    const detected = detectInputType(traceInput);
                    const val = (detected.query || traceInput).trim();
                    const activeChain = resolveOmnibarChainHint(detected, detectedChain, chain);
                    // Validate address format before submitting
                    if (_type === 'unknown') {
                        // Detect near-miss patterns and give specific guidance
                        if (/^0x[a-fA-F0-9]+$/i.test(val) && val.length !== 42) {
                            toast.error(`Invalid EVM address: ${val.length} chars (expected 42). Check for missing or extra characters.`);
                        } else if (/^T[A-Za-z0-9]+$/.test(val) && val.length !== 34) {
                            toast.error(`Invalid TRON address: ${val.length} chars (expected 34). Check for missing or extra characters.`);
                        } else if ((/^(1|3)[a-km-zA-HJ-NP-Z1-9]+$/.test(val) || /^bc1/i.test(val)) && (val.length < 26 || val.length > 62)) {
                            toast.error(`Invalid BTC address length: ${val.length} chars. Legacy: 25-34, Bech32: 42-62.`);
                        } else {
                            toast.error('Unrecognized address or transaction format. Please check the input.');
                        }
                        return;
                    }
                    if (isTx) {
                        handleTraceTx(val, activeChain);
                    } else {
                        handleTraceAddress(val, activeChain);
                    }
                }}
                // Show node-level action buttons whenever there's a selected node OR a session.
                // Forensic overlay loads from ?case_id=X without setting effectiveChain/Address,
                // so gating purely on session was hiding the buttons in that flow.
                hasSession={!!(effectiveChain && effectiveAddress) || !!selectedNode}
                hasElements={elements.length > 0}
                showHelp={showHelp}
                showLegend={showLegend}
                onAutoTrace={handleAutoTrace}
                onToggleAnalyze={() => {
                    // Always land on the Analysis tab → Macro Graph Analysis (same as G).
                    usePanelStore.getState().setAnalysisCardFocus('analyze-graph');
                    useSelectionStore.getState().setNodeDetailMinimized(false);
                    if (selectedNode) {
                        setActiveTab('analysis');
                    } else {
                        const firstNode = elements.find(el => !el.data?.source);
                        if (firstNode?.data) {
                            setSelectedNode(firstNode.data as any);
                            setTimeout(() => setActiveTab('analysis'), 0);
                        } else {
                            toast.info('Add an address to the canvas first');
                        }
                    }
                }}
                onToggleAutoInvestigate={() => {
                    // Always land on the Analysis tab → Targeted Node Intelligence (same as I).
                    usePanelStore.getState().setAnalysisCardFocus('auto-investigate');
                    useSelectionStore.getState().setNodeDetailMinimized(false);
                    if (selectedNode) {
                        setActiveTab('analysis');
                    } else {
                        const firstNode = elements.find(el => !el.data?.source);
                        if (firstNode?.data) {
                            setSelectedNode(firstNode.data as any);
                            setTimeout(() => setActiveTab('analysis'), 0);
                        } else {
                            toast.info('Add an address to the canvas first');
                        }
                    }
                }}
                onToggleLegend={() => setShowLegend(!showLegend)}
                onSaveState={() => { void handleSaveState(); }}
                onExportPng={() => graphRef.current?.exportPng()}
                onExportCsv={exportToCSV}
                onExportExcel={exportToExcel}
                onToggleHelp={() => setShowHelp(!showHelp)}
                onToggleFilter={() => useFilterStore.getState().setShowFilterBar(!useFilterStore.getState().showFilterBar)}
                showFilter={showFilterBar}
                hasUnsavedChanges={hasUnsavedWork}
                canvasNodeCount={nodeCount}
                onImportCsv={() => setShowCsvImport(true)}
                onCheckConnections={handleCheckConnections}
                isCheckingConnections={isCheckingConnections}
                selectedCount={multiSelect.count}
                onNewInvestigation={handleNewInvestigation}
                onClearCanvas={() => {
                    if (elements.length === 0) return;
                    if (!window.confirm(`Clear the canvas? This removes all ${nodeCount} nodes. The case, investigation and search input are kept; use New Investigation to start over.`)) return;
                    recordUserAction('clear_canvas', '', 'workbench_header', `nodes=${nodeCount}`);
                    logStoreReset('Clear Canvas', { nodeCount, edgeCount: elements.filter(e => e.data?.source).length });
                    // Sheet #43: Clear Canvas resets the GRAPH only (elements,
                    // selection, filters, stash) and keeps the case id, URL,
                    // session subject and Omnibar text. New Investigation is
                    // the full reset (resetWorkbenchToFresh).
                    useGraphStore.getState().pushSnapshot();
                    clearWorkbenchCanvasOnly();
                    useTriageStore.getState().clearTriage();
                    usePanelStore.getState().resetOnNodeChange();
                    usePanelStore.getState().setGraphSummaryOpen(false);
                    usePanelStore.getState().setGraphSummaryMeta(null);
                    useFtmStore.getState().clearAll();
                    if (followMoneyMode) setFollowMoneyMode(false);
                    setEdgeTxMap(new Map());
                    setContextMenuNode(null);
                    setContextMenuEdge(null);
                    setHoverNodeData(null);
                    setHoverNodeBBox(null);
                    setEdgeFocusActive(false);
                    setSelectedEdgeIds([]);
                    setSelectedEdgePair(null);
                    toast.success('Canvas cleared — case kept; Undo restores the canvas');
                }}
                onShowAllNodes={() => {
                    const cy = graphRef.current?.getCy() || (window as any).__cy;
                    if (!cy) return;
                    cy.elements().forEach((ele: any) => {
                        ele.data('_visibility', 1.0);
                    });
                    if (followMoneyMode) {
                        setFollowMoneyMode(false);
                        graphRef.current?.toggleFollowMoney(false);
                    }
                    useFilterStore.getState().resetFilters();
                    toast.success('All nodes visible');
                }}
                showDemoGuideButton={!!activeDemoId && !!demoGraphs[activeDemoId]}
                demoGuideOpen={demoGuideVisible}
                onToggleDemoGuide={() => setDemoGuideVisible(v => !v)}
                dataMode={demoDataMode}
                onDataModeChange={handleWorkbenchDataModeChange}
            />
            </div>

            {/* TriageRibbon retired — its chips are now rendered inside the
                Node Inspector identity card via NodeIdentityChips, reclaiming
                ~52px of canvas vertical space and eliminating the canvas-reflow
                flicker on every node click. See VISUAL_LANGUAGE_V2.md Step 1c. */}

            {/* Graph filters render in WorkbenchHeader popover adjacent to Analyze */}

            {/* R-AI-03: Full-width escalation banner post-investigation */}
            {(() => {
                const sigData = autoInvestigation.sections.signal_assessment;
                const esc = sigData?.status === 'loaded' ? sigData.data?.escalation_recommendation : null;
                const verdict = sigData?.status === 'loaded' ? sigData.data?.verdict_summary : null;
                const exchangeName = autoInvestigation.sections.exchange_detection?.status === 'loaded'
                    ? autoInvestigation.sections.exchange_detection.data?.exchange_name || autoInvestigation.sections.exchange_detection.data?.entity_name : null;
                if (!esc) return null;
                return (
                    <div className={`w-full px-4 py-2.5 flex items-center gap-3 border-b ${
                        esc === 'ESCALATE' ? 'bg-red-500/10 border-red-500/30' :
                        esc === 'INVESTIGATE' ? 'bg-amber-500/10 border-amber-500/30' :
                        esc === 'MONITOR' ? 'bg-blue-500/10 border-blue-500/30' :
                        'bg-emerald-500/10 border-emerald-500/30'
                    }`}>
                        <span className={`px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wider ${
                            esc === 'ESCALATE' ? 'bg-red-600 text-foreground' :
                            esc === 'INVESTIGATE' ? 'bg-amber-500 text-foreground' :
                            esc === 'MONITOR' ? 'bg-blue-500 text-foreground' :
                            'bg-emerald-500 text-foreground'
                        }`}>{esc}</span>
                        <span className="text-xs text-foreground flex-1 truncate">
                            {verdict || (
                                esc === 'ESCALATE' ? 'Multiple risk signals converged. Escalate to Cyber Cell / ED for immediate action.' :
                                esc === 'INVESTIGATE' ? 'Suspicious indicators found. Conduct detailed investigation before taking action.' :
                                esc === 'MONITOR' ? 'Inconclusive signals. Add to watchlist and monitor for new activity.' :
                                'No significant risk indicators. Standard case processing.'
                            )}
                        </span>
                        {esc === 'ESCALATE' && exchangeName && (
                            <button
                                onClick={() => setNoticeModalOpen(true)}
                                className="px-3 py-1 text-xs font-medium bg-red-600 text-foreground rounded hover:bg-red-700 transition-colors shrink-0"
                            >
                                Send Section 91 to {exchangeName}
                            </button>
                        )}
                        {esc === 'INVESTIGATE' && (
                            <button
                                onClick={() => { if (selectedNode) setActiveTab('analysis'); else setShowAutoInvestigateCard(true); }}
                                className="px-3 py-1 text-xs font-medium bg-amber-500 text-foreground rounded hover:bg-amber-600 transition-colors shrink-0"
                            >
                                View Investigation
                            </button>
                        )}
                    </div>
                );
            })()}

            {/* Privacy Chain Warning Banner */}
            {privacyChainWarning && (
                <div className="w-full px-4 py-2.5 flex flex-col gap-2 border-b bg-primary/10 border-primary/30">
                    <div className="flex items-start gap-3">
                        <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
                            <span className="px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-brand-ink text-white dark:bg-primary dark:text-primary-foreground">
                                {privacyChainWarning.symbol || privacyChainWarning.chain_id}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider ${
                                privacyChainWarning.tier === 1 ? 'bg-red-600 text-foreground' : 'bg-amber-500 text-foreground'
                            }`}>
                                TIER {privacyChainWarning.tier} PRIVACY
                            </span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-foreground">{privacyChainWarning.title}</p>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{privacyChainWarning.summary}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                onClick={async () => {
                                    // Find the tx hash and timestamp from elements
                                    const txNode = elements.find(el => el.data?.type === 'transaction' && el.data?.tx_hash);
                                    const txHash = txNode?.data?.tx_hash || '';
                                    const timestamp = txNode?.data?.timestamp || '';
                                    if (!txHash || !timestamp) {
                                        toast.error('Missing transaction data for deep checks');
                                        return;
                                    }
                                    setDeepCheckLoading(true);
                                    try {
                                        // Collect canvas addresses for correlation
                                        const canvasAddresses = elements
                                            .filter(el => !el.data?.source && el.data?.id && el.data?.type !== 'transaction')
                                            .map(el => ({ address: el.data.id, chain: el.data.chain || '' }))
                                            .filter(a => a.address);
                                        const result = await investigationAnalysisService.privacyDeepCheck(
                                            txHash,
                                            privacyChainWarning.chain_id || privacyChainWarning.symbol,
                                            String(timestamp),
                                            30,
                                            canvasAddresses,
                                        );
                                        setDeepCheckResult(result);
                                        const hits = result?.total_hits || 0;
                                        if (hits > 0) {
                                            toast.success(`Deep check: ${hits} lead(s) found`, {
                                                description: result.assessment_detail?.substring(0, 100),
                                            });
                                        } else {
                                            toast.info('Deep check: no correlated activity found', {
                                                description: 'Exchange subpoenas based on timestamp remain the primary path.',
                                            });
                                        }
                                    } catch (e: any) {
                                        toast.error(`Deep check failed: ${e.message || 'Unknown error'}`);
                                    } finally {
                                        setDeepCheckLoading(false);
                                    }
                                }}
                                disabled={deepCheckLoading}
                                className="px-2.5 py-1 text-xs font-medium bg-primary text-primary-foreground rounded hover:bg-brand-gold-hover transition-colors disabled:opacity-50"
                            >
                                {deepCheckLoading ? 'Checking...' : 'Deep Checks'}
                            </button>
                            {privacyChainWarning.explorer_urls?.length > 0 && (
                                <a
                                    href={privacyChainWarning.explorer_urls[0]}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-2.5 py-1 text-xs font-medium border border-border text-foreground rounded hover:bg-muted transition-colors"
                                >
                                    Explorer
                                </a>
                            )}
                            <button
                                onClick={() => { setPrivacyChainWarning(null); setDeepCheckResult(null); }}
                                className="text-muted-foreground hover:text-foreground transition-colors text-sm leading-none"
                                title="Dismiss"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                    {/* Deep Check Results */}
                    {deepCheckResult && (
                        <div className="ml-0 pl-3 border-l-2 border-primary/40">
                            <div className="flex items-center gap-2 mb-1">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                                    deepCheckResult.assessment === 'CANVAS_LEAD' ? 'bg-green-500 text-foreground' :
                                    deepCheckResult.assessment === 'EXCHANGE_LEAD' ? 'bg-green-600 text-foreground' :
                                    deepCheckResult.assessment === 'SWAP_LEAD' ? 'bg-primary text-primary-foreground' :
                                    deepCheckResult.assessment === 'P2P_LEAD' ? 'bg-amber-600 text-foreground' :
                                    deepCheckResult.assessment === 'WEAK_LEADS' ? 'bg-gray-600 text-foreground' :
                                    'bg-gray-700 text-foreground'
                                }`}>
                                    {deepCheckResult.assessment?.replace('_', ' ')}
                                </span>
                                <span className="text-[11px] text-muted-foreground">
                                    {deepCheckResult.total_hits} lead{deepCheckResult.total_hits !== 1 ? 's' : ''} found
                                </span>
                            </div>
                            <p className="text-xs text-muted-foreground">{deepCheckResult.assessment_detail}</p>
                            {deepCheckResult.top_leads?.length > 0 && (
                                <div className="mt-1.5 space-y-1">
                                    {deepCheckResult.top_leads.slice(0, 3).map((lead: any, i: number) => (
                                        <div key={i} className="flex items-center gap-2 text-[11px]">
                                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                                                lead.confidence >= 0.7 ? 'bg-green-500' :
                                                lead.confidence >= 0.4 ? 'bg-amber-500' : 'bg-gray-500'
                                            }`} />
                                            <span className="text-foreground truncate">{lead.summary}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="flex-1 min-h-0 relative z-0 bg-canvas overflow-hidden" style={{ display: 'grid', gridTemplateColumns: (selectedNode || multiSelect.isMultiSelect || graphSummaryOpen) ? '1fr auto' : '1fr' }} data-testid="wb-graph-area" data-tour="graph-canvas">
              {/* ── Grid Column 1: Canvas + floating tools ── */}
              <div className="relative min-w-0 min-h-0 h-full overflow-hidden">
                {loading && (
                    <div className="absolute top-0 left-0 right-0 z-50 h-1 bg-primary/20 overflow-hidden">
                        <div className="h-full bg-primary w-1/3 animate-pulse rounded-r" />
                    </div>
                )}
                {accessDenied && (
                    <div
                        className="absolute inset-0 z-40 flex items-center justify-center bg-canvas/90 p-6"
                        data-testid="wb-access-denied"
                        role="alert"
                    >
                        <div className="max-w-md rounded-lg border border-border bg-card p-6 text-center shadow-lg">
                            <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-amber-500" />
                            <h2 className="text-lg font-semibold">Access denied</h2>
                            <p className="mt-2 text-sm text-muted-foreground">
                                You are signed in, but this investigation is not shared with you.
                                The graph was not loaded.
                            </p>
                            <button
                                type="button"
                                className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground"
                                onClick={() => navigate('/cases')}
                            >
                                Back to cases
                            </button>
                        </div>
                    </div>
                )}

                {/* Case wallets: the case spine's wallets when opened from a case.
                    Below the subject-island banner row so its dismiss stays reachable. */}
                {caseIdForLoad && (
                    <div className="absolute top-14 right-2 z-30">
                        <CaseWalletsPanel
                            caseId={caseIdForLoad}
                            canvasElements={elements}
                            onSelectWallet={(row) => { void handleSelectCaseWallet(row); }}
                        />
                    </div>
                )}

                {/* Analyze Card & Overlays */}
                

                {/* Auto-Investigate Card — floating version (hidden when docked) */}
                

                {activeOverlay && legend.length > 0 && (
                    <GraphOverlayLegend
                        activeOverlay={activeOverlay}
                        legend={legend}
                        isComputing={isComputing}
                        onClear={clearOverlay}
                    />
                )}

                {elements.length > 0 ? (
                    <>
                        {subjectIsland.islanded && !islandBannerDismissed && (
                            <div
                                data-testid="wb-subject-island-banner"
                                className="relative z-20 mx-4 mt-2 mb-2 px-4 py-2 rounded-md border border-amber-500/40 bg-amber-500/10 flex items-center justify-between gap-3 text-[12px]"
                                role="status"
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-amber-800 dark:text-amber-300 font-semibold shrink-0">⚠ Subject islanded</span>
                                    <InfoHint
                                        label="What does Subject islanded mean?"
                                        text="The subject wallet is on the canvas but has no displayed transaction edge connecting it to another displayed node. This does not mean the wallet has no blockchain activity—its transactions may not have been loaded, or the other canvas groups may be unrelated. Trace or expand the subject to load its connections."
                                        iconClassName="w-3.5 h-3.5 text-amber-700 dark:text-amber-400"
                                    />
                                    <span className="text-amber-900/80 dark:text-amber-100/90 truncate">
                                        No on-chain connections found in current canvas
                                        {subjectIsland.orphanComponentCount > 0
                                            ? ` — ${subjectIsland.orphanComponentCount} unrelated component${subjectIsland.orphanComponentCount === 1 ? '' : 's'} below`
                                            : ''}
                                        {subjectIsland.totalNodes > 1 ? ` (${subjectIsland.totalNodes} addresses on canvas)` : ''}.
                                    </span>
                                </div>
                                <button
                                    onClick={() => {
                                        if (!subjectIsland.subjectId) return;
                                        setDismissedIslandIds(prev => {
                                            const next = new Set(prev);
                                            next.add(subjectIsland.subjectId!);
                                            return next;
                                        });
                                    }}
                                    className="text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-200 shrink-0 px-1"
                                    aria-label="Dismiss subject-islanded banner"
                                >
                                    ×
                                </button>
                            </div>
                        )}
                        <div data-testid="wb-graph-canvas" className="absolute inset-0">
                            <GraphCanvas
                                ref={graphRef}
                                elements={styledElements}
                                onNodeClick={handleNodeClick}
                                onEdgeClick={handleCanvasEdgeClick}
                                onNodeSelectionChange={(nodeIds) => {
                                    if (nodeIds.length > 1) {
                                        const selectedNodesData = elements
                                            .filter(el => !el.data.source && nodeIds.includes(el.data.id))
                                            .map(el => el.data);
                                        multiSelect.setSelection(selectedNodesData);
                                    } else {
                                        multiSelect.clearSelection();
                                    }
                                }}
                                onEdgeSelectionChange={(edgeIds) => {
                                    setSelectedEdgeIds(edgeIds);
                                    // Sync Ctrl+Click edge selection to Txs tab
                                    if (edgeIds.length > 0) {
                                        const edgeTxs = edgeIds.flatMap(eid => {
                                            const edgeEl = elements.find(el => el.data?.id === eid);
                                            if (!edgeEl) return [];
                                            // E1: Consolidated edge — expand to individual txs from edgeTxMap
                                            if (edgeEl.data.is_consolidated) {
                                                const key = `${edgeEl.data.source}->${edgeEl.data.target}`;
                                                return (edgeTxMap.get(key) || []).map((tx: any) => ({
                                                    tx_hash: tx.tx_hash,
                                                    from_address: tx.from_address,
                                                    to_address: tx.to_address,
                                                    value: tx.value,
                                                    timestamp: tx.timestamp || null,
                                                    token_symbol: tx.token_symbol || null,
                                                    direction: tx.from_address === selectedNode?.id ? 'outgoing' : 'incoming',
                                                    counterparty: tx.from_address === selectedNode?.id ? tx.to_address : tx.from_address,
                                                }));
                                            }
                                            return [{
                                                tx_hash: edgeEl.data.tx_hash || edgeEl.data.id,
                                                from_address: edgeEl.data.source,
                                                to_address: edgeEl.data.target,
                                                value: typeof edgeEl.data.value === 'number' ? edgeEl.data.value : parseFloat(String(edgeEl.data.label || '0').replace(/[^0-9.]/g, '')) || 0,
                                                timestamp: edgeEl.data.timestamp || null,
                                                token_symbol: edgeEl.data.token_symbol || null,
                                                direction: edgeEl.data.direction || null,
                                                counterparty: edgeEl.data.source === selectedNode?.id ? edgeEl.data.target : edgeEl.data.source,
                                            }];
                                        });
                                        setSelectedTxs(edgeTxs as any[]);
                                        if (selectedNode) {
                                            setPanelMode('details');
                                            setActiveTab('transfers');
                                        }
                                    } else {
                                        setSelectedTxs([]);
                                    }
                                }}
                                onEdgeContextMenu={(edge, pos) => {
                                    setContextMenuNode(null);
                                    setContextMenuEdge(edge);
                                    setContextMenuEdgePosition(pos);
                                }}
                                onNodeContextMenu={(node, pos) => {
                                    setContextMenuEdge(null);
                                    setContextMenuNode(node);
                                    setContextMenuPosition(pos);
                                }}
                                onNodeHover={(node, pos) => {
                                    useSelectionStore.getState().setHoveredNode(node?.id ?? null, 'canvas');
                                    if (hoverHideTimerRef.current) {
                                        clearTimeout(hoverHideTimerRef.current);
                                        hoverHideTimerRef.current = null;
                                    }
                                    if (node) {
                                        setHoverNodeData(node);
                                        setHoverNodeBBox(pos?.bbox ?? null);
                                    } else {
                                        // Delay hiding so mouse can travel from node to flanking buttons
                                        hoverHideTimerRef.current = setTimeout(() => {
                                            setHoverNodeData(null);
                                            setHoverNodeBBox(null);
                                        }, 200);
                                    }
                                }}
                                selectedNodeId={selectedNode?.id || null}
                                onViewportChange={() => {
                                    // Re-query bbox for the currently pinned hover node after any pan/zoom/drag.
                                    // Synchronous (no RAF) so the buttons track the node in the SAME frame
                                    // Cytoscape paints. The previous RAF-defer caused a visible 1-frame lag
                                    // where every other element on canvas had finished moving but the
                                    // hover buttons were still catching up. React auto-batches the setState
                                    // across many viewport events, so the perf cost is bounded.
                                    if (!hoverNodeDataRef.current?.id) return;
                                    const pinnedId = hoverNodeDataRef.current.id;
                                    const cy = graphRef.current?.getCy();
                                    if (!cy) return;
                                    const node = cy.getElementById(pinnedId);
                                    if (!node || node.length === 0) return;
                                    const containerRect = cy.container()?.getBoundingClientRect();
                                    if (!containerRect) return;
                                    const renderedBBox = node.renderedBoundingBox();
                                    if (!renderedBBox) return;
                                    setHoverNodeBBox({
                                        x1: containerRect.left + renderedBBox.x1,
                                        y1: containerRect.top + renderedBBox.y1,
                                        x2: containerRect.left + renderedBBox.x2,
                                        y2: containerRect.top + renderedBBox.y2,
                                    });
                                }}
                            />
                        </div>

                        {/* Canvas Tools — always-visible, graph-global controls
                            (not node-scoped). Bottom-center to avoid the minimap
                            + keyboard shortcuts stack (bottom-right) and legend
                            (bottom-left). Per-node actions (Expand, Follow the
                            Money, Focus toggle, FTM report) moved into the Node
                            Inspector → Analysis tab. */}
                        {elements.length > 0 && (() => {
                            const pendingTotal = pendingRemoved + pendingAdded;
                            const hasPending = pendingTotal > 0;
                            const pendingLabel = [
                                pendingAdded > 0 ? `+${pendingAdded} new` : null,
                                pendingRemoved > 0 ? `-${pendingRemoved} removed` : null,
                            ].filter(Boolean).join(' · ');
                            return (
                            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-card/95 backdrop-blur-md border border-border rounded-lg shadow-overlay px-2 py-1.5">
                                <GraphFindBox
                                    elements={elements}
                                    getCy={getCanvasCy}
                                    onSelectNode={handleFindSelectNode}
                                    onSelectEdge={handleCanvasEdgeClick}
                                />
                                <button
                                    type="button"
                                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background ${
                                        hasPending
                                            ? 'border-amber-500/40 bg-amber-500/15 text-amber-800 dark:text-amber-200 hover:bg-amber-500/25'
                                            : 'border-border/60 bg-canvas-surface/80 text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                                    }`}
                                    title={
                                        hasPending
                                            ? `${pendingLabel} since last layout — Recompute removes hidden nodes and re-lays out the full canvas`
                                            : 'Recompute — drop hidden/stashed nodes from layout and re-run ELK for a compact canvas'
                                    }
                                    aria-label={
                                        hasPending
                                            ? `Recompute canvas, ${pendingTotal} pending changes`
                                            : 'Recompute canvas layout'
                                    }
                                    onClick={() => {
                                        graphRef.current?.rearrangeLayout();
                                        usePendingLayoutStore.getState().clear();
                                        toast.info('Canvas re-laid out');
                                    }}
                                    data-tour="canvas-recompute"
                                >
                                    ⊞ Recompute
                                    {hasPending && (
                                        <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-md bg-amber-500/30 text-amber-900 dark:text-amber-100">
                                            {pendingTotal}
                                        </span>
                                    )}
                                </button>
                                {(() => {
                                    const selected = multiSelect.count >= 2 ? multiSelect.selectedIds : null;
                                    const scopeSuffix = selected ? ` (${multiSelect.count})` : '';
                                    const runMode = (mode: RearrangeMode, label: string) => {
                                        // Undo boundary before a user-initiated canvas mutation (gotcha 27).
                                        useGraphStore.getState().pushSnapshot();
                                        const result = graphRef.current?.rearrangeLayout(selected ?? undefined, mode);
                                        // Rearrange of the full canvas commits all pending changes.
                                        // A subset rearrange (user multi-selected) does NOT — other
                                        // pending items elsewhere still warrant a nudge.
                                        if (!selected) usePendingLayoutStore.getState().clear();
                                        const scope = selected ? `${multiSelect.count} selected` : 'canvas';
                                        if (mode === 'smart' && result && 'mode' in result) {
                                            const picked = result.mode;
                                            const reason = result.reason ? ` — ${result.reason}` : '';
                                            toast.info(
                                                `Smart: chose ${picked} for ${scope}${reason}`,
                                                { duration: 3000 }
                                            );
                                        } else {
                                            toast.info(
                                                `Rearranging ${scope} (${label})…`,
                                                { duration: 1500 }
                                            );
                                        }
                                    };
                                    const modeBtn =
                                        'px-2.5 py-1.5 text-[11px] font-medium rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background';
                                    return (
                                        <div
                                            role="group"
                                            aria-label="Rearrange layout"
                                            className="flex items-center gap-0.5 rounded-lg border border-border bg-muted/30 p-0.5"
                                        >
                                            <span className="px-2 py-1.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/70 cursor-default select-none">
                                                ⇄ Rearrange as{scopeSuffix}
                                            </span>
                                            <button
                                                type="button"
                                                className={`${modeBtn} font-semibold text-foreground/90 hover:bg-canvas-surface hover:text-foreground`}
                                                title={`Smart — inspect graph shape (density, hubs, cycles) and pick the best layout automatically${selected ? ' (selected only)' : ''}`}
                                                onClick={() => runMode('smart', 'smart')}
                                            >
                                                Smart
                                            </button>
                                            <button
                                                type="button"
                                                className={`${modeBtn} text-muted-foreground hover:bg-canvas-surface hover:text-foreground`}
                                                title={`Money Flow — layered left-to-right, best for clean directional tx flow${selected ? ' (selected only)' : ''}`}
                                                onClick={() => runMode('flow', 'flow')}
                                            >
                                                Flow
                                            </button>
                                            <button
                                                type="button"
                                                className={`${modeBtn} text-muted-foreground hover:bg-canvas-surface hover:text-foreground`}
                                                title={`Organic — stress-majorization force layout; best for cyclic/dense graphs and fan-in patterns${selected ? ' (selected only)' : ''}`}
                                                onClick={() => runMode('organic', 'organic')}
                                            >
                                                Organic
                                            </button>
                                            <button
                                                type="button"
                                                className={`${modeBtn} text-muted-foreground hover:bg-canvas-surface hover:text-foreground`}
                                                title={`Radial — radial tree; best for hub-and-spoke patterns around a central node${selected ? ' (selected only)' : ''}`}
                                                onClick={() => runMode('radial', 'radial')}
                                            >
                                                Radial
                                            </button>
                                            <button
                                                type="button"
                                                data-testid="wb-layout-circle"
                                                className={`${modeBtn} text-muted-foreground hover:bg-canvas-surface hover:text-foreground`}
                                                title={`Circle — every node on one ring, ordered by address; quick overview of who is on the canvas${selected ? ' (selected only)' : ''}`}
                                                onClick={() => runMode('circle', 'circle')}
                                            >
                                                Circle
                                            </button>
                                            <button
                                                type="button"
                                                data-testid="wb-layout-grid"
                                                className={`${modeBtn} text-muted-foreground hover:bg-canvas-surface hover:text-foreground`}
                                                title={`Grid — nodes in rows and columns, ordered by address; easiest to scan every label${selected ? ' (selected only)' : ''}`}
                                                onClick={() => runMode('grid', 'grid')}
                                            >
                                                Grid
                                            </button>
                                        </div>
                                    );
                                })()}
                            </div>
                            );
                        })()}

                        {/* Node hover expand buttons — flanking ← → arrows */}
                        {hoverNodeData && hoverNodeBBox && (
                            <NodeHoverActions
                                node={hoverNodeData}
                                bbox={hoverNodeBBox}
                                onExpandIn={(nodeId) => {
                                    setHoverNodeData(null);
                                    setSelectedNode(hoverNodeData);
                                    expandNode({ ...hoverNodeData, id: nodeId }, 'in');
                                }}
                                onExpandOut={(nodeId) => {
                                    setHoverNodeData(null);
                                    setSelectedNode(hoverNodeData);
                                    expandNode({ ...hoverNodeData, id: nodeId }, 'out');
                                }}
                                onNodeContextMenu={(node, pos) => {
                                    setContextMenuEdge(null);
                                    setContextMenuNode(node as any);
                                    setContextMenuPosition(pos);
                                }}
                                onMouseEnter={() => {
                                    if (hoverHideTimerRef.current) {
                                        clearTimeout(hoverHideTimerRef.current);
                                        hoverHideTimerRef.current = null;
                                    }
                                }}
                                onMouseLeave={() => {
                                    hoverHideTimerRef.current = setTimeout(() => {
                                        setHoverNodeData(null);
                                        setHoverNodeBBox(null);
                                    }, 100);
                                }}
                            />
                        )}

                        {/* Playback/Timeline moved below graph */}
                    </>
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-muted-foreground" data-tour="canvas-empty">
                        <div className="text-center max-w-sm px-4">
                            <p className="mb-1 text-foreground font-medium">Start an investigation</p>
                            <p className="text-xs mb-4">Paste an address in the Omnibar, or load a training graph first.</p>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                                <button
                                    type="button"
                                    data-tour="canvas-empty-demo"
                                    onClick={() => void loadDemoGraph('entity-taxonomy-showcase')}
                                    className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
                                >
                                    Start with Demo
                                </button>
                                <button
                                    type="button"
                                    onClick={() => navigate('/demos')}
                                    className="px-4 py-2 text-sm font-medium rounded-lg border border-border bg-card text-foreground hover:bg-muted"
                                >
                                    Browse demos
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Legend popup is canvas-relative (upper-left by default) — not Omnibar-relative. */}
                {showLegend && (
                    <GraphLegend onClose={() => setShowLegend(false)} />
                )}

                {/* Shortcuts help stays mounted on empty canvas so ? / F1 / legend CTA work
                    before any nodes are loaded. Graph-summary tray button + minimap only
                    when the graph is non-empty. Stack (top→bottom): summary, shortcuts, minimap. */}
                <div className="absolute bottom-2 right-2 z-20 flex flex-col items-end gap-2">
                    {elements.length > 0 && (
                        <GraphSummaryTrayButton
                            onClick={handleOpenGraphSummary}
                            loading={inlineAiLoading}
                        />
                    )}
                    <KeyboardShortcutsOverlay />
                    {elements.length > 0 && (
                        <GraphMinimap
                            cy={graphRef.current?.getCy() || null}
                            nodeCount={elements.filter(el => !el.data?.source).length}
                            embedded
                        />
                    )}
                </div>

                {/* Context Menu */}
                {contextMenuNode && (() => {
                    // Convergence-node detection: this node is the convergence
                    // of at least one starburst cluster currently on canvas.
                    // Drives the "Re-bundle expanded co-spenders" entry below.
                    const cmNodeId = contextMenuNode.id;
                    const hasBundledClusters = elements.some(el =>
                        el?.data && !el.data.source &&
                        el.data.type === 'cluster' &&
                        el.data.convergence_node_id === cmNodeId
                    );
                    
                    const cy = graphRef.current?.getCy();
                    const selectedNodeIds = cy ? cy.elements('node:selected').map(n => n.id()) : [cmNodeId];
                    const activeNodeIds = selectedNodeIds.includes(cmNodeId) ? selectedNodeIds : [cmNodeId];

                    return (
                    <NodeContextMenu
                        node={contextMenuNode as any}
                        position={contextMenuPosition}
                        chain={chain || undefined}
                        hasBundledClusters={hasBundledClusters}
                        selectedNodeIds={activeNodeIds}
                        onClose={() => setContextMenuNode(null)}
                        onHolisticTrace={() => {
                            const nodeChain = (contextMenuNode as any).chain || chain || 'ETH';
                            useHolisticStore.getState().openModal(nodeChain, activeNodeIds);
                        }}
                        onViewClusterInTransfers={() => openClusterInTransfers(contextMenuNode)}
                        onExpandClusterOnCanvas={() => expandClusterOnCanvas(contextMenuNode)}
                        onCopyClusterTxHash={() => {
                            const h = (contextMenuNode as any)?.tx_hash;
                            if (h) {
                                navigator.clipboard.writeText(h);
                                toast.success('Copied tx hash');
                            }
                        }}
                        onRebundleStarbursts={() => {
                            useGraphStore.getState().pushSnapshot();
                            setElements(prev => bundleStarburstEdges(prev));
                            toast.success('Re-bundled co-spenders');
                        }}
                        onExpand={() => {
                            setSelectedNode(contextMenuNode);
                            expandNode(contextMenuNode);
                        }}
                        onViewDetails={() => {
                            handleNodeClick(contextMenuNode);
                        }}
                        onHide={() => {
                            const nodeId = contextMenuNode.id;
                            // Route through canvasController: stashes node + auto-stashes
                            // orphan edges with the correct reason so filter changes
                            // don't silently restore a user-hidden node.
                            canvasController.stash([nodeId], 'user-hide', 'user');
                            if (selectedNode?.id === nodeId) setSelectedNode(null);
                            toast('Stashed node ' + nodeId.slice(0, 8) + '…', {
                                action: {
                                    label: 'Undo',
                                    onClick: () => canvasController.restore([nodeId]),
                                },
                            });
                        }}
                        onPin={() => {
                            const cy = graphRef.current?.getCy();
                            if (!cy) return;
                            const cyNode = cy.getElementById(contextMenuNode.id);
                            if (!cyNode || cyNode.length === 0) return;

                            const shouldLock = !cyNode.locked();
                            graphRef.current?.setNodeManualLock(contextMenuNode.id, shouldLock);
                            toast.success(shouldLock ? 'Node pinned — position locked' : 'Node unpinned');
                        }}
                        onTraceForward={() => {
                            setSelectedNode(contextMenuNode);
                            const nodeChain = chain || detectChain(contextMenuNode.id);
                            setLoading(true);
                            investigationAnalysisService.forwardTrace(nodeChain, contextMenuNode.id, 3).then(response => {
                                const hops = addTracePathToGraph(response, 'forward', nodeChain);
                                if (hops > 0) {
                                    toast.success(`Forward trace added (${hops} hops)`);
                                } else {
                                    toast.info('No forward path found within search depth.');
                                }
                            }).catch(() => toast.error('Forward trace failed')).finally(() => setLoading(false));
                        }}
                        onTraceBackward={() => {
                            setSelectedNode(contextMenuNode);
                            const nodeChain = chain || detectChain(contextMenuNode.id);
                            setLoading(true);
                            investigationAnalysisService.backwardTrace(nodeChain, contextMenuNode.id, 3).then(response => {
                                const hops = addTracePathToGraph(response, 'backward', nodeChain);
                                if (hops > 0) {
                                    toast.success(`Backward trace added (${hops} hops)`);
                                } else {
                                    toast.info('No backward path found within search depth.');
                                }
                            }).catch(() => toast.error('Backward trace failed')).finally(() => setLoading(false));
                        }}
                        onInvestigate={() => {
                            setSelectedNode(contextMenuNode);
                            handleNodeClick(contextMenuNode);
                            if (selectedNode) setActiveTab('analysis'); else setShowAutoInvestigateCard(true);
                            // Sequential SSE path via AutoInvestigateCard (never parallelize steps).
                            // Preserves canvas seed; card cancels any in-flight run before retargeting.
                            const nodeChain = chain || detectChain(contextMenuNode.id);
                            useInvestigationStore.getState().requestInvestigate(contextMenuNode.id, nodeChain);
                        }}
                        onFlag={() => {
                            const nodeId = contextMenuNode.id;
                            setElements(prev => prev.map(el => {
                                if (el.data?.id !== nodeId || el.data?.source) return el;
                                const existingLabels = normalizeStringArray(el.data.node_labels);
                                const existingTags = normalizeStringArray(el.data.entity_tags);
                                if (existingTags.includes('flagged_for_review')) return el; // already flagged
                                return {
                                    ...el,
                                    data: {
                                        ...el.data,
                                        node_labels: [...existingLabels, '🚩 Flagged'],
                                        entity_tags: [...existingTags, 'flagged_for_review'],
                                        label: formatLabel(el.data.id, el.data.entity_name),
                                    },
                                };
                            }));
                            toast.success('Node flagged for review');
                        }}
                        onMarkSuspicious={async () => {
                            // R-SSOT-7: same canonical flagOps.markSuspicious as the
                            // Inspector + Stash Tray. Previously this right-click path
                            // skipped the flagService persist (the lone inconsistency);
                            // it now persists like every other surface.
                            const res = await markSuspicious(contextMenuNode.id);
                            toast.success(res === 'saved'
                                ? 'Node marked as suspicious (risk → 85%, saved)'
                                : 'Node marked as suspicious (risk → 85%)');
                        }}
                    />
                    );
                })()}

                {contextMenuEdge && (
                    <EdgeContextMenu
                        edge={contextMenuEdge}
                        position={contextMenuEdgePosition}
                        onClose={() => setContextMenuEdge(null)}
                        isSelected={selectedTxs.some(t => t.tx_hash === (contextMenuEdge.tx_hash || contextMenuEdge.id))}
                        onSelect={(edge) => {
                            // Add edge's transaction to selectedTxs — canvas emphasis is handled by the sync useEffect
                            const txHash = edge.tx_hash || edge.id;
                            const alreadySelected = selectedTxs.some(t => t.tx_hash === txHash);
                            if (!alreadySelected) {
                                setSelectedTxs(prev => [...prev, {
                                    tx_hash: txHash,
                                    from_address: edge.source,
                                    to_address: edge.target,
                                    value: typeof edge.value === 'number' ? edge.value : parseFloat(String(edge.label || '0').replace(/[^0-9.]/g, '')) || 0,
                                    timestamp: edge.timestamp || null,
                                    token_symbol: edge.token_symbol || null,
                                    direction: edge.direction || null,
                                    counterparty: edge.source === selectedNode?.id ? edge.target : edge.source,
                                } as any]);
                            }
                        }}
                        onDeselect={(edge) => {
                            const txHash = edge.tx_hash || edge.id;
                            setSelectedTxs(prev => prev.filter(t => t.tx_hash !== txHash));
                        }}
                        onDeemphasize={(edgeId) => graphRef.current?.deemphasizeEdge(edgeId)}
                        onClearEmphasis={(edgeId) => graphRef.current?.clearEdgeEmphasis(edgeId)}
                        onStash={() => {
                            const edgeId = contextMenuEdge.id;
                            canvasController.stash([edgeId], 'user-hide', 'user');
                            toast('Stashed edge', {
                                action: {
                                    label: 'Undo',
                                    onClick: () => canvasController.restore([edgeId]),
                                },
                            });
                        }}
                    />
                )}
                {/* Stash tray subscribes directly to useStashStore. Restore
                    actions go through canvasController so they dedupe, re-
                    attach orphan edges, and push a single undo snapshot. */}
                <StashTray
                    onRestoreAllReason={(reason) => {
                        const before = canvasController.getStashed(reason).length;
                        canvasController.restoreByReason(reason);
                        if (before > 0) toast.success(`Restored ${before} items from stash`);
                    }}
                />
              </div>{/* ── End Grid Column 1 (canvas) ── */}

                {/* ── Grid Column 2: Graph Summary / Inspector / MultiSelect ──
                    Graph Summary wins the slot while open: it is canvas-scoped
                    and is what the user just asked for. "Back to node" hands the
                    slot back to the Node Inspector. */}
                {graphSummaryOpen ? (
                    <GraphSummaryPanel
                        summary={inlineAiSummary}
                        loading={inlineAiLoading}
                        meta={graphSummaryMeta}
                        nodeCount={elements.filter(el => !el.data?.source).length}
                        edgeCount={elements.filter(el => !!el.data?.source).length}
                        onRegenerate={() => { void runGraphSummary(); }}
                        onClose={() => usePanelStore.getState().setGraphSummaryOpen(false)}
                        onBackToNode={selectedNode
                            ? () => usePanelStore.getState().setGraphSummaryOpen(false)
                            : undefined}
                    />
                ) : multiSelect.isMultiSelect ? (
                    <div
                        className="bg-card border-l border-border z-10 flex flex-col transition-opacity w-[450px]"
                        style={{ maxHeight: '100%', overflow: 'hidden' }}
                    >
                        <MultiSelectView
                            selectedNodes={multiSelect.selectedNodes}
                            count={multiSelect.count}
                            isBatchOverLimit={multiSelect.isBatchOverLimit}
                            riskSummary={multiSelect.getRiskSummary()}
                            onClose={() => {
                                multiSelect.clearSelection();
                                const cy = graphRef.current?.getCy();
                                if (cy) cy.nodes().unselect();
                            }}
                            onRemoveNode={(nodeId) => {
                                multiSelect.removeNode(nodeId);
                                const cy = graphRef.current?.getCy();
                                if (cy) cy.getElementById(nodeId).unselect();
                            }}
                            onBatchOFAC={async () => {
                                const nodes = multiSelect.getSelectedArray();
                                const addresses = nodes.map(n => n.id);
                                toast.info(`Screening ${addresses.length} addresses for OFAC sanctions...`);
                                try {
                                    const results = await addressService.batchSanctions(addresses);
                                    // The backend answers { flagged: { [address]: hit }, ... }; see utils/sanctionsBatch.
                                    const hits = sanctionHitsFromBatch(results);
                                    const screened = sanctionBatchTotal(results, addresses.length);
                                    if (hits.length > 0) {
                                        toast.error(`${hits.length} sanctioned address${hits.length > 1 ? 'es' : ''} found!`);
                                        // Flag sanctioned nodes in graph
                                        setElements(prev => prev.map(el => {
                                            const hit = hits.find((h) => h.address === el.data.id);
                                            if (!hit) return el;
                                            return { ...el, data: { ...el.data, flagged: true, entity_category: 'sanctioned', risk_score: 1.0 } };
                                        }));
                                    } else {
                                        toast.success(`All ${screened} addresses clear — no OFAC hits`);
                                    }
                                } catch (e: any) {
                                    toast.error(e?.response?.data?.detail || 'Batch OFAC screening failed');
                                }
                            }}
                            onBatchNotice={() => {
                                setBatchNoticeModalOpen(true);
                            }}
                            onBatchWatchlist={async () => {
                                const nodes = multiSelect.getSelectedArray();
                                let added = 0;
                                let failed = 0;
                                for (const node of nodes) {
                                    try {
                                        await addToWatchlistForCase(
                                            node.id,
                                            node.chain || chain || detectChain(node.id),
                                            node.entity_name || node.id,
                                            caseIdForLoad,
                                        );
                                        added++;
                                    } catch {
                                        failed++;
                                    }
                                }
                                if (failed === 0) {
                                    toast.success(`Added ${added} address${added > 1 ? 'es' : ''} to watchlist`);
                                } else {
                                    toast.warning(`Added ${added}, failed ${failed} of ${nodes.length}`);
                                }
                            }}
                            onBatchLabel={async () => {
                                const nodes = multiSelect.getSelectedArray();
                                const input = await prompt({
                                    title: `Label ${nodes.length} addresses`,
                                    message: 'Enter one or more labels (comma-separated) to apply to all selected addresses.',
                                    placeholder: 'Suspect cluster, Mule network',
                                    confirmText: 'Apply to All',
                                });
                                if (!input) return;
                                const labels = input.split(',').map((l: string) => l.trim()).filter(Boolean);
                                if (labels.length === 0) { toast.error('Please provide at least one label.'); return; }
                                let applied = 0;
                                let failed = 0;
                                for (const node of nodes) {
                                    try {
                                        await entityService.upsertManualLabel({
                                            address: node.id,
                                            chain: node.chain || chain || detectChain(node.id),
                                            label: labels[0],
                                            labels,
                                        });
                                        applied++;
                                    } catch {
                                        failed++;
                                    }
                                }
                                // Update node labels in graph elements
                                setElements(prev => prev.map(el => {
                                    if (!multiSelect.selectedIds.has(el.data.id)) return el;
                                    const existing = Array.isArray(el.data.node_labels) ? el.data.node_labels : [];
                                    const merged = [...new Set([...existing, ...labels])];
                                    return { ...el, data: { ...el.data, node_labels: merged, entity_name: el.data.entity_name || labels[0] } };
                                }));
                                if (failed === 0) {
                                    toast.success(`Label "${labels[0]}" applied to ${applied} addresses`);
                                } else {
                                    toast.warning(`Applied to ${applied}, failed ${failed} of ${nodes.length}`);
                                }
                            }}
                            onBatchStash={(nodeIds) => {
                                if (!nodeIds || nodeIds.length === 0) return;
                                canvasController.stash(nodeIds, 'manual-stash', 'user');
                                multiSelect.clearSelection();
                                toast.success(`Stashed ${nodeIds.length} node${nodeIds.length === 1 ? '' : 's'} to tray`, {
                                    action: {
                                        label: 'Undo',
                                        onClick: () => canvasController.restore(nodeIds),
                                    },
                                });
                            }}
                            onPersistHolisticReport={handlePersistHolisticReport}
                        />
                    </div>
                ) : selectedNode && (
                    <>
                        <NodeInspector
                            selectedNode={selectedNode!}
                            chain={effectiveChain}
                            onClose={() => setSelectedNode(null)}

                            // Overview tab (UPI linking)
                            linkedUPIs={linkedUPIs}
                            upiLinkInput={upiLinkInput}
                            setUpiLinkInput={setUpiLinkInput}
                            handleLinkUPI={handleLinkUPI}
                            handleUnlinkUPI={handleUnlinkUPI}
                            upiLinkLoading={upiLinkLoading}
                            formatCurrency={formatCurrency}

                            // Transfers tab callbacks
                            selectedEdgeTxHash={
                                elements.find(el => el.data?.id === selectedEdgeIds?.[0])?.data?.tx_hash
                                || selectedEdgeIds?.[0]
                                || null
                            }
                            autoExpandTxHash={autoExpandTxHash}
                            caseIdForLoad={caseIdForLoad}
                            loadTransactionContext={loadTransactionContext}
                            setSelectedTxForNotice={setSelectedTxForNotice as any}
                            txStartDate={txStartDate}
                            txEndDate={txEndDate}
                            setTxStartDate={setTxStartDate}
                            setTxEndDate={setTxEndDate}
                            onLoadMore={() => expandNode(selectedNode, undefined, NEXT_TX_BATCH)}
                            onExpandNode={(node) => expandNode(node)}
                            loadingMore={loading}
                            onLoadByCount={loadTransactionsByCount}
                            onLoadByDateRange={loadTransactionsByDateRange}
                            cancelDateRangeRef={cancelDateRangeRef}
                            dateRangeAllLoaded={dateRangeAllLoaded}

                            // Canvas interaction callbacks
                            onAddCounterpartyToCanvas={handleAddCounterpartyToCanvas}
                            onRemoveFromCanvas={handleRemoveFromCanvas}
                            onEntityEnriched={handleEntityEnriched}
                            onContractDetected={handleContractDetected}
                            onTraceAddress={handleTraceAddress}
                            onDexSwapsLoaded={handleDexSwapsLoaded}

                            // Trace path context (computed in Workbench from investigation store)
                            traceContext={traceContext}

                            // Granular actions
                            onRunGranularAction={handleRunGranularAction}

                            // CHAIN-8: re-probe button in OverviewTab
                            onProbeChain={(addr, suggestedChain) => {
                                loadInitialGraph(suggestedChain, addr);
                            }}

                            // Background enrichment status for TxLoadBar
                            enrichmentStatus={enrichmentStatus}

                            // Analysis / Auto-Investigate callbacks for AnalysisTab
                            onRunAnalysis={handleRunAnalysis}
                            macroAiResults={macroAiResults}
                            caseScopedDisabledReason={caseIdForLoad ? null : CASE_REQUIRED_REASON}
                            cytoscapeInstance={graphRef.current?.getCy() || null}
                            onAddElementsToGraph={handleAddTraceToGraph}
                            onPersistReport={handlePersistAutoInvestigateReport}
                            onPersistHolisticReport={handlePersistHolisticReport}
                            savedAutoInvestigateReportFor={savedAutoInvestigateReportFor}
                            onOpenSavedAutoInvestigateReport={handleOpenSavedAutoInvestigateReport}
                            savedReportArtifacts={savedReportArtifacts}
                            dataMode={activeDemoId ? demoDataMode : 'live'}
                            onApplyFilterPreset={handleApplyFilterPreset}

                            // Triage quick actions (relocated from canvas overlay)
                            triageLoading={triageLoading}
                            triageError={triageError}
                            onRefreshTriage={() => {
                                const nodeId = selectedNode?.id;
                                if (!nodeId) return;
                                const nodeChain = normalizeChain(chain || detectChain(nodeId));
                                loadTriageForNode(nodeId, nodeChain);
                            }}
                            onTriageExpandTransactions={() => expandNode()}
                            onTriageSanctionsScreen={() => {
                                if (!selectedNode?.id) return;
                                setComplianceLoading(true);
                                const nodeChain = chain || detectChain(selectedNode.id);
                                addressService.checkSanctions(nodeChain, selectedNode.id).then(result => {
                                    const compliance: ComplianceHit = {
                                        address: selectedNode.id,
                                        sanctioned: !!result.sanctioned,
                                        hit: result.hit || null,
                                    };
                                    setNodeCompliance(compliance);
                                    if (compliance.sanctioned) {
                                        toast.error(`Sanctions hit: ${compliance.hit?.entity || 'Unknown entity'}`);
                                    } else {
                                        toast.success('No sanctions hit for this node');
                                    }
                                }).catch(e => {
                                    console.error('Node compliance check failed', e);
                                    toast.error('Compliance check failed');
                                }).finally(() => {
                                    setComplianceLoading(false);
                                });
                            }}
                            onTriageAutoTrace={handleAutoTrace}

                            // Follow the Money — per-node, lives in Analysis tab
                            onRunFollowMoney={handleAutoTrace}
                            isFollowMoneyRunning={isFollowMoneyRunningForSelected}
                            onOpenFollowMoneyReport={handleOpenFollowMoneyReport}
                            followMoneyMode={followMoneyMode}
                            onToggleFollowMoneyMode={(next) => {
                                setFollowMoneyMode(next);
                                graphRef.current?.toggleFollowMoney(next);
                            }}

                            // Actions tab
                            onNotice={(exchangeName?: string) => {
                                setNoticeExchangeOverride(exchangeName?.trim() || null);
                                setNoticeModalOpen(true);
                            }}
                            onLabel={applyManualLabel}
                            onStash={() => {
                                if (!selectedNode) return;
                                const nodeId = selectedNode.id;
                                canvasController.stash([nodeId], 'manual-stash', 'user');
                                setSelectedNode(null);
                                toast.success('Node stashed', {
                                    action: {
                                        label: 'Undo',
                                        onClick: () => canvasController.restore([nodeId]),
                                    },
                                });
                            }}
                            onMarkSuspicious={async () => {
                                if (!selectedNode) return;
                                // R-SSOT-7: single canonical mark-suspicious action
                                // (flagOps). Stamps manually_flagged + '⚠ Suspicious',
                                // raises risk to 0.85, mirrors onto the selected node,
                                // and persists via flagService — same code path as the
                                // right-click menu and the Stash Tray.
                                const res = await markSuspicious(selectedNode.id, chain || undefined);
                                toast.success(res === 'saved'
                                    ? 'Node flagged as suspicious (saved)'
                                    : 'Node flagged as suspicious (local only)');
                            }}
                            onUnflag={async () => {
                                if (!selectedNode) return;
                                // Strip ALL flag tags + labels in one pass (auto + manual
                                // + review collapse to one toggle) via flagOps.unmarkAll.
                                const res = await unmarkAll(selectedNode.id);
                                toast.success(res === 'saved' ? 'Flag removed' : 'Flag removed (local)');
                            }}
                            onEscalateToSuspicious={async () => {
                                if (!selectedNode) return;
                                // §9j.3 escalation == the canonical mark-suspicious (clean
                                // slate then manually_flagged). Same flagOps action.
                                const res = await markSuspicious(selectedNode.id, chain || undefined);
                                toast.success(res === 'saved'
                                    ? 'Promoted to suspicious (saved)'
                                    : 'Promoted to suspicious (local only)');
                            }}
                            onWatchlist={async () => {
                                if (!selectedNode) return;
                                try {
                                    await addToWatchlistForCase(
                                        selectedNode.id,
                                        chain || detectChain(selectedNode.id),
                                        selectedNode.entity_name || selectedNode.id,
                                        caseIdForLoad,
                                    );
                                    toast.success('Added to watchlist');
                                } catch (e: any) {
                                    toast.error(e?.response?.data?.detail || 'Failed to add to watchlist');
                                }
                            }}
                            onReport={() => exportToCSV()}
                            onOpenAnalyzeCard={() => setShowAnalyzeCard(true)}
                            onCluster={handleCluster}
                        />
                    </>
                )}
            </div>

            {/* Debug API Trace Panel — Ctrl+Shift+D to toggle */}
            {
                debugMode && showTracePanel && lastTrace && (
                    <div className="border-t border-amber-800/50 bg-amber-950/30 px-3 py-2 max-h-[200px] overflow-y-auto text-[11px] font-mono">
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-amber-400 font-bold text-xs">🔧 API Trace — {lastTrace.request_id}</span>
                            <div className="flex gap-2 items-center">
                                <span className="text-amber-300/70">
                                    {lastTrace.total_api_calls} calls | {lastTrace.succeeded}✓ {lastTrace.failed > 0 ? `${lastTrace.failed}✗` : ''} | {lastTrace.elapsed_ms}ms
                                    {lastTrace.retried > 0 && ` | ${lastTrace.retried} retries`}
                                </span>
                                <button onClick={() => setShowTracePanel(false)} className="text-amber-500 hover:text-amber-300 text-xs">✕</button>
                            </div>
                        </div>
                        {lastTrace.calls?.map((call: any, i: number) => (
                            <div key={i} className={`flex gap-2 py-0.5 border-b border-amber-900/30 ${call.ok ? 'text-green-400/80' : 'text-red-400'}`}>
                                <span className="text-amber-500/70 w-4">{i + 1}</span>
                                <span className="w-16 text-amber-300/70">{call.provider}</span>
                                <span className={`w-8 ${call.ok ? 'text-green-400' : 'text-red-400'}`}>{call.status || '—'}</span>
                                <span className="w-12 text-right text-amber-300/50">{call.latency_ms}ms</span>
                                <span className="flex-1 truncate text-muted-foreground">{call.url}</span>
                                {call.retries > 0 && <span className="text-yellow-500 text-[10px]">⟳{call.retries}</span>}
                                {call.api_message && <span className="text-red-400 text-[10px]">{call.api_message}: {call.api_result}</span>}
                                {call.error && <span className="text-red-400 text-[10px]">{call.error}</span>}
                            </div>
                        ))}
                        {lastTrace.errors && lastTrace.errors.length > 0 && (
                            <div className="mt-1 text-red-400 text-[10px]">
                                ⚠ {lastTrace.errors.length} failed call(s) — check provider rate limits or API keys
                            </div>
                        )}
                    </div>
                )
            }

            {/* Debug mode indicator */}
            {
                debugMode && (
                    <div className="absolute top-2 right-2 z-50 bg-amber-900/80 text-amber-300 text-[10px] px-2 py-0.5 rounded font-mono">
                        🔧 DEBUG (Ctrl+Shift+D)
                    </div>
                )
            }

            {/* ── Bottom Panel: Playback & Timeline (collapsible) ── */}
            {elements.length > 0 && (
                <div className="border-t border-border bg-card/50">
                    {/* Toggle strip — always visible, minimal height */}
                    <button
                        type="button"
                        onClick={() => setShowPlayback(!showPlayback)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-1.5 text-xs text-foreground/80 hover:text-foreground hover:bg-muted/50 transition-colors"
                        data-tour="canvas-playback"
                        aria-expanded={showPlayback}
                    >
                        <Film className="w-3.5 h-3.5 text-primary" />
                        <span className="font-medium">{showPlayback ? 'Hide' : 'Show'} Fund Flow Playback</span>
                        {transactions.length > 0 && (
                            <span className="text-muted-foreground">({transactions.length} txs)</span>
                        )}
                    </button>

                    {/* Expanded playback panel */}
                    {showPlayback && (
                        transactions.length > 0 ? (
                            <div className="px-4 py-3 space-y-2">
                                <PlaybackControls
                                    isPlaying={isAnimating}
                                    currentStep={animationStep}
                                    totalSteps={transactions.length}
                                    speed={animationSpeed}
                                    currentTimestamp={transactions[animationStep]?.timestamp}
                                    onPlay={startAnimation}
                                    onPause={pauseAnimation}
                                    onRestart={restartAnimation}
                                    onStepBack={() => stepPlayback(-1)}
                                    onStepForward={() => stepPlayback(1)}
                                    onSeek={seekPlayback}
                                    onSpeedChange={changeAnimationSpeed}
                                />
                                <TimelineBar
                                    transactions={transactions}
                                    currentIndex={animationStep}
                                    onSeek={seekPlayback}
                                />
                            </div>
                        ) : (
                            <div className="px-4 py-2 text-center text-xs text-muted-foreground">
                                Needs canvas edges with on-chain times — expand transfers on a node (Transfers tab) first.
                            </div>
                        )
                    )}
                </div>
            )}
            {/* Auto-Case Notification */}
            {
                autoCaseNotification && (
                    <AutoCaseNotification
                        caseId={autoCaseNotification.caseId}
                        caseName={autoCaseNotification.caseName}
                        address={autoCaseNotification.address}
                        riskScore={autoCaseNotification.riskScore}
                        onClose={() => setAutoCaseNotification(null)}
                    />
                )
            }
            {/* Demo Guide Panel */}
            {
                demoGuideVisible && activeDemoId && demoGraphs[activeDemoId] && (
                    <DemoGuide
                        demo={demoGraphs[activeDemoId]}
                        onClose={() => setDemoGuideVisible(false)}
                        dataMode={demoDataMode}
                        onModeChange={handleDemoModeChange}
                        hasCachedData={demoCachedAvailable.has(activeDemoId)}
                        guestMode={isGuestDemo()}
                    />
                )
            }

            {
                selectedNode && (
                    <NoticeModal
                        isOpen={isNoticeModalOpen}
                        onClose={() => {
                            setNoticeModalOpen(false);
                            setNoticeExchangeOverride(null);
                            setSelectedTxForNotice(null);
                        }}
                        exchangeName={noticeExchangeName}
                        targetAddress={selectedNode.id}
                        chain={chain || 'ETH'}
                        selectedTransactions={noticeTransactions as NoticeTransaction[]}
                        relatedNodes={noticeRelatedNodes}
                        targetTxHash={selectedTxForNotice?.tx_hash || selectedNode.tx_hash}
                        targetAmount={selectedTxForNotice?.value ? `${selectedTxForNotice.value} ${safeStr(selectedTxForNotice?.token_symbol) || ''}`.trim() : undefined}
                        targetDate={selectedTxForNotice?.timestamp ? toSafeDate(selectedTxForNotice.timestamp)?.toISOString().split('T')[0] : undefined}
                        initialCaseContext={caseContext || undefined}
                        targetExtras={noticeTargetExtrasFull}
                        caseId={caseIdForLoad || undefined}
                        investigationId={investigationId || undefined}
                    />
                )
            }

            <BatchNoticeModal
                isOpen={isBatchNoticeModalOpen}
                onClose={() => setBatchNoticeModalOpen(false)}
                selectedNodes={multiSelect.getSelectedArray()}
                caseId={caseIdForLoad || undefined}
                investigationId={investigationId || undefined}
                initialCaseContext={caseContext || undefined}
            />

            {/* Hot Wallet / Omnibus Expansion Guardrail Modal */}
            {hotWalletModalNode && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in">
                    <div className="bg-card text-foreground border border-amber-500/40 rounded-xl max-w-lg w-full p-6 shadow-2xl space-y-4">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-600 dark:text-amber-400 shrink-0">
                                    <AlertTriangle className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                                        Exchange Hot Wallet Detected
                                    </h3>
                                    <p className="text-xs text-muted-foreground font-mono mt-0.5 break-all">
                                        {hotWalletModalNode.entity_name || hotWalletModalNode.label || hotWalletModalNode.id}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setHotWalletModalNode(null);
                                    setPendingExpandAction(null);
                                }}
                                className="text-muted-foreground hover:text-foreground p-1 rounded-lg"
                                aria-label="Close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="text-sm text-foreground/90 space-y-2 bg-muted/40 p-3.5 rounded-lg border border-border">
                            <p>
                                <strong className="text-foreground">On-chain fund tracing terminates here.</strong> This address is an exchange-pooled omnibus wallet (commingled customer funds).
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Expanding this node will load dozens of transactions from unrelated exchange customers, creating a confusing hairball on your canvas.
                            </p>
                            <div className="mt-2 pt-2 border-t border-border text-xs text-foreground font-medium">
                                Recommended action: send a notice to this exchange under Section 91 CrPC / Section 94 BNSS to obtain off-chain KYC, bank details and IP logs.
                            </div>
                        </div>

                        <div className="flex flex-col gap-2 pt-2">
                            <button
                                type="button"
                                onClick={() => {
                                    const targetNode = hotWalletModalNode;
                                    setHotWalletModalNode(null);
                                    setPendingExpandAction(null);
                                    if (targetNode) {
                                        setSelectedNode(targetNode);
                                        setNoticeExchangeOverride(targetNode.entity_name || 'Exchange');
                                    }
                                    setNoticeModalOpen(true);
                                }}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary hover:bg-brand-gold-hover text-primary-foreground font-medium rounded-lg shadow-sm transition-colors text-sm"
                            >
                                <FileText className="w-4 h-4" />
                                <span>Prepare Section 91 CrPC / Section 94 BNSS Notice (Recommended)</span>
                            </button>

                            <div className="flex items-center justify-between gap-2 mt-1">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setHotWalletModalNode(null);
                                        setPendingExpandAction(null);
                                    }}
                                    className="px-4 py-2 bg-secondary hover:bg-secondary/80 text-foreground border border-border rounded-lg text-xs transition-colors"
                                >
                                    Cancel (Keep Canvas Clean)
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        const action = pendingExpandAction;
                                        setHotWalletModalNode(null);
                                        setPendingExpandAction(null);
                                        if (action) action();
                                    }}
                                    className="px-3 py-2 text-muted-foreground hover:text-foreground text-xs underline transition-colors"
                                >
                                    Expand 20 commingled transfers anyway
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <CaseSelectionModal
                isOpen={isCaseModalOpen}
                onClose={() => { setIsCaseModalOpen(false); resolvePendingSave(null); }}
                onSelectCase={handleSelectExistingCaseFromSave}
                onCreateNewCase={handleCreateNewCaseFromSave}
            />

            <CsvImportModal
                isOpen={showCsvImport}
                onClose={() => setShowCsvImport(false)}
                onAddToCanvas={async (addresses: ParsedAddress[]) => {
                    const newElements: CytoscapeElement[] = addresses.map(a => ({
                        data: {
                            id: a.address,
                            label: a.label || formatLabel(a.address),
                            user_label: a.label || undefined,
                            type: 'address' as const,
                            chain: a.chain,
                            _visibility: 1.0,
                        }
                    }));
                    await addNodesToCanvas(newElements, {
                        chain: null, // mixed chains — enrichment auto-detects per-node
                        userAction: true,
                        skipConnectionCheck: addresses.length > 10, // skip for large imports
                    });
                    toast.success(`Added ${addresses.length} addresses to canvas`);
                }}
            />

            {/* Holistic trace confirmation modal — self-hides when modalOpen=false */}
            <HolisticTraceModal onConfirm={runHolisticTrace} />

            {confirmDialog}
            {promptDialog}
        </div >
    );
};
